import { action, computed, makeAutoObservable, ObservableMap, runInAction, transaction } from 'mobx';
import DragStateManager from '../utils/DragStateManager';
import * as THREE from 'three';
import { CutoutType, WallType, ClippingPolygonType, DoorType, DoubleDoorType, WindowType, VoidType, CircleStairsType, RectStairsType, TurnStairsType, SvgType, SmartTableType, BookShelfType, SymbolType, AreaSymbolType, AreaFillSymbolType, isDoorType, isSlidingDoorType, isDoubleDoorType, isWindowType, isVoidType, WallConnectionStart, WallConnectionEnd, WallConnectionMiddle, WallConnectionType, isCircleStairsType, isRectStairsType, isTurnStairsType, isSvgType, isSmartTableType, isBookShelfType, TextType, isTextType, isSquareSymbolType, SquareSymbolType, isCircleSymbolType, isTriangleSymbolType, isPolygonSymbolType, PolygonSymbolType, SingleLineType, isWallType, isSingleLineType, RulerLineType, Ruler3DType, isRulerLineType, isRuler3DType, isAreaSymbolType, isAreaFillSymbolType, RoomType, BackdropImageType, isBackdropImageType, GroupType, isGroupType, FloorplanLightingSettings, WallShapeType, WallCornerKey, WallCornerPersistedMap, PersistedCorner, WallEdgeConnection, FloorplanCatalogDictionary, FloorplanThreeDMaterialDefaults, LineType } from '../types/floorplannerTypes';
import { createContext, useContext } from 'react';
import { ApolloClient, ApolloError, gql } from '@apollo/client';
import { DeleteFloorplanDocument, EditFloorplanDocument, EditFloorplanMutation, FloorplanDocument } from '../gql';
import {
  EditFloorplanWithVersionDocument,
  FloorplanWithSharingDocument,
  FloorplansWithSharingDocument,
} from '../gql/floorplanCollab';
import { checkFloorplannerStoreIntegrity } from './checkFloorplannerStoreIntegrity';
import { calculateArcLength, getArcTangentAtEndpoint, getQuadraticBezierPoint, getQuadraticBezierTangent, arcLengthToT, tToArcLength, getWallArcLength, projectPointOntoArc, transformArcControlPoint } from '../utils/arcUtils';
import { getSymbolInsertGeometry, getSymbolRenderCenterOffset, getSymbolRenderTotalWidth, getSymbolWidthInfo } from '../utils/symbolAttachmentGeometry';
import { computeInnerMeasurementSide } from '../utils/innerMeasurementSide';
// Import wallUpdateManager lazily to avoid circular dependency
import { updateWallConnections } from '../components/FloorPlan/updateWallConnections';
import type { EditorStoreType } from './editorStore';
import { userStore } from './userStore';
import {
  DEFAULT_FLOORPLAN_CURRENCY,
  normalizeCurrency,
  type Currency,
} from '../utils/currency';
import { snapToOtherObjects, snapToAlignmentLines, getSnappingPoint, getSnappingPointFromAlignmentLines, SnappingResult, getSnapCandidates, calculateAlignmentSnap, AlignmentSnapResult, AlignmentSnapState, getEdgeSnappingPositionForDrag, EdgeSnappingPreviewResult, EdgeSnappingSourceOverrides, snapToOtherObjectsEdgeBased as snapToOtherObjectsEdgeBasedFn } from './snapToOtherObjects';

// Lazy accessor to avoid circular dependency with editorStore
let _editorStore: EditorStoreType | null = null;
const getEditorStore = (): EditorStoreType | null => {
  if (!_editorStore) {
    const globalStore = (globalThis as typeof globalThis & { __boniEditorStore?: EditorStoreType }).__boniEditorStore;
    if (globalStore) {
      _editorStore = globalStore;
    }
  }
  return _editorStore;
};
// renderStore -> composeLines -> floorplannerStore creates a circular dependency
// We import renderStore for use throughout the file, but bindFloorplannerStore
// is imported dynamically in the constructor to avoid initialization order issues
import { renderStore } from './renderStore';
import { wallGeometryCache } from '../utils/WallGeometryCache';
import { RECT_CORNER_ORDER, getCornerPoint, populateDefaultCorners } from '../utils/wallCornerHelpers';
import { calculateWallEdgePoint, createEdgeConnection, findNonPrimaryEdgeConnections, getClosestPosition, getWallAngle, getWallAngleAtEndpoint, isEdgeConnectionWithinJoinRange, recomputeEdgeConnectionForCurrentGeometry, selectPrimaryEdgeConnections } from '../utils/edgeConnectionHelpers';
import { getConnectionPoint } from '../utils/connectionGeometry';
import { undoRedoManager, ObjectChange } from './undoRedoManager';
import { toOptionalVector2, toVector2, toVector2Array } from '../utils/vector2Serialization';
import { repairLoadedSvgSymbol } from '../utils/legacySvgSymbolRepair';
import { maybeSimplifySvgPath } from '../utils/simplifySvgMarkup';
import { reconcileLoadedLibraryPrimitive3D } from '../features/floorplan3d/symbols/registry/primitiveSymbol3DConfig';
import { omitProduct3DFootprintSvg } from '../features/floorplan3d/products/product3DAssetHydration';
import {
  createDefaultSmartRectangleTableConfig,
  createSmartTable3DMetadata,
  normalizeSmartTableConfig,
  normalizeSmartTableHeight,
  SMART_ROUND_TABLE_LIBRARY_KEY,
  SMART_ROUND_TABLE_MODEL_KEY,
} from '../features/floorplan3d/symbols/dining/smartTableConfig';
import {
  BOOK_SHELF_LIBRARY_KEY,
  createBookShelf3DMetadata,
  createDefaultBookShelfConfig,
  normalizeBookShelfConfig,
} from '../features/floorplan3d/symbols/livingRoom/bookShelfConfig';
import { getSymbolFootprintSnapPoints } from '../utils/symbolFootprintSnapping';
import {
  defaultLineTypeForElevatedInsert,
  getPlanBaseElevation,
  overheadLineTypeDefaultOnElevationChange,
} from '../utils/planCutPlane';

const logLoadTiming = (
  label: string,
  startedAt: number,
  extra?: Record<string, unknown>,
) => {
  const ms = Math.round(performance.now() - startedAt);
  if (extra) {
    console.log(`[load] ${label} ${ms}ms`, extra);
    return;
  }
  console.log(`[load] ${label} ${ms}ms`);
};

/**
 * Maximum allowed wall length in meters (100m).
 * Any wall exceeding this length will be rejected as invalid.
 */
export const MAX_WALL_LENGTH = 100;

/**
 * Maximum allowed wall width in meters (1m).
 * Any wall exceeding this width will be rejected as invalid.
 */
export const MAX_WALL_WIDTH = 1;

type Vector3Like = THREE.Vector3 | { x?: unknown; y?: unknown; z?: unknown } | null | undefined;

const finiteNumber = (value: unknown, fallback = 0): number => {
  const numeric = Number(value);
  return Number.isFinite(numeric) ? numeric : fallback;
};

const toVector3 = (point: Vector3Like, fallback?: Vector3Like): THREE.Vector3 => {
  const fallbackX = fallback ? finiteNumber(fallback.x) : 0;
  const fallbackY = fallback ? finiteNumber(fallback.y) : 0;
  const fallbackZ = fallback ? finiteNumber(fallback.z) : 0;

  if (point instanceof THREE.Vector3) {
    return point.clone();
  }

  return new THREE.Vector3(
    finiteNumber(point?.x, fallbackX),
    finiteNumber(point?.y, fallbackY),
    finiteNumber(point?.z, fallbackZ),
  );
};

/**
 * Maximum reasonable delta during drag operations in meters (50m).
 * If a single drag operation tries to move a wall by more than this distance,
 * it indicates a bug in the calculation and should be rejected.
 */
export const MAX_DRAG_DELTA = 50;

const createEmptyCatalogDictionary = (): FloorplanCatalogDictionary => ({
  productsById: {},
  surfacesById: {},
  materialsById: {},
});

const normalizeCatalogDictionary = (
  value: unknown,
): FloorplanCatalogDictionary => {
  const dictionary = value as Partial<FloorplanCatalogDictionary> | undefined;
  return {
    productsById: { ...(dictionary?.productsById ?? {}) },
    surfacesById: { ...(dictionary?.surfacesById ?? {}) },
    materialsById: { ...(dictionary?.materialsById ?? {}) },
  };
};

/**
 * Validates that a proposed wall length is within acceptable bounds.
 * @param start The start position of the wall
 * @param end The end position of the wall
 * @returns Object with isValid flag and error message if invalid
 */
export const validateWallDimensions = (start: THREE.Vector2, end: THREE.Vector2, width?: number): { isValid: boolean; error?: string } => {
  const length = start.distanceTo(end);

  if (!Number.isFinite(length)) {
    return {
      isValid: false,
      error: `Invalid wall length: ${length} (NaN or Infinity)`,
    };
  }

  // Minimum wall length: must be at least 0.1m (10cm), or at least as long as the wall width if specified
  const MIN_WALL_LENGTH = 0.1;
  let minRequiredLength = MIN_WALL_LENGTH;
  if (width !== undefined && width > 0) {
    minRequiredLength = Math.max(MIN_WALL_LENGTH, width * 1.1); // Wall must be at least 10% longer than its width
  }

  if (length < minRequiredLength) {
    return {
      isValid: false,
      error: `Wall length ${length.toFixed(4)}m is too short. Minimum required: ${minRequiredLength.toFixed(4)}m${width !== undefined ? ` (wall width: ${width.toFixed(4)}m)` : ''}`,
    };
  }

  if (length > MAX_WALL_LENGTH) {
    return {
      isValid: false,
      error: `Wall length ${length.toFixed(2)}m exceeds maximum of ${MAX_WALL_LENGTH}m`,
    };
  }

  if (width !== undefined) {
    if (!Number.isFinite(width)) {
      return {
        isValid: false,
        error: `Invalid wall width: ${width} (NaN or Infinity)`,
      };
    }
    if (width <= 0) {
      return { isValid: false, error: `Wall width must be greater than 0` };
    }
    if (width > MAX_WALL_WIDTH) {
      return {
        isValid: false,
        error: `Wall width ${width.toFixed(2)}m exceeds maximum of ${MAX_WALL_WIDTH}m`,
      };
    }
  }

  // Also check for unreasonable coordinate values (should be within ±10000m of origin)
  const MAX_COORD = 10000;
  if (Math.abs(start.x) > MAX_COORD || Math.abs(start.y) > MAX_COORD || Math.abs(end.x) > MAX_COORD || Math.abs(end.y) > MAX_COORD) {
    return {
      isValid: false,
      error: `Wall coordinates exceed maximum of ±${MAX_COORD}m: start(${start.x.toFixed(2)}, ${start.y.toFixed(2)}), end(${end.x.toFixed(2)}, ${end.y.toFixed(2)})`,
    };
  }

  return { isValid: true };
};

/**
 * Validates that a drag delta is within reasonable bounds.
 * @param delta The delta vector from drag operation
 * @returns Object with isValid flag and error message if invalid
 */
export const validateDragDelta = (delta: THREE.Vector2): { isValid: boolean; error?: string } => {
  const magnitude = delta.length();

  if (!Number.isFinite(magnitude)) {
    return {
      isValid: false,
      error: `Invalid drag delta: magnitude is ${magnitude} (NaN or Infinity)`,
    };
  }

  if (magnitude > MAX_DRAG_DELTA) {
    return {
      isValid: false,
      error: `Drag delta ${magnitude.toFixed(2)}m exceeds maximum of ${MAX_DRAG_DELTA}m - this indicates a bug in the drag calculation`,
    };
  }

  return { isValid: true };
};

// Utility to debounce function calls
const debounce = (func: any, wait: number) => {
  let timeout: NodeJS.Timeout;
  return (...args: any[]) => {
    clearTimeout(timeout);
    timeout = setTimeout(() => func.apply(this, args), wait);
  };
};

const waitForNextFrame = (): Promise<void> =>
  new Promise((resolve) => {
    if (typeof window !== 'undefined' && typeof window.requestAnimationFrame === 'function') {
      window.requestAnimationFrame(() => resolve());
      return;
    }

    setTimeout(resolve, 0);
  });

const refreshWallGeometry = (wallId: string) => {
  import('../utils/wallUpdateManager').then(({ wallUpdateManager }) => {
    wallUpdateManager.updateWall(wallId, true);
  });
};

const LOCAL_STORAGE_KEY = 'floorplannerStoreState';
/**
 * Soft cap for the browser crash-recovery cache only — not a project-size limit.
 * Typical origin quota is 5–10MB. Chrome counts UTF-16 (~2 bytes/char), so
 * 2.5M characters is about 5MB with a little headroom so we skip instead of
 * throwing QuotaExceededError during load.
 */
const LOCAL_STORAGE_MAX_CHARS = 2_500_000;
/** JSON large enough that opening 3D is likely to kill the tab. */
export const PLAN_JSON_CRITICAL_CHARS = 10_000_000;
const CORNER_EPSILON = 1e-6;

const isLocalStorageQuotaError = (error: unknown): boolean => {
  if (!error || typeof error !== 'object') {
    return false;
  }
  const maybeError = error as { name?: string; code?: number };
  return (
    maybeError.name === 'QuotaExceededError' ||
    maybeError.name === 'NS_ERROR_DOM_QUOTA_REACHED' ||
    maybeError.code === 22 ||
    maybeError.code === 1014
  );
};

const LEGACY_CONNECTION_POINT_TOLERANCE = 1e-3;

// One directed boundary edge of a detected room face. `fromPos`/`toPos` are the
// centerline junction positions in counter-clockwise traversal order; `object`
// is the wall/line it belongs to (`isFull` is false when that wall was split at
// a T-junction and this edge covers only part of it).
type FaceDart = {
  object: WallType | SingleLineType;
  fromPos: THREE.Vector2;
  toPos: THREE.Vector2;
  isFull: boolean;
};
type RoomFillGeometrySource = 'stored' | 'face' | 'objects' | 'empty';
type RoomFillGeometry = {
  vertices: THREE.Vector2[];
  source: RoomFillGeometrySource;
};
const PERSISTED_CORNER_KEYS: WallCornerKey[] = ['topLeft', 'topRight', 'bottomRight', 'bottomLeft'];
const UNDO_REDO_LIMIT = 50;

const FLOORPLAN_REPAIR_MAX_EXTENT = 500;
const FLOORPLAN_REPAIR_TARGET_EXTENT = 40;
const FLOORPLAN_REPAIR_MIN_EXTENT = 0.5;
const FLOORPLAN_REPAIR_TARGET_MIN_EXTENT = 10;
const FLOORPLAN_REPAIR_TRANSLATION_EPSILON = 0.05;
const FLOORPLAN_REPAIR_SCALE_EPSILON = 0.001;
const FLOORPLAN_REPAIR_MIN_DIMENSION_EPSILON = 1e-3;
const MEASUREMENT_SIDE_EPSILON = 1e-4;

interface FloorplanRepairMetrics {
  minX: number;
  maxX: number;
  minY: number;
  maxY: number;
  width: number;
  height: number;
  pointCount: number;
}

export interface FloorplanRepairOptions {
  includeSymbols?: boolean;
  includeMeasurements?: boolean;
  targetCenter?: THREE.Vector2;
}

export type FloorplanRepairResult =
  | {
      applied: true;
      scale: number;
      translation: THREE.Vector2;
      metrics: FloorplanRepairMetrics;
      normalizedObjects: number;
    }
  | {
      applied: false;
      scale: number;
      translation: THREE.Vector2;
      metrics: FloorplanRepairMetrics | null;
      normalizedObjects: number;
      reason: 'EMPTY_SCENE' | 'NO_ADJUSTMENT_REQUIRED';
    };

const scaleNumericProperty = (target: Record<string, any>, key: string, factor: number) => {
  const value = target[key];
  if (typeof value === 'number' && Number.isFinite(value)) {
    target[key] = value * factor;
  }
};

type EdgeSnapPreviewState = {
  snappingPosition: THREE.Vector2;
  edgeConnection: WallEdgeConnection;
};

type PivotAnchorState = {
  primaryAnchor: THREE.Vector2;
  alternateAnchor?: THREE.Vector2;
  currentAnchor: 'primary' | 'alternate';
  initialSideSign: 1 | -1;
  currentSideSign: 1 | -1;
  targetAngle: number;
  lastRelativeSign?: 1 | -1 | 0;
  primaryIsLeft: boolean;
  /** Tracks hysteresis distance for colinear switching to prevent flicker */
  hysteresisDistance?: number;
};

type OutlineExtent = {
  startT: number;
  endT: number;
  length: number;
};

export class FloorplannerStore {
  private roomsFound: Set<string> = new Set<string>();
  private objectIdsInFoundRooms: Set<string> = new Set<string>();
  scene: THREE.Scene | undefined;

  wallsMap: ObservableMap<string, WallType> = new ObservableMap();
  symbolsMap: ObservableMap<string, SymbolType> = new ObservableMap();
  @computed get symbolKeys() {
    return Array.from(this.symbolsMap.keys());
  } // used as a workaround for observable map keys not being reactive
  singleLinesMap: ObservableMap<string, SingleLineType> = new ObservableMap();
  rulerLinesMap: ObservableMap<string, RulerLineType> = new ObservableMap();
  rulers3DMap: ObservableMap<string, Ruler3DType> = new ObservableMap();
  roomsMap: ObservableMap<string, RoomType> = new ObservableMap();
  groupsMap: ObservableMap<string, GroupType> = new ObservableMap();

  /**
   * Total count of user-drawable items on this floorplan. Used by the freemium
   * shape cap: walls + symbols + single lines + ruler lines. Rooms and groups
   * are derived/auto-managed and intentionally excluded so they don't burn
   * cap slots.
   */
  @computed get totalShapeCount(): number {
    return (
      this.wallsMap.size +
      this.symbolsMap.size +
      this.singleLinesMap.size +
      this.rulerLinesMap.size +
      this.rulers3DMap.size
    );
  }

  /**
   * Freemium shape-cap check. Returns true when the user is allowed to add
   * `count` more drawable items to this floorplan. Paid plans return true
   * unconditionally (entitlement limit is null/undefined). Used to gate
   * addSymbol/addWall/addSingleLine/addRulerLine and editorStore.paste.
   */
  canAddShape = (count: number = 1): boolean => {
    const limit = userStore.maxFloorplanShapes;
    if (limit == null) return true;
    return this.totalShapeCount + count <= limit;
  };
  client: ApolloClient<any> | undefined;
  // Tracks the last JSON snapshot successfully saved to the backend.
  // Used to avoid redundant saves when data hasn't materially changed.
  lastSavedJson: string | null = null;
  /**
   * Once a snapshot is known to exceed the localStorage cap, skip toJSON() on
   * later persistStore() calls. Serializing Lionbar-scale plans (~6.7MB) on
   * every post-load persist froze 2D even though the write was then skipped.
   */
  private skipLocalStoragePersistForSize = false;
  /** Loaded/saved JSON character count for the 3D oversized-plan confirm. */
  planJsonCharCount = 0;
  dirty: boolean = false;
  blockDirty: boolean = false;
  id: string = '';
  loadedFloorplanId: string | null = null;
  isSaving: boolean = false;
  initialized: boolean = false;
  /**
   * Monotonic token for in-flight loadFromCloud calls. A newer load (or
   * invalidateCloudLoads) must not let an older fetch apply stale JSON.
   */
  cloudLoadGeneration: number = 0;
  /** True when another collaborator holds the edit lease. */
  lockedByOther: boolean = false;
  /** Display name of the current lease holder (when lockedByOther). */
  editingByName: string | null = null;
  editingById: string | null = null;
  /** True when this client currently holds the edit lease. */
  collabIsMine: boolean = false;
  /** True when share access was revoked while this tab was open. */
  collabAccessLost: boolean = false;
  /**
   * True when the lease is held by this tab but the latest saved version could
   * not be loaded, so editing stays disabled rather than risk saving a stale
   * canvas over another collaborator's work.
   */
  collabReloadFailed: boolean = false;
  /**
   * True once the view-only treatment has been applied for the current lock.
   * Lease heartbeats re-report the same locked state every few seconds, so
   * without this the treatment would clear a viewer's selection over and over
   * while they inspect the plan.
   */
  private lockedViewApplied: boolean = false;
  /**
   * True when a save was rejected as stale and local edits are being held in
   * memory rather than overwritten. Unused under simple take-over (no version
   * tokens); kept so older UI code does not crash if it still reads the flag.
   */
  saveConflict: boolean = false;
  /** Whether the current user owns this floorplan. */
  isOwner: boolean = true;
  /** Whether this floorplan is shared with at least one collaborator (or we are a collaborator). */
  isShared: boolean = false;
  sharedWith: Array<{ id: string; userName?: string | null; email?: string | null }> = [];
  ownerUser: { id: string; userName?: string | null; email?: string | null } | null = null;
  // undoStack/redoStack removed - now using delta-based UndoRedoManager
  boundingBox: THREE.Box3 | null = null;
  enableSpatialOptimization: boolean = true;

  // Default properties
  wallWidth: number = 0.1;
  lineWeight: number = 0.5;
  lineColor: number = 0x000000;
  fillColor: number = 0xffffff; //0x888888;
  hoverColor: number = 0x0000ff;
  wallLineWeight = 1.5;
  singleLineWeight = 1.5;
  rulerLineWeight = 0.5;
  doorWidth = 0.9;
  doorFrameWidth = 0.05;
  doorLineWeight = 1;
  doorBladeThickness = 0.05;
  doubleDoorWidth = this.doorWidth;
  openAngle = Math.PI / 2;
  windowWidth = 0.1;
  windowFrameWidth = 0.05;
  windowLength = 1.2;
  windowLineWeight = 1;
  circleStairsWidth = 0.9;
  rectStairsWidth = 0.9;
  rectStairsHeight = 2 * this.rectStairsWidth;
  turnStairsWidth = 0.9;
  turnStairsFirstFlightSteps = 6;
  turnStairsSecondFlightSteps = 6;
  turnStairsLandingDepth = 0.9;
  symbolHandleSize = 0.09;
  symbolLineWeight = 0.5;
  svgLength = 2;
  svgHeight = 1;
  svgPath = '';
  lockAspectRatio: boolean = true;
  stairStepSize = 0.32;
  textBoxWidth = 1; // Default scale X (1 = no scaling)
  textBoxHeight = 1; // Default scale Y (1 = no scaling)
  fontSize = 0.5;
  fontStyle = 'normal';
  fontWeight = 'normal'; // "normal", or "bold"
  fontFamily = 'sans-serif'; // Default system font
  textAlign: 'left' | 'center' | 'right' = 'left'; // Default left alignment
  lineHeight = 1;
  text = 'Add Text';
  squareWidth = 0.9;
  squareHeight = 0.9;
  triangleHeight = 0.9;
  triangleWidth = 0.9;
  circleWidth = 0.9;
  circleHeight = 0.9;
  circleRadius = 0.9;
  polygonWidth = 0.9;
  polygonHeight = 0.9;
  name: string = 'Untitled';
  updatedAt: string | null = null;
  /**
   * Per-floorplan display currency for catalog price summary / info modal.
   * Catalog unit prices stay SEK; conversion is display-only.
   */
  currency: Currency = DEFAULT_FLOORPLAN_CURRENCY;

  // Backdrop image properties
  backdropImagePath: string = ''; // URL/path to the backdrop image file
  backdropSize: number = 10; // Size in millimeters that corresponds to a portion of the image width
  backdropRatio: number = 1; // What portion of the image width the size represents (0-1)

  lighting: FloorplanLightingSettings = {
    sunEnabled: true,
    sunTimeOfDay: 0.45,
    roomLightsEnabled: false,
    // Match previous ceiling look (wall slot default).
    ceilingColor: 0xd8d0c4,
  };

  catalogDictionary: FloorplanCatalogDictionary = createEmptyCatalogDictionary();
  threeDMaterialDefaults: FloorplanThreeDMaterialDefaults = {};

  objectToRoomsMap: Map<string, Set<string>> = new Map(); // Map to keep track of objects and the rooms they belong to
  objectsNeedingRoomUpdate: Set<string> = new Set(); // Set to keep track of walls whose rooms need to be recalculated
  suppressedAutoAreaFillRoomSignatures: Set<string> = new Set();
  deferRoomUpdates = false; // When true, skip triggering room recalculations (e.g., during construction)
  isInitializing = false; // True during floorplan loading - used to disable UI interactions
  loadingStep = 0; // Current step number during loading (1-based)
  loadingTotalSteps = 8; // Total number of loading steps
  loadingStepDescription = ''; // Human-readable description of current loading step
  processRoomUpdates = debounce(() => {
    this.updateAffectedRooms();
  }, 100); // Debounced method to process room updates

  // Performance optimization: Spatial index for connected walls
  // Maps a grid cell key to wall IDs that have vertices in that cell
  private roomDetectionVertexIndex: Map<string, Set<string>> = new Map();
  // Maps wall ID -> endpoint -> Set of connected wall IDs for O(1) connection lookup
  private roomDetectionConnectionGraph: Map<string, { start: Set<string>; end: Set<string> }> = new Map();
  private roomDetectionIndexVersion: number = 0; // Increments when index needs rebuild
  private lastRoomDetectionIndexVersion: number = -1; // Last version when index was built

  // Deferred connection management system
  wallDragState = new Map<
    string,
    {
      originalConnections: any[];
      originalEdgeConnections?: any[];
      isDragging: boolean;
      isCommitting?: boolean;
      visualSnapping?: any;
      nearbyWallIds?: string[];
      alignmentSnap?: {
        start?: AlignmentSnapState;
        end?: AlignmentSnapState;
      };
      edgeSnapping?: {
        start?: EdgeSnapPreviewState;
        end?: EdgeSnapPreviewState;
      };
      alignmentLocks?: {
        start?: THREE.Vector2;
        end?: THREE.Vector2;
      };
      fixedEndpoints?: {
        start: THREE.Vector2;
        end: THREE.Vector2;
      };
      pivotAnchors?: Record<'start' | 'end', PivotAnchorState>;
    }
  >(); // Track drag state per wall
  deferConnectionOperations = false; // Global flag to defer connection operations
  // Stable measurement side selection for walls to avoid flicker during drag/construction
  measurementSideLocks: Map<string, 'left' | 'right'> = new Map();
  measurementSidePreference: Map<string, 'left' | 'right'> = new Map();

  constructor() {
    makeAutoObservable(this);

    // Set the global reference early so that modules with lazy imports
    // (e.g. outlineSegmentation.ts) can access the store during rehydration.
    // This must happen before rehydrateStore() to avoid circular dependency errors.
    if (typeof globalThis !== 'undefined') {
      (
        globalThis as typeof globalThis & {
          __boniFloorplannerStore?: FloorplannerStore;
        }
      ).__boniFloorplannerStore = this;
    }

    // Defer store initialization to avoid circular dependency issues
    // renderStore -> composeLines -> floorplannerStore creates a cycle
    // Using queueMicrotask ensures this runs after all modules finish initializing
    queueMicrotask(() => {
      import('./renderStore').then(({ bindFloorplannerStore }) => {
        bindFloorplannerStore(this);
        // Restore persisted state after renderStore is bound
        // rehydrateStore uses renderStore, so it must run after binding
        this.rehydrateStore();
      });
    });
  }

  private getFloorplanIdFromLocation = (): string | null => {
    if (typeof window === 'undefined') {
      return null;
    }
    const match = window.location.pathname.match(/\/floorplan\/([^/?#]+)/);
    return match ? match[1] : null;
  };

  private clearPersistedState = () => {
    localStorage.removeItem(LOCAL_STORAGE_KEY);
    localStorage.removeItem(`${LOCAL_STORAGE_KEY}_undoStack`);
    localStorage.removeItem(`${LOCAL_STORAGE_KEY}_redoStack`);
  };

  rehydrateStore = () => {
    // Guard against race condition: if a floorplan has already been loaded from cloud
    // (via initialize -> loadFromCloud), or is currently being initialized,
    // skip rehydration to avoid clearing the undo/redo stacks that were just set up.
    if (this.loadedFloorplanId !== null || this.isInitializing) {
      return;
    }

    this.loadedFloorplanId = null;
    undoRedoManager.reset();

    const urlFloorplanId = this.getFloorplanIdFromLocation();

    // Undo/redo history is intentionally session-only. Clear any persisted stacks from older versions
    // to avoid localStorage quota issues and long parse times on large floorplans.
    localStorage.removeItem(`${LOCAL_STORAGE_KEY}_undoStack`);
    localStorage.removeItem(`${LOCAL_STORAGE_KEY}_redoStack`);

    // If a specific floorplan is requested via URL, skip localStorage rehydration to avoid
    // blocking the main thread; initialize() will load from cloud instead.
    if (urlFloorplanId) {
      return;
    }

    const persistedState = localStorage.getItem(LOCAL_STORAGE_KEY);

    if (persistedState) {
      try {
        const parsedState = JSON.parse(persistedState);
        const persistedFloorplanId: string | null = typeof parsedState?.id === 'string' ? parsedState.id : null;

        const canRestore = !persistedFloorplanId || (persistedFloorplanId === '' && !urlFloorplanId) || (!!urlFloorplanId && urlFloorplanId === persistedFloorplanId);

        if (canRestore) {
          this.fromJSON(persistedState);
          this.loadedFloorplanId = persistedFloorplanId ?? null;
        } else {
          this.clearPersistedState();
          return;
        }
      } catch (error) {
        console.error('Error rehydrating store from local storage', error);
        // Clear the local storage if an error occurs
        this.clearPersistedState();
        return;
      }
    }
  };

  persistStore = (serializedState?: string) => {
    // Skip persistence during active drags to avoid performance issues and quota errors
    if (DragStateManager.isCurrentlyDragging()) {
      return;
    }

    // Avoid another full serialize when we already know this plan will not fit.
    if (!serializedState && this.skipLocalStoragePersistForSize) {
      return;
    }
    if (
      !serializedState &&
      this.lastSavedJson &&
      this.lastSavedJson.length > LOCAL_STORAGE_MAX_CHARS
    ) {
      this.skipLocalStoragePersistForSize = true;
      this.notePersistedJsonSize(this.lastSavedJson.length);
      this.clearPersistedState();
      return;
    }

    const state = serializedState ?? this.toJSON();
    this.notePersistedJsonSize(state.length);
    if (state.length > LOCAL_STORAGE_MAX_CHARS) {
      this.skipLocalStoragePersistForSize = true;
      console.warn(
        `Skipping localStorage persist; floorplan JSON is ${state.length} characters (limit ${LOCAL_STORAGE_MAX_CHARS})`,
      );
      this.clearPersistedState();
      return;
    }

    this.skipLocalStoragePersistForSize = false;

    try {
      const persistStartedAt = performance.now();
      localStorage.setItem(LOCAL_STORAGE_KEY, state);
      // Undo/redo history is session-only; do not persist it.
      localStorage.removeItem(`${LOCAL_STORAGE_KEY}_undoStack`);
      localStorage.removeItem(`${LOCAL_STORAGE_KEY}_redoStack`);
      logLoadTiming('persistStore localStorage write', persistStartedAt, {
        chars: state.length,
      });
    } catch (e) {
      // Preserve in-memory state. Do not retry the same oversized payload — that
      // is what previously threw QuotaExceededError during loadFromCloud.
      if (isLocalStorageQuotaError(e)) {
        console.warn('LocalStorage quota exceeded while persisting floorplan state; skipping local cache');
      } else {
        console.error('Error persisting store to local storage, clearing history', e);
      }
      this.clearPersistedState();
    }
  };

  /**
   * Records the on-disk JSON size used by the 3D oversized-plan confirm.
   * Call with the JSON that was actually loaded or saved.
   */
  notePersistedJsonSize = (length: number) => {
    this.planJsonCharCount = Number.isFinite(length) ? length : 0;
  };

  /** Size band from the loaded/saved JSON for the 3D entry confirm. */
  get planJsonSizeLevel(): "critical" | null {
    if (this.planJsonCharCount > PLAN_JSON_CRITICAL_CHARS) return "critical";
    return null;
  }

  /**
   * Updates the loading step with a description
   * Uses runInAction to ensure state updates are batched efficiently
   * @param step The current step number (1-based)
   * @param description Human-readable description of the current step
   */
  setLoadingStep = (step: number, description: string) => {
    runInAction(() => {
      this.loadingStep = step;
      this.loadingStepDescription = description;
    });
  };

  private hasRenderableContent = (): boolean =>
    this.wallsMap.size > 0 ||
    this.symbolsMap.size > 0 ||
    this.singleLinesMap.size > 0 ||
    this.rulerLinesMap.size > 0 ||
    this.rulers3DMap.size > 0;

  /**
   * Cheap 2D content bounds from store geometry (walls/lines/symbols). Prefer this
   * over scene mesh traversal during load — Lionbar-scale scenes make setFromObject
   * dominate initialize time (~20s) even after walls are already drawn.
   */
  private getContentBoundingBox2D = (): THREE.Box3 | null => {
    let minX = Number.POSITIVE_INFINITY;
    let minY = Number.POSITIVE_INFINITY;
    let maxX = Number.NEGATIVE_INFINITY;
    let maxY = Number.NEGATIVE_INFINITY;
    let pointCount = 0;

    const registerPoint = (point?: THREE.Vector2 | null) => {
      if (!point) return;
      if (!Number.isFinite(point.x) || !Number.isFinite(point.y)) return;
      if (point.x < minX) minX = point.x;
      if (point.y < minY) minY = point.y;
      if (point.x > maxX) maxX = point.x;
      if (point.y > maxY) maxY = point.y;
      pointCount += 1;
    };

    this.wallsMap.forEach((wall) => {
      registerPoint(wall.start);
      registerPoint(wall.end);
      if (wall.controlPoint) {
        registerPoint(wall.controlPoint);
      }
    });
    this.singleLinesMap.forEach((line) => {
      registerPoint(line.start);
      registerPoint(line.end);
      if (line.controlPoint) {
        registerPoint(line.controlPoint);
      }
    });
    this.rulerLinesMap.forEach((line) => {
      registerPoint(line.start);
      registerPoint(line.end);
      if (line.controlPoint) {
        registerPoint(line.controlPoint);
      }
    });
    this.symbolsMap.forEach((symbol) => {
      registerPoint(symbol.position);
      if (isAreaSymbolType(symbol)) {
        symbol.vertices.forEach(registerPoint);
      } else {
        getSymbolFootprintSnapPoints(symbol).forEach(({ position }) => {
          registerPoint(position);
        });
      }
    });

    if (pointCount === 0 || minX === Number.POSITIVE_INFINITY) {
      return null;
    }
    if (minX === maxX && minY === maxY) {
      // Degenerate single-point plan — expand slightly so zoom math has a size.
      minX -= 0.5;
      maxX += 0.5;
      minY -= 0.5;
      maxY += 0.5;
    }

    return new THREE.Box3(
      new THREE.Vector3(minX, minY, 0),
      new THREE.Vector3(maxX, maxY, 0),
    );
  };

  private resetZoomAfterInitialRender = async (): Promise<void> => {
    const editorStore = getEditorStore();
    if (!editorStore) {
      console.log('[load] resetZoom skipped; editorStore not ready');
      return;
    }

    const startedAt = performance.now();
    if (!this.hasRenderableContent()) {
      console.log('[load] resetZoom empty floorplan, fitting origin');
      editorStore.resetZoom({ warnOnInvalidBounds: false });
      logLoadTiming('resetZoom empty', startedAt);
      return;
    }

    // Prefer store geometry — avoids multi-second scene mesh traversals on load.
    const contentBox = this.getContentBoundingBox2D();
    if (contentBox) {
      const stats = editorStore.resetZoom({
        warnOnInvalidBounds: false,
        preferredBox: contentBox,
      });
      if (stats.hasValidBox) {
        logLoadTiming('resetZoom 2D from store bounds', startedAt, {
          size: stats.size,
          center: stats.center,
        });
        return;
      }
    }

    // Short fallback if store bounds failed (should be rare). No 100ms sleeps.
    const maxAttempts = 5;
    for (let attempt = 1; attempt <= maxAttempts; attempt += 1) {
      const attemptStartedAt = performance.now();
      const stats = editorStore.resetZoom({ warnOnInvalidBounds: false });

      if (stats.hasValidBox) {
        logLoadTiming('resetZoom 2D scene fallback success', startedAt, {
          attempt,
          meshes: stats.meshCount,
          validBoxes: stats.validBoxCount,
          attemptMs: Math.round(performance.now() - attemptStartedAt),
        });
        return;
      }

      console.log('[load] resetZoom 2D no valid box yet', {
        attempt,
        hasScene: stats.hasScene,
        meshes: stats.meshCount,
        validBoxes: stats.validBoxCount,
        attemptMs: Math.round(performance.now() - attemptStartedAt),
      });

      await waitForNextFrame();
    }

    console.warn('[floorplannerStore] resetZoom timed out waiting for valid bounding box (content exists but no bbox found)');
    editorStore.resetZoom({ warnOnInvalidBounds: true });
    logLoadTiming('resetZoom 2D timeout', startedAt, { attempts: maxAttempts });
  };

  async initialize(client: ApolloClient<any>, id?: string) {
    this.client = client;
    const initializeStartedAt = performance.now();

    if (id) {
      // Set loading state before starting the load
      runInAction(() => {
        this.isInitializing = true;
        this.loadingStep = 0;
        this.loadingTotalSteps = 8;
      });

      this.setLoadingStep(1, 'Initializing store');
      const cloudLoadStartedAt = performance.now();
      await this.setId(id);
      logLoadTiming('initialize cloud load', cloudLoadStartedAt, { id });

      this.setLoadingStep(7, 'Rebuilding wall geometry');
      const redrawStartedAt = performance.now();
      await this.triggerFullWallRedraw();
      logLoadTiming('initialize 2D wall redraw', redrawStartedAt, {
        walls: this.wallsMap.size,
        symbols: this.symbolsMap.size,
      });

      this.setLoadingStep(8, 'Adjusting view to fit content');
      const zoomStartedAt = performance.now();
      await this.resetZoomAfterInitialRender();
      logLoadTiming('initialize 2D camera fit', zoomStartedAt);

      runInAction(() => {
        this.isInitializing = false;
        this.loadingStep = 0;
        this.loadingStepDescription = '';
      });
      logLoadTiming('initialize total', initializeStartedAt, { id });
    } else {
      this.loadDefaultFloorplan();
    }

    this.initialized = true;
  }

  loadDefaultFloorplan = (filterList?: string) => {
    runInAction(() => {
      if (filterList === 'symbols') {
        this.symbolsMap.clear();
      } else {
        this.wallsMap.clear();
        this.symbolsMap.clear();
        this.singleLinesMap.clear();
        this.rulerLinesMap.clear();
        this.rulers3DMap.clear();
        this.roomsMap.clear();
        this.objectToRoomsMap.clear();
        this.id = '';
        this.loadedFloorplanId = null;
      }
      this.name = 'Untitled';
      this.updatedAt = null;
      this.currency = DEFAULT_FLOORPLAN_CURRENCY;
      this.dirty = true;
      this.blockDirty = false;
      this.isOwner = true;
      this.isShared = false;
      this.sharedWith = [];
      this.ownerUser = null;
      this.clearCollabLock();
      this.integrityCheck();

      this.persistStore();
      this.resetUndoRedoStacks();
      this.saveToCloud(false, true);
    });
  };

  integrityCheck = (options: { resetCameraAfterRedraw?: boolean } = {}) => {
    checkFloorplannerStoreIntegrity(this, options);
  };

  generateId = () => {
    return Math.random().toString(36).substr(2, 9);
  };

  setId = async (id: string) => {
    if (this.loadedFloorplanId !== id) {
      this.loadedFloorplanId = null;
      undoRedoManager.reset();
    }
    this.id = id;
    // Load the floorplan from the cloud
    await this.loadFromCloud(id);
  };

  setDirty = (flag?: boolean) => {
    // Respect blockDirty: ignore attempts to mark dirty while blocked,
    // but always allow clearing the dirty flag (flag === false)
    if (this.blockDirty && flag !== false) {
      return;
    }
    // Locked / access-revoked clients are read-only — do not accumulate dirty
    // state that would later overwrite the lease holder.
    if ((this.lockedByOther || this.collabAccessLost) && flag !== false) {
      return;
    }
    this.dirty = flag === undefined ? true : flag;
  };

  setBlockDirty = (block: boolean) => {
    this.blockDirty = block;
  };

  /**
   * Admin-only: simplify oversized SVG data-URL footprints in place and mark
   * the plan dirty so autosave persists the smaller JSON. Used on a production
   * copy before sharing the light plan with a customer.
   * Enforced here (not only in the Toolbar) so console callers cannot bypass.
   */
  optimiseSvgPerformance = (): {
    changedCount: number;
    beforeChars: number;
    afterChars: number;
  } => {
    const beforeChars = this.toJSON().length;
    const denied = { changedCount: 0, beforeChars, afterChars: beforeChars };
    if (!userStore.isAdmin || this.lockedByOther || this.collabAccessLost) {
      return denied;
    }

    let changedCount = 0;

    this.symbolsMap.forEach((symbol, id) => {
      if (!isSvgType(symbol)) {
        return;
      }
      let next: SvgType = symbol;
      let changed = false;

      if (typeof next.svgPath === 'string' && next.svgPath) {
        const simplified = maybeSimplifySvgPath(next.svgPath);
        if (simplified.changed) {
          next = { ...next, svgPath: simplified.path };
          changed = true;
        }
      }

      const footprintSvgPath = next.threeD?.userModel?.footprintSvgPath;
      if (typeof footprintSvgPath === 'string' && footprintSvgPath && next.threeD?.userModel) {
        const simplifiedFootprint = maybeSimplifySvgPath(footprintSvgPath);
        if (simplifiedFootprint.changed) {
          next = {
            ...next,
            threeD: {
              ...next.threeD,
              userModel: {
                ...next.threeD.userModel,
                footprintSvgPath: simplifiedFootprint.path,
              },
            },
          };
          changed = true;
        }
      }

      if (changed) {
        this.symbolsMap.set(id, next);
        changedCount += 1;
      }
    });

    const afterChars = this.toJSON().length;
    if (changedCount > 0) {
      this.notePersistedJsonSize(afterChars);
      this.setDirty();
    }
    return { changedCount, beforeChars, afterChars };
  };

  setCollabLock = (state: {
    lockedByOther: boolean;
    editingByName: string | null;
    editingById: string | null;
    isMine: boolean;
  }) => {
    this.lockedByOther = state.lockedByOther;
    this.editingByName = state.editingByName;
    this.editingById = state.editingById;
    this.collabIsMine = state.isMine;
    if (state.isMine || !state.lockedByOther) {
      this.collabAccessLost = false;
      this.collabReloadFailed = false;
    }
    if (state.lockedByOther) {
      this.enableLockedViewNavigation();
    } else {
      this.lockedViewApplied = false;
    }
  };

  clearCollabLock = () => {
    this.lockedByOther = false;
    this.editingByName = null;
    this.editingById = null;
    this.collabIsMine = false;
    this.collabAccessLost = false;
    this.collabReloadFailed = false;
    this.lockedViewApplied = false;
  };

  /**
   * Adopt a server `updatedAt` observed from any mutation or query.
   * Moves forward only — image-only writes and JSON saves can race.
   */
  noteServerUpdatedAt = (updatedAt: string | null | undefined) => {
    if (!updatedAt) return;
    if (
      this.updatedAt &&
      new Date(updatedAt).getTime() <= new Date(this.updatedAt).getTime()
    ) {
      return;
    }
    this.updatedAt = updatedAt;
  };

  /**
   * Persist display currency for this floorplan (optimistic local update).
   * Does not rewrite plan JSON — only the Floorplan.currency field.
   */
  setCurrency = async (nextCurrency: string): Promise<void> => {
    const currency = normalizeCurrency(nextCurrency);
    if (currency === this.currency) return;
    if (this.lockedByOther || this.collabAccessLost) return;

    const previous = this.currency;
    runInAction(() => {
      this.currency = currency;
    });

    if (!this.id) {
      return;
    }

    try {
      if (!this.client) {
        const editorClient = getEditorStore()?.client;
        if (editorClient) {
          this.client = editorClient;
        }
      }
      if (!this.client) {
        throw new Error('Apollo client not initialized');
      }

      const result = await this.client.mutate({
        mutation: EditFloorplanDocument,
        variables: {
          id: this.id,
          currency,
        },
      });
      const saved = result?.data?.editFloorplan as
        | { currency?: string | null; updatedAt?: string | null }
        | undefined;
      runInAction(() => {
        if (saved?.currency) {
          this.currency = normalizeCurrency(saved.currency);
        }
        this.noteServerUpdatedAt(saved?.updatedAt);
      });
    } catch (error) {
      runInAction(() => {
        this.currency = previous;
      });
      console.error('Failed to update floorplan currency', error);
      throw error;
    }
  };

  setCollabAccessLost = () => {
    this.collabAccessLost = true;
    this.lockedByOther = true;
    this.collabIsMine = false;
    this.editingByName = null;
    this.editingById = null;
    this.enableLockedViewNavigation();
  };

  /**
   * Hold the canvas in view-only after a lease hand-off could not load the
   * latest saved JSON. Reuses lockedByOther so every existing read-only guard
   * (content mutators, setDirty, saveToCloud, 3D selection) applies without
   * further changes.
   */
  setCollabReloadFailed = () => {
    this.collabReloadFailed = true;
    this.lockedByOther = true;
    this.collabIsMine = false;
    this.enableLockedViewNavigation();
  };

  /**
   * When lease-locked, allow pan/orbit instead of freezing the canvas.
   *
   * Runs once per lock. The editor store may not exist yet on the first call,
   * in which case the next heartbeat retries.
   */
  private enableLockedViewNavigation = () => {
    const editor = getEditorStore();
    if (!editor) return;
    if (this.lockedViewApplied) return;
    this.lockedViewApplied = true;
    // Drawing tools stay armed across a lease hand-off otherwise, and their
    // pointer/undo paths mutate the plan behind the read-only guards.
    editor.exitConstructionModes();
    editor.clearSelections();
    editor.panning = !editor.view3D;
  };

  setSharedWith = (
    members: Array<{ id: string; userName?: string | null; email?: string | null }>,
  ) => {
    this.sharedWith = members;
    this.isShared = members.length > 0 || this.isOwner === false;
  };

  applySharingMetadata = (floorplan: {
    isOwner?: boolean | null;
    sharedWith?: Array<{ id: string; userName?: string | null; email?: string | null }> | null;
    user?: { id: string; userName?: string | null; email?: string | null } | null;
  }) => {
    this.isOwner = floorplan.isOwner !== false;
    this.sharedWith = floorplan.sharedWith ? [...floorplan.sharedWith] : [];
    this.ownerUser = floorplan.user ?? null;
    this.isShared = this.sharedWith.length > 0 || this.isOwner === false;
  };


  updateLighting = (updates: Partial<FloorplanLightingSettings>) => {
    const nextCeilingColor =
      typeof updates.ceilingColor === "number" &&
      Number.isFinite(updates.ceilingColor)
        ? updates.ceilingColor >>> 0
        : this.lighting.ceilingColor;
    this.lighting = {
      ...this.lighting,
      ...updates,
      sunTimeOfDay: THREE.MathUtils.clamp(updates.sunTimeOfDay ?? this.lighting.sunTimeOfDay, 0, 1),
      ceilingColor: nextCeilingColor,
    };
    this.setDirty();
  };

  setDeferRoomUpdates = (defer: boolean, options: { processPending?: boolean } = {}) => {
    const { processPending = true } = options;
    this.deferRoomUpdates = defer;
    // If we're turning deferral off and have pending work, process it now (debounced)
    if (processPending && !defer && this.objectsNeedingRoomUpdate.size > 0) {
      this.processRoomUpdates();
    }
  };

  pushToUndoStack = (options?: { skipPersist?: boolean }) => {
    // End the current transaction in the delta-based undo manager
    // This captures only the objects that were changed, not the entire state
    undoRedoManager.endTransaction();
    if (!options?.skipPersist) {
      this.persistStore();
    }
  };

  /**
   * Begin tracking changes for undo. Call this BEFORE making modifications.
   * All changes until pushToUndoStack() will be grouped as one undo entry.
   */
  beginUndoTransaction = (description?: string) => {
    undoRedoManager.beginTransaction(description);
  };

  /**
   * Cancel the current undo transaction without saving changes.
   * Use this when an interaction ends without any meaningful edits.
   */
  cancelUndoTransaction = () => {
    undoRedoManager.cancelTransaction();
  };

  /**
   * Record that a wall is about to be modified.
   * Call this BEFORE making changes to capture the previous state.
   */
  recordWallForUndo = (wallId: string) => {
    const wall = this.wallsMap.get(wallId);
    undoRedoManager.recordObjectState('wall', wallId, wall ?? null);
  };

  /**
   * Record that a symbol is about to be modified.
   */
  recordSymbolForUndo = (symbolId: string) => {
    const symbol = this.symbolsMap.get(symbolId);
    undoRedoManager.recordObjectState('symbol', symbolId, symbol ?? null);
  };

  /**
   * Record that a single line is about to be modified.
   */
  recordLineForUndo = (lineId: string) => {
    const line = this.singleLinesMap.get(lineId);
    undoRedoManager.recordObjectState('singleLine', lineId, line ?? null);
  };

  /**
   * Record that a ruler line is about to be modified.
   */
  recordRulerForUndo = (rulerId: string) => {
    const ruler = this.rulerLinesMap.get(rulerId);
    undoRedoManager.recordObjectState('rulerLine', rulerId, ruler ?? null);
  };

  /**
   * Record that a 3D ruler is about to be modified.
   */
  recordRuler3DForUndo = (rulerId: string) => {
    const ruler = this.rulers3DMap.get(rulerId);
    undoRedoManager.recordObjectState('ruler3D', rulerId, ruler ?? null);
  };

  /**
   * Record that a room is about to be modified.
   */
  recordRoomForUndo = (roomId: string) => {
    const room = this.roomsMap.get(roomId);
    undoRedoManager.recordObjectState('room', roomId, room ?? null);
  };

  /**
   * Record that a group is about to be modified.
   */
  recordGroupForUndo = (groupId: string) => {
    const group = this.groupsMap.get(groupId);
    undoRedoManager.recordObjectState('group', groupId, group ?? null);
  };

  /**
   * Record any object for undo by automatically detecting its type.
   */
  recordObjectForUndo = (objectId: string) => {
    if (this.wallsMap.has(objectId)) {
      this.recordWallForUndo(objectId);
    } else if (this.symbolsMap.has(objectId)) {
      this.recordSymbolForUndo(objectId);
    } else if (this.singleLinesMap.has(objectId)) {
      this.recordLineForUndo(objectId);
    } else if (this.rulerLinesMap.has(objectId)) {
      this.recordRulerForUndo(objectId);
    } else if (this.rulers3DMap.has(objectId)) {
      this.recordRuler3DForUndo(objectId);
    } else if (this.roomsMap.has(objectId)) {
      this.recordRoomForUndo(objectId);
    } else if (this.groupsMap.has(objectId)) {
      this.recordGroupForUndo(objectId);
    }
  };

  /**
   * Record the final state of a wall after modification.
   */
  recordWallChange = (wallId: string, type: 'add' | 'modify' | 'delete') => {
    const wall = this.wallsMap.get(wallId);
    undoRedoManager.recordChange(type, 'wall', wallId, wall ?? null);
  };

  /**
   * Record the final state of a symbol after modification.
   */
  recordSymbolChange = (symbolId: string, type: 'add' | 'modify' | 'delete') => {
    const symbol = this.symbolsMap.get(symbolId);
    undoRedoManager.recordChange(type, 'symbol', symbolId, symbol ?? null);
  };

  /**
   * Record the final state of a line after modification.
   */
  recordLineChange = (lineId: string, type: 'add' | 'modify' | 'delete') => {
    const line = this.singleLinesMap.get(lineId);
    undoRedoManager.recordChange(type, 'singleLine', lineId, line ?? null);
  };

  /**
   * Record the final state of a ruler after modification.
   */
  recordRulerChange = (rulerId: string, type: 'add' | 'modify' | 'delete') => {
    const ruler = this.rulerLinesMap.get(rulerId);
    undoRedoManager.recordChange(type, 'rulerLine', rulerId, ruler ?? null);
  };

  /**
   * Record the final state of a 3D ruler after modification.
   */
  recordRuler3DChange = (rulerId: string, type: 'add' | 'modify' | 'delete') => {
    const ruler = this.rulers3DMap.get(rulerId);
    undoRedoManager.recordChange(type, 'ruler3D', rulerId, ruler ?? null);
  };

  /**
   * Record the final state of a room after modification.
   */
  recordRoomChange = (roomId: string, type: 'add' | 'modify' | 'delete') => {
    const room = this.roomsMap.get(roomId);
    undoRedoManager.recordChange(type, 'room', roomId, room ?? null);
  };

  /**
   * Record the final state of a group after modification.
   */
  recordGroupChange = (groupId: string, type: 'add' | 'modify' | 'delete') => {
    const group = this.groupsMap.get(groupId);
    undoRedoManager.recordChange(type, 'group', groupId, group ?? null);
  };

  /**
   * Record the final state of any object by automatically detecting its type.
   */
  recordObjectChange = (objectId: string, type: 'add' | 'modify' | 'delete') => {
    if (this.wallsMap.has(objectId) || type === 'delete') {
      // For walls, always try to record (for deletions the object may already be gone)
      this.recordWallChange(objectId, type);
    }
    if (this.symbolsMap.has(objectId)) {
      this.recordSymbolChange(objectId, type);
    }
    if (this.singleLinesMap.has(objectId)) {
      this.recordLineChange(objectId, type);
    }
    if (this.rulerLinesMap.has(objectId)) {
      this.recordRulerChange(objectId, type);
    }
    if (this.rulers3DMap.has(objectId)) {
      this.recordRuler3DChange(objectId, type);
    }
    if (this.roomsMap.has(objectId)) {
      this.recordRoomChange(objectId, type);
    }
    if (this.groupsMap.has(objectId)) {
      this.recordGroupChange(objectId, type);
    }
  };

  /**
   * Apply a single change from the undo/redo manager.
   * This is called for each changed object during undo/redo.
   */
  private applyUndoRedoChange = (change: ObjectChange, isUndo: boolean) => {
    const stateToApply = isUndo ? change.previousState : change.newState;

    if (change.type === 'delete' && isUndo) {
      // Undo a deletion = restore the object
      if (stateToApply) {
        this.restoreObjectFromJSON(change.objectType, change.objectId, stateToApply);
      }
    } else if (change.type === 'add' && isUndo) {
      // Undo an addition = delete the object
      this.deleteObjectById(change.objectType, change.objectId);
    } else if (change.type === 'modify') {
      // Apply the state (previous for undo, new for redo)
      if (stateToApply) {
        this.restoreObjectFromJSON(change.objectType, change.objectId, stateToApply);
      }
    } else if (change.type === 'delete' && !isUndo) {
      // Redo a deletion = delete the object
      this.deleteObjectById(change.objectType, change.objectId);
    } else if (change.type === 'add' && !isUndo) {
      // Redo an addition = restore the object
      if (stateToApply) {
        this.restoreObjectFromJSON(change.objectType, change.objectId, stateToApply);
      }
    }
  };

  /**
   * Restore a single object from its serialized JSON state.
   */
  private restoreObjectFromJSON = (objectType: 'wall' | 'symbol' | 'singleLine' | 'rulerLine' | 'ruler3D' | 'room' | 'group', objectId: string, jsonState: string) => {
    const parsed = JSON.parse(jsonState);

    switch (objectType) {
      case 'wall': {
        const wall: WallType = {
          ...parsed,
          start: new THREE.Vector2(parsed.start?.x ?? 0, parsed.start?.y ?? 0),
          end: new THREE.Vector2(parsed.end?.x ?? 0, parsed.end?.y ?? 0),
          controlPoint: parsed.controlPoint ? new THREE.Vector2(parsed.controlPoint.x, parsed.controlPoint.y) : undefined,
          cutouts:
            parsed.cutouts?.map((c: any) => ({
              ...c,
              appliesTo: new Set(c.appliesTo || []),
            })) || [],
          edgeConnections: parsed.edgeConnections?.map((conn: any) => ({
            ...conn,
            sourceEdgePoint: new THREE.Vector2(conn.sourceEdgePoint?.x ?? 0, conn.sourceEdgePoint?.y ?? 0),
            targetEdgePoint: new THREE.Vector2(conn.targetEdgePoint?.x ?? 0, conn.targetEdgePoint?.y ?? 0),
          })),
        };
        this.wallsMap.set(objectId, wall);
        this.refreshSymbolAttachmentsForWall(objectId);
        break;
      }
      case 'symbol': {
        const symbol: SymbolType = {
          ...parsed,
          position: toVector2(parsed.position),
        };
        if ((parsed.type === 'area' || parsed.type === 'areaFill') && Array.isArray(parsed.vertices)) {
          (symbol as AreaSymbolType | AreaFillSymbolType).vertices = toVector2Array(parsed.vertices);
        }
        this.symbolsMap.set(objectId, symbol);
        break;
      }
      case 'singleLine': {
        const line: SingleLineType = {
          ...parsed,
          start: new THREE.Vector2(parsed.start?.x ?? 0, parsed.start?.y ?? 0),
          end: new THREE.Vector2(parsed.end?.x ?? 0, parsed.end?.y ?? 0),
          controlPoint: parsed.controlPoint ? new THREE.Vector2(parsed.controlPoint.x, parsed.controlPoint.y) : undefined,
          zIndex: parsed.zIndex,
        };
        this.singleLinesMap.set(objectId, line);
        break;
      }
      case 'rulerLine': {
        const ruler: RulerLineType = {
          ...parsed,
          start: new THREE.Vector2(parsed.start?.x ?? 0, parsed.start?.y ?? 0),
          end: new THREE.Vector2(parsed.end?.x ?? 0, parsed.end?.y ?? 0),
          controlPoint: parsed.controlPoint ? new THREE.Vector2(parsed.controlPoint.x, parsed.controlPoint.y) : undefined,
        };
        this.rulerLinesMap.set(objectId, ruler);
        break;
      }
      case 'ruler3D': {
        const ruler: Ruler3DType = {
          ...parsed,
          id: objectId,
          type: 'ruler3D',
          measurementMode: parsed.measurementMode === 'free' ? 'free' : 'vertical',
          start: toVector3(parsed.start),
          end: toVector3(parsed.end, {
            x: parsed.start?.x ?? 0,
            y: parsed.start?.y ?? 0,
            z: 2.4,
          }),
        };
        this.rulers3DMap.set(objectId, ruler);
        break;
      }
      case 'room': {
        const room: RoomType = {
          ...parsed,
          centroid: new THREE.Vector2(parsed.centroid?.x ?? 0, parsed.centroid?.y ?? 0),
          vertices: Array.isArray(parsed.vertices) ? toVector2Array(parsed.vertices) : undefined,
        };
        this.roomsMap.set(objectId, room);
        break;
      }
      case 'group': {
        this.groupsMap.set(objectId, parsed as GroupType);
        break;
      }
    }
  };

  /**
   * Delete an object by type and ID.
   */
  private deleteObjectById = (objectType: 'wall' | 'symbol' | 'singleLine' | 'rulerLine' | 'ruler3D' | 'room' | 'group', objectId: string) => {
    switch (objectType) {
      case 'wall':
        this.wallsMap.delete(objectId);
        renderStore.clearWallShapes(objectId);
        getEditorStore()?.setWallsHiddenIn3D([objectId], false);
        break;
      case 'symbol':
        this.symbolsMap.delete(objectId);
        break;
      case 'singleLine':
        this.singleLinesMap.delete(objectId);
        break;
      case 'rulerLine':
        this.rulerLinesMap.delete(objectId);
        break;
      case 'ruler3D':
        this.rulers3DMap.delete(objectId);
        break;
      case 'room':
        this.roomsMap.delete(objectId);
        break;
      case 'group':
        this.groupsMap.delete(objectId);
        break;
    }
  };

  undo = () => {
    // Another collaborator holds the edit lease, so this view is read-only.
    if (this.lockedByOther) return;
    if (!undoRedoManager.canUndo) {
      return;
    }

    // Apply changes and get list of affected object IDs
    const affectedIds = undoRedoManager.undo(this.applyUndoRedoChange);

    if (affectedIds.length > 0) {
      // Wall/line topology may have changed; rebuild rooms and rematch floor fills.
      // Area fills restored by undo are rematched by room signature here.
      this.objectsNeedingRoomUpdate.clear();
      this.recomputeAllRooms();

      this.persistStore();
      this.setDirty();

      // Only update geometry for affected walls - FAST!
      requestAnimationFrame(() => {
        affectedIds.forEach((id) => {
          const wall = this.wallsMap.get(id);
          if (wall) {
            renderStore.clearWallShapes(id);
            this.objectNeedsUpdate(id);
          }
        });
      });
    }
  };

  redo = () => {
    // Another collaborator holds the edit lease, so this view is read-only.
    if (this.lockedByOther) return;
    if (!undoRedoManager.canRedo) {
      return;
    }

    // Apply changes and get list of affected object IDs
    const affectedIds = undoRedoManager.redo(this.applyUndoRedoChange);

    if (affectedIds.length > 0) {
      this.objectsNeedingRoomUpdate.clear();
      this.recomputeAllRooms();

      this.persistStore();
      this.setDirty();

      // Only update geometry for affected walls - FAST!
      requestAnimationFrame(() => {
        affectedIds.forEach((id) => {
          const wall = this.wallsMap.get(id);
          if (wall) {
            renderStore.clearWallShapes(id);
            this.objectNeedsUpdate(id);
          }
        });
      });
    }
  };

  resetUndoRedoStacks = (options?: { skipPersist?: boolean }) => {
    undoRedoManager.reset();
    if (!options?.skipPersist) {
      this.persistStore();
    }
  };

  /**
   * Recenters and rescales misplaced geometry so that all walls and symbols become visible again.
   * This walks every drawable object, computes a bounding box, and applies a uniform transform
   * that (a) brings the plan back to the current camera center and (b) clamps the overall size
   * into a sane range so tiny or gigantic layouts become viewable.
   */
  repairFloorplanLayout = (options?: FloorplanRepairOptions): FloorplanRepairResult => {
    const includeSymbols = options?.includeSymbols ?? true;
    const includeMeasurements = options?.includeMeasurements ?? true;

    const walls = Array.from(this.wallsMap.values());
    const singleLines = includeMeasurements ? Array.from(this.singleLinesMap.values()) : [];
    const rulerLines = includeMeasurements ? Array.from(this.rulerLinesMap.values()) : [];
    const symbols = includeSymbols ? Array.from(this.symbolsMap.values()) : [];

    const metrics = this.calculateRepairMetrics(walls, singleLines, rulerLines, symbols);
    const zeroVector = new THREE.Vector2(0, 0);

    if (!metrics) {
      return {
        applied: false,
        scale: 1,
        translation: zeroVector,
        metrics: null,
        normalizedObjects: 0,
        reason: 'EMPTY_SCENE',
      };
    }

    const layoutCenter = new THREE.Vector2((metrics.minX + metrics.maxX) / 2, (metrics.minY + metrics.maxY) / 2);

    const [visibleCenterX, visibleCenterY] = getEditorStore()?.getVisibleCenter() ?? [0, 0];
    const targetCenter = options?.targetCenter ? options.targetCenter.clone() : new THREE.Vector2(visibleCenterX, visibleCenterY);
    const translation = targetCenter.clone().sub(layoutCenter);

    const largestDimension = Math.max(metrics.width, metrics.height);
    const safeLargest = Math.max(largestDimension, FLOORPLAN_REPAIR_MIN_DIMENSION_EPSILON);
    let scaleFactor = 1;

    if (largestDimension > FLOORPLAN_REPAIR_MAX_EXTENT) {
      scaleFactor = Math.min(scaleFactor, FLOORPLAN_REPAIR_TARGET_EXTENT / safeLargest);
    } else if (largestDimension < FLOORPLAN_REPAIR_MIN_EXTENT) {
      scaleFactor = Math.max(scaleFactor, FLOORPLAN_REPAIR_TARGET_MIN_EXTENT / safeLargest);
    }

    if (!Number.isFinite(scaleFactor) || scaleFactor <= 0) {
      scaleFactor = 1;
    }

    const scaleNeeded = Math.abs(scaleFactor - 1) > FLOORPLAN_REPAIR_SCALE_EPSILON;
    const translationNeeded = translation.length() > FLOORPLAN_REPAIR_TRANSLATION_EPSILON;

    if (!scaleNeeded && !translationNeeded) {
      return {
        applied: false,
        scale: 1,
        translation,
        metrics,
        normalizedObjects: 0,
        reason: 'NO_ADJUSTMENT_REQUIRED',
      };
    }

    const transformPoint = (point?: THREE.Vector2 | null): THREE.Vector2 | undefined => {
      if (!point) {
        return undefined;
      }
      if (!Number.isFinite(point.x) || !Number.isFinite(point.y)) {
        return point.clone();
      }
      const relative = point.clone().sub(layoutCenter);
      relative.multiplyScalar(scaleFactor);
      return relative.add(targetCenter);
    };

    const transformTuple = (tuple?: [number, number]): [number, number] | undefined => {
      if (!tuple) return undefined;
      const vector = new THREE.Vector2(tuple[0], tuple[1]);
      const transformed = transformPoint(vector);
      return transformed ? [transformed.x, transformed.y] : tuple;
    };

    const symbolDimensionKeys = ['doorWidth', 'doorFrameWidth', 'doorBladeThickness', 'doubleDoorWidth', 'windowWidth', 'windowLength', 'windowFrameWidth', 'voidWidth', 'voidLength', 'circleStairsWidth', 'rectStairsWidth', 'rectStairsHeight', 'stairWidth', 'landingDepth', 'squareWidth', 'squareHeight', 'triangleWidth', 'triangleHeight', 'circleWidth', 'circleHeight', 'circleRadius', 'polygonWidth', 'polygonHeight', 'svgLength', 'svgHeight', 'stairStepSize', 'attachedDistance', 'textBoxWidth', 'textBoxHeight', 'fontSize', 'lineHeight', 'lineWidth'];

    const wallsNeedingRoomUpdate: string[] = [];
    let normalizedObjects = 0;

    // Begin delta tracking for all objects that will be modified
    this.beginUndoTransaction('repairFloorplanLayout');
    walls.forEach((wall) => this.recordWallForUndo(wall.id));
    singleLines.forEach((line) => this.recordLineForUndo(line.id));
    rulerLines.forEach((ruler) => this.recordRulerForUndo(ruler.id));
    symbols.forEach((symbol) => this.recordSymbolForUndo(symbol.id));

    transaction(() => {
      walls.forEach((wall) => {
        const updatedWall: WallType = {
          ...wall,
          start: transformPoint(wall.start) ?? wall.start.clone(),
          end: transformPoint(wall.end) ?? wall.end.clone(),
          controlPoint: wall.controlPoint ? transformPoint(wall.controlPoint) : undefined,
          position: wall.position ? (transformTuple(wall.position) as [number, number]) : wall.position,
          wallWidth: typeof wall.wallWidth === 'number' ? wall.wallWidth * scaleFactor : wall.wallWidth,
          symbolAttachments: wall.symbolAttachments?.map((attachment) => ({
            ...attachment,
            positionFromStart: attachment.positionFromStart * scaleFactor,
          })),
          cutouts: wall.cutouts?.map((cutout) => ({
            ...cutout,
            positionFromStart: cutout.positionFromStart * scaleFactor,
            length: cutout.length * scaleFactor,
          })),
          edgeConnections: wall.edgeConnections?.map((connection) => ({
            ...connection,
            sourceEdgePoint: transformPoint(connection.sourceEdgePoint) ?? connection.sourceEdgePoint.clone(),
            targetEdgePoint: transformPoint(connection.targetEdgePoint) ?? connection.targetEdgePoint.clone(),
            gapDistance: connection.gapDistance * scaleFactor,
          })),
        };
        normalizedObjects += 1;
        wallsNeedingRoomUpdate.push(wall.id);
        this.wallsMap.set(wall.id, updatedWall);
      });

      if (includeMeasurements) {
        singleLines.forEach((line) => {
          const updatedLine: SingleLineType = {
            ...line,
            start: transformPoint(line.start) ?? line.start.clone(),
            end: transformPoint(line.end) ?? line.end.clone(),
            controlPoint: line.controlPoint ? transformPoint(line.controlPoint) : undefined,
            position: line.position ? (transformTuple(line.position) as [number, number]) : line.position,
          };
          normalizedObjects += 1;
          this.singleLinesMap.set(line.id, updatedLine);
        });

        rulerLines.forEach((line) => {
          const updatedLine: RulerLineType = {
            ...line,
            start: transformPoint(line.start) ?? line.start.clone(),
            end: transformPoint(line.end) ?? line.end.clone(),
            controlPoint: line.controlPoint ? transformPoint(line.controlPoint) : undefined,
            position: line.position ? (transformTuple(line.position) as [number, number]) : line.position,
          };
          normalizedObjects += 1;
          this.rulerLinesMap.set(line.id, updatedLine);
        });
      }

      if (includeSymbols) {
        symbols.forEach((symbol) => {
          const transformedPosition = transformPoint(symbol.position);
          const updatedSymbol: SymbolType = {
            ...symbol,
            position: transformedPosition ?? symbol.position.clone(),
          };
          symbolDimensionKeys.forEach((key) => scaleNumericProperty(updatedSymbol as unknown as Record<string, any>, key, scaleFactor));
          if (isAreaSymbolType(updatedSymbol)) {
            updatedSymbol.vertices = (updatedSymbol as AreaSymbolType).vertices.map((vertex) => transformPoint(vertex) ?? vertex.clone());
          }
          normalizedObjects += 1;
          this.symbolsMap.set(symbol.id, updatedSymbol);
        });
      }

      this.roomsMap.clear();
      this.roomsFound.clear();
      this.objectIdsInFoundRooms.clear();
      this.objectToRoomsMap.clear();
    });

    wallsNeedingRoomUpdate.forEach((wallId) => this.markObjectForRoomUpdate(wallId));

    // Record all modified objects for undo
    walls.forEach((wall) => this.recordWallChange(wall.id, 'modify'));
    singleLines.forEach((line) => this.recordLineChange(line.id, 'modify'));
    rulerLines.forEach((ruler) => this.recordRulerChange(ruler.id, 'modify'));
    symbols.forEach((symbol) => this.recordSymbolChange(symbol.id, 'modify'));
    this.pushToUndoStack();

    const editor = getEditorStore();
    if (editor) {
      const refreshedSelections = editor.selections
        .map((selection) => {
          return this.wallsMap.get(selection.id) || this.symbolsMap.get(selection.id) || this.singleLinesMap.get(selection.id) || this.rulerLinesMap.get(selection.id);
        })
        .filter((selection): selection is WallType | SymbolType | SingleLineType | RulerLineType => Boolean(selection));

      runInAction(() => {
        editor.selections = refreshedSelections;
      });
    }

    renderStore.clearWallShapes('');
    this.triggerFullWallRedraw();
    this.setDirty();
    this.processRoomUpdates();

    return {
      applied: true,
      scale: scaleFactor,
      translation,
      metrics,
      normalizedObjects,
    };
  };

  /**
   * Computes the bounding box for all repairable objects so we can derive scale + translation deltas.
   */
  private calculateRepairMetrics = (walls: WallType[], singleLines: SingleLineType[], rulerLines: RulerLineType[], symbols: SymbolType[]): FloorplanRepairMetrics | null => {
    let minX = Number.POSITIVE_INFINITY;
    let minY = Number.POSITIVE_INFINITY;
    let maxX = Number.NEGATIVE_INFINITY;
    let maxY = Number.NEGATIVE_INFINITY;
    let pointCount = 0;

    const registerPoint = (point?: THREE.Vector2 | null) => {
      if (!point) return;
      if (!Number.isFinite(point.x) || !Number.isFinite(point.y)) return;
      if (point.x < minX) minX = point.x;
      if (point.y < minY) minY = point.y;
      if (point.x > maxX) maxX = point.x;
      if (point.y > maxY) maxY = point.y;
      pointCount += 1;
    };

    walls.forEach((wall) => {
      registerPoint(wall.start);
      registerPoint(wall.end);
      if (wall.controlPoint) {
        registerPoint(wall.controlPoint);
      }
    });

    singleLines.forEach((line) => {
      registerPoint(line.start);
      registerPoint(line.end);
      if (line.controlPoint) {
        registerPoint(line.controlPoint);
      }
    });

    rulerLines.forEach((line) => {
      registerPoint(line.start);
      registerPoint(line.end);
      if (line.controlPoint) {
        registerPoint(line.controlPoint);
      }
    });

    symbols.forEach((symbol) => {
      registerPoint(symbol.position);
      if (isAreaSymbolType(symbol)) {
        symbol.vertices.forEach(registerPoint);
      }
    });

    if (pointCount === 0 || minX === Number.POSITIVE_INFINITY) {
      return null;
    }

    return {
      minX,
      maxX,
      minY,
      maxY,
      width: Math.max(maxX - minX, 0),
      height: Math.max(maxY - minY, 0),
      pointCount,
    };
  };

  setName = (name: string) => {
    // Another collaborator holds the edit lease, so this view is read-only.
    if (this.lockedByOther) return;
    if (name === this.name) return;
    this.pushToUndoStack();
    this.name = name;
    this.setDirty();
  };

  setLineWeight = (lineWeight: number) => {
    this.lineWeight = lineWeight;
  };

  convertLineWeightToWorld = (lineWeight: number) => {
    // Adjust for camera zoom level as well
    const zoomLevel = getEditorStore()?.camera?.zoom || 1;
    // Return lineWeight in world units based on the zoom level
    return lineWeight / zoomLevel / 44;
  };

  setLineColor = (color: number) => {
    this.lineColor = color;
  };

  setFillColor = (color: number) => {
    this.fillColor = color;
  };

  setHoverColor = (color: number) => {
    this.hoverColor = color;
  };

  findObjectId = (id: string): WallType | SingleLineType | RulerLineType | undefined => {
    let object = this.wallsMap.get(id) as WallType | SingleLineType | RulerLineType | undefined;
    if (!object) {
      object = this.singleLinesMap.get(id);
    }
    if (!object) {
      object = this.rulerLinesMap.get(id);
    }
    return object;
  };

  setWalls = (walls: { [key: string]: WallType }) => {
    // Record all existing walls for undo
    this.beginUndoTransaction('setWalls');
    this.wallsMap.forEach((_, id) => this.recordWallForUndo(id));

    this.wallsMap = new ObservableMap(walls);

    // Record all new walls
    this.wallsMap.forEach((_, id) => this.recordWallChange(id, 'modify'));
    this.pushToUndoStack();
    this.setDirty();
  };

  /**
   * @param options.commitUndo When false, records the wall into the active
   * undo transaction without ending it. Callers that batch multiple walls
   * (e.g. seedRoomFromTemplate) must call beginUndoTransaction beforehand and
   * pushToUndoStack after all related changes.
   */
  addWall = (wall: WallType, options: { commitUndo?: boolean } = {}) => {
    const { commitUndo = true } = options;
    // Another collaborator holds the edit lease, so this view is read-only.
    if (this.lockedByOther) return;
    // Freemium gate: reject and surface the paywall when adding this wall
    // would push the user past their plan's per-floorplan shape cap.
    if (!this.canAddShape()) {
      getEditorStore()?.showPaywall("shapes");
      return;
    }
    if (commitUndo) {
      this.beginUndoTransaction('addWall');
    }
    const newWall: WallType = {
      ...wall,
      type: 'wall',
    };
    this.wallsMap.set(wall.id, newWall);
    this.recordWallChange(wall.id, 'add');
    if (commitUndo) {
      this.pushToUndoStack();
    }
    this.markObjectForRoomUpdate(wall.id);
    // Trigger wall update manager to ensure proper rendering and connections
    this.objectNeedsUpdate(wall.id);
    this.setDirty();
  };

  /**
   * @param options.force Bypass the read-only lock. Reserved for construction
   * tooling discarding its own unfinished preview wall, which has to complete
   * even when a lease hand-off drops this client into view-only mid-draw.
   */
  removeWall = (id: string, options: { force?: boolean } = {}) => {
    // Another collaborator holds the edit lease, so this view is read-only.
    if (!options.force && this.lockedByOther) return;
    this.wallsMap.get(id)?.symbolAttachments?.forEach((attachment) => {
      this.detachSymbolFromWall(attachment.symbolId);
    });
    const wall = this.wallsMap.get(id);
    if (!wall) return;

    // Begin undo tracking
    this.beginUndoTransaction('removeWall');
    this.recordWallForUndo(id);
    // Also record connected walls that will be modified
    wall.centerConnections?.forEach((conn) => this.recordWallForUndo(conn.id));

    // Clear joint fills / geometry caches before the wall disappears so bulk
    // delete of a traced drawing does not leave orphan white corner patches.
    renderStore.invalidateJointsForWall(id);
    renderStore.clearWallShapes(id);
    wallGeometryCache.clearWall(id);
    wall.centerConnections?.forEach((conn) => {
      renderStore.invalidateJointsForWall(conn.id);
    });
    wall.edgeConnections?.forEach((connection) => {
      renderStore.invalidateJointsForWall(connection.targetWallId);
    });

    // Remove from any groups
    this.removeObjectFromGroups(id);
    // Remove the wall from any rooms that include it
    if (wall?.roomIds) {
      wall.roomIds.forEach((roomId) => {
        const room = this.roomsMap.get(roomId);
        if (room) {
          // Remove the wall from the room's wallIds
          room.objectIds = room.objectIds.filter((objectId) => objectId !== id);

          // If the room has less than 3 walls, remove it
          if (room.objectIds.length < 3) {
            this.roomsMap.delete(roomId);
          } else {
            // Recalculate room properties (area, centroid)
            this.updateRoomProperties(room);
          }
        }
      });
    }
    // Remove the wall from the centerConnections of other walls
    wall?.centerConnections?.forEach((connection) => {
      const connectedWall = this.wallsMap.get(connection.id);
      if (connectedWall) {
        connectedWall.centerConnections = connectedWall.centerConnections?.filter((c) => c.id !== id);
        // Trigger update for the connected wall
        this.objectNeedsUpdate(connection.id);
      }
    });
    // Remove any clipping polygons on other walls that belong to this wall through centerConnections
    wall?.centerConnections?.forEach((connection) => {
      const connectedWall = this.wallsMap.get(connection.id);
      if (connectedWall) {
        renderStore.removeWallClipping(connection.id, id);
      }
    });
    // Remove any edge connections on other walls that belong to this wall
    wall?.edgeConnections?.forEach((connection) => {
      const connectedWall = this.wallsMap.get(connection.targetWallId);
      if (connectedWall) {
        connectedWall.edgeConnections = connectedWall.edgeConnections?.filter((c) => c.targetWallId !== id);
      }
    });
    // Remove any edge connections on this wall that belong to the other wall
    wall?.edgeConnections?.forEach((connection) => {
      if (connection.targetWallId === id) {
        wall.edgeConnections = wall.edgeConnections?.filter((c) => c.targetWallId !== id);
      }
    });

    // Record the deletion and connected wall modifications for undo
    this.recordWallChange(id, 'delete');
    wall.centerConnections?.forEach((conn) => this.recordWallChange(conn.id, 'modify'));

    // Remove the wall, then sync rooms/fills while the undo transaction is still open
    // so orphaned auto floor fills are recorded and can be restored on undo.
    this.wallsMap.delete(id);
    // Session-only 3D hide set must not retain deleted wall IDs (banner/counts).
    getEditorStore()?.setWallsHiddenIn3D([id], false);
    this.objectsNeedingRoomUpdate.clear();
    this.recomputeAllRooms();

    this.pushToUndoStack();
    this.setDirty();
  };

  addCutout = (wallId: string, cutout: CutoutType) => {
    this.incrementWallGeometryVersion(wallId);
    this.wallsMap.get(wallId)?.cutouts?.push(cutout);
    this.objectNeedsUpdate(wallId);
    this.setDirty();
  };

  removeCutout = (wallId: string, cutoutIndex: number) => {
    this.incrementWallGeometryVersion(wallId);
    this.wallsMap.get(wallId)?.cutouts?.splice(cutoutIndex, 1);
    this.objectNeedsUpdate(wallId);
    this.setDirty();
  };

  updateObjectPosition = (source: string, start: THREE.Vector2, end: THREE.Vector2, options?: { bypassEdgeConstraints?: boolean; skipInvalidation?: boolean }) => {
    const object = this.findObjectId(source);
    if (!object) {
      return;
    }

    // Validate wall dimensions before applying the update
    const validation = validateWallDimensions(start, end);
    if (!validation.isValid) {
      console.error(`updateObjectPosition: Rejecting invalid wall update for ${source}: ${validation.error}`);
      console.error('Attempted update:', {
        wallId: source,
        start: { x: start.x, y: start.y },
        end: { x: end.x, y: end.y },
        currentStart: { x: object.start.x, y: object.start.y },
        currentEnd: { x: object.end.x, y: object.end.y },
        length: start.distanceTo(end),
        currentLength: object.start.distanceTo(object.end),
        stack: new Error().stack,
      });
      return;
    }

    if (isWallType(object)) {
      // For middle handle drags, bypass edge constraints because the entire wall moves together
      // including all its connections
      const bypassEdgeConstraints = options?.bypassEdgeConstraints || false;

      if (!bypassEdgeConstraints) {
        const violationMessages: string[] = [];
        const tolerance = 5e-4;
        const hasEdgeConstraint = (endpoint: 'start' | 'end', proposedPoint: THREE.Vector2): boolean => {
          const connections = object.edgeConnections?.filter((conn) => conn.sourceEndpoint === endpoint) || [];
          if (connections.length === 0) {
            return false;
          }

          for (const connection of connections) {
            if (connection.sourceEdgePoint) {
              // Determine target wall angle to ensure consistent side calculation
              const targetWall = this.findObjectId(connection.targetWallId) as WallType;
              const connectionAngle = targetWall ? (connection.targetEndpoint === 'start' || connection.targetEndpoint === 'end' ? getWallAngleAtEndpoint(targetWall, connection.targetEndpoint) : getWallAngle(targetWall)) : 0;

              // Create a temporary wall with the proposed NEW geometry (start/end are the function arguments)
              const tempWall = { ...object, start, end } as WallType;

              // Calculate the edge point for this proposed geometry
              // We must validate that the edge point (corner) remains at the anchor,
              // rather than validating that the center point is at the anchor (which is wrong).
              const calculated = calculateWallEdgePoint(tempWall, endpoint, connectionAngle, undefined, undefined, connection.sourceSide);

              // Check if the calculated edge point matches the required connection point
              if (!this.vectorsEqual(calculated.edgePosition, connection.sourceEdgePoint, tolerance)) {
                violationMessages.push(`Blocked move for edge-connected ${endpoint} of wall ${source}. Calculated edge (${calculated.edgePosition.x.toFixed(4)}, ${calculated.edgePosition.y.toFixed(4)}) != stored anchor (${connection.sourceEdgePoint.x.toFixed(4)}, ${connection.sourceEdgePoint.y.toFixed(4)}).`);
                return true;
              }
            } else {
              // Legacy validation where sourceEdgePoint might be missing
              const requiredPoint = endpoint === 'start' ? object.start.clone() : object.end.clone();

              if (!this.vectorsEqual(proposedPoint, requiredPoint, tolerance)) {
                violationMessages.push(`Blocked move for constrained ${endpoint} of wall ${source} (Legacy Check).`);
                return true;
              }
            }
          }

          return false;
        };

        if (hasEdgeConstraint('start', start) || hasEdgeConstraint('end', end)) {
          violationMessages.forEach((message) => console.warn(message));
          return;
        }
      }

      const oldStart = object.start;
      const oldEnd = object.end;
      const transformedControlPoint = object.lineForm === 'arc' && object.controlPoint ? transformArcControlPoint(object.controlPoint, oldStart, oldEnd, start, end) : object.controlPoint;
      const controlPointChanged = !!(transformedControlPoint && object.controlPoint && transformedControlPoint.distanceTo(object.controlPoint) > 1e-6);

      // Track which endpoints changed for efficient invalidation in edge connections mode
      const startChanged = !start.equals(oldStart);
      const endChanged = !end.equals(oldEnd);

      const updatedWall = {
        ...object,
        start,
        end,
        controlPoint: transformedControlPoint,
        geometryVersion: controlPointChanged ? (object.geometryVersion || 0) + 1 : object.geometryVersion,
      };
      this.wallsMap.set(object.id, updatedWall);
      if ((startChanged || endChanged || controlPointChanged) && updatedWall.symbolAttachments?.length) {
        this.refreshSymbolAttachmentsForWall(object.id);
      }

      // Use endpoint-specific invalidation for better performance
      // Import wallUpdateManager dynamically to avoid circular dependency
      // Skip invalidation if options.skipInvalidation is true (e.g., during middle handle drags)
      const skipInvalidation = options?.skipInvalidation || false;
      const joinAffectedWallIds = !skipInvalidation && (startChanged || endChanged || controlPointChanged)
        ? this.resolveWallJoinsForGeometryChange([object.id], 'wall_position_change')
        : [object.id];
      if (!skipInvalidation) {
        import('../utils/wallUpdateManager').then(({ wallUpdateManager }) => {
          if (startChanged) {
            wallUpdateManager.invalidateWallByEndpointChange(source, 'start');
          }
          if (endChanged) {
            wallUpdateManager.invalidateWallByEndpointChange(source, 'end');
          }
          // If neither changed, just trigger normal update
          if (!startChanged && !endChanged) {
            this.objectNeedsUpdate(source);
          }
          joinAffectedWallIds.forEach((wallId) => {
            if (wallId !== source) {
              wallUpdateManager.updateWallPosition(wallId);
            }
          });
        });
      }
      // Update the Wall Shape Fill if the wall has been moved
      if (!skipInvalidation) {
        renderStore.composeWallShape(source, undefined, true);
      }
    } else if (isSingleLineType(object)) {
      const transformedControlPoint = object.lineForm === 'arc' && object.controlPoint ? transformArcControlPoint(object.controlPoint, object.start, object.end, start, end) : object.controlPoint;
      this.updateSingleLine({
        ...(object as SingleLineType),
        start,
        end,
        controlPoint: transformedControlPoint,
      });
    } else if (isRulerLineType(object)) {
      const transformedControlPoint = object.lineForm === 'arc' && object.controlPoint ? transformArcControlPoint(object.controlPoint, object.start, object.end, start, end) : object.controlPoint;
      this.updateRulerLine({
        ...(object as RulerLineType),
        start,
        end,
        controlPoint: transformedControlPoint,
      });
    } else {
      // Legacy mode: use standard invalidation
      this.objectNeedsUpdate(source);
    }
    this.setDirty();
  };

  objectNeedsUpdate = (objectId: string) => {
    const object = this.findObjectId(objectId);
    let lastUpdate = 0;
    if (object) {
      // Update the object lastUpdate - this is critical for triggering re-renders
      // when properties like wallWidth, lineWeight, or lineType change
      lastUpdate = (object.lastUpdate || 0) + 1;
      this.setObjectProperty(object.id, 'lastUpdate', lastUpdate);

      // Use the new wall update manager for better performance
      if (isWallType(object)) {
        // SKIP wallUpdateManager.updateWallPosition entirely during active drags.
        // Drag handlers (dragMiddleVHandle, dragMiddleHHandle) already manage geometry
        // directly via composeWallShape/trimShapes calls. Calling updateWallPosition
        // during drag triggers expensive O(n) operations:
        // - Clones entire wallsMap: new Map(floorplannerStore.wallsMap)
        // - Spatial index lookups and updates
        // - Joint detection and dependency graph updates
        // These are unnecessary during drag and cause significant lag.
        if (!DragStateManager.isCurrentlyDragging() && !DragStateManager.shouldSuppressWallUpdate(objectId)) {
          // Let the wall update manager handle all updates including connected walls
          // Also skip if wall is in progressive mode (wall construction) to prevent
          // cascading updates - progressive walls are updated via direct composeWallShape calls
          import('../utils/wallUpdateManager').then(({ wallUpdateManager }) => {
            if (!wallUpdateManager.isWallInProgressiveMode(objectId)) {
              wallUpdateManager.updateWallPosition(objectId);
            }
          });
        }
      }
    }
    return lastUpdate;
  };
  setSymbolsMap = (symbols: { [key: string]: SymbolType }) => {
    this.symbolsMap = new ObservableMap(symbols);
  };

  updateSymbolProperty = (
    symbolId: string,
    property: string,
    value: any,
    options: { preserveUserModel3DTargetDimensions?: boolean } = {},
  ) => {
    // View-only tabs may highlight a selection, but must not change content.
    if (this.lockedByOther && property !== 'selected') return;
    const symbol = this.symbolsMap.get(symbolId);

    if (symbol) {
      // Check if property has changed
      if (symbol[property as keyof SymbolType] === value) return;
      else if (property === 'position') {
        if (symbol.position.x === value[0] && symbol.position.y === value[1]) return;
      }
      // Update symbol property
      const syncUserModelDimension =
        !options.preserveUserModel3DTargetDimensions &&
        isSvgType(symbol) &&
        symbol.threeD?.userModel &&
        (property === 'svgLength' || property === 'svgHeight') &&
        typeof value === 'number' &&
        Number.isFinite(value) &&
        value > 0;
      const dimensionProperty = property === 'svgLength' ? 'width' : 'depth';
      const updatedThreeD = syncUserModelDimension
        ? {
            ...symbol.threeD,
            dimensions: {
              ...symbol.threeD?.dimensions,
              [dimensionProperty]: value,
            },
            userModel: {
              ...symbol.threeD!.userModel!,
              dimensions: {
                ...symbol.threeD!.userModel!.dimensions,
                [dimensionProperty]: value,
              },
            },
          }
        : symbol.threeD;
      // One-shot: crossing above the plan cut plane suggests dashed strokes;
      // the user can still change Style afterward.
      const overheadDashDefault =
        property === 'threeD'
          ? overheadLineTypeDefaultOnElevationChange(
              getPlanBaseElevation(symbol),
              getPlanBaseElevation({ threeD: value }),
              symbol.lineType,
            )
          : null;
      const updatedSymbol = {
        ...symbol,
        [property]: property === 'position' ? new THREE.Vector2(value[0], value[1]) : property === 'vertices' ? toVector2Array(value) : property === 'rotation' && value === 0 ? 0.0001 : value,
        ...(syncUserModelDimension ? { threeD: updatedThreeD } : {}),
        ...(overheadDashDefault ? { lineType: LineType.dashed } : {}),
      };
      this.symbolsMap.set(symbolId, updatedSymbol);

      if (property !== 'selected') {
        this.setDirty();
      }
    }
  };

  updateSymbolPositionRespectingAttachment = (symbolId: string, position: [number, number], lightweight: boolean = false): boolean => {
    const symbol = this.symbolsMap.get(symbolId);
    const attachedWallId = symbol?.attachedTo;
    const wall = attachedWallId ? this.wallsMap.get(attachedWallId) : undefined;
    const attachment = wall?.symbolAttachments?.find((item) => item.symbolId === symbolId);

    if (attachedWallId && wall && attachment) {
      this.updateSymbolAttachmentPosition(symbolId, attachedWallId, position, lightweight);
      return true;
    }

    this.updateSymbolProperty(symbolId, 'position', position);
    return false;
  };

  /**
   * @param options.commitUndo When false, records the symbol into the active
   * undo transaction without ending it. Callers that batch related edits
   * (e.g. catalog insert + wall attach) must call beginUndoTransaction beforehand
   * and pushToUndoStack after all related changes.
   */
  addSymbol = (
    type: 'symbol' | 'window' | 'door' | 'slidingDoor' | 'doubleDoor' | 'circleStairs' | 'rectStairs' | 'turnStairs' | 'square' | 'circle' | 'triangle' | 'polygon' | 'svg' | 'smartTable' | 'bookShelf' | 'text' | 'area' | 'areaFill' | 'backdropImage' | 'void',
    position: [number, number],
    props?: any,
    options: { commitUndo?: boolean } = {},
  ): string => {
    const { commitUndo = true } = options;
    // Another collaborator holds the edit lease, so this view is read-only.
    // Returns an empty string so callers that key on `id` simply no-op.
    if (this.lockedByOther) return '';
    // Freemium gate: reject and surface the paywall when adding this symbol
    // would push the user past their plan's per-floorplan shape cap. Returns
    // an empty string so callers that key on `id` simply no-op.
    if (!this.canAddShape()) {
      getEditorStore()?.showPaywall('shapes');
      return '';
    }
    if (commitUndo) {
      this.beginUndoTransaction('addSymbol');
    }
    const id = Math.random().toString(36).substr(2, 9);
    const previousBlockDirty = this.blockDirty;
    let symbolAdded = false;
    try {
      this.blockDirty = true;
      // Place the symbol at the visible center if no position is provided
      const [centerX, centerY] = getEditorStore()?.getVisibleCenter() ?? [0, 0];
      const posX = Number.isFinite(position[0]) ? position[0] : centerX;
      const posY = Number.isFinite(position[1]) ? position[1] : centerY;

      // Assign z-index inline (same as sendToFront) so we do not call
      // sendToFront mid-transaction — that would end the undo entry early.
      let zIndex = props?.zIndex;
      if (zIndex === undefined) {
        const allObjects = [
          ...Array.from(this.symbolsMap.values()),
          ...Array.from(this.singleLinesMap.values()),
        ];
        const maxZIndex = allObjects.length > 0
          ? Math.max(...allObjects.map((s) => s.zIndex || 0))
          : 0;
        zIndex = maxZIndex + 1;
      }

      // Create base symbol with required properties
      const newSymbol: SymbolType = {
        id,
        type,
        position: new THREE.Vector2(posX, posY),
        zIndex,
        rotation: props?.rotation ?? 0.0001,
        selected: false,
      };

      // Copy all properties from props, falling back to defaults only when needed
      if (props) {
        // Get the default values for this symbol type
        const defaults = this.getDefaultPropertiesForType(type);

        // Merge props with defaults, preferring props values
        Object.entries(defaults).forEach(([key, defaultValue]) => {
          if (key !== 'id' && key !== 'type' && key !== 'position' && key !== 'selected' && key !== 'zIndex') {
            (newSymbol as any)[key] = props[key] ?? defaultValue;
          }
        });

        // Also copy any additional properties from props that might not be in defaults
        Object.entries(props).forEach(([key, value]) => {
          if (key !== 'id' && key !== 'type' && key !== 'position' && key !== 'selected' && key !== 'zIndex' && !(key in newSymbol)) {
            (newSymbol as any)[key] = value;
          }
        });
      } else {
        // If no props provided, use all defaults
        Object.assign(newSymbol, this.getDefaultPropertiesForType(type));
        newSymbol.zIndex = zIndex;
      }

      if ((type === 'area' || type === 'areaFill') && Array.isArray((newSymbol as any).vertices)) {
        (newSymbol as AreaSymbolType | AreaFillSymbolType).vertices = toVector2Array((newSymbol as any).vertices);
      }

      // Insert already above the cut plane → default to dashed unless Style was set explicitly.
      const elevatedInsertLineType = defaultLineTypeForElevatedInsert(
        newSymbol,
        props?.lineType,
      );
      if (elevatedInsertLineType) {
        (newSymbol as { lineType?: LineType }).lineType = LineType.dashed;
      }

      runInAction(() => {
        this.symbolsMap.set(id, newSymbol);
        if (props?.zIndex === undefined) {
          this.normalizeZIndexes();
        }
        symbolAdded = true;
      });
    } finally {
      this.blockDirty = previousBlockDirty;
    }
    if (symbolAdded) {
      this.recordSymbolChange(id, 'add');
      if (commitUndo) {
        this.pushToUndoStack();
      }
      this.setDirty();
    } else if (commitUndo) {
      this.cancelUndoTransaction();
    }
    return id;
  };

  getDefaultPropertiesForType = (type: string): any => {
    const defaults: any = {
      lineWeight: this.lineWeight,
      lineColor: this.lineColor,
      fillColor: this.fillColor,
      hoverColor: this.hoverColor,
    };

    switch (type) {
      case 'door':
        return {
          ...defaults,
          lineWeight: this.doorLineWeight,
          doorWidth: this.doorWidth,
          doorFrameWidth: this.doorFrameWidth,
          openAngle: this.openAngle,
          doorBladeThickness: this.doorBladeThickness,
          // Opening width vs 3D height starts unlocked (doors are often resized independently).
          lockAspectRatio: false,
        };
      case 'slidingDoor':
        return {
          ...defaults,
          lineWeight: this.doorLineWeight,
          doorWidth: this.doorWidth,
          doorFrameWidth: this.doorFrameWidth,
          // Sliding doors store open fraction in openAngle (0 = closed, 1 = fully open).
          openAngle: 0,
          doorBladeThickness: this.doorBladeThickness,
          lockAspectRatio: false,
        };
      case 'doubleDoor':
        return {
          ...defaults,
          lineWeight: this.doorLineWeight,
          doubleDoorWidth: this.doubleDoorWidth,
          openAngle: this.openAngle,
          lockAspectRatio: false,
        };
      case 'window':
        return {
          ...defaults,
          lineWeight: this.windowLineWeight,
          windowWidth: this.windowWidth,
          windowLength: this.windowLength,
          windowFrameWidth: this.windowFrameWidth,
          openAngle: 0,
          lockAspectRatio: false,
        };
      case 'void':
        return {
          ...defaults,
          lineWeight: this.windowLineWeight,
          voidWidth: this.windowWidth,
          voidLength: this.windowLength,
        };
      case 'circleStairs':
        return {
          ...defaults,
          circleStairsWidth: this.circleStairsWidth,
          openAngle: this.openAngle,
          stairStepSize: this.stairStepSize,
        };
      case 'rectStairs':
        return {
          ...defaults,
          rectStairsWidth: this.rectStairsWidth,
          rectStairsHeight: this.rectStairsHeight,
          openAngle: this.openAngle,
          stairStepSize: this.stairStepSize,
          // Stairs keep width/length independent — no aspect lock on add.
          lockAspectRatio: false,
        };
      case 'turnStairs':
        return {
          ...defaults,
          stairShape: 'L',
          turnDirection: 'right',
          stairWidth: this.turnStairsWidth,
          firstFlightSteps: this.turnStairsFirstFlightSteps,
          secondFlightSteps: this.turnStairsSecondFlightSteps,
          landingDepth: this.turnStairsLandingDepth,
          stairStepSize: this.stairStepSize,
        };
      case 'square':
        return {
          ...defaults,
          squareWidth: this.squareWidth,
          squareHeight: this.squareHeight,
          openAngle: this.openAngle,
          // Geometric shapes start unlocked so width/length can be set independently.
          lockAspectRatio: false,
        };
      case 'circle':
        return {
          ...defaults,
          circleWidth: this.circleWidth,
          circleHeight: this.circleHeight,
          circleRadius: this.circleRadius,
          openAngle: this.openAngle,
          lockAspectRatio: false,
        };
      case 'polygon':
        return {
          ...defaults,
          polygonKind: 'hexagon',
          polygonSides: 6,
          polygonWidth: this.polygonWidth,
          polygonHeight: this.polygonHeight,
          openAngle: this.openAngle,
          lockAspectRatio: false,
        };
      case 'triangle':
        return {
          ...defaults,
          triangleWidth: this.triangleWidth,
          triangleHeight: this.triangleHeight,
          openAngle: this.openAngle,
          lockAspectRatio: false,
        };
      case 'svg':
        return {
          ...defaults,
          svgPath: this.svgPath,
          svgLength: this.svgLength,
          svgHeight: this.svgHeight,
          lockAspectRatio: this.lockAspectRatio,
          flipped: false, // Default to false for new symbols
        };
      case 'smartTable':
        return {
          ...defaults,
          name: 'Smart rectangular table',
          libraryKey: 'MANUAL/smart-rectangular-table',
          smartTable: createDefaultSmartRectangleTableConfig(),
          threeD: createSmartTable3DMetadata('rectangle'),
        };
      case 'bookShelf':
        return {
          ...defaults,
          name: 'Bookshelf',
          libraryKey: BOOK_SHELF_LIBRARY_KEY,
          bookShelf: createDefaultBookShelfConfig(),
          threeD: createBookShelf3DMetadata(),
        };
      case 'text':
        return {
          ...defaults,
          textBoxWidth: this.textBoxWidth,
          textBoxHeight: this.textBoxHeight,
          fontSize: this.fontSize,
          fontStyle: this.fontStyle,
          fontWeight: this.fontWeight,
          fontFamily: this.fontFamily,
          textAlign: this.textAlign,
          lineHeight: this.lineHeight,
          text: this.text,
        };
      case 'areaFill':
        // Locked by default so room floors are not accidentally dragged or reshaped.
        return {
          ...defaults,
          locked: true,
        };
      case 'backdropImage':
        // Create a backdrop image symbol with the following properties:
        // - backdropImagePath: URL/path to the image file
        // - backdropSize: Physical size in millimeters that corresponds to a portion of the image width
        // - backdropRatio: What portion of the image width the size represents (0-1)
        // - zIndex: -1 to ensure it renders behind other elements
        // - locked: true so the image does not steal canvas clicks (open via Drawings & images)
        // - opacity / visible: underlay display controls (legacy plans default at render time)
        // Example: If size=1000mm and ratio=0.5, the full image width will be 2000mm (2m)
        return {
          ...defaults,
          backdropImagePath: this.backdropImagePath,
          backdropSize: this.backdropSize,
          backdropRatio: this.backdropRatio,
          zIndex: -1,
          locked: true,
          opacity: 0.8,
          visible: true,
        };
      default:
        return defaults;
    }
  };

  /**
   * @param options.force Bypass the read-only lock. Reserved for construction
   * tooling discarding its own unfinished preview symbol, which has to complete
   * even when a lease hand-off drops this client into view-only mid-draw.
   */
  removeSymbol = (id: string, options: { force?: boolean } = {}) => {
    // Another collaborator holds the edit lease, so this view is read-only.
    if (!options.force && this.lockedByOther) return;
    this.beginUndoTransaction('removeSymbol');
    this.recordSymbolForUndo(id);
    const symbol = this.symbolsMap.get(id);
    if (symbol && isAreaFillSymbolType(symbol) && symbol.roomBinding?.roomSignature) {
      this.suppressedAutoAreaFillRoomSignatures.add(symbol.roomBinding.roomSignature);
    }
    this.detachSymbolFromWall(id);
    // Remove from any groups
    this.removeObjectFromGroups(id);
    this.recordSymbolChange(id, 'delete');
    this.pushToUndoStack();
    this.symbolsMap.delete(id);
    this.setDirty();
  };

  symbolIsAttached = (symbolId: string) => {
    const symbol = this.symbolsMap.get(symbolId);
    if (symbol) return symbol.attachedTo;
    else return null;
  };

  getSymbolAttachmentPreview = (symbolId: string, wallId: string, position: [number, number]) => {
    const symbol = this.symbolsMap.get(symbolId);
    const wall = this.wallsMap.get(wallId);
    if (!symbol || !wall) {
      return null;
    }

    const snappedPos = new THREE.Vector2(position[0], position[1]);
    let positionFromStart: number;

    if (wall.lineForm === 'arc' && wall.controlPoint) {
      const { t } = projectPointOntoArc(snappedPos, wall.start, wall.controlPoint, wall.end);
      positionFromStart = tToArcLength(wall, t);
    } else {
      const wallStart = new THREE.Vector2(wall.start.x, wall.start.y);
      const wallEnd = new THREE.Vector2(wall.end.x, wall.end.y);
      const wallDirection = wallEnd.clone().sub(wallStart).normalize();
      const symbolDistance = snappedPos.clone().sub(wallStart).dot(wallDirection);
      positionFromStart = wallStart.clone().add(wallDirection.clone().multiplyScalar(symbolDistance)).distanceTo(wallStart);
    }

    const insertGeom = getSymbolInsertGeometry(wall, symbol, positionFromStart, this.doorFrameWidth);
    const renderCenterOffset = getSymbolRenderCenterOffset(wall, symbol, positionFromStart, this.doorFrameWidth);
    const symbolPositionOnWall = insertGeom.midpoint.clone().sub(insertGeom.tangent.clone().multiplyScalar(renderCenterOffset));

    return {
      wallId,
      position: symbolPositionOnWall,
      rotation: Math.atan2(insertGeom.tangent.y, insertGeom.tangent.x),
      positionFromStart,
    };
  };

  detachSymbolFromWall = (symbolId: string) => {
    const symbol = this.symbolsMap.get(symbolId);
    if (symbol && symbol.attachedTo) {
      runInAction(() => {
        // Remove the attachment from the wall
        const wallId = symbol.attachedTo as string;
        const wall = this.wallsMap.get(wallId);
        if (!wall) {
          console.error('Wall not found when detachSymbolFromWall ran', wallId);
          return;
        }
        const wallCopy = { ...wall };
        wallCopy.symbolAttachments = wallCopy.symbolAttachments?.filter((attachment) => attachment.symbolId !== symbolId);
        this.wallsMap.set(wallId, wallCopy);
        renderStore.removeWallClipping(wallId, symbolId);

        // Invalidate geometry cache and trigger wall mesh rebuild to remove symbol hole immediately
        this.incrementWallGeometryVersion(wallId);
        this.objectNeedsUpdate(wallId);

        // Regenerate outline segments to remove the gap for the detached symbol
        renderStore.generateOutlineSegments(wallId);
        const symbolCopy = { ...symbol };
        delete symbolCopy.attachedTo;
        delete symbolCopy.attachedDistance;
        this.symbolsMap.set(symbolId, symbolCopy);
        // If the object is a window, restore the window width to the default value
        if (isWindowType(symbol)) {
          this.updateSymbolProperty(symbolId, 'windowWidth', this.windowWidth);
        }
        // If the object is a void, restore the void width to the default value
        if (isVoidType(symbol)) {
          this.updateSymbolProperty(symbolId, 'voidWidth', this.windowWidth);
        }
      });
      this.setDirty();
    }
  };

  attachSymbolToWall = (symbolId: string, wallId: string, position: [number, number]) => {
    const symbol = this.symbolsMap.get(symbolId);

    if (symbol) {
      if (symbol.attachedTo) {
        console.error('Trying to attach a symbol to a new wall while it is already attached to a wall, please detach first', symbolId);
        return;
      }
      runInAction(() => {
        const wall = this.wallsMap.get(wallId);
        if (!wall) {
          console.error('Wall not found when attachSymbolToWall ran', wallId);
          return;
        }
        const wallCopy = { ...wall };
        if (!wallCopy.start || !wallCopy.end) {
          console.error('Wall does not have start or end points', wallId);
          return;
        }

        // Compute positionFromStart as arc-length along the wall.
        // For arc walls this uses the quadratic Bézier arc length so the
        // position stays stable under control-point edits.
        let positionFromStart: number;
        const snappedPos = new THREE.Vector2(position[0], position[1]);

        if (wallCopy.lineForm === 'arc' && wallCopy.controlPoint) {
          // The snapped point already lies on the arc (from AttachableSymbol).
          // Find the closest t on the curve and convert to arc-length.
          const { t } = projectPointOntoArc(snappedPos, wallCopy.start, wallCopy.controlPoint, wallCopy.end);
          positionFromStart = tToArcLength(wallCopy, t);

          // Validate: the insert must fit on the curve and not overlap existing attachments
          const { totalWidth, centerOffset } = getSymbolWidthInfo(symbol, this.doorFrameWidth);
          const halfWidth = totalWidth / 2;
          const sCenter = positionFromStart + centerOffset;
          const totalArcLen = getWallArcLength(wallCopy);
          if (sCenter - halfWidth < -0.001 || sCenter + halfWidth > totalArcLen + 0.001) {
            console.warn('attachSymbolToWall: symbol insert falls outside arc wall bounds, rejecting', symbolId);
            return;
          }

          // Check overlap against other attachments on this wall
          const currentAttachments = wallCopy.symbolAttachments || [];
          for (const att of currentAttachments) {
            const existingSym = this.symbolsMap.get(att.symbolId);
            if (!existingSym) continue;
            const existing = getSymbolInsertGeometry(wallCopy, existingSym, att.positionFromStart, this.doorFrameWidth);
            if (sCenter - halfWidth < existing.sB && sCenter + halfWidth > existing.sA) {
              console.warn('attachSymbolToWall: symbol insert overlaps existing attachment, rejecting', symbolId);
              return;
            }
          }
        } else {
          // Straight wall — project onto chord
          const wallStart = new THREE.Vector2(wallCopy.start.x, wallCopy.start.y);
          const wallEnd = new THREE.Vector2(wallCopy.end.x, wallCopy.end.y);
          const wallDirection = wallEnd.clone().sub(wallStart).normalize();
          const symbolDistance = snappedPos.clone().sub(wallStart).dot(wallDirection);
          const symbolPositionOnWall = wallStart.clone().add(wallDirection.clone().multiplyScalar(symbolDistance));
          positionFromStart = symbolPositionOnWall.distanceTo(wallStart);
        }

        if (!wallCopy.symbolAttachments) wallCopy.symbolAttachments = [];
        wallCopy.symbolAttachments.push({ symbolId, positionFromStart });

        // Compute actual world position and rotation using insert geometry
        const insertGeom = getSymbolInsertGeometry(wallCopy, symbol, positionFromStart, this.doorFrameWidth);
        const renderCenterOffset = getSymbolRenderCenterOffset(wallCopy, symbol, positionFromStart, this.doorFrameWidth);
        const symbolPositionOnWall = insertGeom.midpoint.clone().sub(insertGeom.tangent.clone().multiplyScalar(renderCenterOffset));
        const wallAngle = Math.atan2(insertGeom.tangent.y, insertGeom.tangent.x);

        // Add a clipping shape in the wall for the symbol
        const clippingPolygon = renderStore.getClippingPolygon(symbol, wallCopy);
        renderStore.addWallClipping(wallId, clippingPolygon);
        this.wallsMap.set(wallId, wallCopy);

        this.incrementWallGeometryVersion(wallId);
        this.objectNeedsUpdate(wallId);

        const updatedObject = {
          ...symbol,
          attachedTo: wallId,
          attachedPosition: position,
        };
        this.symbolsMap.set(symbolId, updatedObject);

        this.updateSymbolProperty(symbolId, 'position', symbolPositionOnWall.toArray());
        this.updateSymbolProperty(symbolId, 'rotation', wallAngle);

        if (isWindowType(symbol)) {
          const windowWidth = (wallCopy.wallWidth || this.wallWidth) < 0.1 ? wallCopy.wallWidth || this.wallWidth : 0.1;
          this.updateSymbolProperty(symbolId, 'windowWidth', windowWidth);
        }
        if (isVoidType(symbol)) {
          const voidWidth = (wallCopy.wallWidth || this.wallWidth) < 0.1 ? wallCopy.wallWidth || this.wallWidth : 0.1;
          this.updateSymbolProperty(symbolId, 'voidWidth', voidWidth);
        }

        renderStore.generateOutlineSegments(wallId);
      });

      this.setDirty();
    }
  };

  /**
   * Efficiently updates the position of a symbol that is already attached to a wall.
   * This avoids the expensive detach/attach cycle when the symbol is just sliding along the same wall.
   * Updates the wall's attachment position record and refreshes the wall clipping.
   *
   * PERFORMANCE OPTIMIZATION:
   * Set `lightweight` to true during drag operations to skip expensive geometry rebuilds.
   * When lightweight is true, only the essential data is updated:
   * - Symbol position in the store
   * - Attachment position on the wall
   * - Wall clipping polygon (needed for visual cutout)
   *
   * The expensive operations (geometry version increment, wall mesh rebuild, outline regeneration)
   * are skipped and should be performed once when the drag ends.
   *
   * @param symbolId The ID of the symbol to update
   * @param wallId The ID of the wall the symbol is attached to
   * @param position The new position for the symbol
   * @param lightweight If true, skip expensive geometry rebuilds (use during drag)
   */
  updateSymbolAttachmentPosition = (symbolId: string, wallId: string, position: [number, number], lightweight: boolean = false) => {
    const symbol = this.symbolsMap.get(symbolId);
    const wall = this.wallsMap.get(wallId);

    if (!symbol || !wall) {
      console.warn('updateSymbolAttachmentPosition: Symbol or wall not found', symbolId, wallId);
      return;
    }

    const symbolAttachment = wall.symbolAttachments?.find((attachment) => attachment.symbolId === symbolId);
    if (!symbolAttachment) {
      console.warn('updateSymbolAttachmentPosition: Symbol attachment not found', symbolId, wallId);
      return;
    }

    runInAction(() => {
      // Compute new positionFromStart as arc-length (arc walls) or chord distance (straight)
      const snappedPos = new THREE.Vector2(position[0], position[1]);
      let newPositionFromStart: number;

      if (wall.lineForm === 'arc' && wall.controlPoint) {
        const { t } = projectPointOntoArc(snappedPos, wall.start, wall.controlPoint, wall.end);
        newPositionFromStart = tToArcLength(wall, t);
      } else {
        const wallStart = new THREE.Vector2(wall.start.x, wall.start.y);
        const wallEnd = new THREE.Vector2(wall.end.x, wall.end.y);
        const wallDirection = wallEnd.clone().sub(wallStart).normalize();
        const symbolDistance = snappedPos.clone().sub(wallStart).dot(wallDirection);
        newPositionFromStart = wallStart.clone().add(wallDirection.clone().multiplyScalar(symbolDistance)).distanceTo(wallStart);
      }

      /**
       * Replace attachment + wall references instead of mutating in place.
       * Arrow-key nudges update openings frequently; memoized 3D wall meshes
       * (observer/React.memo) only see prop changes when the wall identity or
       * geometryVersion changes, so in-place positionFromStart edits left the
       * wall hole behind the moved window.
       */
      const updatedAttachments = (wall.symbolAttachments ?? []).map((attachment) =>
        attachment.symbolId === symbolId
          ? { ...attachment, positionFromStart: newPositionFromStart }
          : attachment,
      );
      const updatedWall: WallType = {
        ...wall,
        symbolAttachments: updatedAttachments,
      };
      this.wallsMap.set(wallId, updatedWall);

      // Compute final world position and rotation via insert geometry
      const insertGeom = getSymbolInsertGeometry(updatedWall, symbol, newPositionFromStart, this.doorFrameWidth);
      const renderCenterOffset = getSymbolRenderCenterOffset(updatedWall, symbol, newPositionFromStart, this.doorFrameWidth);
      const symbolPositionOnWall = insertGeom.midpoint.clone().sub(insertGeom.tangent.clone().multiplyScalar(renderCenterOffset));

      if (!lightweight) {
        renderStore.removeWallClipping(wallId, symbolId);
        const clippingPolygon = renderStore.getClippingPolygon(symbol, updatedWall);
        renderStore.addWallClipping(wallId, clippingPolygon);
      }

      // Update the symbol position
      const updatedSymbol = {
        ...symbol,
        position: symbolPositionOnWall.clone(),
        rotation: Math.atan2(insertGeom.tangent.y, insertGeom.tangent.x),
        attachedPosition: position,
      };
      this.symbolsMap.set(symbolId, updatedSymbol);

      if (lightweight) {
        // LIGHTWEIGHT MODE (during drag):
        // Skip wall clipping/outline/mesh updates. The symbol itself moves via
        // local/store position updates, and final wall cutouts are rebuilt on drop.
        // NOTE: We skip these expensive operations in lightweight mode:
        // - clipping polygon rebuild
        // - lastUpdate bumps that rebuild the wall mesh
        // - outline segment generation
        // - incrementWallGeometryVersion (invalidates caches and triggers expensive rebuilds)
        // - wallUpdateManager.updateWallPosition (updates connections, dependency graph, etc.)
        // These will be done once when drag ends via finalizeSymbolAttachmentPosition
      } else {
        // FULL MODE (when drag ends or for non-drag operations):
        // Perform all operations including expensive geometry rebuilds
        this.incrementWallGeometryVersion(wallId);
        this.objectNeedsUpdate(wallId);
        renderStore.generateOutlineSegments(wallId);
      }
    });
    this.setDirty();
  };

  /**
   * Finalizes a symbol attachment after drag ends.
   * Call this to perform the expensive geometry operations that were skipped during lightweight updates.
   * @param symbolId The ID of the symbol that was being dragged
   * @param wallId The ID of the wall the symbol is attached to
   */
  finalizeSymbolAttachmentPosition = (symbolId: string, wallId: string) => {
    const wall = this.wallsMap.get(wallId);
    const symbol = this.symbolsMap.get(symbolId);
    if (!wall || !symbol) return;

    runInAction(() => {
      renderStore.removeWallClipping(wallId, symbolId);
      const clippingPolygon = renderStore.getClippingPolygon(symbol, wall);
      renderStore.addWallClipping(wallId, clippingPolygon);

      // Perform the expensive operations that were skipped during lightweight updates
      this.incrementWallGeometryVersion(wallId);
      this.objectNeedsUpdate(wallId);
      renderStore.generateOutlineSegments(wallId);
    });
    this.setDirty();
  };

  refreshSymbolAttachmentOnWall = (symbolId: string, wallId: string): boolean => {
    const wall = this.wallsMap.get(wallId);
    const symbol = this.symbolsMap.get(symbolId);
    const attachment = wall?.symbolAttachments?.find((item) => item.symbolId === symbolId);
    if (!wall || !symbol || !attachment) {
      return false;
    }

    const insertGeom = getSymbolInsertGeometry(wall, symbol, attachment.positionFromStart, this.doorFrameWidth);
    const renderCenterOffset = getSymbolRenderCenterOffset(wall, symbol, attachment.positionFromStart, this.doorFrameWidth);
    const position = insertGeom.midpoint
      .clone()
      .sub(insertGeom.tangent.clone().multiplyScalar(renderCenterOffset));
    const rotation = Math.atan2(insertGeom.tangent.y, insertGeom.tangent.x) || 0.0001;

    const positionChanged = symbol.position.distanceTo(position) > 1e-9;
    const rotationChanged = Math.abs((symbol.rotation ?? 0) - rotation) > 1e-9;
    const attachmentChanged = symbol.attachedTo !== wallId;
    if (!positionChanged && !rotationChanged && !attachmentChanged) {
      return false;
    }

    this.symbolsMap.set(symbolId, {
      ...symbol,
      attachedTo: wallId,
      position,
      rotation,
    });
    return true;
  };

  refreshSymbolAttachmentsForWall = (wallId: string): boolean => {
    const wall = this.wallsMap.get(wallId);
    if (!wall?.symbolAttachments?.length) {
      return false;
    }

    let changed = false;
    wall.symbolAttachments.forEach((attachment) => {
      changed = this.refreshSymbolAttachmentOnWall(attachment.symbolId, wallId) || changed;
    });
    return changed;
  };

  getSymbolPositionOfWall = (symbolId: string, wallId: string) => {
    const symbol = this.symbolsMap.get(symbolId);
    const wall = this.wallsMap.get(wallId);
    if (symbol && wall) {
      const symbolAttachment = wall.symbolAttachments?.find((attachment) => attachment.symbolId === symbolId);
      if (symbolAttachment) {
        const wallStart = new THREE.Vector2(wall.start.x, wall.start.y);
        const wallEnd = new THREE.Vector2(wall.end.x, wall.end.y);
        const wallDirection = wallEnd.clone().sub(wallStart).normalize();
        const symbolPosition = wallStart.clone().add(wallDirection.clone().multiplyScalar(symbolAttachment.positionFromStart));
        return symbolPosition;
      }
    }
    return null;
  };

  cloneSymbol = (symbolId: string, newPosition?: THREE.Vector2) => {
    const symbol = this.symbolsMap.get(symbolId);
    if (symbol) {
      this.beginUndoTransaction('cloneSymbol');
      const newSymbol = {
        ...symbol,
        id: this.generateId(),
        attachedTo: undefined,
        attachedPosition: undefined,
        attachedDistance: undefined,
        clipWall: undefined,
        position: newPosition || symbol.position.clone().add(new THREE.Vector2(0.2, 0.2)),
        zIndex: this.symbolsMap.size + 1,
      };
      this.symbolsMap.set(newSymbol.id, newSymbol);
      this.recordSymbolChange(newSymbol.id, 'add');
      this.pushToUndoStack();
      this.setDirty();
    }
  };

  updateSymbolPosition = (symbolId: string, newPosition: THREE.Vector2) => {
    const symbol = this.symbolsMap.get(symbolId);
    if (symbol) {
      const updatedSymbol = {
        ...symbol,
        position: newPosition,
      };
      this.symbolsMap.set(symbolId, updatedSymbol);
      this.setDirty();
    }
  };

  setSingleLines = (lines: { [key: string]: SingleLineType }) => {
    this.beginUndoTransaction('setSingleLines');
    this.singleLinesMap.forEach((_, id) => this.recordLineForUndo(id));
    this.singleLinesMap = new ObservableMap(lines);
    Object.keys(lines).forEach((id) => this.recordLineChange(id, 'modify'));
    this.pushToUndoStack();
    this.setDirty();
  };
  setRulerLines = (lines: { [key: string]: RulerLineType }) => {
    this.beginUndoTransaction('setRulerLines');
    this.rulerLinesMap.forEach((_, id) => this.recordRulerForUndo(id));
    this.rulerLinesMap = new ObservableMap(lines);
    Object.keys(lines).forEach((id) => this.recordRulerChange(id, 'modify'));
    this.pushToUndoStack();
    this.setDirty();
  };
  setRulers3D = (rulers: { [key: string]: Ruler3DType }) => {
    this.beginUndoTransaction('setRulers3D');
    this.rulers3DMap.forEach((_, id) => this.recordRuler3DForUndo(id));
    this.rulers3DMap = new ObservableMap(rulers);
    Object.keys(rulers).forEach((id) => this.recordRuler3DChange(id, 'modify'));
    this.pushToUndoStack();
    this.setDirty();
  };
  addSingleLine = (line: SingleLineType) => {
    // Another collaborator holds the edit lease, so this view is read-only.
    if (this.lockedByOther) return;
    // Freemium gate: surface the paywall when this would exceed the cap.
    if (!this.canAddShape()) {
      getEditorStore()?.showPaywall("shapes");
      return;
    }
    this.beginUndoTransaction('addSingleLine');

    // Set default z-index if not present
    if (line.zIndex === undefined) {
      const allObjects = [...Array.from(this.symbolsMap.values()), ...Array.from(this.singleLinesMap.values())];
      const maxZIndex = allObjects.length > 0 ? Math.max(...allObjects.map((s) => s.zIndex || 0)) : 0;
      line.zIndex = maxZIndex + 1;
    }

    this.singleLinesMap.set(line.id, line);
    this.recordLineChange(line.id, 'add');
    this.pushToUndoStack();
    this.markObjectForRoomUpdate(line.id);
    this.setDirty();
  };
  addRulerLine = (line: RulerLineType) => {
    // Another collaborator holds the edit lease, so this view is read-only.
    if (this.lockedByOther) return;
    // Freemium gate: surface the paywall when this would exceed the cap.
    if (!this.canAddShape()) {
      getEditorStore()?.showPaywall("shapes");
      return;
    }
    this.beginUndoTransaction('addRulerLine');
    this.rulerLinesMap.set(line.id, line);
    this.recordRulerChange(line.id, 'add');
    this.pushToUndoStack();
    this.setDirty();
  };

  /**
   * True when the plan already has walls, symbols, lines, or rulers.
   * Used to decide whether an image-import insert should be grouped/offset.
   */
  hasFloorplanDrawableContent = (): boolean =>
    this.wallsMap.size > 0 ||
    this.symbolsMap.size > 0 ||
    this.singleLinesMap.size > 0 ||
    this.rulerLinesMap.size > 0;

  /**
   * Insert a validated image-import draft as one all-or-nothing editor action.
   * The caller must provide fresh IDs and fully constructed native objects.
   * Inserted objects stay ungrouped so they can be edited immediately.
   */
  applyFloorplanImportBatch = ({
    walls,
    symbols,
    lines,
    rulers,
    sourceImportId,
  }: {
    walls: WallType[];
    symbols: SymbolType[];
    lines: SingleLineType[];
    rulers: RulerLineType[];
    sourceImportId?: string;
  }): { objectIds: string[]; alreadyApplied: boolean; groupId?: string } => {
    // Another collaborator holds the edit lease, so this view is read-only.
    if (this.lockedByOther || this.collabAccessLost) {
      throw new Error(
        "This floorplan is view-only while another collaborator is editing.",
      );
    }
    const objectIds = [
      ...walls.map((item) => item.id),
      ...symbols.map((item) => item.id),
      ...lines.map((item) => item.id),
      ...rulers.map((item) => item.id),
    ];
    if (objectIds.length === 0) {
      throw new Error("The import does not contain any selected drawing objects.");
    }
    if (!this.canAddShape(objectIds.length)) {
      getEditorStore()?.showPaywall("shapes");
      throw new Error("This import exceeds the floorplan shape limit.");
    }
    if (new Set(objectIds).size !== objectIds.length) {
      throw new Error("The import contains duplicate object IDs.");
    }
    if (
      objectIds.some(
        (id) =>
          this.wallsMap.has(id) ||
          this.symbolsMap.has(id) ||
          this.singleLinesMap.has(id) ||
          this.rulerLinesMap.has(id) ||
          this.groupsMap.has(id),
      )
    ) {
      throw new Error("The import conflicts with an existing object ID.");
    }

    const copyIndex = this.getFloorplanImportCopyIndex(sourceImportId);
    const stampImportMeta = <T extends { sourceImportId?: string; sourceImportCopyIndex?: number }>(
      item: T,
    ): T =>
      sourceImportId
        ? { ...item, sourceImportId, sourceImportCopyIndex: copyIndex }
        : item;

    this.beginUndoTransaction("Import drawing from image");
    this.setDeferRoomUpdates(true, { processPending: false });
    try {
      runInAction(() => {
        walls.forEach((wall) => this.wallsMap.set(wall.id, stampImportMeta(wall)));
        symbols.forEach((symbol) =>
          this.symbolsMap.set(symbol.id, stampImportMeta(symbol)),
        );
        lines.forEach((line) =>
          this.singleLinesMap.set(line.id, stampImportMeta(line)),
        );
        rulers.forEach((ruler) =>
          this.rulerLinesMap.set(ruler.id, stampImportMeta(ruler)),
        );
      });

      walls.forEach((wall) => {
        this.recordWallChange(wall.id, "add");
        this.markObjectForRoomUpdate(wall.id);
      });
      symbols.forEach((symbol) => this.recordSymbolChange(symbol.id, "add"));
      lines.forEach((line) => {
        this.recordLineChange(line.id, "add");
        this.markObjectForRoomUpdate(line.id);
      });
      rulers.forEach((ruler) => this.recordRulerChange(ruler.id, "add"));

      this.invalidateRoomDetectionIndex();
      this.integrityCheck();
      renderStore.queueBulkInsertedWalls(walls.map((wall) => wall.id));
      this.setDirty();
      this.pushToUndoStack();
    } catch (error) {
      runInAction(() => {
        walls.forEach((wall) => this.wallsMap.delete(wall.id));
        symbols.forEach((symbol) => this.symbolsMap.delete(symbol.id));
        lines.forEach((line) => this.singleLinesMap.delete(line.id));
        rulers.forEach((ruler) => this.rulerLinesMap.delete(ruler.id));
      });
      this.cancelUndoTransaction();
      throw error;
    } finally {
      this.setDeferRoomUpdates(false);
    }

    return { objectIds, alreadyApplied: false };
  };

  /**
   * Next 0-based copy index for an image-import id, based on objects already
   * in the plan (imports always stay ungrouped).
   */
  getFloorplanImportCopyIndex = (sourceImportId?: string): number => {
    if (!sourceImportId) return 0;
    let maxIndex = -1;
    const consider = (copyIndex?: number) => {
      if (typeof copyIndex === "number" && copyIndex > maxIndex) {
        maxIndex = copyIndex;
      }
    };
    this.wallsMap.forEach((wall) => {
      if (wall.sourceImportId === sourceImportId) consider(wall.sourceImportCopyIndex);
    });
    this.symbolsMap.forEach((symbol) => {
      if (symbol.sourceImportId === sourceImportId) consider(symbol.sourceImportCopyIndex);
    });
    this.singleLinesMap.forEach((line) => {
      if (line.sourceImportId === sourceImportId) consider(line.sourceImportCopyIndex);
    });
    this.rulerLinesMap.forEach((ruler) => {
      if (ruler.sourceImportId === sourceImportId) consider(ruler.sourceImportCopyIndex);
    });
    // Legacy grouped inserts still count toward copy offset.
    let legacyGroupCopies = 0;
    this.groupsMap.forEach((group) => {
      if (group.sourceImportId === sourceImportId) {
        legacyGroupCopies += 1;
      }
    });
    return Math.max(maxIndex + 1, legacyGroupCopies);
  };

  /** Whether any object (or legacy group) from this import is still in the plan. */
  hasFloorplanImportInPlan = (sourceImportId?: string): boolean => {
    if (!sourceImportId) return false;
    if (
      Array.from(this.wallsMap.values()).some(
        (wall) => wall.sourceImportId === sourceImportId,
      ) ||
      Array.from(this.symbolsMap.values()).some(
        (symbol) => symbol.sourceImportId === sourceImportId,
      ) ||
      Array.from(this.singleLinesMap.values()).some(
        (line) => line.sourceImportId === sourceImportId,
      ) ||
      Array.from(this.rulerLinesMap.values()).some(
        (ruler) => ruler.sourceImportId === sourceImportId,
      )
    ) {
      return true;
    }
    return Array.from(this.groupsMap.values()).some(
      (group) => group.sourceImportId === sourceImportId,
    );
  };
  addRuler3D = (ruler: Ruler3DType) => {
    // Another collaborator holds the edit lease, so this view is read-only.
    if (this.lockedByOther) return;
    // Freemium gate: surface the paywall when this would exceed the cap.
    if (!this.canAddShape()) {
      getEditorStore()?.showPaywall("shapes");
      return;
    }
    this.beginUndoTransaction('addRuler3D');
    this.rulers3DMap.set(ruler.id, ruler);
    this.recordRuler3DChange(ruler.id, 'add');
    this.pushToUndoStack();
    this.setDirty();
  };
  removeSingleLine = (id: string) => {
    const line = this.singleLinesMap.get(id);
    if (line) {
      this.beginUndoTransaction('removeSingleLine');
      this.recordLineForUndo(id);
      // Remove from any groups
      this.removeObjectFromGroups(id);
      // Remove the line from any rooms that include it
      if (line?.roomIds) {
        line.roomIds.forEach((roomId) => {
          const room = this.roomsMap.get(roomId);
          if (room) {
            // Remove the line from the room's wallIds
            room.objectIds = room.objectIds.filter((objectId) => objectId !== id);

            // If the room has less than 3 lines, remove it
            if (room.objectIds.length < 3) {
              this.roomsMap.delete(roomId);
            } else {
              // Recalculate room properties (area, centroid)
              this.updateRoomProperties(room);
            }
          }
        });
      }
      // Remove the line from the centerConnections of other lines
      line?.centerConnections?.forEach((connection) => {
        const connectedWall = this.wallsMap.get(connection.id);
        if (connectedWall) {
          connectedWall.centerConnections = connectedWall.centerConnections?.filter((c) => c.id !== id);
          // Trigger update for the connected wall
          this.objectNeedsUpdate(connection.id);
        }
      });
      this.recordLineChange(id, 'delete');

      // Remove the line, then sync rooms/fills while the undo transaction is still open
      // so orphaned auto floor fills are recorded and can be restored on undo.
      this.singleLinesMap.delete(id);
      this.objectsNeedingRoomUpdate.clear();
      this.recomputeAllRooms();

      this.pushToUndoStack();
      this.setDirty();
    }
  };
  removeRulerLine = (id: string) => {
    this.beginUndoTransaction('removeRulerLine');
    this.recordRulerForUndo(id);
    // Remove from any groups
    this.removeObjectFromGroups(id);
    this.recordRulerChange(id, 'delete');
    this.pushToUndoStack();
    this.rulerLinesMap.delete(id);
    this.setDirty();
  };
  removeRuler3D = (id: string) => {
    this.beginUndoTransaction('removeRuler3D');
    this.recordRuler3DForUndo(id);
    // Remove from any groups
    this.removeObjectFromGroups(id);
    this.recordRuler3DChange(id, 'delete');
    this.pushToUndoStack();
    this.rulers3DMap.delete(id);
    this.setDirty();
  };
  updateSingleLine = (line: SingleLineType) => {
    this.singleLinesMap.set(line.id, line);
    this.markObjectForRoomUpdate(line.id);
    this.setDirty();
  };
  updateRulerLine = (line: RulerLineType) => {
    this.rulerLinesMap.set(line.id, line);
    this.setDirty();
  };
  updateRuler3D = (ruler: Ruler3DType) => {
    this.rulers3DMap.set(ruler.id, ruler);
    this.setDirty();
  };
  updateSingleLineProp = <K extends keyof SingleLineType>(id: string, prop: K, value: SingleLineType[K]) => {
    const line = this.singleLinesMap.get(id);
    if (line) {
      line[prop] = value;
      this.singleLinesMap.set(id, line);
      this.setDirty();
    }
  };
  updateRulerLineProp = <K extends keyof RulerLineType>(id: string, prop: K, value: RulerLineType[K]) => {
    const line = this.rulerLinesMap.get(id);
    if (line) {
      line[prop] = value;
      this.rulerLinesMap.set(id, line);
      this.setDirty();
    }
  };
  updateRuler3DProp = <K extends keyof Ruler3DType>(id: string, prop: K, value: Ruler3DType[K]) => {
    const ruler = this.rulers3DMap.get(id);
    if (ruler) {
      ruler[prop] = value;
      this.rulers3DMap.set(id, ruler);
      this.setDirty();
    }
  };
  setSingleLineLength = (id: string, length: number, lineEndToAdjust: 'start' | 'end' | undefined) => {
    const line = this.singleLinesMap.get(id);
    if (line) {
      this.pushToUndoStack();

      // If no end specified, determine which end to adjust based on centerConnections
      if (!lineEndToAdjust) {
        // Check centerConnections at each end
        const startConnection = line.centerConnections?.find((connection) => connection.sourcePosition === WallConnectionStart);
        const endConnection = line.centerConnections?.find((connection) => connection.sourcePosition === WallConnectionEnd);

        if (!startConnection && endConnection) {
          lineEndToAdjust = 'start';
        } else if (startConnection && !endConnection) {
          lineEndToAdjust = 'end';
        } else {
          // If both ends are connected or neither end is connected,
          // choose the end that points towards right or bottom
          const direction = line.end.clone().sub(line.start);
          const deltaX = direction.x;
          const deltaY = direction.y;

          if ((deltaX > 0 && Math.abs(deltaX) >= Math.abs(deltaY)) || (deltaY < 0 && Math.abs(deltaY) >= Math.abs(deltaX))) {
            // Line is pointing towards right or down
            lineEndToAdjust = 'end';
          } else {
            lineEndToAdjust = 'start';
          }
        }
      }

      const direction = line.end.clone().sub(line.start).normalize();

      if (lineEndToAdjust === 'start') {
        // When adjusting start point, calculate new start from end point
        const newStart = line.end.clone().sub(direction.multiplyScalar(length));
        line.start = newStart;
        updateWallConnections(line.id, 'start', [line.id], this);
      } else {
        // When adjusting end point, calculate new end from start point
        const newEnd = line.start.clone().add(direction.multiplyScalar(length));
        line.end = newEnd;
        updateWallConnections(line.id, 'end', [line.id], this);
      }

      this.singleLinesMap.set(id, line);
      this.objectNeedsUpdate(id);
      this.markObjectForRoomUpdate(id);
      this.setDirty();
    }
  };
  setRulerLineLength = (id: string, length: number, lineEndToAdjust: 'start' | 'end' | undefined) => {
    const line = this.rulerLinesMap.get(id);
    if (line) {
      if (!Number.isFinite(length)) return;

      this.beginUndoTransaction('setRulerLineLength');
      this.recordRulerForUndo(id);

      const direction = line.end.clone().sub(line.start);
      const normalizedDirection = direction.lengthSq() > 1e-12 ? direction.normalize() : new THREE.Vector2(1, 0);

      if (lineEndToAdjust === 'start') {
        line.start = line.end.clone().sub(normalizedDirection.clone().multiplyScalar(length));
      } else if (lineEndToAdjust === 'end' || !lineEndToAdjust) {
        line.end = line.start.clone().add(normalizedDirection.clone().multiplyScalar(length));
      }
      this.rulerLinesMap.set(id, line);
      this.recordRulerChange(id, 'modify');
      this.pushToUndoStack();
      this.setDirty();
    }
  };
  singleLineLength = (id: string): number => {
    const line = this.singleLinesMap.get(id);
    if (!line || !line.start || !line.end) return 0;
    const lengthOfLine = new THREE.Vector2().subVectors(line.end, line.start).length();
    return lengthOfLine;
  };
  rulerLineLength = (id: string): number => {
    const line = this.rulerLinesMap.get(id);
    if (!line) return 0;
    const lengthOfLine = new THREE.Vector2().subVectors(line.end, line.start).length();
    return lengthOfLine;
  };

  getConnectedLine = (wall: SingleLineType, position: number): SingleLineType | undefined => {
    let connectedWall: SingleLineType | undefined = undefined;
    const tempWall = this.singleLinesMap.get(wall.id);
    if (!tempWall) return undefined;
    const connectedWallId = tempWall.centerConnections?.find((connection) => connection.sourcePosition === position)?.id;
    if (connectedWallId) {
      connectedWall = this.singleLinesMap.get(connectedWallId);
    }
    return connectedWall;
  };
  getConnectedRuler = (wall: RulerLineType, position: number): RulerLineType | undefined => {
    let connectedWall: RulerLineType | undefined = undefined;
    const tempWall = this.rulerLinesMap.get(wall.id);
    if (!tempWall) return undefined;
    const connectedWallId = tempWall.centerConnections?.find((connection) => connection.sourcePosition === position)?.id;
    if (connectedWallId) {
      connectedWall = this.rulerLinesMap.get(connectedWallId);
    }
    return connectedWall;
  };
  disconnectLineEnd = (lineId: string, connectionPos: number) => {
    const tempLine = this.singleLinesMap.get(lineId);
    if (!tempLine) return;
    const connectedLineId = tempLine.centerConnections?.find((connection) => connection.sourcePosition === connectionPos)?.id;
    let changed = false;
    if (connectedLineId) {
      const connectedLine = this.singleLinesMap.get(connectedLineId);
      if (connectedLine) {
        const updatedLine = {
          ...connectedLine,
          centerConnections: connectedLine.centerConnections?.filter((connection) => connection.id !== lineId),
        };
        this.singleLinesMap.set(connectedLineId, updatedLine);
        changed = true;
      }
      const updatedWall = {
        ...tempLine,
        centerConnections: tempLine.centerConnections?.filter((connection) => connection.sourcePosition !== connectionPos),
      };
      this.singleLinesMap.set(lineId, updatedWall);
      this.markObjectForRoomUpdate(lineId);
      changed = true;
    }
    if (changed) {
      this.setDirty();
    }
  };

  sendToFront = (id: string) => {
    // Another collaborator holds the edit lease, so this view is read-only.
    if (this.lockedByOther) return;
    const symbol = this.symbolsMap.get(id);
    const line = this.singleLinesMap.get(id);
    const obj = symbol || line;

    if (obj) {
      this.pushToUndoStack();
      const allObjects = [...Array.from(this.symbolsMap.values()), ...Array.from(this.singleLinesMap.values())];
      const maxZIndex = Math.max(...allObjects.map((s) => s.zIndex || 0));
      obj.zIndex = maxZIndex + 1;
      this.normalizeZIndexes();
      this.setDirty();
    }
  };

  sendToBack = (id: string) => {
    // Another collaborator holds the edit lease, so this view is read-only.
    if (this.lockedByOther) return;
    const symbol = this.symbolsMap.get(id);
    const line = this.singleLinesMap.get(id);
    const obj = symbol || line;

    if (obj) {
      this.pushToUndoStack();
      const allObjects = [...Array.from(this.symbolsMap.values()), ...Array.from(this.singleLinesMap.values())];
      const minZIndex = Math.min(...allObjects.map((s) => s.zIndex || 0));
      obj.zIndex = minZIndex - 1;
      this.normalizeZIndexes();
      this.setDirty();
    }
  };

  bringForward = (id: string) => {
    // Another collaborator holds the edit lease, so this view is read-only.
    if (this.lockedByOther) return;
    const symbol = this.symbolsMap.get(id);
    const line = this.singleLinesMap.get(id);
    const obj = symbol || line;

    if (obj) {
      const allObjects = [...Array.from(this.symbolsMap.values()), ...Array.from(this.singleLinesMap.values())];
      const sortedObjects = allObjects.sort((a, b) => (a.zIndex || 0) - (b.zIndex || 0));
      const index = sortedObjects.findIndex((s) => s.id === id);

      if (index < sortedObjects.length - 1) {
        this.pushToUndoStack();
        const nextObject = sortedObjects[index + 1];
        const tempZIndex = obj.zIndex || 0;
        obj.zIndex = nextObject.zIndex || 0;
        nextObject.zIndex = tempZIndex;
        this.normalizeZIndexes();
        this.setDirty();
      }
    }
  };

  bringBackward = (id: string) => {
    // Another collaborator holds the edit lease, so this view is read-only.
    if (this.lockedByOther) return;
    const symbol = this.symbolsMap.get(id);
    const line = this.singleLinesMap.get(id);
    const obj = symbol || line;

    if (obj) {
      const allObjects = [...Array.from(this.symbolsMap.values()), ...Array.from(this.singleLinesMap.values())];
      const sortedObjects = allObjects.sort((a, b) => (a.zIndex || 0) - (b.zIndex || 0));
      const index = sortedObjects.findIndex((s) => s.id === id);

      if (index > 0) {
        this.pushToUndoStack();
        const prevObject = sortedObjects[index - 1];
        const tempZIndex = obj.zIndex || 0;
        obj.zIndex = prevObject.zIndex || 0;
        prevObject.zIndex = tempZIndex;
        this.normalizeZIndexes();
        this.setDirty();
      }
    }
  };

  // Normalize zIndex values to be sequential
  normalizeZIndexes = () => {
    const allObjects = [...Array.from(this.symbolsMap.values()), ...Array.from(this.singleLinesMap.values())];
    const sortedObjects = allObjects.sort((a, b) => (a.zIndex || 0) - (b.zIndex || 0));
    sortedObjects.forEach((obj, index) => {
      obj.zIndex = index;
    });
  };

  isFront = (id: string) => {
    const symbol = this.symbolsMap.get(id);
    const line = this.singleLinesMap.get(id);
    const obj = symbol || line;
    if (obj) {
      const allObjects = [...Array.from(this.symbolsMap.values()), ...Array.from(this.singleLinesMap.values())];
      const maxZIndex = Math.max(...allObjects.map((s) => s.zIndex || 0));
      return (obj.zIndex || 0) >= maxZIndex;
    }
    return false;
  };

  isBack = (id: string) => {
    const symbol = this.symbolsMap.get(id);
    const line = this.singleLinesMap.get(id);
    const obj = symbol || line;
    if (obj) {
      const allObjects = [...Array.from(this.symbolsMap.values()), ...Array.from(this.singleLinesMap.values())];
      const minZIndex = Math.min(...allObjects.map((s) => s.zIndex || 0));
      return (obj.zIndex || 0) <= minZIndex;
    }
    return false;
  };

  wallLength = (wall: WallType): number => {
    if (!wall?.start || !wall?.end) return 0;
    if (wall.lineForm === 'arc' && wall.controlPoint) {
      return calculateArcLength(wall.start, wall.controlPoint, wall.end);
    }
    return new THREE.Vector2().subVectors(wall.end, wall.start).length();
  };

  setWallLength = (wallId: string, newLength: number, whichEndToUpdate: number | null = null) => {
    const wall = this.wallsMap.get(wallId);
    if (!wall) return;
    if (getEditorStore()?.wallConstructionMode) {
      whichEndToUpdate = WallConnectionEnd;
    }
    // Validate newLength so it is a positive number
    if (newLength <= 0) {
      console.error('New length must be a positive number');
      return;
    }
    this.pushToUndoStack();

    // Arc walls: scale proportionally from center to preserve shape
    if (wall.lineForm === 'arc' && wall.controlPoint) {
      const currentLength = this.wallLength(wall);
      if (currentLength < 1e-6) return;
      const scale = newLength / currentLength;
      const center = wall.start.clone().add(wall.end).multiplyScalar(0.5);
      const newStart = center.clone().add(wall.start.clone().sub(center).multiplyScalar(scale));
      const newEnd = center.clone().add(wall.end.clone().sub(center).multiplyScalar(scale));
      const newCP = center.clone().add(wall.controlPoint.clone().sub(center).multiplyScalar(scale));
      this.setWallEndPoints(wall.id, newStart, newEnd);
      this.setObjectProperty(wall.id, 'controlPoint', newCP);
      updateWallConnections(wall.id, null, [wall.id], this);
      this.objectNeedsUpdate(wall.id);
      this.markObjectForRoomUpdate(wall.id);
      this.setDirty();
      return;
    }

    // Pick an end to extend if not specified
    if (whichEndToUpdate === null) {
      const endsAvailable = this.getAvailableWallConnectionPositions(wall);
      if (endsAvailable.size === 0) {
        // No ends available, so we look at what end that points towards right or bottom
        const direction = wall.end.clone().sub(wall.start);
        const deltaX = direction.x;
        const deltaY = direction.y;

        if ((deltaX > 0 && Math.abs(deltaX) >= Math.abs(deltaY)) || (deltaY < 0 && Math.abs(deltaY) >= Math.abs(deltaX))) {
          // Wall is pointing towards right or down
          whichEndToUpdate = WallConnectionEnd;
        } else {
          whichEndToUpdate = WallConnectionStart;
        }
      } else {
        whichEndToUpdate = endsAvailable.values().next().value as number;
      }
    }

    // Store original endpoint position to calculate movement delta
    const originalEnd = wall.end.clone();
    const originalStart = wall.start.clone();

    // Update the wall length directly (no cap length adjustments - we use actual wall length)
    if (whichEndToUpdate === WallConnectionEnd) {
      const wallDirection = new THREE.Vector2().subVectors(wall.end, wall.start).normalize();
      const updatedEnd = wall.start.clone().add(wallDirection.clone().multiplyScalar(newLength));
      this.setWallEndPoints(wall.id, wall.start, updatedEnd);

      // Calculate the movement delta for connected walls
      const movementDelta = new THREE.Vector2().subVectors(updatedEnd, originalEnd);

      // Propagate movement to connected walls at the END
      this.propagateEndpointMovement(wall.id, 'end', movementDelta);

      updateWallConnections(wall.id, null, [wall.id], this);
    } else if (whichEndToUpdate === WallConnectionStart) {
      const wallDirection = new THREE.Vector2().subVectors(wall.start, wall.end).normalize();
      const updatedStart = wall.end.clone().add(wallDirection.clone().multiplyScalar(newLength));
      this.setWallEndPoints(wall.id, updatedStart, wall.end);

      // Calculate the movement delta for connected walls
      const movementDelta = new THREE.Vector2().subVectors(updatedStart, originalStart);

      // Propagate movement to connected walls at the START
      this.propagateEndpointMovement(wall.id, 'start', movementDelta);

      updateWallConnections(wall.id, null, [wall.id], this);
    }
    updateWallConnections(wall.id, null, [wall.id], this);
    this.objectNeedsUpdate(wall.id);
    this.markObjectForRoomUpdate(wall.id);
    this.setDirty();
  };

  /**
   * Propagate endpoint movement to all walls connected at the specified endpoint.
   * This moves connected walls by the specified delta to maintain connections.
   */
  propagateEndpointMovement = (wallId: string, endpoint: 'start' | 'end', movementDelta: THREE.Vector2) => {
    if (movementDelta.lengthSq() < 1e-9) return; // No significant movement

    const wall = this.wallsMap.get(wallId);
    if (!wall) return;

    const processedWalls = new Set<string>();
    processedWalls.add(wallId); // Don't move the source wall

    // Helper to move a connected wall's endpoint
    const moveConnectedWall = (connectedWallId: string, connectedEndpoint: 'start' | 'end') => {
      if (processedWalls.has(connectedWallId)) return;
      processedWalls.add(connectedWallId);

      const connectedWall = this.wallsMap.get(connectedWallId);
      if (!connectedWall) return;

      let newStart = connectedWall.start.clone();
      let newEnd = connectedWall.end.clone();

      if (connectedEndpoint === 'start') {
        newStart.add(movementDelta);
      } else {
        newEnd.add(movementDelta);
      }

      this.setWallEndPoints(connectedWallId, newStart, newEnd);
      this.objectNeedsUpdate(connectedWallId);
      this.markObjectForRoomUpdate(connectedWallId);
    };

    // Process edge connections
    wall.edgeConnections?.forEach((edgeConn) => {
      // Check if this edge connection is at the endpoint we're moving
      if (edgeConn.sourceEndpoint === endpoint) {
        moveConnectedWall(edgeConn.targetWallId, edgeConn.targetEndpoint as 'start' | 'end');
      }
    });

    // Process center connections
    wall.centerConnections?.forEach((centerConn) => {
      // For center connections, check sourcePosition
      const isAtEndpoint = (endpoint === 'start' && centerConn.sourcePosition === WallConnectionStart) || (endpoint === 'end' && centerConn.sourcePosition === WallConnectionEnd);

      if (isAtEndpoint) {
        // Determine which endpoint of the connected wall to move
        const connectedWall = this.wallsMap.get(centerConn.id);
        if (connectedWall) {
          // Check targetPosition to know which end of the connected wall
          let connectedEndpoint: 'start' | 'end' = 'start';
          if (centerConn.targetPosition === WallConnectionEnd) {
            connectedEndpoint = 'end';
          } else if (centerConn.targetPosition === WallConnectionStart) {
            connectedEndpoint = 'start';
          } else {
            // Mid-point connection - don't move these
            return;
          }
          moveConnectedWall(centerConn.id, connectedEndpoint);
        }
      }
    });

    // Also check for walls that connect TO this wall at the moved endpoint
    // (in case connections are stored on the other wall)
    this.wallsMap.forEach((otherWall, otherWallId) => {
      if (processedWalls.has(otherWallId)) return;

      // Check edge connections pointing to this wall at the moved endpoint
      otherWall.edgeConnections?.forEach((edgeConn) => {
        if (edgeConn.targetWallId === wallId && edgeConn.targetEndpoint === endpoint) {
          moveConnectedWall(otherWallId, edgeConn.sourceEndpoint as 'start' | 'end');
        }
      });

      // Check center connections pointing to this wall at the moved endpoint
      otherWall.centerConnections?.forEach((centerConn) => {
        if (centerConn.id === wallId) {
          const targetIsAtEndpoint = (endpoint === 'start' && centerConn.targetPosition === WallConnectionStart) || (endpoint === 'end' && centerConn.targetPosition === WallConnectionEnd);

          if (targetIsAtEndpoint) {
            let sourceEndpoint: 'start' | 'end' = 'start';
            if (centerConn.sourcePosition === WallConnectionEnd) {
              sourceEndpoint = 'end';
            } else if (centerConn.sourcePosition === WallConnectionStart) {
              sourceEndpoint = 'start';
            } else {
              // Mid-point source - don't move
              return;
            }
            moveConnectedWall(otherWallId, sourceEndpoint);
          }
        }
      });
    });
  };

  setWallEndPoints = (wallId: string, newStart: THREE.Vector2, newEnd: THREE.Vector2) => {
    const wall = this.wallsMap.get(wallId);
    if (wall) {
      this.pushToUndoStack();
      const updatedWall = {
        ...wall,
        start: newStart,
        end: newEnd,
      };
      this.wallsMap.set(wallId, updatedWall);
      if (updatedWall.symbolAttachments?.length) {
        this.refreshSymbolAttachmentsForWall(wallId);
      }
      this.markObjectForRoomUpdate(wallId);
      this.setDirty();
    }
  };

  private areVoidFillMetadataEqual = (a?: WallEdgeConnection['voidFill'], b?: WallEdgeConnection['voidFill']): boolean => {
    if (!a && !b) return true;
    if (!a || !b) return false;
    const tolerance = 1e-6;
    return a.required === b.required && a.ownerWallId === b.ownerWallId && a.reason === b.reason && a.fillColor === b.fillColor && a.outlineColor === b.outlineColor && Math.abs(a.outlineWidth - b.outlineWidth) < tolerance && !!a.outlineDashed === !!b.outlineDashed;
  };

  private areResolvedEdgeConnectionsEqual = (a: WallEdgeConnection, b: WallEdgeConnection): boolean => {
    const tolerance = 1e-6;
    return a.id === b.id &&
      a.sourceWallId === b.sourceWallId &&
      a.targetWallId === b.targetWallId &&
      a.sourceEndpoint === b.sourceEndpoint &&
      a.targetEndpoint === b.targetEndpoint &&
      a.sourceSide === b.sourceSide &&
      a.targetSide === b.targetSide &&
      a.sourceEdgePoint.distanceTo(b.sourceEdgePoint) < tolerance &&
      a.targetEdgePoint.distanceTo(b.targetEdgePoint) < tolerance &&
      Math.abs(a.gapDistance - b.gapDistance) < tolerance &&
      Math.abs(a.connectionAngle - b.connectionAngle) < tolerance &&
      this.areVoidFillMetadataEqual(a.voidFill, b.voidFill);
  };

  private getCanonicalEdgeJoinKey = (connection: WallEdgeConnection): string => {
    const forwardKey = `${connection.sourceWallId}:${connection.sourceEndpoint}->${connection.targetWallId}:${connection.targetEndpoint}`;
    if (connection.targetEndpoint === 'middle') {
      return forwardKey;
    }
    const reverseKey = `${connection.targetWallId}:${connection.targetEndpoint}->${connection.sourceWallId}:${connection.sourceEndpoint}`;
    return forwardKey < reverseKey ? forwardKey : reverseKey;
  };

  private upsertResolvedEdgeConnection = (wall: WallType, connection: WallEdgeConnection): boolean => {
    const list = wall.edgeConnections ?? [];
    const index = list.findIndex((existing) =>
      existing.sourceWallId === connection.sourceWallId &&
      existing.targetWallId === connection.targetWallId &&
      existing.sourceEndpoint === connection.sourceEndpoint &&
      existing.targetEndpoint === connection.targetEndpoint
    );

    if (index >= 0) {
      if (this.areResolvedEdgeConnectionsEqual(list[index], connection)) {
        return false;
      }
      const updated = [...list];
      updated[index] = connection;
      wall.edgeConnections = updated;
      this.wallsMap.set(wall.id, wall);
      return true;
    }

    wall.edgeConnections = [...list, connection];
    this.wallsMap.set(wall.id, wall);
    return true;
  };

  private createReciprocalResolvedEdgeConnection = (connection: WallEdgeConnection): WallEdgeConnection | null => {
    if (connection.targetEndpoint === 'middle') {
      return null;
    }

    return {
      id: connection.sourceWallId,
      sourceWallId: connection.targetWallId,
      targetWallId: connection.sourceWallId,
      sourceEndpoint: connection.targetEndpoint,
      targetEndpoint: connection.sourceEndpoint,
      sourceSide: connection.targetSide,
      targetSide: connection.sourceSide,
      sourceEdgePoint: connection.targetEdgePoint.clone(),
      targetEdgePoint: connection.sourceEdgePoint.clone(),
      gapDistance: connection.gapDistance,
      connectionAngle: connection.connectionAngle,
      voidFill: connection.voidFill ? { ...connection.voidFill } : undefined,
    };
  };

  resolveWallJoinsForGeometryChange = (wallIds: Iterable<string>, reason: string = 'geometry_change'): string[] => {
    const seedWallIds = new Set(Array.from(wallIds).filter((wallId) => this.wallsMap.has(wallId)));
    if (seedWallIds.size === 0) {
      return [];
    }

    const affectedWallIds = new Set<string>(seedWallIds);
    const changedWallIds = new Set<string>();
    const processedJoins = new Set<string>();

    this.wallsMap.forEach((sourceWall) => {
      // Snapshot so removeEdgeConnection during iteration cannot skip siblings.
      const connectionsSnapshot = [...(sourceWall.edgeConnections ?? [])];
      connectionsSnapshot.forEach((connection) => {
        const sourceWallId = connection.sourceWallId || sourceWall.id;
        const targetWallId = connection.targetWallId;
        if (!seedWallIds.has(sourceWallId) && !seedWallIds.has(targetWallId) && !seedWallIds.has(sourceWall.id)) {
          return;
        }

        const joinKey = this.getCanonicalEdgeJoinKey(connection);
        if (processedJoins.has(joinKey)) {
          return;
        }
        processedJoins.add(joinKey);

        const currentSourceWall = this.wallsMap.get(sourceWallId) ?? sourceWall;
        const currentTargetWall = this.wallsMap.get(targetWallId);
        if (!currentSourceWall || !currentTargetWall) {
          return;
        }

        const updatedConnection = recomputeEdgeConnectionForCurrentGeometry(
          currentSourceWall,
          currentTargetWall,
          connection,
        );
        affectedWallIds.add(currentSourceWall.id);
        affectedWallIds.add(currentTargetWall.id);

        // Stale joins can keep gapDistance ≈ 0 while endpoints drifted metres apart.
        // Drop them so corners heal instead of stretching to a phantom meet point.
        if (
          !updatedConnection ||
          !isEdgeConnectionWithinJoinRange(updatedConnection, currentSourceWall, currentTargetWall)
        ) {
          this.removeEdgeConnection(
            currentSourceWall.id,
            currentTargetWall.id,
            connection.sourceEndpoint,
          );
          changedWallIds.add(currentSourceWall.id);
          changedWallIds.add(currentTargetWall.id);
          return;
        }

        if (this.upsertResolvedEdgeConnection(currentSourceWall, updatedConnection)) {
          changedWallIds.add(currentSourceWall.id);
        }

        const reciprocalConnection = this.createReciprocalResolvedEdgeConnection(updatedConnection);
        if (reciprocalConnection) {
          const reciprocalWall = this.wallsMap.get(reciprocalConnection.sourceWallId);
          if (reciprocalWall && this.upsertResolvedEdgeConnection(reciprocalWall, reciprocalConnection)) {
            changedWallIds.add(reciprocalWall.id);
          }
        }
      });
    });

    // Drop duplicate / looser joins at the same endpoint so the wall does not
    // keep stretching toward a secondary neighbour (1612 affinity).
    affectedWallIds.forEach((wallId) => {
      if (this.pruneNonPrimaryEdgeJoinsOnWall(wallId)) {
        changedWallIds.add(wallId);
      }
    });

    const wallsToInvalidate = new Set([...affectedWallIds, ...changedWallIds]);
    wallsToInvalidate.forEach((wallId) => {
      renderStore.invalidateJointsForWall(wallId, `resolveWallJoinsForGeometryChange:${reason}`, {
        scheduleAffectedWallUpdates: false,
      });
      renderStore.clearOutlineSegments(wallId);
      wallGeometryCache.clearSimplifiedGeometry(wallId);
    });

    changedWallIds.forEach((wallId) => {
      this.incrementWallGeometryVersion(wallId);
    });

    return Array.from(wallsToInvalidate);
  };

  /**
   * Remove duplicate edge joins to the same target at the same source endpoint
   * (same-target dedupe). Keeps the primary join from selectPrimaryEdgeConnections;
   * only drops losers by full (targetWallId, sourceEndpoint, targetEndpoint) key
   * so the kept join is never wiped.
   */
  pruneNonPrimaryEdgeJoinsOnWall = (wallId: string): boolean => {
    const wall = this.wallsMap.get(wallId);
    if (!wall?.edgeConnections?.length) {
      return false;
    }
    // Snapshot neighbours before prune (includes targets of removed duplicates).
    const neighbourIds = new Set(
      wall.edgeConnections.map((connection) => connection.targetWallId),
    );
    const changed = this.pruneNonPrimaryEdgeJoinsOnWallOnce(wallId);
    if (!changed) {
      return false;
    }
    neighbourIds.forEach((targetId) => {
      this.pruneNonPrimaryEdgeJoinsOnWallOnce(targetId);
    });
    return true;
  };

  /**
   * Single-pass same-target dedupe. Assigns the primary set, then strips exact
   * reciprocals for removed joins only (never removeEdgeConnection by endpoint
   * alone — that deleted the kept join too).
   */
  private pruneNonPrimaryEdgeJoinsOnWallOnce = (wallId: string): boolean => {
    const wall = this.wallsMap.get(wallId);
    if (!wall?.edgeConnections?.length) {
      return false;
    }

    const before = wall.edgeConnections;
    const toRemove = findNonPrimaryEdgeConnections(before);
    if (toRemove.length === 0) {
      return false;
    }

    wall.edgeConnections = selectPrimaryEdgeConnections(before);
    this.resetWallGeometryAfterConnectionChange(wallId);

    toRemove.forEach((connection) => {
      // T-junctions (target middle) are one-way — no reciprocal to clear.
      if (connection.targetEndpoint === 'middle') {
        return;
      }
      const targetWall = this.wallsMap.get(connection.targetWallId);
      if (!targetWall?.edgeConnections?.length) {
        return;
      }
      const next = targetWall.edgeConnections.filter(
        (conn) =>
          !(
            conn.targetWallId === wallId &&
            conn.sourceEndpoint === connection.targetEndpoint &&
            conn.targetEndpoint === connection.sourceEndpoint
          ),
      );
      if (next.length !== targetWall.edgeConnections.length) {
        targetWall.edgeConnections = next;
        this.resetWallGeometryAfterConnectionChange(targetWall.id);
        renderStore.invalidateJointsForWall(targetWall.id, 'pruneNonPrimaryEdgeJoins', {
          scheduleAffectedWallUpdates: false,
        });
      }
    });

    renderStore.invalidateJointsForWall(wallId, 'pruneNonPrimaryEdgeJoins', {
      scheduleAffectedWallUpdates: false,
    });
    this.objectNeedsUpdate(wallId);
    return true;
  };

  setObjectProperty = (source: string, property: string, value: any) => {
    const object = this.findObjectId(source);
    if (object) {
      // Validate wall width if that's what's being set
      if (property === 'wallWidth' && typeof value === 'number') {
        if (!Number.isFinite(value)) {
          console.error(`setObjectProperty: Rejecting invalid wallWidth for ${source}: ${value} (NaN or Infinity)`);
          return;
        }
        if (value > MAX_WALL_WIDTH) {
          console.error(`setObjectProperty: Rejecting wallWidth ${value}m for ${source} - exceeds maximum of ${MAX_WALL_WIDTH}m`);
          return;
        }
        if (value < 0) {
          console.error(`setObjectProperty: Rejecting negative wallWidth ${value}m for ${source}`);
          return;
        }
      }

      if (isWallType(object)) {
        const isWallVisualStyleProperty =
          property === 'lineType' ||
          property === 'lineWeight' ||
          property === 'lineColor' ||
          property === 'fillColor' ||
          property === 'fillOpacity' ||
          property === 'fillPattern';
        const isWallWidthProperty = property === 'wallWidth';
        const isWallAttachmentGeometryProperty =
          isWallWidthProperty ||
          property === 'controlPoint' ||
          property === 'lineForm';
        const shouldSkipRoomUpdate =
          isWallVisualStyleProperty ||
          isWallWidthProperty ||
          property === 'lastUpdate' ||
          property === 'selected' ||
          property === 'threeD' ||
          property === 'cornerState';
        const updatedObject: WallType = {
          ...object,
          [property]: value,
        };
        if (property === 'controlPoint') {
          updatedObject.geometryVersion = (object.geometryVersion || 0) + 1;
        }
        if (isWallWidthProperty) {
          updatedObject.geometryVersion = (object.geometryVersion || 0) + 1;
          updatedObject.lastUpdate = Date.now();
        }
        if (isWallVisualStyleProperty) {
          updatedObject.lastUpdate = (object.lastUpdate || 0) + 1;
        }
        // Ready / catalog wall materials live on threeD; bump lastUpdate so
        // 3D signatures refresh when switching or clearing materials.
        if (property === 'threeD') {
          updatedObject.lastUpdate = Date.now();
        }
        this.wallsMap.set(object.id, updatedObject);
        if (updatedObject.symbolAttachments?.length && isWallAttachmentGeometryProperty) {
          if (isWallWidthProperty) {
            updatedObject.symbolAttachments.forEach((attachment) => {
              const symbol = this.symbolsMap.get(attachment.symbolId);
              if (isWindowType(symbol)) {
                const windowWidth = (updatedObject.wallWidth || this.wallWidth) < 0.1 ? updatedObject.wallWidth || this.wallWidth : 0.1;
                this.updateSymbolProperty(symbol.id, 'windowWidth', windowWidth);
              }
              if (isVoidType(symbol)) {
                const voidWidth = (updatedObject.wallWidth || this.wallWidth) < 0.1 ? updatedObject.wallWidth || this.wallWidth : 0.1;
                this.updateSymbolProperty(symbol.id, 'voidWidth', voidWidth);
              }
            });
          }
          this.refreshSymbolAttachmentsForWall(object.id);
        }
        let wallJoinAffectedIds: string[] = [object.id];
        if (isWallVisualStyleProperty) {
          if (property === 'lineType' || property === 'lineWeight' || property === 'lineColor') {
            renderStore.clearOutlineSegments(object.id);
            renderStore.generateOutlineSegments(object.id);
            renderStore.updateJointLineStyleForWall(
              object.id,
              updatedObject.lineColor ?? this.lineColor,
              updatedObject.lineWeight ?? this.wallLineWeight,
              (updatedObject.lineType as LineType | undefined) ?? LineType.solid,
            );
          }

          if (property === 'fillColor' || property === 'fillOpacity' || property === 'fillPattern') {
            renderStore.updateJointFillStyleForWall(
              object.id,
              updatedObject.fillColor ?? this.fillColor,
              updatedObject.fillPattern,
              updatedObject.fillOpacity,
            );
          }

          renderStore.requestInvalidation(object.id);
        } else if (isWallWidthProperty) {
          wallJoinAffectedIds = this.resolveWallJoinsForGeometryChange([object.id], 'wallWidth_change');
          renderStore.refreshWallWidthGeometry(wallJoinAffectedIds.length > 0 ? wallJoinAffectedIds : [object.id]);
        } else if (property !== 'lastUpdate') {
          this.objectNeedsUpdate(object.id);
        }
        if (isWallWidthProperty) {
          const roomAffectedObjectIds = new Set(this.getWallRoomGeometryAffectedObjectIds(updatedObject));
          wallJoinAffectedIds.forEach((wallId) => {
            roomAffectedObjectIds.add(wallId);
          });
          this.refreshRoomsForObjects(roomAffectedObjectIds);
        } else if (!shouldSkipRoomUpdate) {
          this.markObjectForRoomUpdate(object.id);
        }
      } else {
        const updatedObject = {
          ...object,
          [property]: value,
        };
        if (isSingleLineType(updatedObject)) {
          this.singleLinesMap.set(object.id, updatedObject);
        } else if (isRulerLineType(updatedObject)) {
          this.rulerLinesMap.set(object.id, updatedObject);
        }
      }
      if (property !== 'lastUpdate') {
        this.setDirty();
      }
    }
  };

  angleBetweenTwoWalls = (wall1: WallType, wall2: WallType): number => {
    // For arc walls, use tangent at the shared endpoint for accurate angle calculation
    let wall1Direction: THREE.Vector2;
    let wall2Direction: THREE.Vector2;

    if (wall1.lineForm === 'arc' && wall1.controlPoint) {
      // Determine which endpoint of wall1 is closest to wall2
      const d1Start = Math.min(wall1.start.distanceTo(wall2.start), wall1.start.distanceTo(wall2.end));
      const d1End = Math.min(wall1.end.distanceTo(wall2.start), wall1.end.distanceTo(wall2.end));
      const endpoint1 = d1Start < d1End ? 'start' : 'end';
      wall1Direction = getArcTangentAtEndpoint(wall1, endpoint1);
    } else {
      wall1Direction = new THREE.Vector2().subVectors(wall1.end, wall1.start).normalize();
    }

    if (wall2.lineForm === 'arc' && wall2.controlPoint) {
      const d2Start = Math.min(wall2.start.distanceTo(wall1.start), wall2.start.distanceTo(wall1.end));
      const d2End = Math.min(wall2.end.distanceTo(wall1.start), wall2.end.distanceTo(wall1.end));
      const endpoint2 = d2Start < d2End ? 'start' : 'end';
      wall2Direction = getArcTangentAtEndpoint(wall2, endpoint2);
    } else {
      wall2Direction = new THREE.Vector2().subVectors(wall2.end, wall2.start).normalize();
    }

    return wall1Direction.angleTo(wall2Direction);
  };

  wallStartCapLength = (source: WallType): number => {
    if (!source) return 0;
    const wall = this.wallsMap.get(source.id);
    if (!wall) return 0;

    // Check for edge connections first - if there's an edge connection, never adjust length
    // Check BOTH when this wall is the source AND when it's the target
    // Also check dragState.originalEdgeConnections since connections may be cleared during drag/construction
    const dragState = this.wallDragState.get(wall.id);
    const allEdgeConnections = [...(wall.edgeConnections || []), ...(dragState?.originalEdgeConnections || [])];

    if (allEdgeConnections.length > 0) {
      const startEdgeConnection = allEdgeConnections.find((connection) => (connection.sourceWallId === wall.id && connection.sourceEndpoint === 'start') || (connection.targetWallId === wall.id && connection.targetEndpoint === 'start'));
      if (startEdgeConnection) {
        // For edge connections, always use raw wall length - no adjustment
        return 0;
      }
    }

    let startConnectedWall: WallType | undefined = undefined;
    const startConnection = wall.centerConnections?.find((connection) => connection.sourcePosition === WallConnectionStart);
    if (startConnection) {
      startConnectedWall = this.wallsMap.get(startConnection.id);
    }

    if (!startConnectedWall) {
      return 0;
    }
    // Get the angle between the connected wall and the current wall
    const angle = Math.abs(this.angleBetweenTwoWalls(startConnectedWall, wall));
    // When the angle 180 degrees, the start cap length is 0 and increases as the angle decreases
    return ((startConnectedWall.wallWidth || this.wallWidth) / 2) * Math.sin(angle);
  };

  wallEndCapLength = (source: WallType): number => {
    if (!source) return 0;
    const wall = this.wallsMap.get(source.id);
    if (!wall) return 0;

    // Check for edge connections first - if there's an edge connection, never adjust length
    // Check BOTH when this wall is the source AND when it's the target
    // Also check dragState.originalEdgeConnections since connections may be cleared during drag/construction
    const dragState = this.wallDragState.get(wall.id);
    const allEdgeConnections = [...(wall.edgeConnections || []), ...(dragState?.originalEdgeConnections || [])];

    if (allEdgeConnections.length > 0) {
      const endEdgeConnection = allEdgeConnections.find((connection) => (connection.sourceWallId === wall.id && connection.sourceEndpoint === 'end') || (connection.targetWallId === wall.id && connection.targetEndpoint === 'end'));
      if (endEdgeConnection) {
        // For edge connections, always use raw wall length - no adjustment
        return 0;
      }
    }

    let endConnectedWall: WallType | undefined = undefined;
    const endConnection = wall.centerConnections?.find((connection) => connection.sourcePosition === WallConnectionEnd);
    if (endConnection) {
      endConnectedWall = this.wallsMap.get(endConnection.id);
    }

    if (!endConnectedWall) {
      return 0;
    }
    // Get the angle between the connected wall and the current wall
    const angle = Math.abs(this.angleBetweenTwoWalls(endConnectedWall, wall));
    // When the angle 180 degrees, the end cap length is 0 and increases as the angle decreases
    return ((endConnectedWall.wallWidth || this.wallWidth) / 2) * Math.sin(angle);
  };

  /**
   * Calculates the effective outline length for a specific wall side using the
   * pre-segmented outlines. This captures endpoint trimming caused by attached
   * walls while ignoring interior gaps (doors, cutouts, mid connections).
   *
   * Falls back to null if outline segments are unavailable so callers can use
   * geometric defaults instead.
   */
  getOutlineSideExtents = (wall: WallType, side: 'left' | 'right'): { startT: number; endT: number; length: number } | null => {
    if (!wall) return null;

    const wallDirectionVec = new THREE.Vector2().subVectors(wall.end, wall.start);
    const wallLength = wallDirectionVec.length();
    if (wallLength < 1e-6) {
      return { startT: 0, endT: 0, length: 0 };
    }

    const outlineSegments = renderStore.getOutlineSegments(wall.id);
    const sideSegments = outlineSegments ? (side === 'left' ? outlineSegments.leftSegments : outlineSegments.rightSegments) : null;

    if (!sideSegments || sideSegments.length === 0) {
      return null;
    }

    const wallDirection = wallDirectionVec.normalize();
    const wallPerpendicular = new THREE.Vector2(-wallDirection.y, wallDirection.x);
    const offset = ((wall.wallWidth || this.wallWidth) / 2) * (side === 'left' ? 1 : -1);
    const sideOrigin = new THREE.Vector2().copy(wall.start).addScaledVector(wallPerpendicular, offset);

    let minT = Infinity;
    let maxT = -Infinity;

    for (const segment of sideSegments) {
      const startT = new THREE.Vector2().subVectors(segment.start, sideOrigin).dot(wallDirection);
      const endT = new THREE.Vector2().subVectors(segment.end, sideOrigin).dot(wallDirection);
      const segMin = Math.min(startT, endT);
      const segMax = Math.max(startT, endT);
      if (segMin < minT) minT = segMin;
      if (segMax > maxT) maxT = segMax;
    }

    if (!Number.isFinite(minT) || !Number.isFinite(maxT) || maxT < minT) {
      return null;
    }

    // Clamp to the original wall length to avoid tiny overshoots from float error
    const extentLength = Math.min(Math.max(0, maxT - minT), wallLength);
    return {
      startT: Math.max(0, minT),
      endT: Math.min(wallLength, maxT),
      length: extentLength,
    };
  };

  /**
   * Calculates the outer measure of a wall (longest outline start to end distance).
   * The outer measure is the longest distance between the start and end points of either outline.
   *
   * @param wall - The wall to calculate the outer measure for
   * @returns The outer measure length
   */
  wallOuterLength = (wall: WallType): number => {
    if (!wall) return 0;

    // Prefer outline-based extents so endpoint trimming is respected
    const leftExtent = this.getOutlineSideExtents(wall, 'left')?.length ?? null;
    const rightExtent = this.getOutlineSideExtents(wall, 'right')?.length ?? null;
    const outlineExtents = [leftExtent, rightExtent].filter((v): v is number => v !== null);
    if (outlineExtents.length > 0) {
      return Math.max(...outlineExtents);
    }

    // Calculate wall direction vector
    const wallDirectionVec = new THREE.Vector2().subVectors(wall.end, wall.start);
    const wallLength = wallDirectionVec.length();

    // Handle zero-length walls
    if (wallLength < 1e-6) {
      return 0;
    }

    // Normalize direction and calculate perpendicular
    const wallDirection = wallDirectionVec.normalize();
    const wallPerpendicular = new THREE.Vector2(-wallDirection.y, wallDirection.x);

    // Get wall width
    const wallWidth = wall.wallWidth || this.wallWidth;
    const leftOffset = wallWidth / 2;
    const rightOffset = -wallWidth / 2;

    // Calculate outline start and end points
    const leftStart = new THREE.Vector2().copy(wall.start).addScaledVector(wallPerpendicular, leftOffset);
    const leftEnd = new THREE.Vector2().copy(wall.end).addScaledVector(wallPerpendicular, leftOffset);
    const rightStart = new THREE.Vector2().copy(wall.start).addScaledVector(wallPerpendicular, rightOffset);
    const rightEnd = new THREE.Vector2().copy(wall.end).addScaledVector(wallPerpendicular, rightOffset);

    // Calculate distances for both outlines
    const leftDistance = leftStart.distanceTo(leftEnd);
    const rightDistance = rightStart.distanceTo(rightEnd);

    // Outer measure is the longest distance
    return Math.max(leftDistance, rightDistance);
  };

  /**
   * Calculates the inner measure of a wall (shortest outline start to end distance).
   * The inner measure is the shortest distance between the start and end points of either outline.
   *
   * @param wall - The wall to calculate the inner measure for
   * @returns The inner measure length
   */
  wallInnerLength = (wall: WallType): number => {
    if (!wall) return 0;

    // Prefer outline-based extents so endpoint trimming is respected
    const leftExtent = this.getOutlineSideExtents(wall, 'left')?.length ?? null;
    const rightExtent = this.getOutlineSideExtents(wall, 'right')?.length ?? null;
    const outlineExtents = [leftExtent, rightExtent].filter((v): v is number => v !== null);
    if (outlineExtents.length > 0) {
      return Math.min(...outlineExtents);
    }

    // Calculate wall direction vector
    const wallDirectionVec = new THREE.Vector2().subVectors(wall.end, wall.start);
    const wallLength = wallDirectionVec.length();

    // Handle zero-length walls
    if (wallLength < 1e-6) {
      return 0;
    }

    // Normalize direction and calculate perpendicular
    const wallDirection = wallDirectionVec.normalize();
    const wallPerpendicular = new THREE.Vector2(-wallDirection.y, wallDirection.x);

    // Get wall width
    const wallWidth = wall.wallWidth || this.wallWidth;
    const leftOffset = wallWidth / 2;
    const rightOffset = -wallWidth / 2;

    // Calculate outline start and end points
    const leftStart = new THREE.Vector2().copy(wall.start).addScaledVector(wallPerpendicular, leftOffset);
    const leftEnd = new THREE.Vector2().copy(wall.end).addScaledVector(wallPerpendicular, leftOffset);
    const rightStart = new THREE.Vector2().copy(wall.start).addScaledVector(wallPerpendicular, rightOffset);
    const rightEnd = new THREE.Vector2().copy(wall.end).addScaledVector(wallPerpendicular, rightOffset);

    // Calculate distances for both outlines
    const leftDistance = leftStart.distanceTo(leftEnd);
    const rightDistance = rightStart.distanceTo(rightEnd);

    // Inner measure is the shortest distance
    return Math.min(leftDistance, rightDistance);
  };

  /**
   * Choose which side of the wall to render the measurement line on.
   * The returned side refers to the offset direction (left/right of the wall vector).
   * Uses a small hysteresis window so tiny outline differences don't flip sides mid-drag.
   */
  private computeMeasurementDisplaySide = (wallId: string, leftExtent?: OutlineExtent | null, rightExtent?: OutlineExtent | null): 'left' | 'right' => {
    const wall = this.wallsMap.get(wallId);
    if (!wall) return 'right';

    const previous = this.measurementSidePreference.get(wallId);

    // 1. Length-Based Decision
    // If we have distinct lengths, the longer side is always "outer"
    if (leftExtent && rightExtent) {
      const diff = leftExtent.length - rightExtent.length;
      if (Math.abs(diff) >= MEASUREMENT_SIDE_EPSILON) {
        // If left is shorter (inner), we want the measurement on the outer (right)
        // If left is longer (diff > 0), left is outer.
        return diff < 0 ? 'right' : 'left';
      }
    } else if (leftExtent && !rightExtent) {
      // If we only have left extent, the wall likely has "inner" features on the left?
      // Or simply, we default to "right" (Outer) to keep it clean.
    }

    // 2. Connection-Based Fallback
    // Used when lengths are equal or outlines are missing (e.g. SingleLines or simple walls)
    // We analyze the direction of connected walls to determine which side is "Inner" (concave) vs "Outer" (convex).
    // The measurement should be placed on the Outer side.

    let leftInnerScore = 0; // Higher score => Left side is Inner (Turns Left)
    let rightInnerScore = 0; // Higher score => Right side is Inner (Turns Right)

    let leftDirtyScore = 0; // Higher score => Left side has middle connections sticking out
    let rightDirtyScore = 0; // Higher score => Right side has middle connections sticking out

    const wallDir = new THREE.Vector2().subVectors(wall.end, wall.start).normalize();

    // Helper to analyze a connected wall's direction relative to this wall
    const analyzeConnection = (
      otherWallId: string,
      connectionType: 'start' | 'end' | 'middle', // Where it connects on THIS wall
      targetEndpoint?: 'start' | 'end' | 'middle' | number, // Where THIS wall connects on the OTHER wall (if known)
    ) => {
      const otherWall = this.wallsMap.get(otherWallId);
      if (!otherWall || !isWallType(otherWall)) return;

      // Determine the vector of the other wall pointing AWAY from the connection
      let otherDir: THREE.Vector2 | null = null;

      // If we know the target endpoint, use it
      if (targetEndpoint === 'start' || targetEndpoint === WallConnectionStart) {
        otherDir = new THREE.Vector2().subVectors(otherWall.end, otherWall.start);
      } else if (targetEndpoint === 'end' || targetEndpoint === WallConnectionEnd) {
        otherDir = new THREE.Vector2().subVectors(otherWall.start, otherWall.end);
      } else {
        // Semantic fallback if targetEndpoint is not explicit or is "middle"
        // Heuristic: Check distance to Start/End of other wall to guess orientation
        // (Only safe if connection is at an endpoint of other wall)
        const connectionPoint = connectionType === 'start' ? wall.start : connectionType === 'end' ? wall.end : null;

        if (connectionPoint) {
          const dStart = otherWall.start.distanceToSquared(connectionPoint);
          const dEnd = otherWall.end.distanceToSquared(connectionPoint);
          if (dStart < dEnd) otherDir = new THREE.Vector2().subVectors(otherWall.end, otherWall.start);
          else otherDir = new THREE.Vector2().subVectors(otherWall.start, otherWall.end);
        }
      }

      if (!otherDir) return;

      otherDir.normalize();
      const cp = wallDir.x * otherDir.y - wallDir.y * otherDir.x;

      // Threshold for "turning"
      const CP_THRESHOLD = 0.01;

      if (connectionType === 'start' || connectionType === 'end') {
        // Endpoint Connections: Determine Inner/Outer Corner
        // If CP > 0: Other wall turns Left. Left side is Inner.
        // If CP < 0: Other wall turns Right. Right side is Inner.
        if (cp > CP_THRESHOLD) leftInnerScore++;
        else if (cp < -CP_THRESHOLD) rightInnerScore++;
      } else {
        // Middle Connections: Determine Cleanest Side
        // If CP > 0: Points Left. Left side is "dirty".
        // If CP < 0: Points Right. Right side is "dirty".
        if (cp > CP_THRESHOLD) leftDirtyScore++;
        else if (cp < -CP_THRESHOLD) rightDirtyScore++;
      }
    };

    // Process Center Connections
    wall.centerConnections?.forEach((conn) => {
      const type = conn.sourcePosition === WallConnectionStart ? 'start' : conn.sourcePosition === WallConnectionEnd ? 'end' : 'middle';
      analyzeConnection(conn.id, type, conn.targetPosition as any);
    });

    // Process Edge Connections
    wall.edgeConnections?.forEach((conn) => {
      const type = conn.sourceEndpoint; // "start" | "end" | "middle"
      analyzeConnection(conn.targetWallId, type, conn.targetEndpoint);
    });

    // Decision Priority 1: Inner/Outer Corner Logic (Geometric "Outer" is preferred)
    // Inner Score means "Concave" / "Busy". We want "Outer" / "Clear".
    if (leftInnerScore > rightInnerScore) return 'right'; // Left is Inner, so Right is Outer
    if (rightInnerScore > leftInnerScore) return 'left'; // Right is Inner, so Left is Outer

    // Decision Priority 2: Cleanest Side (Fewer middle obstructions)
    if (leftDirtyScore > rightDirtyScore) return 'right'; // Left is dirty, pick Right
    if (rightDirtyScore > leftDirtyScore) return 'left'; // Right is dirty, pick Left

    // Fallback: Use exisiting extents if they exist (even if diff is small, maybe one is non-null?)
    if (leftExtent && !rightExtent) return 'right';
    if (rightExtent && !leftExtent) return 'left';

    // Final Fallback: Stability or Default
    return previous ?? 'right';
  };

  setMeasurementSidePreference = (wallId: string, side: 'left' | 'right') => {
    if (this.measurementSidePreference.get(wallId) !== side) {
      this.measurementSidePreference.set(wallId, side);
    }
  };

  /**
   * Returns the side to offset the measurement line to, honoring active locks during drags.
   */
  getMeasurementDisplaySide = (wall: WallType, leftExtent?: OutlineExtent | null, rightExtent?: OutlineExtent | null): 'left' | 'right' => {
    const locked = this.measurementSideLocks.get(wall.id);
    if (locked) {
      return locked;
    }
    return this.computeMeasurementDisplaySide(wall.id, leftExtent, rightExtent);
  };

  lockMeasurementDisplaySide = (wallId: string, side?: 'left' | 'right') => {
    const wall = this.wallsMap.get(wallId);
    if (!wall || !isWallType(wall)) {
      return;
    }
    const leftExtent = this.getOutlineSideExtents(wall, 'left');
    const rightExtent = this.getOutlineSideExtents(wall, 'right');
    const resolvedSide = side ?? this.computeMeasurementDisplaySide(wallId, leftExtent, rightExtent);
    this.measurementSideLocks.set(wallId, resolvedSide);
  };

  unlockMeasurementDisplaySide = (wallId: string) => {
    this.measurementSideLocks.delete(wallId);
  };

  /**
   * Determine which side of the wall (left/right of the wall's forward
   * direction) corresponds to the architectural "inner" (concave) side,
   * based purely on connection geometry at the wall's endpoints.
   *
   * This is used to drive the displayed wall length: we measure along the
   * inner side's outline so the value matches the inside dimension of the
   * room, regardless of whether that happens to be the longer or shorter
   * outline (which varies for L-corners vs curved / obtuse junctions).
   *
   * Returns null when connections don't clearly indicate an inner side so
   * callers can fall back to the generic outline overlap.
   *
   * See {@link computeInnerMeasurementSide} for the geometric details.
   */
  getInnerMeasurementSide = (wall: WallType): 'left' | 'right' | null => {
    return computeInnerMeasurementSide(wall, (id) => this.wallsMap.get(id));
  };

  /**
   * Returns the perpendicular vector to the object that points towards the inner side of the room.
   *
   * To decide which side of the object is the inner side, we must look at the connected walls and their directions.
   * If the connected line crosses the wall direction, the inner side is the side where the connected line is pointing towards.
   *
   * @param object The wall to calculate the perpendicular vector for.
   * @returns The perpendicular vector to the wall that points towards the inner side of the room.
   *
   */
  objectPerpendicularInner = (object: WallType | SingleLineType | RulerLineType): THREE.Vector2 => {
    // Step 1: Get the direction of the wall (normalized)
    const objectDirection = new THREE.Vector2().subVectors(object.end, object.start).normalize();

    // Step 2: Find the connected wall at the start or end (find does not work so let's use forEach)
    let startConnection: WallConnectionType | undefined = undefined as WallConnectionType | undefined;
    let endConnection: WallConnectionType | undefined = undefined as WallConnectionType | undefined;
    let connectedWallStart: THREE.Vector2 | null = null;
    let connectedWallEnd: THREE.Vector2 | null = null;
    let connectionPoint: THREE.Vector2 | null = null;

    object.centerConnections?.forEach((connection) => {
      if (connection.sourcePosition === WallConnectionStart) {
        startConnection = connection;
      } else if (connection.sourcePosition === WallConnectionEnd) {
        endConnection = connection;
      }
    });

    // Step 3: Get the connected wall's start and end points and determine the connection point
    if (isWallType(object)) {
      if (startConnection && (startConnection.targetPosition === WallConnectionStart || startConnection.targetPosition === WallConnectionEnd) && this.wallsMap.get(startConnection.id)) {
        const connectedWall = this.wallsMap.get(startConnection.id);
        connectedWallStart = new THREE.Vector2(connectedWall?.start.x, connectedWall?.start.y);
        connectedWallEnd = new THREE.Vector2(connectedWall?.end.x, connectedWall?.end.y);
        connectionPoint = object.start;
      } else if (endConnection && this.wallsMap.get(endConnection.id)) {
        const connectedWall = this.wallsMap.get(endConnection.id);
        connectedWallStart = new THREE.Vector2(connectedWall?.start.x, connectedWall?.start.y);
        connectedWallEnd = new THREE.Vector2(connectedWall?.end.x, connectedWall?.end.y);
        connectionPoint = object.end;
      }
    } else if (isSingleLineType(object)) {
      if (startConnection && this.singleLinesMap.get(startConnection.id)) {
        const connectedLine = this.singleLinesMap.get(startConnection.id);
        connectedWallStart = new THREE.Vector2(connectedLine?.start.x, connectedLine?.start.y);
        connectedWallEnd = new THREE.Vector2(connectedLine?.end.x, connectedLine?.end.y);
        connectionPoint = object.start;
      } else if (endConnection && this.singleLinesMap.get(endConnection.id)) {
        const connectedLine = this.singleLinesMap.get(endConnection.id);
        connectedWallStart = new THREE.Vector2(connectedLine?.start.x, connectedLine?.start.y);
        connectedWallEnd = new THREE.Vector2(connectedLine?.end.x, connectedLine?.end.y);
        connectionPoint = object.end;
      }
    } else if (isRulerLineType(object)) {
      if (startConnection && this.rulerLinesMap.get(startConnection.id)) {
        const connectedLine = this.rulerLinesMap.get(startConnection.id);
        connectedWallStart = new THREE.Vector2(connectedLine?.start.x, connectedLine?.start.y);
        connectedWallEnd = new THREE.Vector2(connectedLine?.end.x, connectedLine?.end.y);
        connectionPoint = object.start;
      } else if (endConnection && this.rulerLinesMap.get(endConnection.id)) {
        const connectedLine = this.rulerLinesMap.get(endConnection.id);
        connectedWallStart = new THREE.Vector2(connectedLine?.start.x, connectedLine?.start.y);
        connectedWallEnd = new THREE.Vector2(connectedLine?.end.x, connectedLine?.end.y);
        connectionPoint = object.end;
      }
    }
    // Step 4: If no connected wall is found, return the default perpendicular vector
    if (!connectedWallStart || !connectedWallEnd || !connectionPoint) {
      return new THREE.Vector2(-objectDirection.y, objectDirection.x); // Default perpendicular vector
    }

    // Step 5: Calculate the vector from the connection point to the connected wall
    // If it's a start connection, calculate from the start point of the wall to the connected wall's start point
    // If it's an end connection, calculate from the end point of the wall to the connected wall's start point
    let toConnectedWall: THREE.Vector2;
    if (connectedWallStart.distanceTo(connectionPoint) > connectedWallEnd.distanceTo(connectionPoint)) {
      toConnectedWall = new THREE.Vector2().subVectors(connectedWallStart, connectionPoint);
    } else {
      toConnectedWall = new THREE.Vector2().subVectors(connectedWallEnd, connectionPoint);
    }

    // Step 6: Calculate the cross product between the wall direction and the vector to the connected wall
    const crossProduct = objectDirection.x * toConnectedWall.y - objectDirection.y * toConnectedWall.x;

    // Step 7: Based on the sign of the cross product, decide the perpendicular direction
    if (crossProduct < 0) {
      // The connected wall is on one side of the wall, so return the flipped perpendicular vector
      return new THREE.Vector2(objectDirection.y, -objectDirection.x);
    } else {
      // The connected wall has crossed the source wall, so return the default perpendicular vector
      return new THREE.Vector2(-objectDirection.y, objectDirection.x);
    }
  };

  objectPerpendicularOuter = (object: WallType | SingleLineType | RulerLineType): THREE.Vector2 => {
    // the opposite direction of the inner perpendicular
    return this.objectPerpendicularInner(object).negate();
  };

  objectDirection = (object: WallType | SingleLineType | RulerLineType): THREE.Vector2 => {
    return new THREE.Vector2().subVectors(object.end, object.start).normalize();
  };

  objectOppositeDirection = (object: WallType | SingleLineType | RulerLineType): THREE.Vector2 => {
    return this.objectDirection(object).negate();
  };

  // Return a set containing WallConnectionStart or WallConnectionEnd that is available for connection
  getAvailableWallConnectionPositions = (wall: WallType): Set<number> => {
    const availablePositions = new Set([WallConnectionStart, WallConnectionEnd]);
    wall.centerConnections?.forEach((connection) => {
      availablePositions.delete(connection.sourcePosition);
    });
    // Also check edge connections - check BOTH when this wall is the source AND when it's the target
    wall.edgeConnections?.forEach((edgeConnection) => {
      // When this wall is the source
      if (edgeConnection.sourceWallId === wall.id) {
        if (edgeConnection.sourceEndpoint === 'start') {
          availablePositions.delete(WallConnectionStart);
        } else if (edgeConnection.sourceEndpoint === 'end') {
          availablePositions.delete(WallConnectionEnd);
        }
      }
      // When this wall is the target
      if (edgeConnection.targetWallId === wall.id) {
        if (edgeConnection.targetEndpoint === 'start') {
          availablePositions.delete(WallConnectionStart);
        } else if (edgeConnection.targetEndpoint === 'end') {
          availablePositions.delete(WallConnectionEnd);
        }
      }
    });
    return availablePositions;
  };

  wallIsConnected = (wall: WallType, onlyCheckEnds: boolean = false): boolean => {
    let connected = false;
    wall.centerConnections?.forEach((connection) => {
      if (onlyCheckEnds) {
        if (connection.sourcePosition === WallConnectionStart || connection.sourcePosition === WallConnectionEnd) {
          connected = true;
        }
        //} else if (this.walls[connection.id]) {
      } else if (this.wallsMap.get(connection.id)) {
        connected = true;
      }
    });
    // Also check edge connections - check BOTH when this wall is the source AND when it's the target
    if (!connected && wall.edgeConnections) {
      wall.edgeConnections.forEach((edgeConnection) => {
        if (onlyCheckEnds) {
          // When this wall is the source, check sourceEndpoint
          if (edgeConnection.sourceWallId === wall.id) {
            if (edgeConnection.sourceEndpoint === 'start' || edgeConnection.sourceEndpoint === 'end') {
              connected = true;
            }
          }
          // When this wall is the target, check targetEndpoint
          if (edgeConnection.targetWallId === wall.id) {
            if (edgeConnection.targetEndpoint === 'start' || edgeConnection.targetEndpoint === 'end') {
              connected = true;
            }
          }
        } else if (this.wallsMap.get(edgeConnection.targetWallId)) {
          connected = true;
        }
      });
    }
    return connected;
  };

  objectConnectedTo = (objectId: string, atPosition: number): string | undefined => {
    const object = this.findObjectId(objectId);
    if (!object) return undefined;
    const connection = object.centerConnections?.find((connection) => connection.sourcePosition === atPosition);
    if (connection) {
      return connection.id;
    }
    // Also check edge connections for walls
    if (isWallType(object) && object.edgeConnections) {
      const endpoint = atPosition === WallConnectionStart ? 'start' : atPosition === WallConnectionEnd ? 'end' : undefined;
      if (endpoint) {
        const edgeConnection = object.edgeConnections.find((conn) => conn.sourceEndpoint === endpoint);
        if (edgeConnection) {
          return edgeConnection.targetWallId;
        }
      }
    }
    return undefined;
  };

  getWallsConnectedAtPosition = (targetId: string, targetPosition: number): string[] => {
    const targetObject = this.findObjectId(targetId);
    if (!targetObject) return [];

    const targetPoint = getConnectionPoint(targetObject.start, targetObject.end, targetPosition);

    // Find all walls connected to the target at this geometric point
    const connectedWallIds: Set<string> = new Set();

    if (targetObject.centerConnections) {
      for (const connection of targetObject.centerConnections) {
        const connectedObject = this.findObjectId(connection.id);
        if (connectedObject) {
          // Get the geometric point of the connection on the connected object
          let connectedPoint: THREE.Vector2;
          if (connection.targetPosition === WallConnectionStart) {
            connectedPoint = connectedObject.start.clone();
          } else {
            connectedPoint = getConnectionPoint(connectedObject.start, connectedObject.end, connection.targetPosition);
          }

          // Check if the points are at the same location (within tolerance)
          if (targetPoint.distanceTo(connectedPoint) < 0.001) {
            connectedWallIds.add(connection.id);
          }
        }
      }
    }

    // Also check edge connections (vital for corner connections)
    if (isWallType(targetObject) && targetObject.edgeConnections) {
      const isStart = targetPosition === WallConnectionStart;
      const isEnd = targetPosition === WallConnectionEnd;

      if (isStart || isEnd) {
        const checkEndpoint = isStart ? 'start' : 'end';
        targetObject.edgeConnections.forEach((conn) => {
          if (conn.sourceEndpoint === checkEndpoint) {
            connectedWallIds.add(conn.targetWallId);
          }
        });
      }
    }

    return Array.from(connectedWallIds);
  };

  /**
   * Creates a center-based connection between two objects.
   * This method is primarily for mid-point connections.
   * For endpoint connections, prefer addEdgeConnection().
   *
   * @param source - ID of the source object
   * @param target - ID of the target object
   * @param sourcePosition - Position on source (0=start, 10000=end, or custom for mid-point)
   * @param targetPosition - Position on target (0=start, 10000=end, or custom for mid-point)
   */
  addConnection = (source: string, target: string, sourcePosition: number, targetPosition: number, options?: { mode?: 'edge' | 'center' }) => {
    const sourceObject = this.findObjectId(source);
    const targetObject = this.findObjectId(target);
    if (sourceObject && targetObject) {
      // Helper function to deduplicate connections by id+positions
      const dedupeConnections = (connections: any[]) => {
        const seen = new Set();
        return connections.filter((conn) => {
          const key = `${conn.id}-${conn.sourcePosition}-${conn.targetPosition}`;
          if (seen.has(key)) return false;
          seen.add(key);
          return true;
        });
      };

      // Local pair-only duplicate guard - filter out existing connection between source and target
      const filteredSourceConnections = sourceObject.centerConnections?.filter((c) => !(c.id === target)) || [];
      const filteredTargetConnections = targetObject.centerConnections?.filter((c) => !(c.id === source)) || [];

      // Find all walls already connected at the target connection point
      const existingWallsAtTargetPoint = this.getWallsConnectedAtPosition(target, targetPosition);

      // Create the primary connection: source ↔ target
      const connectionMode = options?.mode;
      const primaryPair = {
        id: target,
        sourcePosition,
        targetPosition,
        mode: connectionMode,
      };
      const primaryPairReverse = {
        id: source,
        sourcePosition: targetPosition,
        targetPosition: sourcePosition,
        mode: connectionMode,
      };

      // Accumulate all new connections for the source in a single array
      const newSourceConnections = dedupeConnections([
        ...filteredSourceConnections,
        primaryPair,
        ...existingWallsAtTargetPoint
          .filter((existingWallId) => existingWallId !== source)
          .map((existingWallId) => {
            const existingWall = this.findObjectId(existingWallId);
            if (existingWall) {
              const existingConnection = existingWall.centerConnections?.find((c) => c.id === target);
              if (existingConnection) {
                return {
                  id: existingWallId,
                  sourcePosition,
                  targetPosition: existingConnection.sourcePosition,
                  mode: connectionMode,
                };
              }
            }
            return null;
          })
          .filter(Boolean),
      ]);

      // Create updated objects with all connections
      const updatedSourceObject = {
        ...sourceObject,
        centerConnections: newSourceConnections,
      };
      const updatedTargetObject = {
        ...targetObject,
        centerConnections: dedupeConnections([...filteredTargetConnections, primaryPairReverse]),
      };

      // Update existing walls that need to connect to source
      const updatedExistingWalls = new Map();
      existingWallsAtTargetPoint.forEach((existingWallId) => {
        if (existingWallId !== source) {
          const existingWall = this.findObjectId(existingWallId);
          if (existingWall) {
            const existingConnection = existingWall.centerConnections?.find((c) => c.id === target);
            if (existingConnection) {
              const newConnection = {
                id: source,
                sourcePosition: existingConnection.sourcePosition,
                targetPosition: sourcePosition,
              };
              const updatedExistingWall = {
                ...existingWall,
                centerConnections: dedupeConnections([...(existingWall.centerConnections?.filter((c) => c.id !== source) || []), newConnection]),
              };
              updatedExistingWalls.set(existingWallId, updatedExistingWall);
            }
          }
        }
      });

      // Update all objects in the store in a single runInAction
      runInAction(() => {
        // Update primary connection objects
        if (isWallType(sourceObject)) {
          this.wallsMap.set(source, updatedSourceObject as WallType);
          this.objectNeedsUpdate(source);
          this.incrementWallGeometryVersion(source);
        } else if (isSingleLineType(sourceObject)) {
          this.singleLinesMap.set(source, updatedSourceObject as SingleLineType);
        } else if (isRulerLineType(sourceObject)) {
          this.rulerLinesMap.set(source, updatedSourceObject as RulerLineType);
        }

        if (isWallType(targetObject)) {
          this.wallsMap.set(target, updatedTargetObject as WallType);
          this.objectNeedsUpdate(target);
          this.incrementWallGeometryVersion(target);
        } else if (isSingleLineType(targetObject)) {
          this.singleLinesMap.set(target, updatedTargetObject as SingleLineType);
        } else if (isRulerLineType(targetObject)) {
          this.rulerLinesMap.set(target, updatedTargetObject as RulerLineType);
        }

        // Update existing walls
        updatedExistingWalls.forEach((updatedWall, wallId) => {
          const originalWall = this.findObjectId(wallId);
          if (originalWall) {
            if (isWallType(originalWall)) {
              this.wallsMap.set(wallId, updatedWall as WallType);
              this.objectNeedsUpdate(wallId);
              this.incrementWallGeometryVersion(wallId);
            } else if (isSingleLineType(originalWall)) {
              this.singleLinesMap.set(wallId, updatedWall as SingleLineType);
            } else if (isRulerLineType(originalWall)) {
              this.rulerLinesMap.set(wallId, updatedWall as RulerLineType);
            }
          }
        });
      });

      // Mark all affected objects for room update
      this.markObjectForRoomUpdate(source);
      this.markObjectForRoomUpdate(target);
      existingWallsAtTargetPoint.forEach((wallId) => this.markObjectForRoomUpdate(wallId));

      this.setDirty();

      // Regenerate outline segments for all affected walls so center endpoint connections keep outlines visible
      const affectedWalls = new Set<string>();
      if (isWallType(sourceObject)) affectedWalls.add(source);
      if (isWallType(targetObject)) affectedWalls.add(target);
      updatedExistingWalls.forEach((_val, wallId) => affectedWalls.add(wallId));

      affectedWalls.forEach((wallId) => {
        renderStore.clearOutlineSegments(wallId);
        renderStore.generateOutlineSegments(wallId);
        refreshWallGeometry(wallId);
      });
    }
  };

  /**
   * Creates an edge-to-edge connection between two walls at their endpoints.
   * This is the preferred method for endpoint connections.
   * Automatically creates reciprocal connections and handles edge point calculations.
   *
   * @param sourceWallId - ID of the source wall
   * @param targetWallId - ID of the target wall
   * @param sourceEndpoint - Which endpoint of the source wall ("start" or "end")
   * @param targetEndpoint - Which endpoint of the target wall ("start", "end", or "middle")
   */
  addEdgeConnection = (sourceWallId: string, targetWallId: string, sourceEndpoint: 'start' | 'end', targetEndpoint: 'start' | 'end' | 'middle', precomputedConnection?: WallEdgeConnection) => {
    const areVoidFillEqual = (a?: WallEdgeConnection['voidFill'], b?: WallEdgeConnection['voidFill']): boolean => {
      if (!a && !b) return true;
      if (!a || !b) return false;
      const tolerance = 1e-6;
      return a.required === b.required && a.ownerWallId === b.ownerWallId && a.reason === b.reason && a.fillColor === b.fillColor && a.outlineColor === b.outlineColor && Math.abs(a.outlineWidth - b.outlineWidth) < tolerance && !!a.outlineDashed === !!b.outlineDashed;
    };

    const areEdgeConnectionsEqual = (a: WallEdgeConnection, b: WallEdgeConnection): boolean => {
      const tolerance = 1e-6;
      const pointsMatch = a.sourceEdgePoint.distanceTo(b.sourceEdgePoint) < tolerance && a.targetEdgePoint.distanceTo(b.targetEdgePoint) < tolerance;
      return a.targetWallId === b.targetWallId && a.sourceEndpoint === b.sourceEndpoint && a.targetEndpoint === b.targetEndpoint && Math.abs(a.gapDistance - b.gapDistance) < tolerance && Math.abs(a.connectionAngle - b.connectionAngle) < tolerance && pointsMatch && areVoidFillEqual(a.voidFill, b.voidFill);
    };

    const upsertEdgeConnection = (wall: WallType, connection: WallEdgeConnection, matcher: (connection: WallEdgeConnection) => boolean): { changed: boolean; added: boolean } => {
      const list = wall.edgeConnections ?? [];
      const index = list.findIndex(matcher);
      if (index >= 0) {
        if (areEdgeConnectionsEqual(list[index], connection)) {
          return { changed: false, added: false };
        }
        const updated = [...list];
        updated[index] = connection;
        wall.edgeConnections = updated;
        return { changed: true, added: false };
      }

      wall.edgeConnections = [...list, connection];
      return { changed: true, added: true };
    };

    const sourceWall = this.wallsMap.get(sourceWallId);
    const targetWall = this.wallsMap.get(targetWallId);

    if (!sourceWall || !targetWall) {
      return;
    }

    // Create the edge connection object
    const edgeConnection = (
      precomputedConnection
        ? {
            ...precomputedConnection,
            // Ensure IDs/endpoints align with invocation parameters to avoid stale data
            sourceWallId,
            targetWallId,
            sourceEndpoint,
            targetEndpoint,
            voidFill: precomputedConnection.voidFill ? { ...precomputedConnection.voidFill } : undefined,
          }
        : createEdgeConnection(sourceWall, sourceEndpoint as 'start' | 'end', targetWall, targetEndpoint)
    ) as WallEdgeConnection;

    // Refuse joins across open air — same rule as resolve/integrity (Bug2 / 1608).
    if (!isEdgeConnectionWithinJoinRange(edgeConnection, sourceWall, targetWall)) {
      return;
    }

    // Initialize edgeConnections array if it doesn't exist
    if (!sourceWall.edgeConnections) {
      sourceWall.edgeConnections = [];
    }

    // Check if edge connection already exists (same target wall and endpoints)
    const { changed: sourceChanged, added: sourceAdded } = upsertEdgeConnection(sourceWall, edgeConnection, (conn) => conn.targetWallId === targetWallId && conn.sourceEndpoint === sourceEndpoint && conn.targetEndpoint === targetEndpoint);

    let targetChanged = false;
    let targetAdded = false;

    // Create reciprocal edge connection by swapping endpoints and edge points
    // IMPORTANT: Skip reciprocal creation for mid-point connections
    // Mid-point connections are one-way: source wall connects to target wall's middle
    // but target wall doesn't have a source endpoint at "middle"
    if ((targetEndpoint === 'start' || targetEndpoint === 'end') && (sourceEndpoint === 'start' || sourceEndpoint === 'end')) {
      if (!targetWall.edgeConnections) {
        targetWall.edgeConnections = [];
      }

      const reciprocalConnection: WallEdgeConnection = {
        id: sourceWall.id, // Use sourceWallId as the id
        sourceWallId: targetWall.id,
        targetWallId: sourceWall.id,
        sourceEndpoint: targetEndpoint,
        targetEndpoint: sourceEndpoint,
        sourceSide: edgeConnection.targetSide,
        targetSide: edgeConnection.sourceSide,
        sourceEdgePoint: edgeConnection.targetEdgePoint,
        targetEdgePoint: edgeConnection.sourceEdgePoint,
        gapDistance: edgeConnection.gapDistance,
        connectionAngle: edgeConnection.connectionAngle,
        voidFill: edgeConnection.voidFill ? { ...edgeConnection.voidFill } : undefined,
      };

      const reciprocalResult = upsertEdgeConnection(targetWall, reciprocalConnection, (conn) => conn.targetWallId === sourceWallId && conn.sourceEndpoint === targetEndpoint && conn.targetEndpoint === sourceEndpoint);

      targetChanged = reciprocalResult.changed;
      targetAdded = reciprocalResult.added;
    }

    if (sourceChanged || targetChanged) {
      if (sourceChanged) {
        this.resetWallGeometryAfterConnectionChange(sourceWallId);
      }
      if (targetChanged) {
        this.resetWallGeometryAfterConnectionChange(targetWallId);
      }

      renderStore.invalidateJointsForWall(sourceWallId, 'addEdgeConnection', {
        scheduleAffectedWallUpdates: false,
      });
      renderStore.invalidateJointsForWall(targetWallId, 'addEdgeConnection', {
        scheduleAffectedWallUpdates: false,
      });

      if (sourceChanged) {
        this.objectNeedsUpdate(sourceWallId);
      }
      if (targetChanged) {
        this.objectNeedsUpdate(targetWallId);
      }

      // Bidirectional regeneration: clear and regenerate outline segments for both walls
      // This ensures both walls' outlines are updated when a connection is added or corrected
      // See Comment 5 in verification comments for requirement details
      renderStore.clearOutlineSegments(sourceWallId);
      renderStore.generateOutlineSegments(sourceWallId);

      // During drag, connected walls are in progressive mode and their outlines shouldn't change
      // We preserve their existing outlines to prevent visual flicker
      if (renderStore.deferredUpdateDepth === 0) {
        renderStore.clearOutlineSegments(targetWallId);
        renderStore.generateOutlineSegments(targetWallId);
      }

      this.setDirty();
    }

    if (sourceAdded || targetAdded) {
      const legacySourcePosition = sourceEndpoint === 'start' ? WallConnectionStart : WallConnectionEnd;
      const legacyTargetPosition = targetEndpoint === 'start' ? WallConnectionStart : targetEndpoint === 'end' ? WallConnectionEnd : WallConnectionMiddle;
      this.addConnection(sourceWallId, targetWallId, legacySourcePosition, legacyTargetPosition, { mode: 'edge' });
    }

    // After upsert + reciprocal: drop duplicate / looser joins at this endpoint.
    if (sourceAdded || sourceChanged || targetAdded || targetChanged) {
      this.pruneNonPrimaryEdgeJoinsOnWall(sourceWallId);
    }
  };

  /**
   * Removes edge connections from a wall.
   *
   * This method has multiple signatures to handle different removal scenarios:
   *
   * 1. Remove all connections at a specific endpoint:
   *    removeEdgeConnection(sourceWallId, "start" | "end")
   *
   * 2. Remove a specific connection between two walls:
   *    removeEdgeConnection(sourceWallId, targetWallId, "start" | "end")
   *
   * 3. Remove all connections to a specific wall:
   *    removeEdgeConnection(sourceWallId, targetWallId)
   *
   * 4. Remove all edge connections from a wall:
   *    removeEdgeConnection(sourceWallId)
   *
   * @param sourceWallId - ID of the source wall
   * @param targetWallIdOrEndpoint - Optional. Either:
   *   - A wall ID (string) to remove connections to that wall
   *   - "start" or "end" to remove all connections at that endpoint
   * @param sourceEndpoint - Optional. Only used when targetWallIdOrEndpoint is a wall ID.
   *   Specifies which endpoint on the source wall to remove connections from.
   *
   * @example
   * // Remove all connections at the start endpoint of wall "wall-1"
   * store.removeEdgeConnection("wall-1", "start");
   *
   * @example
   * // Remove the specific connection between wall "wall-1" start and wall "wall-2" end
   * store.removeEdgeConnection("wall-1", "wall-2", "start");
   *
   * @example
   * // Remove all connections from wall "wall-1" to wall "wall-2"
   * store.removeEdgeConnection("wall-1", "wall-2");
   *
   * @example
   * // Remove all edge connections from wall "wall-1"
   * store.removeEdgeConnection("wall-1");
   */
  removeEdgeConnection = (sourceWallId: string, targetWallIdOrEndpoint?: string, sourceEndpoint?: 'start' | 'end' | 'middle') => {
    // Runtime validation of parameters
    if (!sourceWallId || typeof sourceWallId !== 'string') {
      console.error('removeEdgeConnection: sourceWallId must be a non-empty string');
      return;
    }

    if (targetWallIdOrEndpoint !== undefined && targetWallIdOrEndpoint !== 'start' && targetWallIdOrEndpoint !== 'end' && targetWallIdOrEndpoint !== 'middle' && typeof targetWallIdOrEndpoint !== 'string') {
      console.error("removeEdgeConnection: targetWallIdOrEndpoint must be a wall ID string, 'start', 'end', 'middle', or undefined");
      return;
    }

    if (sourceEndpoint !== undefined && sourceEndpoint !== 'start' && sourceEndpoint !== 'end' && sourceEndpoint !== 'middle') {
      console.error("removeEdgeConnection: sourceEndpoint must be 'start', 'end', 'middle', or undefined");
      return;
    }

    const sourceWall = this.wallsMap.get(sourceWallId);

    if (!sourceWall || !sourceWall.edgeConnections) {
      return;
    }

    // If targetWallIdOrEndpoint is "start", "end", or "middle", treat it as sourceEndpoint
    if (targetWallIdOrEndpoint === 'start' || targetWallIdOrEndpoint === 'end' || targetWallIdOrEndpoint === 'middle') {
      sourceEndpoint = targetWallIdOrEndpoint;
      targetWallIdOrEndpoint = undefined;
    }

    // Collect affected target walls for reciprocal removal
    // Store both original sourceEndpoint (S) and targetEndpoint (T) for proper reciprocal matching
    const affectedTargets: Array<{
      targetWallId: string;
      originalSourceEndpoint: 'start' | 'end' | 'middle';
      originalTargetEndpoint: 'start' | 'end' | 'middle';
    }> = [];

    // Filter out the edge connection(s) and collect affected targets
    if (sourceEndpoint) {
      if (targetWallIdOrEndpoint) {
        // Remove only the specific endpoint connection to the target wall
        const connectionsToRemove = sourceWall.edgeConnections.filter((conn) => conn.targetWallId === targetWallIdOrEndpoint && conn.sourceEndpoint === sourceEndpoint);
        // Collect affected target info with both endpoints for reciprocal matching
        connectionsToRemove.forEach((conn) => {
          affectedTargets.push({
            targetWallId: conn.targetWallId,
            originalSourceEndpoint: conn.sourceEndpoint,
            originalTargetEndpoint: conn.targetEndpoint,
          });
        });
        // Filter source wall connections
        sourceWall.edgeConnections = sourceWall.edgeConnections.filter((conn) => !(conn.targetWallId === targetWallIdOrEndpoint && conn.sourceEndpoint === sourceEndpoint));
      } else {
        // Remove all edge connections for the source endpoint
        const connectionsToRemove = sourceWall.edgeConnections.filter((conn) => conn.sourceEndpoint === sourceEndpoint);
        // Collect affected target info with both endpoints for reciprocal matching
        connectionsToRemove.forEach((conn) => {
          affectedTargets.push({
            targetWallId: conn.targetWallId,
            originalSourceEndpoint: conn.sourceEndpoint,
            originalTargetEndpoint: conn.targetEndpoint,
          });
        });
        // Filter source wall connections
        sourceWall.edgeConnections = sourceWall.edgeConnections.filter((conn) => conn.sourceEndpoint !== sourceEndpoint);
      }
    } else {
      if (targetWallIdOrEndpoint) {
        // Remove all edge connections to the target wall
        const connectionsToRemove = sourceWall.edgeConnections.filter((conn) => conn.targetWallId === targetWallIdOrEndpoint);
        // Collect affected target info with both endpoints for reciprocal matching
        connectionsToRemove.forEach((conn) => {
          affectedTargets.push({
            targetWallId: conn.targetWallId,
            originalSourceEndpoint: conn.sourceEndpoint,
            originalTargetEndpoint: conn.targetEndpoint,
          });
        });
        // Filter source wall connections
        sourceWall.edgeConnections = sourceWall.edgeConnections.filter((conn) => conn.targetWallId !== targetWallIdOrEndpoint);
      } else {
        // Remove all edge connections
        const connectionsToRemove = [...sourceWall.edgeConnections];
        // Collect affected target info with both endpoints for reciprocal matching
        connectionsToRemove.forEach((conn) => {
          affectedTargets.push({
            targetWallId: conn.targetWallId,
            originalSourceEndpoint: conn.sourceEndpoint,
            originalTargetEndpoint: conn.targetEndpoint,
          });
        });
        // Clear source wall connections
        sourceWall.edgeConnections = [];
      }
    }

    // Mark source wall as modified and discard endpoint trim state from the old connection.
    this.resetWallGeometryAfterConnectionChange(sourceWallId);

    // Remove reciprocal edge connections from all affected target walls
    const processedTargets = new Set<string>();
    affectedTargets.forEach(({ targetWallId, originalSourceEndpoint, originalTargetEndpoint }) => {
      // Create a unique key for this target wall and connection pair
      const targetKey = `${targetWallId}-${originalSourceEndpoint}-${originalTargetEndpoint}`;

      // Avoid processing the same target wall multiple times for the same connection
      if (processedTargets.has(targetKey)) {
        return;
      }
      processedTargets.add(targetKey);

      const targetWall = this.wallsMap.get(targetWallId);
      if (targetWall && targetWall.edgeConnections) {
        const originalTargetConnectionCount = targetWall.edgeConnections.length;
        if (sourceEndpoint) {
          // Remove the reciprocal connection:
          // Original connection: sourceWall(A) --S--> targetWall(B) --T--
          // Reciprocal on B: sourceWall(B) --T--> targetWall(A) --S--
          // To match reciprocal: conn.targetWallId === A && conn.sourceEndpoint === T && conn.targetEndpoint === S
          targetWall.edgeConnections = targetWall.edgeConnections.filter((conn) => !(conn.targetWallId === sourceWallId && conn.sourceEndpoint === originalTargetEndpoint && conn.targetEndpoint === originalSourceEndpoint));
        } else {
          // Remove all connections to the source wall
          targetWall.edgeConnections = targetWall.edgeConnections.filter((conn) => conn.targetWallId !== sourceWallId);
        }
        if (targetWall.edgeConnections.length !== originalTargetConnectionCount) {
          this.resetWallGeometryAfterConnectionChange(targetWallId);
        }
        this.objectNeedsUpdate(targetWallId);
      }
    });

    renderStore.invalidateJointsForWall(sourceWallId, 'removeEdgeConnection', {
      scheduleAffectedWallUpdates: false,
    });
    affectedTargets.forEach(({ targetWallId }) => {
      renderStore.invalidateJointsForWall(targetWallId, 'removeEdgeConnection', {
        scheduleAffectedWallUpdates: false,
      });
    });
    this.objectNeedsUpdate(sourceWallId);

    // Bidirectional regeneration: clear and regenerate outline segments for both source and target walls
    // This ensures both walls' outlines are updated when a connection is removed
    // See Comment 5 in verification comments for requirement details
    renderStore.clearOutlineSegments(sourceWallId);
    renderStore.generateOutlineSegments(sourceWallId);
    // Also regenerate for all affected target walls
    affectedTargets.forEach(({ targetWallId }) => {
      // During drag, connected walls are in progressive mode and their outlines shouldn't change
      // We preserve their existing outlines to prevent visual flicker
      if (renderStore.deferredUpdateDepth === 0) {
        renderStore.clearOutlineSegments(targetWallId);
        renderStore.generateOutlineSegments(targetWallId);
      }
    });

    this.setDirty();
  };

  clearEdgeConnections = (wallId: string): string[] => {
    const wall = this.wallsMap.get(wallId);

    if (!wall || !wall.edgeConnections) {
      return [];
    }

    // Store all connected wall IDs before clearing
    const connectedWallIds = new Set<string>();
    wall.edgeConnections.forEach((conn) => {
      if (conn.targetWallId) {
        connectedWallIds.add(conn.targetWallId);
      }
    });

    // Clear edge connections
    wall.edgeConnections = [];

    // Mark wall as modified and discard endpoint trim state from the old connection.
    this.resetWallGeometryAfterConnectionChange(wallId);

    // Remove reciprocal edge connections from all previously connected walls
    connectedWallIds.forEach((targetWallId) => {
      const targetWall = this.wallsMap.get(targetWallId);
      if (targetWall && targetWall.edgeConnections) {
        const originalTargetConnectionCount = targetWall.edgeConnections.length;
        targetWall.edgeConnections = targetWall.edgeConnections.filter((conn) => conn.targetWallId !== wallId);
        if (targetWall.edgeConnections.length !== originalTargetConnectionCount) {
          this.resetWallGeometryAfterConnectionChange(targetWallId);
        }
        this.objectNeedsUpdate(targetWallId);
      }
    });

    renderStore.invalidateJointsForWall(wallId, 'clearEdgeConnections', {
      scheduleAffectedWallUpdates: false,
    });
    connectedWallIds.forEach((targetWallId) => {
      renderStore.invalidateJointsForWall(targetWallId, 'clearEdgeConnections', {
        scheduleAffectedWallUpdates: false,
      });
    });
    this.objectNeedsUpdate(wallId);

    // Bidirectional regeneration: clear and regenerate outline segments for the wall and all connected walls
    // This ensures all affected walls' outlines are updated when edge connections are cleared
    // See Comment 5 in verification comments for requirement details
    renderStore.clearOutlineSegments(wallId);
    renderStore.generateOutlineSegments(wallId);
    connectedWallIds.forEach((targetWallId) => {
      // During drag, connected walls are in progressive mode and their outlines shouldn't change
      // We preserve their existing outlines to prevent visual flicker
      if (renderStore.deferredUpdateDepth === 0) {
        renderStore.clearOutlineSegments(targetWallId);
        renderStore.generateOutlineSegments(targetWallId);
      }
    });

    this.setDirty();
    return Array.from(connectedWallIds);
  };

  getEdgeConnectionsForEndpoint = (wallId: string, endpoint: 'start' | 'end' | 'middle'): WallEdgeConnection[] => {
    const wall = this.wallsMap.get(wallId);

    if (!wall || !wall.edgeConnections) {
      return [];
    }

    // Filter edge connections to return only those at the specified endpoint
    return wall.edgeConnections.filter((conn) => conn.sourceEndpoint === endpoint);
  };

  /**
   * Checks if a connection is an endpoint connection (at start or end)
   * @param connection - The connection to check
   * @returns true if connection is at an endpoint (sourcePosition === 0 or 10000)
   */
  isEndpointConnection = (connection: WallConnectionType): boolean => {
    return connection.sourcePosition === WallConnectionStart || connection.sourcePosition === WallConnectionEnd;
  };

  /**
   * Gets all endpoint connections for a wall (connections at start or end, not mid-point)
   * @param wallId - The ID of the wall
   * @returns Array of endpoint connections
   */
  getEndpointConnections = (wallId: string): WallConnectionType[] => {
    const wall = this.wallsMap.get(wallId);
    if (!wall || !wall.centerConnections) {
      return [];
    }

    return wall.centerConnections.filter((conn) => this.isEndpointConnection(conn));
  };

  /**
   * Gets all mid-point connections for a wall (connections not at start or end)
   * @param wallId - The ID of the wall
   * @returns Array of mid-point connections
   */
  getMidPointConnections = (wallId: string): WallConnectionType[] => {
    const wall = this.wallsMap.get(wallId);
    if (!wall || !wall.centerConnections) {
      return [];
    }

    return wall.centerConnections.filter((conn) => !this.isEndpointConnection(conn));
  };

  removeConnectionEnd = (source: string, connectionPos: number) => {
    const object = this.findObjectId(source);
    if (object) {
      // Only iterate centerConnections at the given end and call corrected pair-only removeConnection() for each
      const connectionsToRemove = object.centerConnections?.filter((c) => c.sourcePosition === connectionPos) || [];
      connectionsToRemove.forEach((connection) => {
        this.removeConnection(object.id, connection.id);
      });
      // Also handle edge connections for walls
      if (isWallType(object) && object.edgeConnections) {
        const endpoint = connectionPos === WallConnectionStart ? 'start' : connectionPos === WallConnectionEnd ? 'end' : undefined;
        if (endpoint) {
          const edgeConnectionsToRemove = object.edgeConnections.filter((c) => c.sourceEndpoint === endpoint);
          edgeConnectionsToRemove.forEach((edgeConnection) => {
            this.removeEdgeConnection(object.id, edgeConnection.targetWallId, endpoint);
          });
        }
      }
      this.setDirty();
    }
  };

  removeConnection = (source: string, target: string) => {
    const sourceObject = this.findObjectId(source);
    const targetObject = this.findObjectId(target);

    if (!sourceObject && !targetObject) {
      return;
    }

    runInAction(() => {
      let updatedSourceWall;
      let updatedTargetWall;

      // If source object exists, clean up its connections to target
      if (sourceObject) {
        // Clean up cutouts and clipping data
        if (isWallType(sourceObject)) {
          sourceObject.cutouts = sourceObject.cutouts?.filter((c) => c.belongsTo !== target);
        }

        if (targetObject) {
          renderStore.removeWallClipping(sourceObject.id, targetObject.id);
          renderStore.clearWallShapes(sourceObject.id);
        }

        // Remove the connection from source
        updatedSourceWall = {
          ...sourceObject,
          centerConnections: sourceObject.centerConnections?.filter((connection) => connection.id !== target),
        };
        if (isWallType(sourceObject)) {
          this.refreshCornerStateForWall(updatedSourceWall as WallType);
        }
      }

      // If target object exists, clean up its connections to source
      if (targetObject) {
        // Clean up cutouts and clipping data
        if (isWallType(targetObject)) {
          targetObject.cutouts = targetObject.cutouts?.filter((c) => c.belongsTo !== source);
        }

        if (sourceObject) {
          renderStore.removeWallClipping(targetObject.id, sourceObject.id);
          renderStore.clearWallShapes(targetObject.id);
        }

        // Remove the connection from target
        updatedTargetWall = {
          ...targetObject,
          centerConnections: targetObject.centerConnections?.filter((connection) => connection.id !== source),
        };
        if (isWallType(targetObject)) {
          this.refreshCornerStateForWall(updatedTargetWall as WallType);
        }
      }

      // Update the objects in the store
      if (sourceObject && updatedSourceWall) {
        if (isWallType(sourceObject)) {
          this.wallsMap.set(sourceObject.id, updatedSourceWall as WallType);
          this.objectNeedsUpdate(sourceObject.id);
          this.incrementWallGeometryVersion(sourceObject.id);
        } else if (isSingleLineType(sourceObject)) {
          this.singleLinesMap.set(sourceObject.id, updatedSourceWall as SingleLineType);
        } else if (isRulerLineType(sourceObject)) {
          this.rulerLinesMap.set(sourceObject.id, updatedSourceWall as RulerLineType);
        }
      }

      if (targetObject && updatedTargetWall) {
        if (isWallType(targetObject)) {
          this.wallsMap.set(targetObject.id, updatedTargetWall as WallType);
          this.objectNeedsUpdate(targetObject.id);
          this.incrementWallGeometryVersion(targetObject.id);
        } else if (isSingleLineType(targetObject)) {
          this.singleLinesMap.set(targetObject.id, updatedTargetWall as SingleLineType);
        } else if (isRulerLineType(targetObject)) {
          this.rulerLinesMap.set(targetObject.id, updatedTargetWall as RulerLineType);
        }
      }

      // Clean up wall clippings if no connections remain
      if (sourceObject && isWallType(sourceObject)) {
        const finalUpdatedSource = updatedSourceWall as WallType;
        const hasNoCenterConnections = !finalUpdatedSource.centerConnections?.length;
        const hasNoEdgeConnections = !finalUpdatedSource.edgeConnections?.length;
        if (hasNoCenterConnections && hasNoEdgeConnections) {
          if (finalUpdatedSource.cornerState) {
            delete finalUpdatedSource.cornerState;
          }
          renderStore.clearWallClippings(sourceObject.id);
        }
      }
      if (targetObject && isWallType(targetObject)) {
        const finalUpdatedTarget = updatedTargetWall as WallType;
        const hasNoCenterConnections = !finalUpdatedTarget.centerConnections?.length;
        const hasNoEdgeConnections = !finalUpdatedTarget.edgeConnections?.length;
        if (hasNoCenterConnections && hasNoEdgeConnections) {
          if (finalUpdatedTarget.cornerState) {
            delete finalUpdatedTarget.cornerState;
          }
          renderStore.clearWallClippings(targetObject.id);
        }
      }
    });

    if (sourceObject) this.markObjectForRoomUpdate(source);
    if (targetObject) this.markObjectForRoomUpdate(target);
    this.setDirty();

    // Regenerate outlines for affected walls after removal to restore borders
    [sourceObject, targetObject].forEach((obj) => {
      if (obj && isWallType(obj)) {
        renderStore.clearOutlineSegments(obj.id);
        renderStore.generateOutlineSegments(obj.id);
        refreshWallGeometry(obj.id);
      }
    });

    // After disconnection, check if remaining walls at the same position should be auto-connected
    if (sourceObject && targetObject) {
      this.autoConnectRemainingWallsAfterDisconnection(sourceObject, targetObject);
    }
  };

  /**
   * Removes a specific connection pair between two walls based on both IDs and positions.
   * This is more precise than removeConnection() which removes all connections between a pair.
   *
   * @param sourceId - ID of the source wall
   * @param targetId - ID of the target wall
   * @param sourcePosition - Position on source wall
   * @param targetPosition - Position on target wall
   */
  removeSpecificConnectionPair = (sourceId: string, targetId: string, sourcePosition: number, targetPosition: number) => {
    const sourceObject = this.findObjectId(sourceId);
    const targetObject = this.findObjectId(targetId);

    if (!sourceObject || !targetObject) {
      return;
    }

    runInAction(() => {
      // Clean up cutouts and clipping data
      if (isWallType(sourceObject)) {
        sourceObject.cutouts = sourceObject.cutouts?.filter((c) => c.belongsTo !== targetObject.id);
      }
      if (isWallType(targetObject)) {
        targetObject.cutouts = targetObject.cutouts?.filter((c) => c.belongsTo !== sourceObject.id);
      }

      // Remove clipping polygons and render cache
      renderStore.removeWallClipping(sourceObject.id, targetObject.id);
      renderStore.removeWallClipping(targetObject.id, sourceObject.id);
      renderStore.clearWallShapes(sourceObject.id);
      renderStore.clearWallShapes(targetObject.id);

      // Remove the specific connection pair (both directions)
      const updatedSourceWall = {
        ...sourceObject,
        centerConnections: sourceObject.centerConnections?.filter((connection) => !(connection.id === targetObject.id && connection.sourcePosition === sourcePosition && connection.targetPosition === targetPosition)),
      };
      const updatedTargetWall = {
        ...targetObject,
        centerConnections: targetObject.centerConnections?.filter((connection) => !(connection.id === sourceObject.id && connection.sourcePosition === targetPosition && connection.targetPosition === sourcePosition)),
      };

      // Update the primary objects in the store
      if (isWallType(sourceObject)) {
        this.wallsMap.set(sourceObject.id, updatedSourceWall as WallType);
        this.objectNeedsUpdate(sourceObject.id);
      } else if (isSingleLineType(sourceObject)) {
        this.singleLinesMap.set(sourceObject.id, updatedSourceWall as SingleLineType);
      } else if (isRulerLineType(sourceObject)) {
        this.rulerLinesMap.set(sourceObject.id, updatedSourceWall as RulerLineType);
      }

      if (isWallType(targetObject)) {
        this.wallsMap.set(targetObject.id, updatedTargetWall as WallType);
        this.objectNeedsUpdate(targetObject.id);
      } else if (isSingleLineType(targetObject)) {
        this.singleLinesMap.set(targetObject.id, updatedTargetWall as SingleLineType);
      } else if (isRulerLineType(targetObject)) {
        this.rulerLinesMap.set(targetObject.id, updatedTargetWall as RulerLineType);
      }

      // Clean up wall clippings if no connections remain (check both center and edge connections)
      if (isWallType(sourceObject)) {
        const hasNoCenterConnections = !updatedSourceWall.centerConnections?.length;
        const hasNoEdgeConnections = !(sourceObject as WallType).edgeConnections?.length;
        if (hasNoCenterConnections && hasNoEdgeConnections) {
          renderStore.clearWallClippings(sourceObject.id);
        }
      }
      if (isWallType(targetObject)) {
        const hasNoCenterConnections = !updatedTargetWall.centerConnections?.length;
        const hasNoEdgeConnections = !(targetObject as WallType).edgeConnections?.length;
        if (hasNoCenterConnections && hasNoEdgeConnections) {
          renderStore.clearWallClippings(targetObject.id);
        }
      }
    });

    this.markObjectForRoomUpdate(sourceId);
    this.markObjectForRoomUpdate(targetId);
    this.setDirty();

    // After disconnection, check if remaining walls at the same position should be auto-connected
    this.autoConnectRemainingWallsAfterDisconnection(sourceObject, targetObject);
  };

  /**
   * Automatically connect remaining walls that are at the same position after a wall is detached
   * This handles the case where detaching from a multi-joint (T, X, multi) should result in
   * the remaining walls forming new connections between themselves.
   */
  autoConnectRemainingWallsAfterDisconnection = (sourceObject: any, targetObject: any): void => {
    // Check both source and target positions for remaining walls that should be connected
    const positionsToCheck = [
      { object: sourceObject, position: sourceObject.start, connectionPos: 0 },
      { object: sourceObject, position: sourceObject.end, connectionPos: 1 },
      { object: targetObject, position: targetObject.start, connectionPos: 0 },
      { object: targetObject, position: targetObject.end, connectionPos: 1 },
    ];

    positionsToCheck.forEach(({ object, position, connectionPos }) => {
      this.autoConnectWallsAtPosition(object, position, connectionPos);
    });
  };

  /**
   * Auto-connect walls that are at the same position
   */
  autoConnectWallsAtPosition = (referenceObject: any, position: THREE.Vector2, _connectionPos: number): void => {
    const isWallBeingDragged = (wallId: string | undefined) => {
      if (!wallId) return false;
      const dragState = this.wallDragState.get(wallId);
      return !!dragState?.isDragging;
    };

    if (referenceObject?.id && isWallBeingDragged(referenceObject.id)) {
      // Skip auto-connecting for walls currently being dragged to prevent reattachment during detachment
      return;
    }

    const tolerance = (getEditorStore()?.snappingTolerance ?? 0.001) / 3; // Use 1/3 of snapping tolerance for tighter matching, similar to alignment lines snap tolerance
    const wallsAtSamePosition: Array<{
      wall: any;
      wallId: string;
      connectionPos: number;
    }> = [];

    // Check all walls in the store
    this.wallsMap.forEach((wall, wallId) => {
      if (wallId === referenceObject.id) return; // Skip the reference wall
      if (isWallBeingDragged(wallId)) return; // Skip walls currently being dragged

      // Check start position
      if (wall.start.distanceTo(position) < tolerance) {
        wallsAtSamePosition.push({ wall, wallId, connectionPos: 0 }); // WallConnectionStart
      }
      // Check end position
      if (wall.end.distanceTo(position) < tolerance) {
        wallsAtSamePosition.push({ wall, wallId, connectionPos: 1 }); // WallConnectionEnd
      }
    });

    // Check single lines
    this.singleLinesMap.forEach((line, lineId) => {
      if (lineId === referenceObject.id) return; // Skip the reference line
      if (isWallBeingDragged(lineId)) return; // Skip lines currently being dragged

      // Check start position
      if (line.start.distanceTo(position) < tolerance) {
        wallsAtSamePosition.push({
          wall: line,
          wallId: lineId,
          connectionPos: 0,
        }); // WallConnectionStart
      }
      // Check end position
      if (line.end.distanceTo(position) < tolerance) {
        wallsAtSamePosition.push({
          wall: line,
          wallId: lineId,
          connectionPos: 1,
        }); // WallConnectionEnd
      }
    });

    // Check ruler lines
    this.rulerLinesMap.forEach((ruler, rulerId) => {
      if (rulerId === referenceObject.id) return; // Skip the reference ruler
      if (isWallBeingDragged(rulerId)) return; // Skip rulers currently being dragged

      // Check start position
      if (ruler.start.distanceTo(position) < tolerance) {
        wallsAtSamePosition.push({
          wall: ruler,
          wallId: rulerId,
          connectionPos: 0,
        }); // WallConnectionStart
      }
      // Check end position
      if (ruler.end.distanceTo(position) < tolerance) {
        wallsAtSamePosition.push({
          wall: ruler,
          wallId: rulerId,
          connectionPos: 1,
        }); // WallConnectionEnd
      }
    });

    // If we have 2 or more walls at the same position, create connections between them
    if (wallsAtSamePosition.length >= 2) {
      // Create connections between all pairs of walls at the same position
      for (let i = 0; i < wallsAtSamePosition.length; i++) {
        for (let j = i + 1; j < wallsAtSamePosition.length; j++) {
          const wall1 = wallsAtSamePosition[i];
          const wall2 = wallsAtSamePosition[j];

          // Check if they're not already connected (check both center and edge connections)
          const alreadyConnected = wall1.wall.centerConnections?.some((conn: any) => conn.id === wall2.wallId) || wall2.wall.centerConnections?.some((conn: any) => conn.id === wall1.wallId) || (isWallType(wall1.wall) && wall1.wall.edgeConnections?.some((conn: any) => conn.targetWallId === wall2.wallId)) || (isWallType(wall2.wall) && wall2.wall.edgeConnections?.some((conn: any) => conn.targetWallId === wall1.wallId));

          const endpointHasEdgeConnection = (wallEntry: { wall: any; connectionPos: number }, endpointLabel: 'start' | 'end'): boolean => {
            if (!isWallType(wallEntry.wall)) {
              return false;
            }
            return wallEntry.wall.edgeConnections?.some((conn) => conn.sourceEndpoint === endpointLabel) ?? false;
          };

          if (!alreadyConnected) {
            const wall1IsEndpoint = wall1.connectionPos === WallConnectionStart || wall1.connectionPos === WallConnectionEnd;
            const wall2IsEndpoint = wall2.connectionPos === WallConnectionStart || wall2.connectionPos === WallConnectionEnd;

            if (wall1IsEndpoint && wall2IsEndpoint && isWallType(wall1.wall) && isWallType(wall2.wall)) {
              const wall1Endpoint = wall1.connectionPos === WallConnectionStart ? 'start' : 'end';
              const wall2Endpoint = wall2.connectionPos === WallConnectionStart ? 'start' : 'end';

              if (endpointHasEdgeConnection(wall1, wall1Endpoint) || endpointHasEdgeConnection(wall2, wall2Endpoint)) {
                continue;
              }

              this.addEdgeConnection(wall1.wallId, wall2.wallId, wall1Endpoint, wall2Endpoint);
            } else {
              if (wall1IsEndpoint && endpointHasEdgeConnection(wall1, wall1.connectionPos === WallConnectionStart ? 'start' : 'end')) {
                continue;
              }
              if (wall2IsEndpoint && endpointHasEdgeConnection(wall2, wall2.connectionPos === WallConnectionStart ? 'start' : 'end')) {
                continue;
              }
              this.addConnection(wall1.wallId, wall2.wallId, wall1.connectionPos, wall2.connectionPos, { mode: 'center' });
            }
          }
        }
      }
    }
  };

  /**
   * Automatically attaches endpoints of nearby walls to a dragged wall when they land within snapping tolerance.
   * This ensures that intermediate T-junctions are rebuilt immediately after a drag operation without requiring manual snaps.
   *
   * @param draggedWall - The wall that has just completed a drag interaction.
   * @param candidateWallIds - Optional list of nearby wall ids captured during the drag to limit lookup cost.
   * @returns Set of wall ids whose geometry or connections were updated.
   */
  private buildEndpointCorners = (wall: WallType, endpoint: 'start' | 'end'): [THREE.Vector2, THREE.Vector2] | null => {
    const width = wall.wallWidth ?? this.wallWidth;
    const dir = new THREE.Vector2().subVectors(wall.end, wall.start);
    const length = dir.length();
    if (length < 1e-6 || width <= 0) {
      return null;
    }
    const u = dir.clone().normalize();
    const perp = new THREE.Vector2(-u.y, u.x).multiplyScalar(width / 2);
    const base = endpoint === 'start' ? wall.start : wall.end;
    return [base.clone().add(perp), base.clone().sub(perp)];
  };

  private isPointInsideWallRect = (wall: WallType, point: THREE.Vector2, tolerance: number): boolean => {
    const width = wall.wallWidth ?? this.wallWidth;
    const dir = new THREE.Vector2().subVectors(wall.end, wall.start);
    const length = dir.length();
    if (length < 1e-6 || width <= 0) {
      return false;
    }
    const u = dir.clone().normalize();
    const v = new THREE.Vector2(-u.y, u.x);
    const rel = new THREE.Vector2().subVectors(point, wall.start);
    const along = rel.dot(u);
    const perp = rel.dot(v);
    return along >= -tolerance && along <= length + tolerance && Math.abs(perp) <= width / 2 + tolerance;
  };

  private endpointCornersInsideWall = (endpoint: 'start' | 'end', sourceWall: WallType, targetWall: WallType, tolerance: number): boolean => {
    const corners = this.buildEndpointCorners(sourceWall, endpoint);
    if (!corners) {
      return false;
    }
    return this.isPointInsideWallRect(targetWall, corners[0], tolerance) && this.isPointInsideWallRect(targetWall, corners[1], tolerance);
  };

  private autoConnectNearbyWallEndpoints = (draggedWall: WallType, candidateWallIds?: ReadonlyArray<string>): Set<string> => {
    const affectedWalls = new Set<string>();
    const toleranceBase = (getEditorStore()?.snappingTolerance ?? 0.001) / 3; // Use 1/3 of snapping tolerance for tighter matching, similar to alignment lines snap tolerance
    const defaultWidth = this.wallWidth || 0.1;
    const draggedHalfWidth = (draggedWall.wallWidth ?? defaultWidth) / 2;
    const cornerTolerance = 0.02; // Tolerance for corner embedding checks

    const candidateIdSet = new Set<string>();
    if (candidateWallIds && candidateWallIds.length > 0) {
      candidateWallIds.forEach((id) => {
        if (id) {
          candidateIdSet.add(id);
        }
      });
    }

    // Always include all walls so newly encountered neighbors at drop location are considered.
    this.wallsMap.forEach((_wall, id) => {
      if (id) {
        candidateIdSet.add(id);
      }
    });

    for (const candidateId of candidateIdSet) {
      if (!candidateId || candidateId === draggedWall.id) {
        continue;
      }

      const dragState = this.wallDragState.get(candidateId);
      if (dragState?.isDragging) {
        continue;
      }

      const processEndpoint = (endpoint: 'start' | 'end') => {
        const candidateWall = this.wallsMap.get(candidateId);
        if (!candidateWall || !isWallType(candidateWall)) {
          return;
        }

        const endpointVector = endpoint === 'start' ? candidateWall.start.clone() : candidateWall.end.clone();

        const closest = getClosestPosition(endpointVector, draggedWall);
        if (closest.position !== 'middle') {
          return;
        }

        const candidateHalfWidth = (candidateWall.wallWidth ?? defaultWidth) / 2;
        const distanceAllowance = toleranceBase * 1.25 + draggedHalfWidth + candidateHalfWidth;
        if (closest.distance > distanceAllowance) {
          return;
        }

        const endpointInUse = candidateWall.edgeConnections?.some((connection) => connection.sourceEndpoint === endpoint);
        if (endpointInUse) {
          return;
        }

        // Enforce two-corner rule: both endpoint corners of the candidate must be inside/on the dragged wall
        if (!this.endpointCornersInsideWall(endpoint, candidateWall, draggedWall, cornerTolerance)) {
          return;
        }

        const projectedPoint = closest.projectedPoint ? closest.projectedPoint.clone() : draggedWall.start.clone().add(draggedWall.end.clone().sub(draggedWall.start).multiplyScalar(0.5));

        const precomputedConnection = createEdgeConnection(candidateWall, endpoint, draggedWall, 'middle', projectedPoint);

        this.addEdgeConnection(candidateWall.id, draggedWall.id, endpoint, 'middle', precomputedConnection);

        affectedWalls.add(candidateWall.id);
        affectedWalls.add(draggedWall.id);
      };

      processEndpoint('start');
      processEndpoint('end');
    }

    // REVERSE PASS: Check if dragged wall's endpoints can connect to candidate walls' middles
    // This handles the case where you drag a wall and its endpoint lands on another wall's middle
    const processDraggedEndpoint = (endpoint: 'start' | 'end') => {
      const endpointVector = endpoint === 'start' ? draggedWall.start.clone() : draggedWall.end.clone();

      // Check if this endpoint is already connected
      const endpointInUse = draggedWall.edgeConnections?.some((connection) => connection.sourceEndpoint === endpoint);
      if (endpointInUse) {
        return;
      }

      // Find candidate walls where dragged wall's endpoint could connect to their middle
      for (const candidateId of candidateIdSet) {
        if (!candidateId || candidateId === draggedWall.id) {
          continue;
        }

        const dragState = this.wallDragState.get(candidateId);
        if (dragState?.isDragging) {
          continue;
        }

        const candidateWall = this.wallsMap.get(candidateId);
        if (!candidateWall || !isWallType(candidateWall)) {
          continue;
        }

        const closest = getClosestPosition(endpointVector, candidateWall);
        if (closest.position !== 'middle') {
          continue;
        }

        const candidateHalfWidth = (candidateWall.wallWidth ?? defaultWidth) / 2;
        const distanceAllowance = toleranceBase * 1.25 + draggedHalfWidth + candidateHalfWidth;
        if (closest.distance > distanceAllowance) {
          continue;
        }

        // For T-junctions, use an asymmetric corner check:
        // - If dragged wall is narrower or equal to candidate, use standard corner embedding
        // - If dragged wall is wider, check if the centerline endpoint is within the candidate wall's edge
        const draggedWidth = draggedWall.wallWidth ?? defaultWidth;
        const candidateWidth = candidateWall.wallWidth ?? defaultWidth;

        let cornersValid = false;
        if (draggedWidth <= candidateWidth + cornerTolerance * 2) {
          // Standard corner embedding check for narrower or equal width walls
          cornersValid = this.endpointCornersInsideWall(endpoint, draggedWall, candidateWall, cornerTolerance);
        } else {
          // For wider walls connecting to narrower walls (T-junction):
          // Check if the centerline endpoint is touching the candidate wall's edge
          // The perpendicular distance should be approximately half the candidate wall's width
          const candidateDir = new THREE.Vector2().subVectors(candidateWall.end, candidateWall.start);
          const candidateLength = candidateDir.length();
          if (candidateLength > 1e-6) {
            candidateDir.normalize();
            const perpendicular = new THREE.Vector2(-candidateDir.y, candidateDir.x);
            const rel = new THREE.Vector2().subVectors(endpointVector, candidateWall.start);
            const perpDist = Math.abs(rel.dot(perpendicular));
            const alongDist = rel.dot(candidateDir);

            // Centerline endpoint should be at the edge (perpDist ≈ candidateHalfWidth)
            // and along the wall's length (not at endpoints)
            const atEdge = Math.abs(perpDist - candidateHalfWidth) < cornerTolerance + 0.02;
            const alongWall = alongDist > -cornerTolerance && alongDist < candidateLength + cornerTolerance;
            cornersValid = atEdge && alongWall;
          }
        }

        if (!cornersValid) {
          continue;
        }

        const projectedPoint = closest.projectedPoint ? closest.projectedPoint.clone() : candidateWall.start.clone().add(candidateWall.end.clone().sub(candidateWall.start).multiplyScalar(0.5));

        const precomputedConnection = createEdgeConnection(draggedWall, endpoint, candidateWall, 'middle', projectedPoint);

        this.addEdgeConnection(draggedWall.id, candidateWall.id, endpoint, 'middle', precomputedConnection);

        affectedWalls.add(draggedWall.id);
        affectedWalls.add(candidateWall.id);
        break; // Only connect to one wall per endpoint
      }
    };

    processDraggedEndpoint('start');
    processDraggedEndpoint('end');

    return affectedWalls;
  };

  clearConnections = (source: string) => {
    // Skip the operation if deferConnectionOperations is true and the wall is in wallDragState
    if (this.deferConnectionOperations && this.wallDragState.has(source)) {
      return;
    }

    const object = this.findObjectId(source);
    if (object) {
      // Remove all centerConnections from the object
      object.centerConnections?.forEach((connection) => {
        this.removeConnection(object.id, connection.id);
      });

      // Also clear edge connections if this is a wall
      if (isWallType(object) && object.edgeConnections && object.edgeConnections.length > 0) {
        // Create a copy to avoid mutation issues while iterating
        const edgeConnectionsToRemove = [...object.edgeConnections];
        edgeConnectionsToRemove.forEach((edgeConnection) => {
          this.removeEdgeConnection(object.id, edgeConnection.targetWallId, edgeConnection.sourceEndpoint);
        });
      }

      if (isWallType(object)) {
        const latestWall = this.wallsMap.get(object.id);
        if (latestWall && isWallType(latestWall)) {
          this.refreshCornerStateForWall(latestWall, true);
        }
      }

      this.setDirty();
    }
  };

  disconnectSelectionFromExternalConnections = (selectedWallIds: Set<string>): string[] => {
    const affectedWallIds = new Set<string>();

    const markModified = (modifiedWallId: string) => {
      const modifiedWall = this.wallsMap.get(modifiedWallId);
      if (modifiedWall && isWallType(modifiedWall)) {
        this.resetWallGeometryAfterConnectionChange(modifiedWallId);
        this.refreshCornerStateForWall(modifiedWall, true);
      }
      this.objectNeedsUpdate(modifiedWallId);
      this.markObjectForRoomUpdate(modifiedWallId);
      renderStore.invalidateJointsForWall(modifiedWallId, 'disconnectWallFromExternalSelectionConnections', {
        scheduleAffectedWallUpdates: false,
      });
      renderStore.allowVisualUpdatesForWall(modifiedWallId);
      affectedWallIds.add(modifiedWallId);
    };

    for (const [wallId, wall] of this.wallsMap) {
      const isSelectedWall = selectedWallIds.has(wallId);
      let modified = false;

      if (wall.centerConnections?.length) {
        const nextCenterConnections = wall.centerConnections.filter((connection) => {
          const connectionTargetsSelection = selectedWallIds.has(connection.id);
          const keepConnection = isSelectedWall ? connectionTargetsSelection : !connectionTargetsSelection;
          if (!keepConnection) {
            affectedWallIds.add(connection.id);
          }
          return keepConnection;
        });
        if (nextCenterConnections.length !== wall.centerConnections.length) {
          modified = true;
          wall.centerConnections = nextCenterConnections;
        }
      }

      if (wall.edgeConnections?.length) {
        const nextEdgeConnections = wall.edgeConnections.filter((connection) => {
          const connectionTargetsSelection = selectedWallIds.has(connection.targetWallId) || selectedWallIds.has(connection.sourceWallId);
          const keepConnection = isSelectedWall ? selectedWallIds.has(connection.targetWallId) : !connectionTargetsSelection;
          if (!keepConnection) {
            affectedWallIds.add(connection.targetWallId);
            affectedWallIds.add(connection.sourceWallId);
          }
          return keepConnection;
        });
        if (nextEdgeConnections.length !== wall.edgeConnections.length) {
          modified = true;
          wall.edgeConnections = nextEdgeConnections;
        }
      }

      if (modified) {
        markModified(wallId);
      }
    }

    if (affectedWallIds.size > 0) {
      this.setDirty();
    }

    return Array.from(affectedWallIds).filter(Boolean);
  };

  // Begin wall drag operation - store original connections and clear them
  beginWallDrag = (
    wallId: string,
    options: {
      disconnectConnections?: boolean;
      deferDetachedWallRebuild?: boolean;
    } = {
      disconnectConnections: true,
    },
  ) => {
    let wall = this.findObjectId(wallId);
    if (wall && isWallType(wall)) {
      // Compute and store nearbyWallIds for the dragged wall using current wall AABB expanded by margin
      const margin = 2.0;
      const minX = Math.min(wall.start.x, wall.end.x) - margin;
      const maxX = Math.max(wall.start.x, wall.end.x) + margin;
      const minY = Math.min(wall.start.y, wall.end.y) - margin;
      const maxY = Math.max(wall.start.y, wall.end.y) + margin;

      const nearbyWallIds: string[] = [];
      for (const otherWall of this.wallsMap.values()) {
        if (otherWall.id === wallId) continue;

        // Check if wall intersects with expanded AABB
        const wallMinX = Math.min(otherWall.start.x, otherWall.end.x);
        const wallMaxX = Math.max(otherWall.start.x, otherWall.end.x);
        const wallMinY = Math.min(otherWall.start.y, otherWall.end.y);
        const wallMaxY = Math.max(otherWall.start.y, otherWall.end.y);

        if (!(wallMaxX < minX || wallMinX > maxX || wallMaxY < minY || wallMinY > maxY)) {
          nearbyWallIds.push(otherWall.id);
        }
      }

      const originalConnections = wall.centerConnections ? [...wall.centerConnections] : [];
      const originalEdgeConnections = wall.edgeConnections ? [...wall.edgeConnections] : [];
      const originalConnectionIds = Array.from(new Set(originalConnections.map((connection) => connection.id)));
      const disconnectedNeighborIds = new Set<string>();
      let originalEdgeConnectionIds: string[] = [];

      const pivotAnchors: Record<'start' | 'end', PivotAnchorState> = {} as any;

      /**
       * Build a pivot anchor for an edge connection.
       *
       * === PIVOT ANCHOR RULES ===
       * 1. Target wall anchor (primaryAnchor) = connection.targetEdgePoint (on target wall outline)
       * 2. Source wall anchor = the source corner closest to primaryAnchor
       * 3. sideSign is determined by which source corner (left=1, right=-1) is closest to anchor
       * 4. During drag: centerline = primaryAnchor - (perpendicular * halfWidth * sideSign)
       */
      const buildPivotAnchor = (connection: WallEdgeConnection) => {
        // Skip middle connections - they use different anchor logic
        if (connection.targetEndpoint === 'middle') {
          return;
        }
        const targetWall = this.wallsMap.get(connection.targetWallId);
        if (!targetWall || !isWallType(targetWall)) {
          return;
        }

        const sourceAngle = getWallAngle(wall);
        const targetAngle = getWallAngle(targetWall);

        const anchorEndpoint = connection.sourceEndpoint; // Which endpoint stays anchored on this wall

        // CRITICAL FIX: Use the stored edge points from the connection instead of recalculating.
        // This preserves the exact anchor corner that was used when the connection was created,
        // preventing the non-dragged endpoint from jumping to a different anchor on drag start.
        const storedTargetEdgePoint = connection.targetEdgePoint;
        const storedSourceEdgePoint = connection.sourceEdgePoint;

        // Convert stored edge points to THREE.Vector2 (handle both Vector2 instances and plain {x,y} objects)
        const targetEdgePos = 'clone' in storedTargetEdgePoint && typeof storedTargetEdgePoint.clone === 'function' ? storedTargetEdgePoint.clone() : new THREE.Vector2(storedTargetEdgePoint.x, storedTargetEdgePoint.y);
        const sourceEdgePos = 'clone' in storedSourceEdgePoint && typeof storedSourceEdgePoint.clone === 'function' ? storedSourceEdgePoint.clone() : new THREE.Vector2(storedSourceEdgePoint.x, storedSourceEdgePoint.y);

        // Use the stored target edge point as the primary anchor
        const primaryAnchor = targetEdgePos;

        // Calculate the alternate anchor on the opposite side of the target wall
        const targetEndpoint = connection.targetEndpoint as 'start' | 'end';
        const candidateLeft = calculateWallEdgePoint(targetWall, targetEndpoint, sourceAngle, undefined, undefined, 'left').edgePosition;
        const candidateRight = calculateWallEdgePoint(targetWall, targetEndpoint, sourceAngle, undefined, undefined, 'right').edgePosition;

        // Determine which side the stored anchor is on and set the alternate to the opposite
        const distToLeft = targetEdgePos.distanceTo(candidateLeft);
        const distToRight = targetEdgePos.distanceTo(candidateRight);
        const primaryIsLeft = distToLeft < distToRight;
        const alternateAnchor = primaryIsLeft ? candidateRight.clone() : candidateLeft.clone();

        // Determine sideSign based on which side of the source wall centerline the stored source edge point is on
        const sourceEndpointPos = anchorEndpoint === 'start' ? wall.start : wall.end;
        const sourceDir = new THREE.Vector2().subVectors(wall.end, wall.start);
        const sourceLen = sourceDir.length();
        if (sourceLen < 1e-6) {
          return;
        }
        const sourcePerp = new THREE.Vector2(-sourceDir.y / sourceLen, sourceDir.x / sourceLen);

        // Project the source edge point onto the perpendicular to determine which side it's on
        const sourceEdgeOffset = new THREE.Vector2().subVectors(sourceEdgePos, sourceEndpointPos);
        const dotProduct = sourceEdgeOffset.dot(sourcePerp);
        const initialSideSign: 1 | -1 = dotProduct >= 0 ? 1 : -1;

        pivotAnchors[anchorEndpoint] = {
          primaryAnchor,
          alternateAnchor,
          currentAnchor: 'primary',
          initialSideSign,
          currentSideSign: initialSideSign,
          targetAngle,
          // Initialize to initialSideSign so colinear switching works from start
          lastRelativeSign: initialSideSign,
          primaryIsLeft,
        };
      };

      // Store original connections and nearby wall IDs
      this.wallDragState.set(wallId, {
        originalConnections,
        originalEdgeConnections,
        isDragging: true,
        isCommitting: false,
        visualSnapping: undefined,
        nearbyWallIds,
        alignmentSnap: {},
        edgeSnapping: {},
        alignmentLocks: {},
        fixedEndpoints: {
          start: wall.start.clone(),
          end: wall.end.clone(),
        },
        pivotAnchors,
      });

      // Populate pivot anchors for all existing edge connections (one per anchored endpoint)
      originalEdgeConnections.forEach(buildPivotAnchor);

      // Lock the measurement display side so it stays stable throughout the drag
      this.lockMeasurementDisplaySide(wallId);

      // Only disconnect if requested (e.g. wall drag)
      // For endpoint dragging, we maintain connections
      if (options.disconnectConnections) {
        // MULTI-JOINT CLEANUP: Before clearing connections, find all walls that were
        // part of the same multi-joint as the dragged wall. When the dragged wall leaves,
        // connections BETWEEN the remaining walls should also be cleared since they
        // were only geometrically valid because of the dragged wall's presence.

        // Track which walls were connected at each endpoint of the dragged wall
        const wallsAtStart = new Set<string>();
        const wallsAtEnd = new Set<string>();

        // Collect walls connected via edge connections at each endpoint
        for (const edgeConn of originalEdgeConnections) {
          if (edgeConn.sourceEndpoint === 'start') {
            wallsAtStart.add(edgeConn.targetWallId);
          } else {
            wallsAtEnd.add(edgeConn.targetWallId);
          }
        }

        // Also check center connections for multi-joint participants
        for (const centerConn of originalConnections) {
          // Center connections are at endpoints - check which endpoint
          const connectedWall = this.wallsMap.get(centerConn.id);
          if (connectedWall) {
            const distToStart = Math.min(wall.start.distanceTo(connectedWall.start), wall.start.distanceTo(connectedWall.end));
            const distToEnd = Math.min(wall.end.distanceTo(connectedWall.start), wall.end.distanceTo(connectedWall.end));
            if (distToStart < distToEnd) {
              wallsAtStart.add(centerConn.id);
            } else {
              wallsAtEnd.add(centerConn.id);
            }
          }
        }

        // CRITICAL FIX: Scan all walls for any connections TO the dragged wall.
        // This handles "corrupt" states where a remote wall thinks it's connected
        // but the dragged wall doesn't know about it (broken bidirectional link).
        // We do this BEFORE clearConnections to ensure we catch everything.
        for (const otherWall of this.wallsMap.values()) {
          if (otherWall.id === wallId) continue;

          // 1. Check center connections
          if (otherWall.centerConnections?.some((c) => c.id === wallId)) {
            // Remove the connection pair. Since removeConnection now robustly handles
            // missing targets/sources and updates both sides, this cleans up the link.
            // We don't need to add this to originalConnections because if it was missing
            // from the dragged wall's list (corrupt), we don't want to restore it.
            this.removeConnection(otherWall.id, wallId);
            disconnectedNeighborIds.add(otherWall.id);

            // Also add to endpoint sets for multi-joint cleanup
            const distToStart = Math.min(wall.start.distanceTo(otherWall.start), wall.start.distanceTo(otherWall.end));
            const distToEnd = Math.min(wall.end.distanceTo(otherWall.start), wall.end.distanceTo(otherWall.end));
            if (distToStart < distToEnd) {
              wallsAtStart.add(otherWall.id);
            } else {
              wallsAtEnd.add(otherWall.id);
            }
          }

          // 2. Check edge connections
          // IMPORTANT: Find the connection info BEFORE removing it
          const edgeConnToWall = otherWall.edgeConnections?.find((c) => c.targetWallId === wallId);
          if (edgeConnToWall) {
            // Store which endpoint this connects to BEFORE removing the connection
            const targetEndpoint = edgeConnToWall.targetEndpoint;

            // Remove all edge connections from otherWall to wallId
            // removeEdgeConnection(source, target) removes all connections to target
            this.removeEdgeConnection(otherWall.id, wallId);
            disconnectedNeighborIds.add(otherWall.id);

            // Add to endpoint sets for multi-joint cleanup
            if (targetEndpoint === 'start') {
              wallsAtStart.add(otherWall.id);
            } else {
              wallsAtEnd.add(otherWall.id);
            }
          }
        }

        // NOTE: We intentionally do NOT clear connections between the REMAINING walls
        // (e.g., if Green + White A + White B form a multi-joint and White A is dragged away,
        // we should NOT clear the Green ↔ White B connection - they might still be touching).
        // We only clear connections TO/FROM the dragged wall (done above and below).
        // The remaining walls will naturally transition to a 1-to-1 connection if they're still touching.

        // However, we DO need to clear voidFill from remaining connections so they can re-establish
        // proper 1-to-1 voidFill or remain as a multi-joint
        const clearVoidFillFromRemainingConnections = (wallIds: Set<string>) => {
          for (const remainingWallId of wallIds) {
            const remainingWall = this.wallsMap.get(remainingWallId);
            if (!remainingWall?.edgeConnections) continue;

            // Clear voidFill from this wall's connections to other walls in the set
            // This allows them to regenerate proper voidFill as a 1-to-1 connection
            remainingWall.edgeConnections.forEach((ec) => {
              if (wallIds.has(ec.targetWallId) && ec.voidFill) {
                delete ec.voidFill;
              }
            });

            // Mark for redraw
            disconnectedNeighborIds.add(remainingWallId);
          }
        };

        // Clear voidFill from remaining connections so they can regenerate as 1-to-1
        if (wallsAtStart.size >= 2) {
          clearVoidFillFromRemainingConnections(wallsAtStart);
        }
        if (wallsAtEnd.size >= 2) {
          clearVoidFillFromRemainingConnections(wallsAtEnd);
        }

        // Clear connections immediately to prepare for drag
        this.clearConnections(wallId);

        // Also clear edge connections if they exist (edge-based mode)
        if (originalEdgeConnections.length > 0) {
          originalEdgeConnectionIds = this.clearEdgeConnections(wallId);
        }

        // Add cleared edge connections to disconnectedNeighborIds so they get invalidated below
        originalEdgeConnectionIds.forEach((id) => disconnectedNeighborIds.add(id));

        // FINAL SWEEP: Ensure ALL walls have their connections to the dragged wall removed
        // This is a safety net in case the previous removal didn't work correctly
        // We directly filter BOTH edgeConnections AND centerConnections arrays for each wall
        for (const [otherWallId, otherWall] of this.wallsMap) {
          if (otherWallId === wallId) continue;

          let modified = false;

          // Clear edge connections to the dragged wall
          if (otherWall.edgeConnections && otherWall.edgeConnections.length > 0) {
            const originalEdgeLength = otherWall.edgeConnections.length;
            // Filter out any connections that reference the dragged wall
            otherWall.edgeConnections = otherWall.edgeConnections.filter((ec) => ec.targetWallId !== wallId && ec.sourceWallId !== wallId);
            if (otherWall.edgeConnections.length < originalEdgeLength) {
              modified = true;
            }
          }

          // CRITICAL: Also clear CENTER connections to the dragged wall
          // These can be created inadvertently and need to be removed on disconnect
          if (otherWall.centerConnections && otherWall.centerConnections.length > 0) {
            const originalCenterLength = otherWall.centerConnections.length;
            otherWall.centerConnections = otherWall.centerConnections.filter((cc) => cc.id !== wallId);
            if (otherWall.centerConnections.length < originalCenterLength) {
              modified = true;
            }
          }

          // If we removed any connections, mark this wall for update
          if (modified) {
            disconnectedNeighborIds.add(otherWallId);
          }
        }
      } else {
        // Even if not disconnecting, we might need to invalidate joints to ensure smooth updates
        // But do NOT clear connections
      }

      if (options.deferDetachedWallRebuild) {
        renderStore.allowVisualUpdatesForWall(wallId);
        originalConnectionIds.forEach((connectionId) => {
          renderStore.allowVisualUpdatesForWall(connectionId);
        });
        disconnectedNeighborIds.forEach((connectionId) => {
          renderStore.allowVisualUpdatesForWall(connectionId);
        });
        if (this.wallDragState.size === 1) {
          this.deferConnectionOperations = true;
        }
        return;
      }

      // Invalidate cached joint information for the dragged wall and its previous connections
      renderStore.invalidateJointsForWall(wallId);
      originalConnectionIds.forEach((connectionId) => {
        renderStore.invalidateJointsForWall(connectionId);
        renderStore.allowVisualUpdatesForWall(connectionId);
      });
      disconnectedNeighborIds.forEach((connectionId) => {
        renderStore.invalidateJointsForWall(connectionId);
        renderStore.allowVisualUpdatesForWall(connectionId);
      });

      // Trigger wall connection updates so detached walls rebuild their standalone geometry
      import('../utils/wallUpdateManager')
        .then(({ wallUpdateManager }) => {
          wallUpdateManager.enableProgressiveModeForWall(wallId, true);
          wallUpdateManager.updateWallConnections(wallId);

          // Temporarily add disconnected walls to progressive mode for single update
          // This ensures they rebuild immediately to close the gap
          const allDisconnectedWalls = [...new Set([...originalConnectionIds, ...originalEdgeConnectionIds, ...Array.from(disconnectedNeighborIds)])];

          allDisconnectedWalls.forEach((connectionId) => {
            const disconnectedWall = this.wallsMap.get(connectionId);
            if (disconnectedWall && isWallType(disconnectedWall)) {
              this.setObjectProperty(connectionId, 'cornerState', undefined);
            }
          });
          allDisconnectedWalls.forEach((connectionId) => {
            wallUpdateManager.enableProgressiveModeForWall(connectionId, true);
            wallUpdateManager.updateWallConnections(connectionId);
            wallUpdateManager.updateWallPosition(connectionId);
          });

          // Flush pending updates to ensure geometry is rebuilt before render
          if (wallUpdateManager.processPendingUpdatesNow) {
            wallUpdateManager
              .processPendingUpdatesNow()
              .then(() => {
                // Now rebuild the fill mesh (inner wall shapes) for disconnected walls
                // This ensures the hole/gap closes in the fill, not just the outlines
                allDisconnectedWalls.forEach((connectionId) => {
                  try {
                    // CRITICAL: Clear ALL caches before rebuilding to force fresh geometry
                    // This ensures no cached fill shapes persist
                    renderStore.clearWallShapes(connectionId);

                    // Clear wall geometry cache
                    if (typeof (wallUpdateManager as any).wallGeometryCache?.clearWall === 'function') {
                      (wallUpdateManager as any).wallGeometryCache.clearWall(connectionId);
                    }

                    // CRITICAL: Increment geometry version to force complete rebuild
                    // This ensures composeWallShape doesn't use cached geometry
                    this.incrementWallGeometryVersion(connectionId);
                    this.setObjectProperty(connectionId, 'lastUpdate', Date.now());

                    // Force rebuild of fill mesh
                    renderStore.composeWallShape(connectionId, undefined, true);
                    // Also trim to ensure proper shape
                    const disconnectedWall = this.wallsMap.get(connectionId);
                    if (disconnectedWall && isWallType(disconnectedWall)) {
                      renderStore.trimShapes(disconnectedWall, undefined, undefined, { force: true });
                    }
                  } catch (fillError) {
                    console.error('Failed to rebuild fill mesh for disconnected wall', {
                      wallId: connectionId,
                      error: fillError instanceof Error ? fillError.message : fillError,
                    });
                  }
                });

                // Now trigger invalidation to redraw both fill and outlines
                allDisconnectedWalls.forEach((connectionId) => {
                  renderStore.requestInvalidation(connectionId);
                });

                // Remove all disconnected walls from progressive mode after single update
                // This avoids performance impact during drag
                setTimeout(() => {
                  allDisconnectedWalls.forEach((connectionId) => {
                    wallUpdateManager.enableProgressiveModeForWall(connectionId, false);
                  });
                }, 0);
              })
              .catch((error) => {
                console.error('Failed to flush pending wall updates:', error);
                // Even if flush fails, still try to rebuild and invalidate
                allDisconnectedWalls.forEach((connectionId) => {
                  try {
                    // Clear caches even in error case
                    renderStore.clearWallShapes(connectionId);
                    if (typeof (wallUpdateManager as any).wallGeometryCache?.clearWall === 'function') {
                      (wallUpdateManager as any).wallGeometryCache.clearWall(connectionId);
                    }

                    // Force geometry version increment even in error case
                    this.incrementWallGeometryVersion(connectionId);
                    this.setObjectProperty(connectionId, 'lastUpdate', Date.now());

                    renderStore.composeWallShape(connectionId, undefined, true);
                    const disconnectedWall = this.wallsMap.get(connectionId);
                    if (disconnectedWall && isWallType(disconnectedWall)) {
                      renderStore.trimShapes(disconnectedWall, undefined, undefined, { force: true });
                    }
                    renderStore.requestInvalidation(connectionId);
                  } catch (fillError) {
                    console.error('Failed to rebuild fill during error handling', {
                      wallId: connectionId,
                      error: fillError instanceof Error ? fillError.message : fillError,
                    });
                  }
                });
              });
          } else {
            // Fallback if processPendingUpdatesNow is not available
            allDisconnectedWalls.forEach((connectionId) => {
              try {
                // Clear caches in fallback
                renderStore.clearWallShapes(connectionId);
                if (typeof (wallUpdateManager as any).wallGeometryCache?.clearWall === 'function') {
                  (wallUpdateManager as any).wallGeometryCache.clearWall(connectionId);
                }

                // Force geometry version increment in fallback
                this.incrementWallGeometryVersion(connectionId);
                this.setObjectProperty(connectionId, 'lastUpdate', Date.now());

                renderStore.composeWallShape(connectionId, undefined, true);
                const disconnectedWall = this.wallsMap.get(connectionId);
                if (disconnectedWall && isWallType(disconnectedWall)) {
                  renderStore.trimShapes(disconnectedWall, undefined, undefined, { force: true });
                }
                renderStore.requestInvalidation(connectionId);
              } catch (fillError) {
                console.error('Failed to rebuild fill in fallback', {
                  wallId: connectionId,
                  error: fillError instanceof Error ? fillError.message : fillError,
                });
              }
            });
          }
        })
        .catch((error) => {
          console.error('floorplannerStore: Failed to schedule wall updates after detach', error);
        });

      // Immediately rebuild geometry for the dragged wall and any walls it detached from
      const wallsToRebuild = new Set<string>([wallId, ...originalConnectionIds, ...originalEdgeConnectionIds, ...Array.from(disconnectedNeighborIds)]);
      wallsToRebuild.forEach((targetId) => {
        const targetWall = this.wallsMap.get(targetId);
        if (!targetWall || !isWallType(targetWall)) {
          return;
        }

        try {
          // CRITICAL: Force clear corner state to ensure disconnected walls
          // don't have stale trimmed corners applied during recomposition.
          // refreshCornerStateForWall is insufficient because it may preserve
          // corners if other connections exist, or if trimShapes finds no
          // intersections to update the state.
          this.setObjectProperty(targetId, 'cornerState', undefined);

          // CRITICAL: Clear cached wall shapes BEFORE recomposition
          // This ensures no stale geometry persists in the render cache
          renderStore.clearWallShapes(targetId);

          const isDraggedWall = targetId === wallId;
          renderStore.composeWallShape(targetId, undefined, true);

          // Trim shapes for walls that still have connections (but not the dragged wall)
          // The composition already handles disconnected endpoints by resetting to default corners
          if (!isDraggedWall) {
            // CRITICAL: Get fresh wall from store before trimming to ensure
            // we have the latest connection state (connections may have been removed)
            const freshWallForTrim = this.wallsMap.get(targetId);
            if (freshWallForTrim && isWallType(freshWallForTrim)) {
              renderStore.trimShapes(freshWallForTrim, undefined, undefined, {
                force: true,
              });
            }
          }

          renderStore.requestInvalidation(targetId);
        } catch (geometryError) {
          console.error('floorplannerStore: Failed to rebuild geometry during detach', {
            wallId: targetId,
            error: geometryError instanceof Error ? geometryError.message : geometryError,
          });
        }
      });

      // Set global flag if this is the first dragged wall
      if (this.wallDragState.size === 1) {
        this.deferConnectionOperations = true;
      }
    }
  };

  // Begin single line drag operation - store original connections and clear them
  beginLineDrag = (
    lineId: string,
    options: {
      disconnectConnections?: boolean;
      disconnectEnd?: 'start' | 'end';
    } = { disconnectConnections: true },
  ) => {
    const line = this.singleLinesMap.get(lineId);
    if (!line || !isSingleLineType(line)) {
      return;
    }

    const originalConnections = line.centerConnections ? [...line.centerConnections] : [];
    const originalConnectionIds = Array.from(new Set(originalConnections.map((connection) => connection.id)));
    const disconnectedNeighborIds = new Set<string>();

    // Store original connections in wallDragState (reusing the same structure for consistency)
    this.wallDragState.set(lineId, {
      originalConnections,
      originalEdgeConnections: [],
      isDragging: true,
      isCommitting: false,
      visualSnapping: undefined,
      nearbyWallIds: [],
      alignmentSnap: {},
      edgeSnapping: {},
      alignmentLocks: {},
      fixedEndpoints: {
        start: line.start.clone(),
        end: line.end.clone(),
      },
      pivotAnchors: {} as any,
    });

    // Get all objects for scanning connections (used in both branches)
    const allObjects = [...Array.from(this.wallsMap.values()), ...Array.from(this.singleLinesMap.values()), ...Array.from(this.rulerLinesMap.values())];

    // Only disconnect if requested
    if (options.disconnectConnections) {
      // CRITICAL: Scan all objects (walls, single lines, ruler lines) for any connections TO the dragged line.
      // This handles cases where a remote object thinks it's connected but the dragged line doesn't know about it.
      for (const otherObject of allObjects) {
        if (otherObject.id === lineId) continue;

        // Check center connections
        if (otherObject.centerConnections?.some((c) => c.id === lineId)) {
          // Remove the connection pair
          this.removeConnection(otherObject.id, lineId);
          disconnectedNeighborIds.add(otherObject.id);
        }
      }

      // Clear all connections from the dragged line
      line.centerConnections = [];

      // Update the line in the store to persist the change
      this.singleLinesMap.set(lineId, { ...line });

      // Mark all disconnected objects for update
      disconnectedNeighborIds.forEach((connectionId) => {
        this.objectNeedsUpdate(connectionId);
      });
    } else if (options.disconnectEnd) {
      // Disconnect only the specified end (start or end)
      const connectionPos = options.disconnectEnd === 'start' ? WallConnectionStart : WallConnectionEnd;
      const linePoint = connectionPos === WallConnectionStart ? line.start : line.end;

      // Find all connections at this endpoint by checking geometric proximity
      const connectionsAtEnd = originalConnections.filter((conn) => {
        // Check if connection is at the specified endpoint
        if (conn.sourcePosition === connectionPos) {
          return true;
        }
        // Also check if the connection point is geometrically close to the endpoint
        const connPoint = getConnectionPoint(line.start, line.end, conn.sourcePosition);
        return connPoint.distanceTo(linePoint) < 0.001;
      });

      // Remove each connection at this endpoint
      connectionsAtEnd.forEach((conn) => {
        this.removeConnection(lineId, conn.id);
        disconnectedNeighborIds.add(conn.id);
      });

      // Also check for reverse connections (other objects connected to this line at this endpoint)
      // We need to check geometrically, not just by targetPosition, because the other object
      // might be connected at a midpoint that's close to our endpoint
      for (const otherObject of allObjects) {
        if (otherObject.id === lineId) continue;

        if (otherObject.centerConnections) {
          // Find connections from otherObject to this line
          const reverseConns = otherObject.centerConnections.filter((c) => c.id === lineId);

          for (const reverseConn of reverseConns) {
            // Get the connection point on the other object
            const otherPoint = reverseConn.targetPosition === WallConnectionStart ? otherObject.start : reverseConn.targetPosition === WallConnectionEnd ? otherObject.end : getConnectionPoint(otherObject.start, otherObject.end, reverseConn.targetPosition);

            // Check if this connection point is close to our endpoint
            if (linePoint.distanceTo(otherPoint) < 0.001) {
              this.removeConnection(otherObject.id, lineId);
              disconnectedNeighborIds.add(otherObject.id);
              break; // Only need to remove once per object
            }
          }
        }
      }

      // Update the line to remove connections at this endpoint
      line.centerConnections =
        line.centerConnections?.filter((conn) => {
          // Keep connections that are NOT at this endpoint
          if (conn.sourcePosition === connectionPos) {
            return false;
          }
          // Also filter out connections that are geometrically close to this endpoint
          const connPoint = getConnectionPoint(line.start, line.end, conn.sourcePosition);
          return connPoint.distanceTo(linePoint) >= 0.001;
        }) || [];
      this.singleLinesMap.set(lineId, { ...line });

      // Mark all disconnected objects for update
      disconnectedNeighborIds.forEach((connectionId) => {
        this.objectNeedsUpdate(connectionId);
      });
    }

    // Mark the line for room update
    this.markObjectForRoomUpdate(lineId);
    this.objectNeedsUpdate(lineId);
  };

  // End single line drag operation - reconnect if close to other lines
  endLineDrag = (lineId: string): string[] => {
    const line = this.singleLinesMap.get(lineId);
    if (!line || !isSingleLineType(line)) {
      return [];
    }

    const dragState = this.wallDragState.get(lineId);
    const affectedLineIds: string[] = [lineId];

    if (dragState) {
      dragState.isDragging = false;
      dragState.isCommitting = true;
    }

    // Try to reconnect to original connections if still close
    if (dragState && dragState.originalConnections.length > 0) {
      for (const originalConnection of dragState.originalConnections) {
        const targetObject = this.findObjectId(originalConnection.id);
        if (targetObject) {
          // Check if the connection points are still close
          const sourcePoint = originalConnection.sourcePosition === WallConnectionStart ? line.start : originalConnection.sourcePosition === WallConnectionEnd ? line.end : getConnectionPoint(line.start, line.end, originalConnection.sourcePosition);

          const targetPoint = originalConnection.targetPosition === WallConnectionStart ? targetObject.start : originalConnection.targetPosition === WallConnectionEnd ? targetObject.end : getConnectionPoint(targetObject.start, targetObject.end, originalConnection.targetPosition);

          // If points are close (within tolerance), reconnect
          if (sourcePoint.distanceTo(targetPoint) < 0.001) {
            this.addConnection(lineId, originalConnection.id, originalConnection.sourcePosition, originalConnection.targetPosition, { mode: originalConnection.mode });
            if (!affectedLineIds.includes(originalConnection.id)) {
              affectedLineIds.push(originalConnection.id);
            }
          }
        }
      }
    }

    // Try to snap/reconnect at start endpoint if not already connected
    const hasStartConnection = line.centerConnections?.some((conn) => conn.sourcePosition === WallConnectionStart);
    if (!hasStartConnection) {
      const startResult = this.snapToOtherObjects(lineId, WallConnectionStart, line.start, true, { allowDuringDeferred: true });
      if (typeof startResult === 'object' && startResult.snapped && startResult.targetId) {
        if (!affectedLineIds.includes(startResult.targetId)) {
          affectedLineIds.push(startResult.targetId);
        }
      }
    }

    // Try to snap/reconnect at end endpoint if not already connected
    const hasEndConnection = line.centerConnections?.some((conn) => conn.sourcePosition === WallConnectionEnd);
    if (!hasEndConnection) {
      const endResult = this.snapToOtherObjects(lineId, WallConnectionEnd, line.end, true, { allowDuringDeferred: true });
      if (typeof endResult === 'object' && endResult.snapped && endResult.targetId) {
        if (!affectedLineIds.includes(endResult.targetId)) {
          affectedLineIds.push(endResult.targetId);
        }
      }
    }

    // Mark all affected lines for update
    affectedLineIds.forEach((id) => {
      this.objectNeedsUpdate(id);
      this.markObjectForRoomUpdate(id);
    });

    // Clean up drag state
    this.wallDragState.delete(lineId);

    return affectedLineIds;
  };

  // End wall drag operation - restore connections and perform final snapping
  endWallDrag = (wallId: string): string[] => {
    let wall = this.findObjectId(wallId);
    const affectedWallIds: string[] = [];

    if (wall && isWallType(wall)) {
      const dragState = this.wallDragState.get(wallId);
      if (dragState) {
        dragState.isDragging = false;
        dragState.isCommitting = true;
      }

      const addAffectedWall = (targetId?: string) => {
        if (targetId && !affectedWallIds.includes(targetId)) {
          affectedWallIds.push(targetId);
        }
      };

      const hasConnectionBetween = (sourceWall: WallType | undefined, targetId: string): boolean => {
        if (!sourceWall) {
          return false;
        }
        const centerConnection = sourceWall.centerConnections?.some((connection) => connection.id === targetId) ?? false;
        const edgeConnection = sourceWall.edgeConnections?.some((connection) => connection.targetWallId === targetId) ?? false;
        return centerConnection || edgeConnection;
      };

      const hasEdgeConnectionAt = (endpoint: 'start' | 'end') => {
        return !!(wall && isWallType(wall) && wall.edgeConnections?.some((connection: WallEdgeConnection) => connection.sourceEndpoint === endpoint));
      };

      const tryApplyPreviewEdgeSnap = (endpoint: 'start' | 'end'): boolean => {
        if (!wall || !isWallType(wall)) {
          return false;
        }
        const preview = dragState?.edgeSnapping?.[endpoint];
        if (!preview) {
          return false;
        }

        const snappingEnd = endpoint === 'start' ? WallConnectionStart : WallConnectionEnd;
        const endpointPosition = endpoint === 'start' ? wall.start.clone() : wall.end.clone();
        const snapResult = this.snapToOtherObjectsEdgeBased(wallId, snappingEnd, endpointPosition, true, { allowDuringDeferred: true });

        const snapped = typeof snapResult === 'object' ? snapResult.snapped : snapResult;
        if (typeof snapResult === 'object' && snapResult.targetId) {
          addAffectedWall(snapResult.targetId);
        }
        return !!snapped;
      };

      let startSnapped = hasEdgeConnectionAt('start');
      let endSnapped = hasEdgeConnectionAt('end');

      if (dragState && dragState.originalConnections.length > 0) {
        for (const originalConnection of dragState.originalConnections) {
          const targetWall = this.findObjectId(originalConnection.id);
          if (targetWall && isWallType(targetWall)) {
            const sourceEndpoint = originalConnection.sourcePosition === WallConnectionStart ? wall.start : wall.end;
            const targetEndpoint = originalConnection.targetPosition === WallConnectionStart ? targetWall.start : targetWall.end;
            if (this.vectorsEqual(sourceEndpoint, targetEndpoint, 0.001)) {
              this.addConnection(wallId, originalConnection.id, originalConnection.sourcePosition, originalConnection.targetPosition, { mode: originalConnection.mode });
              if (originalConnection.sourcePosition === WallConnectionStart) {
                startSnapped = true;
              } else if (originalConnection.sourcePosition === WallConnectionEnd) {
                endSnapped = true;
              }
              addAffectedWall(originalConnection.id);
            }
          }
        }
      }

      wall.edgeConnections?.forEach((connection) => {
        addAffectedWall(connection.targetWallId);
      });

      if (dragState && dragState.originalEdgeConnections && dragState.originalEdgeConnections.length > 0) {
        for (const originalEdgeConnection of dragState.originalEdgeConnections) {
          const targetWall = this.findObjectId(originalEdgeConnection.targetWallId);
          if (targetWall && isWallType(targetWall)) {
            const targetEdgeConnection = targetWall.edgeConnections?.find((conn) => conn.targetWallId === wallId && conn.sourceEndpoint === originalEdgeConnection.targetEndpoint && conn.targetEndpoint === originalEdgeConnection.sourceEndpoint);

            if (targetEdgeConnection) {
              let restoredConnection = originalEdgeConnection;
              try {
                restoredConnection = recomputeEdgeConnectionForCurrentGeometry(
                  wall,
                  targetWall,
                  originalEdgeConnection,
                ) ?? originalEdgeConnection;
              } catch (error) {
                console.warn('endWallDrag: Failed to recompute edge connection after drag, using original points', {
                  wallId,
                  targetWallId: originalEdgeConnection.targetWallId,
                  error: error instanceof Error ? error.message : error,
                });
              }
              // Do not restore joins that no longer meet within a valid edge gap.
              if (!isEdgeConnectionWithinJoinRange(restoredConnection, wall, targetWall)) {
                continue;
              }
              this.addEdgeConnection(
                wallId,
                originalEdgeConnection.targetWallId,
                originalEdgeConnection.sourceEndpoint,
                originalEdgeConnection.targetEndpoint,
                restoredConnection,
              );
              if (originalEdgeConnection.sourceEndpoint === 'start') {
                startSnapped = true;
              } else if (originalEdgeConnection.sourceEndpoint === 'end') {
                endSnapped = true;
              }
              addAffectedWall(originalEdgeConnection.targetWallId);
            }
          }
        }
      }

      if (!startSnapped) {
        startSnapped = tryApplyPreviewEdgeSnap('start');
      }
      if (!endSnapped) {
        endSnapped = tryApplyPreviewEdgeSnap('end');
      }

      if (!startSnapped) {
        const startResult = this.snapToOtherObjects(wallId, WallConnectionStart, wall.start, true, { allowDuringDeferred: true });
        startSnapped = typeof startResult === 'object' && startResult.snapped;
        if (typeof startResult === 'object') {
          addAffectedWall(startResult.targetId);
        }
      }

      if (!endSnapped) {
        const endResult = this.snapToOtherObjects(wallId, WallConnectionEnd, wall.end, true, { allowDuringDeferred: true });
        endSnapped = typeof endResult === 'object' && endResult.snapped;
        if (typeof endResult === 'object') {
          addAffectedWall(endResult.targetId);
        }
      }

      const latestWallState = this.wallsMap.get(wallId);
      if (latestWallState && isWallType(latestWallState)) {
        wall = latestWallState;
        const autoConnectedWalls = this.autoConnectNearbyWallEndpoints(latestWallState, dragState?.nearbyWallIds);
        autoConnectedWalls.forEach(addAffectedWall);
      }

      // Collect affected wall IDs for updates
      if (wall.centerConnections) {
        wall.centerConnections.forEach((connection) => {
          if (!affectedWallIds.includes(connection.id)) {
            affectedWallIds.push(connection.id);
          }
        });
      }
      if (wall.edgeConnections) {
        wall.edgeConnections.forEach((connection) => {
          addAffectedWall(connection.targetWallId);
        });
      }

      // Force rebuild on walls that were connected when the drag began but are now detached
      const previouslyConnectedWallIds = new Set<string>();
      dragState?.originalConnections?.forEach((connection) => {
        if (connection?.id) {
          previouslyConnectedWallIds.add(connection.id);
        }
      });
      dragState?.originalEdgeConnections?.forEach((connection) => {
        if (connection?.targetWallId) {
          previouslyConnectedWallIds.add(connection.targetWallId);
        }
      });

      previouslyConnectedWallIds.forEach((previousWallId) => {
        if (!previousWallId || previousWallId === wallId) {
          return;
        }

        const candidateWall = this.wallsMap.get(previousWallId);
        if (!candidateWall || !isWallType(candidateWall)) {
          return;
        }
        const detachedWall: WallType = candidateWall;

        const stillConnected = hasConnectionBetween(wall as WallType, previousWallId) || hasConnectionBetween(detachedWall, wallId);

        if (stillConnected) {
          return;
        }

        try {
          renderStore.clearWallShapes(previousWallId);
          this.incrementWallGeometryVersion(previousWallId);
          renderStore.composeWallShape(previousWallId, undefined, true);
          renderStore.trimShapes(detachedWall, undefined, undefined, {
            force: true,
          });
          renderStore.requestInvalidation(previousWallId);
          addAffectedWall(previousWallId);
        } catch (detachedError) {
          console.error('floorplannerStore: Failed to rebuild geometry for detached wall', {
            wallId: previousWallId,
            detachedFrom: wallId,
            error: detachedError instanceof Error ? detachedError.message : detachedError,
          });
        }
      });

      // Ensure walls that were originally connected also receive a final redraw,
      // even if the connection was removed during the drag operation.
      if (dragState?.originalConnections?.length) {
        dragState.originalConnections.forEach((originalConnection) => {
          if (!affectedWallIds.includes(originalConnection.id)) {
            affectedWallIds.push(originalConnection.id);
          }
        });
      }

      // Track the dragged wall itself for a final recompute/redraw.
      if (!affectedWallIds.includes(wallId)) {
        affectedWallIds.push(wallId);
      }
    }

    // Translate visual snapping data to deferredSnappingResults for final render update
    const dragState = this.wallDragState.get(wallId);
    renderStore.deferredSnappingResults.delete(wallId);

    // Release any measurement side lock held for this drag
    this.unlockMeasurementDisplaySide(wallId);

    // Remove wall from drag state
    this.wallDragState.delete(wallId);

    // Clear global flag if no walls remain in drag
    if (this.wallDragState.size === 0) {
      this.deferConnectionOperations = false;
    }

    return affectedWallIds;
  };

  /**
   * Store visual snapping data for a wall during drag operations
   * Wrapper method for renderStore.storeSnappingResult to maintain compatibility
   * Delegates visual snapping results to the render store for proper batch handling
   */
  storeVisualSnapping = (
    wallId: string,
    data: {
      startSnapped: boolean;
      endSnapped: boolean;
      newStart: THREE.Vector2;
      newEnd: THREE.Vector2;
    },
  ): void => {
    renderStore.storeSnappingResult(wallId, data);
  };

  storeEdgeSnappingPreview = (wallId: string, endpoint: 'start' | 'end', preview: EdgeSnappingPreviewResult | null): void => {
    const dragState = this.wallDragState.get(wallId);
    if (!dragState) {
      return;
    }

    if (!preview) {
      if (dragState.edgeSnapping) {
        delete dragState.edgeSnapping[endpoint];
        if (!dragState.edgeSnapping.start && !dragState.edgeSnapping.end) {
          delete dragState.edgeSnapping;
        }
      }
      return;
    }

    if (!dragState.edgeSnapping) {
      dragState.edgeSnapping = {};
    }

    dragState.edgeSnapping[endpoint] = {
      snappingPosition: preview.snappingPosition.clone(),
      edgeConnection: {
        ...preview.edgeConnection,
        sourceEdgePoint: preview.edgeConnection.sourceEdgePoint.clone(),
        targetEdgePoint: preview.edgeConnection.targetEdgePoint.clone(),
      },
    };
  };

  /**
   * Get visual snapping data for a wall during drag operations
   */
  getVisualSnappingData = (wallId: string): any => {
    const dragState = this.wallDragState.get(wallId);
    return dragState?.visualSnapping;
  };

  // Check if any walls are currently being dragged
  isDragInProgress = (): boolean => {
    return this.wallDragState.size > 0;
  };

  // Flag a wall's rooms for update
  markObjectForRoomUpdate = (objectId: string) => {
    this.objectsNeedingRoomUpdate.add(objectId);

    // Skip room calculations during drag operations or when explicitly deferred
    if (renderStore.isDragging || this.deferRoomUpdates) {
      return;
    }

    // Debounce the actual room update to avoid excessive computation during interaction
    this.processRoomUpdates();
  };

  private getRoomIdsForObjects = (objectIds: Iterable<string>): Set<string> => {
    const roomIds = new Set<string>();

    Array.from(objectIds).forEach((objectId) => {
      this.objectToRoomsMap.get(objectId)?.forEach((roomId) => {
        if (this.roomsMap.has(roomId)) {
          roomIds.add(roomId);
        }
      });

      const object = this.findObjectId(objectId);
      if (object && (isWallType(object) || isSingleLineType(object))) {
        object.roomIds?.forEach((roomId) => {
          if (this.roomsMap.has(roomId)) {
            roomIds.add(roomId);
          }
        });
      }
    });

    return roomIds;
  };

  private getWallRoomGeometryAffectedObjectIds = (wall: WallType): Set<string> => {
    const objectIds = new Set<string>([wall.id]);

    wall.centerConnections?.forEach((connection) => {
      if (this.wallsMap.has(connection.id)) {
        objectIds.add(connection.id);
      }
    });

    wall.edgeConnections?.forEach((connection) => {
      if (this.wallsMap.has(connection.targetWallId)) {
        objectIds.add(connection.targetWallId);
      }
    });

    return objectIds;
  };

  private setRoomObjectLink = (objectId: string, roomId: string): void => {
    if (!this.objectToRoomsMap.has(objectId)) {
      this.objectToRoomsMap.set(objectId, new Set<string>());
    }
    this.objectToRoomsMap.get(objectId)!.add(roomId);
  };

  private refreshRoomGeometryFromCurrentFace = (room: RoomType): RoomType | null => {
    // Rebuild from the ordered face darts, not the room's unique object list.
    // A wall that a partition joins in the middle is split into separate face
    // segments. Expanding it back to its full endpoints connects unrelated
    // corners and produces self-crossing room polygons after a width change.
    const vertices = this.getCurrentFaceVerticesForRoom(room);
    if (vertices.length < 3 || this.computeRoomArea(vertices) < 1e-10) {
      this.roomsMap.delete(room.id);
      return null;
    }

    const updatedRoom: RoomType = {
      ...room,
      vertices: vertices.map((vertex) => vertex.clone()),
      centroid: this.computeRoomCentroid(vertices),
      areaWorldUnits: this.computeRoomArea(vertices),
    };

    this.roomsMap.set(room.id, updatedRoom);
    return updatedRoom;
  };

  private refreshBoundAreaFillForRoom = (room: RoomType): void => {
    const signature = this.getRoomObjectSignature(room.objectIds);
    const boundFill: AreaFillSymbolType | undefined = Array.from(this.symbolsMap.values()).find((symbol): symbol is AreaFillSymbolType => (
      isAreaFillSymbolType(symbol) && symbol.roomBinding?.roomSignature === signature
    ));

    if (!boundFill || !room.vertices || room.vertices.length < 3) {
      return;
    }

    this.upsertRoomBoundAreaFill(room, this.closeRoomFillVertices(room.vertices), {
      autoCreated: !!boundFill.roomBinding?.autoCreated,
      existingId: boundFill.id,
    });
  };

  refreshRoomsForObjects = (objectIds: Iterable<string>): void => {
    const objectIdList = Array.from(objectIds);
    const roomIds = this.getRoomIdsForObjects(objectIdList);
    if (roomIds.size === 0) {
      return;
    }

    roomIds.forEach((roomId) => {
      const room = this.roomsMap.get(roomId);
      if (!room) {
        return;
      }

      const updatedRoom = this.refreshRoomGeometryFromCurrentFace(room);
      if (updatedRoom) {
        this.refreshBoundAreaFillForRoom(updatedRoom);
      }
    });

    renderStore.requestInvalidation(objectIdList[0]);
  };

  // Recompute rooms after the wall/line graph changed.
  // A single object edit can split or merge interior faces anywhere in the
  // connected graph (e.g. dropping a partition onto a junction), so we run a
  // full planar-face recompute rather than an incremental per-room pass.
  // recomputeAllRooms() preserves untouched rooms' ids/metadata by signature,
  // and this runs debounced and never during drag, so the cost is bounded.
  updateAffectedRooms = () => {
    const objectsToUpdate = Array.from(this.objectsNeedingRoomUpdate);
    this.objectsNeedingRoomUpdate.clear();

    if (objectsToUpdate.length === 0) {
      return;
    }

    // New walls drawn into a colored room often have no fillColor yet; copy from
    // connected neighbors before room recompute so 2D/3D wall color stays intact.
    objectsToUpdate.forEach((objectId) => {
      this.inheritMissingWallFillColorFromNeighbors(objectId);
    });

    this.recomputeAllRooms();
  };

  /**
   * Initialize all rooms from scratch - used after loading a floorplan
   * This performs a single complete room detection pass rather than incremental updates
   */
  initializeAllRooms = () => {
    this.recomputeAllRooms();
  };

  /**
   * Recompute every room from the current wall/line graph using a planar
   * minimal-face traversal.
   *
   * The previous DFS-based detector searched for *any* cycle and greedily
   * marked its walls as consumed. That breaks for interior partition walls:
   * when one of the two rooms a partition borders extends past the partition's
   * junction (a 3-way / multi-joint), the DFS could close the large outer loop
   * instead of the small interior face, so one of the rooms was never created.
   *
   * Planar face traversal instead walks the half-edges of the wall graph,
   * always taking the next edge clockwise from the reverse of the incoming
   * edge. This yields exactly the bounded faces (one per room) regardless of
   * how walls branch at junctions, and a shared partition naturally belongs to
   * the two faces on either side of it.
   *
   * Existing rooms are matched to recomputed faces by their object-id
   * signature so user-set metadata (id, name, colours, selection) survives the
   * rebuild. Floor styling on auto areaFills is remapped geometrically in
   * syncRoomAreaFills() when a partition/merge changes the signature.
   */
  recomputeAllRooms = () => {
    // Preserve user-editable room metadata keyed by object-id signature so a
    // full recompute does not churn ids/names for rooms that are unchanged.
    const preservedRooms = new Map<string, RoomType>();
    this.roomsMap.forEach((room) => {
      preservedRooms.set(this.getRoomObjectSignature(room.objectIds), room);
    });

    // Reset all derived room state.
    this.roomsFound.clear();
    this.objectIdsInFoundRooms.clear();
    this.objectToRoomsMap.clear();
    this.roomsMap.clear();
    this.wallsMap.forEach((wall) => {
      wall.roomIds = [];
    });
    this.singleLinesMap.forEach((line) => {
      line.roomIds = [];
    });

    const faces = this.computeInteriorWallFaces();
    for (const face of faces) {
      // Distinct bordering objects, in traversal order (a split wall borders a
      // face via one sub-segment but is listed once).
      const orderedObjects: (WallType | SingleLineType)[] = [];
      const seen = new Set<string>();
      for (const dart of face) {
        if (!seen.has(dart.object.id)) {
          seen.add(dart.object.id);
          orderedObjects.push(dart.object);
        }
      }
      if (orderedObjects.length < 3) {
        continue;
      }
      const signature = this.getRoomObjectSignature(orderedObjects.map((o) => o.id));
      if (this.roomsFound.has(signature)) {
        continue;
      }
      const polygon = this.buildInnerPolygonForFace(face);
      this.roomsFound.add(signature);
      this.addObjectsToFoundRooms(orderedObjects);
      this.createRoomFromPath(orderedObjects, preservedRooms.get(signature), polygon);
    }

    this.syncRoomAreaFills();
  };

  private areVectorArraysNearlyEqual = (left: THREE.Vector2[], right: THREE.Vector2[], tolerance: number = 1e-6): boolean => {
    if (left.length !== right.length) return false;
    for (let i = 0; i < left.length; i++) {
      if (left[i].distanceToSquared(right[i]) > tolerance * tolerance) {
        return false;
      }
    }
    return true;
  };

  private closeRoomFillVertices = (vertices: THREE.Vector2[]): THREE.Vector2[] => {
    const cloned = vertices.map((vertex) => vertex.clone());
    if (cloned.length > 1 && cloned[0].distanceToSquared(cloned[cloned.length - 1]) > 1e-12) {
      cloned.push(cloned[0].clone());
    }
    return cloned;
  };

  private getUsableStoredRoomVertices = (room: RoomType): THREE.Vector2[] | null => {
    if (!room.vertices || room.vertices.length < 3) {
      return null;
    }

    const vertices = this.closeRoomFillVertices(room.vertices);
    const area = this.computeRoomArea(vertices);
    if (area < 1e-10) {
      return null;
    }

    if (Number.isFinite(room.areaWorldUnits) && room.areaWorldUnits > 1e-10) {
      const tolerance = Math.max(1e-6, Math.max(area, room.areaWorldUnits) * 1e-4);
      if (Math.abs(area - room.areaWorldUnits) > tolerance) {
        return null;
      }
    }

    return vertices;
  };

  private getCurrentFaceVerticesForRoom = (room: RoomType): THREE.Vector2[] => {
    const face = this.getCurrentFaceForRoom(room);
    return face ? this.buildInnerPolygonForFace(face) : [];
  };

  private getCurrentFaceForRoom = (room: RoomType): FaceDart[] | null => {
    const signature = this.getRoomObjectSignature(room.objectIds);
    if (!signature) {
      return null;
    }

    const faces = this.computeInteriorWallFaces();
    for (const face of faces) {
      const orderedObjectIds: string[] = [];
      const seen = new Set<string>();
      for (const dart of face) {
        if (!seen.has(dart.object.id)) {
          seen.add(dart.object.id);
          orderedObjectIds.push(dart.object.id);
        }
      }

      if (this.getRoomObjectSignature(orderedObjectIds) === signature) {
        return face;
      }
    }

    return null;
  };

  getRoomFillGeometry = (room: RoomType, options: { forceCurrent?: boolean } = {}): RoomFillGeometry => {
    if (!options.forceCurrent) {
      const storedVertices = this.getUsableStoredRoomVertices(room);
      if (storedVertices) {
        return { vertices: storedVertices, source: 'stored' };
      }
    }

    const currentFaceVertices = this.getCurrentFaceVerticesForRoom(room);
    if (currentFaceVertices.length >= 3 && this.computeRoomArea(currentFaceVertices) >= 1e-10) {
      return { vertices: currentFaceVertices, source: 'face' };
    }

    const objects = this.getObjectsOfRoom(room);
    if (objects.length >= 3) {
      const objectVertices = this.getRoomVertices(objects, 'inner');
      if (objectVertices.length >= 3 && this.computeRoomArea(objectVertices) >= 1e-10) {
        return { vertices: objectVertices, source: 'objects' };
      }
    }

    const storedVertices = this.getUsableStoredRoomVertices(room);
    if (storedVertices) {
      return { vertices: storedVertices, source: 'stored' };
    }

    return { vertices: [], source: 'empty' };
  };

  getRoomFillVertices = (room: RoomType, options: { forceCurrent?: boolean } = {}): THREE.Vector2[] => (
    this.getRoomFillGeometry(room, options).vertices
  );

  getRoomRenderFillVertices = (room: RoomType): THREE.Vector2[] => {
    const face = this.getCurrentFaceForRoom(room);
    if (face) {
      const renderVertices = this.buildCenterlinePolygonForFace(face);
      if (renderVertices.length >= 3 && this.computeRoomArea(renderVertices) >= 1e-10) {
        return renderVertices;
      }
    }

    return this.getRoomFillVertices(room);
  };

  private toRelativeAreaFillVertices = (vertices: THREE.Vector2[]): { origin: THREE.Vector2; vertices: THREE.Vector2[] } | null => {
    if (vertices.length < 3) {
      return null;
    }

    const origin = vertices[0].clone();
    return {
      origin,
      vertices: vertices.map((vertex) => vertex.clone().sub(origin)),
    };
  };

  private hasManualAreaFillForRoom = (room: RoomType, vertices: THREE.Vector2[]): boolean => {
    const point = room.centroid ?? this.computeRoomCentroid(vertices);
    return Array.from(this.symbolsMap.values()).some((symbol) => {
      if (!isAreaFillSymbolType(symbol)) return false;
      if (symbol.roomBinding) return false;

      const worldVertices = symbol.vertices.map((vertex) => vertex.clone().add(symbol.position));
      return this.isPointInPolygon(point, worldVertices);
    });
  };

  private isPointInPolygon = (point: THREE.Vector2, vertices: THREE.Vector2[]): boolean => {
    if (vertices.length < 3) {
      return false;
    }

    let inside = false;
    const lastIndex = vertices.length - 1;
    const closedLastIndex = vertices[0].distanceToSquared(vertices[lastIndex]) < 1e-12
      ? lastIndex - 1
      : lastIndex;

    for (let i = 0, j = closedLastIndex; i <= closedLastIndex; j = i++) {
      const current = vertices[i];
      const previous = vertices[j];
      const intersects =
        current.y > point.y !== previous.y > point.y &&
        point.x < ((previous.x - current.x) * (point.y - current.y)) / ((previous.y - current.y) || 1e-12) + current.x;

      if (intersects) {
        inside = !inside;
      }
    }

    return inside;
  };

  /**
   * Snapshot of a room-bound areaFill whose object-id signature no longer
   * matches any active room. Used to transfer floor color/pattern onto
   * successor rooms after a partition/merge changes the signature.
   */
  private collectStaleRoomBoundAreaFillDonors = (
    activeSignatures: Set<string>,
  ): Array<{
    id: string;
    fillColor?: number;
    fillPattern?: AreaFillSymbolType['fillPattern'];
    fillOpacity?: number;
    threeD?: AreaFillSymbolType['threeD'];
    worldVertices: THREE.Vector2[];
    objectIds: string[];
    autoCreated: boolean;
  }> => {
    const donors: Array<{
      id: string;
      fillColor?: number;
      fillPattern?: AreaFillSymbolType['fillPattern'];
      fillOpacity?: number;
      threeD?: AreaFillSymbolType['threeD'];
      worldVertices: THREE.Vector2[];
      objectIds: string[];
      autoCreated: boolean;
    }> = [];

    this.symbolsMap.forEach((symbol) => {
      if (!isAreaFillSymbolType(symbol) || !symbol.roomBinding) {
        return;
      }
      if (activeSignatures.has(symbol.roomBinding.roomSignature)) {
        return;
      }
      // Include auto-created floors and any room-bound fill whose walls still
      // exist (e.g. paste remapped IDs but face signature differs briefly) so
      // fill color / 3D materials can transfer to the successor room.
      const wallsStillExist = symbol.roomBinding.objectIds.every((objectId) =>
        this.wallsMap.has(objectId) || this.singleLinesMap.has(objectId),
      );
      if (!symbol.roomBinding.autoCreated && !wallsStillExist) {
        return;
      }

      donors.push({
        id: symbol.id,
        fillColor: symbol.fillColor,
        fillPattern: symbol.fillPattern,
        fillOpacity: symbol.fillOpacity,
        threeD: symbol.threeD,
        worldVertices: symbol.vertices.map((vertex) => vertex.clone().add(symbol.position)),
        objectIds: symbol.roomBinding.objectIds.slice(),
        autoCreated: !!symbol.roomBinding.autoCreated,
      });
    });

    return donors;
  };

  /**
   * Pick the best stale areaFill to inherit floor styling from when a room's
   * object-id signature changed (e.g. user added a partition wall). Prefers
   * donors whose polygon contains the new room centroid, then highest wall
   * overlap. Returns undefined when no plausible predecessor exists.
   */
  private findBestAreaFillStyleDonor = <T extends {
    id: string;
    worldVertices: THREE.Vector2[];
    objectIds: string[];
  }>(
    donors: T[],
    room: RoomType,
    roomCentroid: THREE.Vector2,
  ): T | undefined => {
    if (donors.length === 0) {
      return undefined;
    }

    const roomObjectIds = new Set(room.objectIds);
    let best: T | undefined;
    let bestScore = -Infinity;

    for (const donor of donors) {
      const overlap = donor.objectIds.reduce(
        (count, objectId) => (roomObjectIds.has(objectId) ? count + 1 : count),
        0,
      );
      const centroidInside = this.isPointInPolygon(roomCentroid, donor.worldVertices);
      if (!centroidInside && overlap < 2) {
        continue;
      }

      // Prefer containment (split/merge stays inside the old floor), then shared
      // walls, then the spatially tighter donor so a nested/partition case does
      // not steal style from a larger unrelated room.
      const donorArea = Math.max(this.computeRoomArea(donor.worldVertices), 1e-6);
      const score =
        (centroidInside ? 1000 : 0) +
        overlap * 10 +
        (overlap / Math.max(donor.objectIds.length, 1)) * 5 -
        donorArea * 1e-4;

      if (score > bestScore) {
        bestScore = score;
        best = donor;
      }
    }

    return best;
  };

  private upsertRoomBoundAreaFill = (
    room: RoomType,
    vertices: THREE.Vector2[],
    options: {
      autoCreated: boolean;
      existingId?: string;
      /** Floor style to apply when creating a new fill (e.g. remapped after partition). */
      style?: {
        fillColor?: number;
        fillPattern?: AreaFillSymbolType['fillPattern'];
        fillOpacity?: number;
        threeD?: AreaFillSymbolType['threeD'];
      };
    },
  ): string | undefined => {
    const signature = this.getRoomObjectSignature(room.objectIds);
    const relative = this.toRelativeAreaFillVertices(vertices);
    if (!relative || this.computeRoomArea(vertices) < 1e-10) {
      return undefined;
    }

    const existingId = options.existingId ?? Array.from(this.symbolsMap.values()).find((symbol) => (
      isAreaFillSymbolType(symbol) && symbol.roomBinding?.roomSignature === signature
    ))?.id;

    const roomBinding = {
      roomId: room.id,
      roomSignature: signature,
      objectIds: room.objectIds.slice(),
      autoCreated: options.autoCreated,
    };

    if (existingId) {
      const existing = this.symbolsMap.get(existingId);
      if (!existing || !isAreaFillSymbolType(existing)) {
        return undefined;
      }

      const trackUndo = undoRedoManager.isTrackingTransaction;
      if (trackUndo) {
        this.recordSymbolForUndo(existingId);
      }

      const nextSymbol: AreaFillSymbolType = {
        ...existing,
        position: relative.origin,
        vertices: relative.vertices,
        roomBinding: {
          ...roomBinding,
          autoCreated: existing.roomBinding?.autoCreated ?? options.autoCreated,
        },
        // Remapped fills keep their own colors; only apply style overrides when
        // the caller explicitly transfers them onto a reused donor symbol that
        // already carries them (no-op) or when merging style onto an existing id.
        ...(options.style?.fillColor !== undefined ? { fillColor: options.style.fillColor } : {}),
        ...(options.style && 'fillPattern' in options.style
          ? { fillPattern: options.style.fillPattern }
          : {}),
        ...(options.style?.fillOpacity !== undefined ? { fillOpacity: options.style.fillOpacity } : {}),
        ...(options.style?.threeD !== undefined ? { threeD: options.style.threeD } : {}),
      };

      const geometryChanged =
        existing.position.distanceToSquared(nextSymbol.position) > 1e-12 ||
        !this.areVectorArraysNearlyEqual(existing.vertices, nextSymbol.vertices);
      const bindingChanged =
        existing.roomBinding?.roomId !== nextSymbol.roomBinding?.roomId ||
        existing.roomBinding?.roomSignature !== nextSymbol.roomBinding?.roomSignature ||
        existing.roomBinding?.objectIds.join(',') !== nextSymbol.roomBinding?.objectIds.join(',');
      const styleChanged =
        existing.fillColor !== nextSymbol.fillColor ||
        existing.fillPattern !== nextSymbol.fillPattern ||
        existing.fillOpacity !== nextSymbol.fillOpacity ||
        existing.threeD !== nextSymbol.threeD;

      if (geometryChanged || bindingChanged || styleChanged) {
        this.symbolsMap.set(existingId, nextSymbol);
        if (trackUndo) {
          this.recordSymbolChange(existingId, 'modify');
        }
        this.setDirty();
      }

      return existingId;
    }

    const id = Math.random().toString(36).substr(2, 9);
    const newSymbol: AreaFillSymbolType = {
      id,
      type: 'areaFill',
      position: relative.origin,
      zIndex: 0,
      rotation: 0.0001,
      selected: false,
      lineWeight: this.lineWeight,
      lineColor: this.lineColor,
      fillColor: options.style?.fillColor ?? this.fillColor,
      fillPattern: options.style && 'fillPattern' in options.style
        ? options.style.fillPattern
        : undefined,
      ...(options.style?.fillOpacity !== undefined ? { fillOpacity: options.style.fillOpacity } : {}),
      ...(options.style?.threeD !== undefined ? { threeD: options.style.threeD } : {}),
      hoverColor: this.hoverColor,
      selectable: true,
      locked: true,
      vertices: relative.vertices,
      roomBinding,
    };

    const trackUndo = undoRedoManager.isTrackingTransaction;
    this.symbolsMap.set(id, newSymbol);
    if (trackUndo) {
      this.recordSymbolChange(id, 'add');
    }
    this.setDirty();
    return id;
  };

  /**
   * When a new wall is drawn into a finished colored room it has no fillColor.
   * Copy the dominant fillColor from already-connected neighbors so wall color
   * stays visually consistent after partitioning.
   */
  private inheritMissingWallFillColorFromNeighbors = (wallId: string): void => {
    const wall = this.wallsMap.get(wallId);
    if (!wall || wall.fillColor !== undefined) {
      return;
    }

    const neighborColorCounts = new Map<number, number>();
    const considerNeighbor = (otherId: string) => {
      if (otherId === wallId) {
        return;
      }
      const other = this.wallsMap.get(otherId);
      if (!other || other.fillColor === undefined) {
        return;
      }
      neighborColorCounts.set(
        other.fillColor,
        (neighborColorCounts.get(other.fillColor) ?? 0) + 1,
      );
    };

    wall.centerConnections?.forEach((connection) => considerNeighbor(connection.id));
    wall.edgeConnections?.forEach((connection) => {
      const otherId =
        connection.sourceWallId === wallId
          ? connection.targetWallId
          : connection.sourceWallId;
      considerNeighbor(otherId);
    });

    if (neighborColorCounts.size === 0) {
      return;
    }

    let bestColor: number | undefined;
    let bestCount = 0;
    neighborColorCounts.forEach((count, color) => {
      if (count > bestCount) {
        bestCount = count;
        bestColor = color;
      }
    });

    if (bestColor === undefined) {
      return;
    }

    this.wallsMap.set(wallId, { ...wall, fillColor: bestColor });
    this.setDirty();
  };

  syncRoomAreaFills = () => {
    const activeSignatures = new Set<string>();

    this.roomsMap.forEach((room) => {
      activeSignatures.add(this.getRoomObjectSignature(room.objectIds));
    });

    // Snapshot styled fills whose signatures vanished (partition/merge/paste)
    // before deleting auto-created ones, so successor rooms inherit materials.
    const styleDonors = this.collectStaleRoomBoundAreaFillDonors(activeSignatures);
    // Only auto-created fills are deleted if unclaimed; manual fills stay put.
    const staleAutoFillIds = new Set(
      styleDonors.filter((donor) => donor.autoCreated).map((donor) => donor.id),
    );
    const claimedDonorIds = new Set<string>();

    Array.from(this.symbolsMap.values()).forEach((symbol) => {
      if (!isAreaFillSymbolType(symbol) || !symbol.roomBinding) {
        return;
      }

      if (!activeSignatures.has(symbol.roomBinding.roomSignature)) {
        if (symbol.roomBinding.autoCreated) {
          // Keep auto-created donors until remapping claims or discards them below.
          return;
        }
        const trackUndo = undoRedoManager.isTrackingTransaction;
        if (trackUndo) {
          this.recordSymbolForUndo(symbol.id);
        }
        const { roomBinding: _detachedRoomBinding, ...detachedSymbol } = symbol;
        this.symbolsMap.set(symbol.id, detachedSymbol as AreaFillSymbolType);
        if (trackUndo) {
          this.recordSymbolChange(symbol.id, 'modify');
        }
        this.setDirty();
      }
    });

    this.roomsMap.forEach((room) => {
      const signature = this.getRoomObjectSignature(room.objectIds);
      if (this.suppressedAutoAreaFillRoomSignatures.has(signature)) {
        return;
      }

      const vertices = this.getRoomFillVertices(room);
      if (vertices.length < 3 || this.computeRoomArea(vertices) < 1e-10) {
        return;
      }

      const existingBoundFill: AreaFillSymbolType | undefined = Array.from(this.symbolsMap.values()).find((symbol): symbol is AreaFillSymbolType => (
        isAreaFillSymbolType(symbol) &&
        symbol.roomBinding?.roomSignature === signature &&
        !staleAutoFillIds.has(symbol.id)
      ));

      let remappedStyle: {
        fillColor?: number;
        fillPattern?: AreaFillSymbolType['fillPattern'];
        fillOpacity?: number;
        threeD?: AreaFillSymbolType['threeD'];
      } | undefined;
      let reuseDonorId: string | undefined;

      if (!existingBoundFill) {
        const roomCentroid = room.centroid ?? this.computeRoomCentroid(vertices);
        // Allow the same donor's *styles* to apply to multiple successor rooms
        // (a split), but only reuse the symbol id once.
        const donor = this.findBestAreaFillStyleDonor(styleDonors, room, roomCentroid);
        if (donor) {
          remappedStyle = {
            fillColor: donor.fillColor,
            fillPattern: donor.fillPattern,
            fillOpacity: donor.fillOpacity,
            threeD: donor.threeD,
          };
          if (!claimedDonorIds.has(donor.id)) {
            reuseDonorId = donor.id;
            claimedDonorIds.add(donor.id);
          }
        }
      }

      if (!existingBoundFill && !remappedStyle && this.hasManualAreaFillForRoom(room, vertices)) {
        return;
      }

      this.upsertRoomBoundAreaFill(room, vertices, {
        autoCreated: existingBoundFill
          ? !!existingBoundFill.roomBinding?.autoCreated
          : true,
        existingId: existingBoundFill?.id ?? reuseDonorId,
        style: remappedStyle,
      });
    });

    // Drop auto fills that never found a successor room.
    staleAutoFillIds.forEach((fillId) => {
      if (claimedDonorIds.has(fillId)) {
        return;
      }
      if (this.symbolsMap.has(fillId)) {
        const trackUndo = undoRedoManager.isTrackingTransaction;
        if (trackUndo) {
          this.recordSymbolForUndo(fillId);
          this.recordSymbolChange(fillId, 'delete');
        }
        this.symbolsMap.delete(fillId);
        this.setDirty();
      }
    });
  };

  /** Include every wall/line id that bounds a room touching any seed object. */
  collectRoomBoundaryObjectIds = (seedObjectIds: Iterable<string>): string[] => {
    const seeds = new Set(seedObjectIds);
    const expanded = new Set<string>(seeds);
    this.roomsMap.forEach((room) => {
      if (room.objectIds.some((objectId) => seeds.has(objectId))) {
        room.objectIds.forEach((objectId) => expanded.add(objectId));
      }
    });
    return Array.from(expanded);
  };

  syncAreaFillsForObjects = (objectIds: Iterable<string>) => {
    const objectIdSet = new Set(objectIds);
    if (objectIdSet.size === 0) {
      return;
    }

    this.roomsMap.forEach((room) => {
      if (!room.objectIds.some((objectId) => objectIdSet.has(objectId))) {
        return;
      }

      const signature = this.getRoomObjectSignature(room.objectIds);
      const boundFill: AreaFillSymbolType | undefined = Array.from(this.symbolsMap.values()).find((symbol): symbol is AreaFillSymbolType => (
        isAreaFillSymbolType(symbol) && symbol.roomBinding?.roomSignature === signature
      ));
      if (!boundFill) {
        return;
      }

      const vertices = this.getRoomFillVertices(room, { forceCurrent: true });
      if (vertices.length < 3 || this.computeRoomArea(vertices) < 1e-10) {
        return;
      }

      room.vertices = vertices.map((vertex) => vertex.clone());
      room.centroid = this.computeRoomCentroid(vertices);
      room.areaWorldUnits = this.computeRoomArea(vertices);

      this.upsertRoomBoundAreaFill(room, vertices, {
        autoCreated: !!boundFill.roomBinding?.autoCreated,
        existingId: boundFill.id,
      });
    });
  };

  /**
   * Extract the bounded interior faces (rooms) of the wall/line graph.
   *
   * Builds a planar graph where nodes are merged wall endpoints (junctions) and
   * each wall/line contributes two directed half-edges ("darts"). Faces are
   * traced with the standard rule: from a dart u→v, the next dart around the
   * face is the one immediately clockwise from the reverse dart (v→u) in the
   * angular order around v. With a screen-space (y-up) atan2 ordering this
   * traces bounded faces counter-clockwise (positive signed area) and the
   * single unbounded outer face clockwise (negative area), which we discard.
   *
   * Returns each interior face as an ordered, de-duplicated list of the
   * walls/lines that border it.
   */
  private computeInteriorWallFaces = (): FaceDart[][] => {
    const tolerance = 0.01; // junction merge tolerance (matches room detection)

    type FaceObject = WallType | SingleLineType;
    const edges: { object: FaceObject; a: THREE.Vector2; b: THREE.Vector2 }[] = [];
    this.wallsMap.forEach((wall) => {
      if (wall.start.distanceTo(wall.end) > 1e-6) {
        edges.push({ object: wall, a: wall.start, b: wall.end });
      }
    });
    this.singleLinesMap.forEach((line) => {
      if (line.start.distanceTo(line.end) > 1e-6) {
        edges.push({ object: line, a: line.start, b: line.end });
      }
    });
    if (edges.length < 3) {
      return [];
    }

    // Merge wall/line endpoints into shared junction nodes. Two endpoints are
    // merged when they are geometrically coincident (tolerance) OR when an
    // explicit endpoint-to-endpoint connection links them. The connection pass
    // is essential for edge ("corner to corner") connections: edge-snapped
    // walls touch along their offset edges, so their centerline endpoints do
    // not always coincide, yet they must still share a junction for the room
    // loop to close. (T-junctions that target a wall's "middle" are not
    // endpoint joins and are handled by geometric coincidence only.)
    //
    // Union-find over endpoint "slots": slot 2*i is edges[i].a, 2*i+1 is .b.
    const slotCount = edges.length * 2;
    const parent = new Array<number>(slotCount);
    for (let i = 0; i < slotCount; i++) parent[i] = i;
    const find = (x: number): number => {
      let root = x;
      while (parent[root] !== root) root = parent[root];
      while (parent[x] !== root) {
        const next = parent[x];
        parent[x] = root;
        x = next;
      }
      return root;
    };
    const union = (a: number, b: number): void => {
      const ra = find(a);
      const rb = find(b);
      if (ra !== rb) parent[ra] = rb;
    };

    const slotPos = (slot: number): THREE.Vector2 => (slot % 2 === 0 ? edges[slot >> 1].a : edges[slot >> 1].b);

    // 1. Geometric coincidence between any two endpoint slots.
    for (let i = 0; i < slotCount; i++) {
      for (let j = i + 1; j < slotCount; j++) {
        if (slotPos(i).distanceTo(slotPos(j)) <= tolerance) {
          union(i, j);
        }
      }
    }

    // 2. Explicit endpoint-to-endpoint connections (center and edge).
    const slotOf = new Map<string, number>(); // `${objectId}:start|end` -> slot
    edges.forEach((edge, i) => {
      slotOf.set(`${edge.object.id}:start`, i * 2);
      slotOf.set(`${edge.object.id}:end`, i * 2 + 1);
    });
    const positionToEndpoint = (position: number): 'start' | 'end' | null => (position === WallConnectionStart ? 'start' : position === WallConnectionEnd ? 'end' : null);
    for (const edge of edges) {
      if (!isWallType(edge.object)) continue;
      const sourceBase = `${edge.object.id}:`;
      if (edge.object.centerConnections) {
        for (const conn of edge.object.centerConnections) {
          const sourceEndpoint = positionToEndpoint(conn.sourcePosition);
          const targetEndpoint = positionToEndpoint(conn.targetPosition);
          if (!sourceEndpoint || !targetEndpoint) continue; // not an endpoint-to-endpoint join
          const a = slotOf.get(`${sourceBase}${sourceEndpoint}`);
          const b = slotOf.get(`${conn.id}:${targetEndpoint}`);
          if (a !== undefined && b !== undefined) union(a, b);
        }
      }
      if (edge.object.edgeConnections) {
        for (const conn of edge.object.edgeConnections) {
          if (conn.targetEndpoint !== 'start' && conn.targetEndpoint !== 'end') continue;
          const a = slotOf.get(`${sourceBase}${conn.sourceEndpoint}`);
          const b = slotOf.get(`${conn.targetWallId}:${conn.targetEndpoint}`);
          if (a !== undefined && b !== undefined) union(a, b);
        }
      }
    }

    // Materialise nodes from union-find roots; node position is the average of
    // its merged endpoint slots (centerline junction approximation). Node
    // positions are finalised before any dart angles are computed.
    const rootToNode = new Map<number, number>();
    const nodeAccum: { sum: THREE.Vector2; count: number }[] = [];
    const slotNode = new Array<number>(slotCount);
    for (let slot = 0; slot < slotCount; slot++) {
      const root = find(slot);
      let node = rootToNode.get(root);
      if (node === undefined) {
        node = nodeAccum.length;
        rootToNode.set(root, node);
        nodeAccum.push({ sum: new THREE.Vector2(), count: 0 });
      }
      slotNode[slot] = node;
      nodeAccum[node].sum.add(slotPos(slot));
      nodeAccum[node].count += 1;
    }
    const nodePositions: THREE.Vector2[] = nodeAccum.map((accum) => accum.sum.clone().divideScalar(accum.count));

    // T-junction splitting: when a wall endpoint connects to the *middle* of
    // another wall (a multi-joint / T-junction), the target wall must be split
    // at that point so the partition acts as a room divider. Without this the
    // partition endpoint is a dangling spur and both sides collapse into one
    // big room. Split points come from explicit connections (center
    // targetPosition === Middle, or edge targetEndpoint === 'middle'); the exact
    // split position is the source endpoint projected onto the target centerline.
    const edgeIndexOfObject = new Map<string, number>();
    edges.forEach((edge, i) => edgeIndexOfObject.set(edge.object.id, i));
    const edgeSplits: { t: number; node: number }[][] = edges.map(() => []);
    const projectSplitPoint = (targetEdgeIndex: number, sourcePoint: THREE.Vector2): { t: number; point: THREE.Vector2 } | null => {
      const edge = edges[targetEdgeIndex];
      if (isWallType(edge.object) && edge.object.lineForm === 'arc' && edge.object.controlPoint) {
        const projection = projectPointOntoArc(sourcePoint, edge.object.start, edge.object.controlPoint, edge.object.end);
        return {
          t: projection.t,
          point: projection.point,
        };
      }

      const ab = new THREE.Vector2().subVectors(edge.object.end, edge.object.start);
      const lenSq = ab.lengthSq();
      if (lenSq < 1e-12) return null;

      const unclampedT = new THREE.Vector2().subVectors(sourcePoint, edge.object.start).dot(ab) / lenSq;
      const t = Math.max(0, Math.min(1, unclampedT));
      return {
        t,
        point: edge.object.start.clone().add(ab.multiplyScalar(t)),
      };
    };
    const recordSplit = (targetEdgeIndex: number, sourceNode: number): void => {
      const na = slotNode[targetEdgeIndex * 2];
      const nb = slotNode[targetEdgeIndex * 2 + 1];
      if (sourceNode === na || sourceNode === nb) return; // already a shared endpoint
      const projection = projectSplitPoint(targetEdgeIndex, nodePositions[sourceNode]);
      if (!projection) return;
      const { t, point } = projection;
      if (t <= 1e-3 || t >= 1 - 1e-3) return; // projects at/beyond an endpoint
      if (edgeSplits[targetEdgeIndex].some((s) => s.node === sourceNode || Math.abs(s.t - t) < 1e-4)) return;
      nodePositions[sourceNode] = point;
      edgeSplits[targetEdgeIndex].push({ t, node: sourceNode });
    };
    for (let i = 0; i < edges.length; i++) {
      const edge = edges[i];
      if (!isWallType(edge.object)) continue;
      if (edge.object.centerConnections) {
        for (const conn of edge.object.centerConnections) {
          if (conn.targetPosition === WallConnectionMiddle) {
            // This wall's endpoint meets the middle of conn.id -> split conn.id.
            const sourceEndpoint = positionToEndpoint(conn.sourcePosition);
            const targetEdgeIndex = edgeIndexOfObject.get(conn.id);
            const sourceSlot = sourceEndpoint ? slotOf.get(`${edge.object.id}:${sourceEndpoint}`) : undefined;
            if (sourceEndpoint && targetEdgeIndex !== undefined && sourceSlot !== undefined) {
              recordSplit(targetEdgeIndex, slotNode[sourceSlot]);
            }
          } else if (conn.sourcePosition === WallConnectionMiddle) {
            // This wall's middle is met by conn.id's endpoint -> split this wall.
            const targetEndpoint = positionToEndpoint(conn.targetPosition);
            const targetSlot = targetEndpoint ? slotOf.get(`${conn.id}:${targetEndpoint}`) : undefined;
            if (targetEndpoint && targetSlot !== undefined) {
              recordSplit(i, slotNode[targetSlot]);
            }
          }
        }
      }
      if (edge.object.edgeConnections) {
        for (const conn of edge.object.edgeConnections) {
          if (conn.targetEndpoint !== 'middle') continue;
          const targetEdgeIndex = edgeIndexOfObject.get(conn.targetWallId);
          const sourceSlot = slotOf.get(`${edge.object.id}:${conn.sourceEndpoint}`);
          if (targetEdgeIndex === undefined || sourceSlot === undefined) continue;
          recordSplit(targetEdgeIndex, slotNode[sourceSlot]);
        }
      }
    }

    interface Dart {
      id: number;
      object: FaceObject;
      from: number;
      to: number;
      angle: number;
      twin: number;
      next: number;
      isFull: boolean; // false when the wall was split (this dart covers only part of it)
    }
    const darts: Dart[] = [];
    const outgoing = new Map<number, number[]>();
    const addOutgoing = (node: number, dartId: number) => {
      const list = outgoing.get(node);
      if (list) {
        list.push(dartId);
      } else {
        outgoing.set(node, [dartId]);
      }
    };
    const addSubEdge = (object: FaceObject, na: number, nb: number, isFull: boolean): void => {
      if (na === nb) return;
      const posA = nodePositions[na];
      const posB = nodePositions[nb];
      const id1 = darts.length;
      const id2 = darts.length + 1;
      darts.push({ id: id1, object, from: na, to: nb, angle: Math.atan2(posB.y - posA.y, posB.x - posA.x), twin: id2, next: -1, isFull });
      darts.push({ id: id2, object, from: nb, to: na, angle: Math.atan2(posA.y - posB.y, posA.x - posB.x), twin: id1, next: -1, isFull });
      addOutgoing(na, id1);
      addOutgoing(nb, id2);
    };

    edges.forEach((edge, i) => {
      const na = slotNode[i * 2];
      const nb = slotNode[i * 2 + 1];
      if (na === nb) {
        return; // collapsed to a point by the merge
      }
      const splits = edgeSplits[i];
      if (splits.length === 0) {
        addSubEdge(edge.object, na, nb, true);
        return;
      }
      // Walk from na to nb through the ordered split nodes.
      const ordered = splits.slice().sort((s1, s2) => s1.t - s2.t);
      let prev = na;
      for (const split of ordered) {
        addSubEdge(edge.object, prev, split.node, false);
        prev = split.node;
      }
      addSubEdge(edge.object, prev, nb, false);
    });

    // Angular order of darts leaving each node, plus each dart's slot index.
    const sortedOutgoing = new Map<number, number[]>();
    const slotInNode = new Map<number, number>();
    outgoing.forEach((dartIds, node) => {
      const sorted = dartIds.slice().sort((a, b) => darts[a].angle - darts[b].angle);
      sortedOutgoing.set(node, sorted);
      sorted.forEach((dartId, slot) => slotInNode.set(dartId, slot));
    });

    // next(u→v) = dart immediately clockwise from the reverse dart (v→u).
    for (const dart of darts) {
      const reverse = darts[dart.twin];
      const ring = sortedOutgoing.get(reverse.from)!;
      const slot = slotInNode.get(reverse.id)!;
      dart.next = ring[(slot - 1 + ring.length) % ring.length];
    }

    // Trace faces and keep the counter-clockwise (positive area) bounded ones.
    const visited = new Set<number>();
    const faces: FaceDart[][] = [];
    for (const startDart of darts) {
      if (visited.has(startDart.id)) {
        continue;
      }
      const faceDarts: Dart[] = [];
      let current = startDart;
      let guard = 0;
      while (!visited.has(current.id) && guard <= darts.length) {
        visited.add(current.id);
        faceDarts.push(current);
        current = darts[current.next];
        guard++;
      }

      let signedArea = 0;
      for (const dart of faceDarts) {
        const p = nodePositions[dart.from];
        const q = nodePositions[dart.to];
        signedArea += p.x * q.y - q.x * p.y;
      }
      signedArea /= 2;
      if (signedArea <= 1e-9) {
        continue; // outer (clockwise) face or degenerate spur
      }

      const distinctObjects = new Set<string>();
      const face: FaceDart[] = faceDarts.map((dart) => {
        distinctObjects.add(dart.object.id);
        return {
          object: dart.object,
          fromPos: nodePositions[dart.from].clone(),
          toPos: nodePositions[dart.to].clone(),
          isFull: dart.isFull,
        };
      });
      if (distinctObjects.size >= 3) {
        faces.push(face);
      }
    }

    return faces;
  };

  private sampleCenterlineArcBetweenPositions = (
    object: WallType | SingleLineType,
    fromPos: THREE.Vector2,
    segmentsCount: number = 16,
  ): THREE.Vector2[] => {
    if (!object.controlPoint) return [];

    const distToStart = fromPos.distanceTo(object.start);
    const distToEnd = fromPos.distanceTo(object.end);
    const forward = distToStart <= distToEnd;

    const samples: THREE.Vector2[] = [];
    for (let i = 1; i < segmentsCount; i++) {
      const tStep = i / segmentsCount;
      const t = forward ? tStep : 1 - tStep;
      samples.push(getQuadraticBezierPoint(t, object.start, object.controlPoint, object.end));
    }
    return samples;
  };

  private buildCenterlinePolygonForFace = (face: FaceDart[]): THREE.Vector2[] => {
    const vertices: THREE.Vector2[] = [];

    for (const dart of face) {
      if (dart.isFull && dart.object.lineForm === 'arc' && dart.object.controlPoint) {
        for (const sample of this.sampleCenterlineArcBetweenPositions(dart.object, dart.fromPos)) {
          vertices.push(sample);
        }
      }
      vertices.push(dart.toPos.clone());
    }

    if (vertices.length > 0 && !vertices[vertices.length - 1].equals(vertices[0])) {
      vertices.push(vertices[0].clone());
    }

    return vertices;
  };

  /**
   * Build the inner (room-facing) polygon for a single planar face.
   *
   * Each face dart is the centerline of a wall (or a sub-segment of a split
   * wall) traversed counter-clockwise, so the room interior is on its left.
   * We offset each dart inward by the wall's half-width, intersect consecutive
   * offset lines for mitred corners, and sample the inner arc of full-length
   * curved walls so the area reflects their bulge. Building from the face's own
   * sub-segments (rather than whole walls) is what makes T-junction partitions
   * yield correct per-room areas.
   */
  private buildInnerPolygonForFace = (face: FaceDart[]): THREE.Vector2[] => {
    const n = face.length;
    if (n < 3) return [];
    const EXTEND = 1000;

    const offsets = face.map((dart) => {
      const dartDir = new THREE.Vector2().subVectors(dart.toPos, dart.fromPos);
      if (dartDir.lengthSq() < 1e-12) return null;
      dartDir.normalize();

      // Build the inner-edge line from the wall's TRUE centerline (start->end)
      // rather than the node-averaged dart endpoints. Junction nodes are the
      // average of the merged wall endpoints, and for edge ("corner to corner")
      // connections those merged centerline endpoints do NOT coincide, so the
      // node-to-node direction (and its perpendicular) deviates slightly from
      // the wall as actually rendered. The renderer draws each wall's inner
      // edge as its centerline offset by half its width, so offsetting the real
      // centerline makes the room corners land exactly on the rendered inner
      // wall edges. Curved walls keep the chord (node-to-node) approximation
      // here because their endpoint perpendicular follows the arc tangent, not
      // the chord, and their bulge is captured separately by arc sampling.
      const obj = dart.object;
      const isArc = obj.lineForm === 'arc' && !!obj.controlPoint && dart.isFull;
      let lineDir = dartDir.clone();
      let linePoint = dart.fromPos.clone();
      if (!isArc) {
        const trueDir = new THREE.Vector2().subVectors(obj.end, obj.start);
        if (trueDir.lengthSq() >= 1e-12) {
          trueDir.normalize();
          // Orient the centerline along the dart traversal so its left normal
          // points into the room interior (faces are traced counter-clockwise).
          if (trueDir.dot(dartDir) < 0) trueDir.negate();
          lineDir = trueDir;
          linePoint = obj.start.clone();
        }
      }

      const innerPerp = new THREE.Vector2(-lineDir.y, lineDir.x); // left normal -> room interior (CCW face)
      const halfWidth = isWallType(obj) ? (obj.wallWidth ?? this.wallWidth) / 2 : 0;
      const offset = innerPerp.clone().multiplyScalar(halfWidth);
      const innerBase = linePoint.clone().add(offset);
      return {
        innerPerp,
        // Fallback corner (only used when adjacent inner lines are parallel and
        // do not intersect): the dart's end node pushed inward by the half-width.
        innerEnd: dart.toPos.clone().add(offset),
        // Extend along the wall so neighbouring offset lines reliably intersect.
        extStart: innerBase.clone().add(lineDir.clone().multiplyScalar(-EXTEND)),
        extEnd: innerBase.clone().add(lineDir.clone().multiplyScalar(EXTEND)),
      };
    });

    // corners[i] is the inner corner shared by dart i (outgoing) and dart i+1.
    const corners: THREE.Vector2[] = [];
    for (let i = 0; i < n; i++) {
      const cur = offsets[i];
      const next = offsets[(i + 1) % n];
      let corner: THREE.Vector2 | null = null;
      if (cur && next) {
        corner = this.getIntersection(cur.extStart, cur.extEnd, next.extStart, next.extEnd);
      }
      if (!corner) {
        corner = cur ? cur.innerEnd.clone() : face[i].toPos.clone();
      }
      corners.push(corner);
    }

    const vertices: THREE.Vector2[] = [];
    for (let i = 0; i < n; i++) {
      const dart = face[i];
      const prevCorner = corners[(i - 1 + n) % n];
      const endCorner = corners[i];
      const innerPerp = offsets[i]?.innerPerp;
      if (dart.isFull && innerPerp && isWallType(dart.object) && dart.object.lineForm === 'arc' && dart.object.controlPoint) {
        for (const sample of this.sampleInnerArcBetweenCorners(dart.object, prevCorner, endCorner, 16, innerPerp)) {
          vertices.push(sample);
        }
      } else if (dart.isFull && isSingleLineType(dart.object) && dart.object.lineForm === 'arc' && dart.object.controlPoint) {
        for (const sample of this.sampleSingleLineArcBetweenCorners(dart.object, prevCorner, endCorner)) {
          vertices.push(sample);
        }
      }
      vertices.push(endCorner);
    }

    if (vertices.length > 0 && !vertices[vertices.length - 1].equals(vertices[0])) {
      vertices.push(vertices[0].clone());
    }
    return vertices;
  };

  // Grid cell size for spatial indexing (10cm grid)
  private readonly ROOM_DETECTION_GRID_SIZE = 0.1;

  /**
   * Build spatial index for efficient room detection.
   * Creates a vertex grid index and a connection graph for O(1) lookups.
   * Only rebuilds the index when it's stale (version mismatch).
   */
  private buildRoomDetectionIndex = (): void => {
    // Check if index is already up to date
    if (this.lastRoomDetectionIndexVersion === this.roomDetectionIndexVersion) {
      return;
    }

    // Clear old indices
    this.roomDetectionVertexIndex.clear();
    this.roomDetectionConnectionGraph.clear();

    const getGridKey = (v: THREE.Vector2): string => {
      const gx = Math.floor(v.x / this.ROOM_DETECTION_GRID_SIZE);
      const gy = Math.floor(v.y / this.ROOM_DETECTION_GRID_SIZE);
      return `${gx},${gy}`;
    };

    // Build vertex spatial index and connection graph for walls
    this.wallsMap.forEach((wall, wallId) => {
      // Add to vertex grid
      const startKey = getGridKey(wall.start);
      const endKey = getGridKey(wall.end);

      if (!this.roomDetectionVertexIndex.has(startKey)) {
        this.roomDetectionVertexIndex.set(startKey, new Set());
      }
      this.roomDetectionVertexIndex.get(startKey)!.add(wallId);

      if (!this.roomDetectionVertexIndex.has(endKey)) {
        this.roomDetectionVertexIndex.set(endKey, new Set());
      }
      this.roomDetectionVertexIndex.get(endKey)!.add(wallId);

      // Build connection graph from edge and center connections
      if (!this.roomDetectionConnectionGraph.has(wallId)) {
        this.roomDetectionConnectionGraph.set(wallId, {
          start: new Set(),
          end: new Set(),
        });
      }
      const wallConnections = this.roomDetectionConnectionGraph.get(wallId)!;

      // Add edge connections
      if (wall.edgeConnections) {
        for (const conn of wall.edgeConnections) {
          if (conn.sourceEndpoint === 'start') {
            wallConnections.start.add(conn.targetWallId);
          } else if (conn.sourceEndpoint === 'end') {
            wallConnections.end.add(conn.targetWallId);
          }
        }
      }

      // Add center connections (endpoint-to-endpoint only)
      if (wall.centerConnections) {
        for (const conn of wall.centerConnections) {
          if (conn.sourcePosition === 0) {
            // WallConnectionStart
            wallConnections.start.add(conn.id);
          } else if (conn.sourcePosition === 10000) {
            // WallConnectionEnd
            wallConnections.end.add(conn.id);
          }
        }
      }
    });

    // Build vertex spatial index for single lines
    this.singleLinesMap.forEach((line, lineId) => {
      const startKey = getGridKey(line.start);
      const endKey = getGridKey(line.end);

      if (!this.roomDetectionVertexIndex.has(startKey)) {
        this.roomDetectionVertexIndex.set(startKey, new Set());
      }
      this.roomDetectionVertexIndex.get(startKey)!.add(lineId);

      if (!this.roomDetectionVertexIndex.has(endKey)) {
        this.roomDetectionVertexIndex.set(endKey, new Set());
      }
      this.roomDetectionVertexIndex.get(endKey)!.add(lineId);
    });

    this.lastRoomDetectionIndexVersion = this.roomDetectionIndexVersion;
  };

  /**
   * Invalidate the room detection spatial index.
   * Call this when walls are added, removed, or moved.
   */
  invalidateRoomDetectionIndex = (): void => {
    this.roomDetectionIndexVersion++;
  };

  // Find rooms starting from a wall
  findRoomsFromObject = (startObject: WallType | SingleLineType) => {
    const path: (WallType | SingleLineType)[] = [];
    const roomDetectionTolerance = 0.01;

    if (this.isObjectInFoundRooms(startObject.id)) {
      // This wall is already part of a found room, skip DFS
      return;
    }

    const visitedEdges = new Set<string>();

    // Helper to check if two walls are connected via edge connections
    const hasEdgeConnectionBetween = (fromWall: WallType | SingleLineType, toWall: WallType | SingleLineType, fromVertex: THREE.Vector2): boolean => {
      if (!isWallType(fromWall)) return false;
      if (!fromWall.edgeConnections) return false;

      const isFromStart = this.vectorsEqual(fromWall.start, fromVertex, roomDetectionTolerance);
      const isFromEnd = this.vectorsEqual(fromWall.end, fromVertex, roomDetectionTolerance);

      return fromWall.edgeConnections.some((conn) => {
        if (conn.targetWallId !== toWall.id) return false;
        if (isFromStart && conn.sourceEndpoint === 'start') return true;
        if (isFromEnd && conn.sourceEndpoint === 'end') return true;
        return false;
      });
    };

    // DFS (Depth First Search) to find rooms
    const dfs = (currentObject: WallType | SingleLineType, startVertex: THREE.Vector2, currentVertex: THREE.Vector2) => {
      path.push(currentObject);
      // Use a rounded edge key to handle floating-point precision issues
      const roundedX = Math.round(currentVertex.x * 100) / 100;
      const roundedY = Math.round(currentVertex.y * 100) / 100;
      const edgeKey = `${currentObject.id}-${roundedX},${roundedY}`;
      if (visitedEdges.has(edgeKey)) {
        path.pop();
        return;
      }
      visitedEdges.add(edgeKey);

      const nextWalls = this.getConnectedObjectsAtVertex(currentObject, currentVertex);

      // CRITICAL FIX: Also allow traversing to the OPPOSITE end of the current wall
      // This ensures we can find connections at both ends of a wall
      const oppositeVertex = this.getOppositeVertex(currentObject, currentVertex);

      // Always check the opposite end for connections (don't check visitedEdges here)
      // The path filtering will prevent invalid loops
      const wallsAtOpposite = this.getConnectedObjectsAtVertex(currentObject, oppositeVertex);
      if (wallsAtOpposite.length > 0) {
        // Add walls from the opposite end to our next walls to explore
        for (const wall of wallsAtOpposite) {
          if (!nextWalls.find((w) => w.id === wall.id)) {
            nextWalls.push(wall);
          }
        }
      }

      // CRITICAL: Filter out any walls that are already in the current path
      // BUT: Allow the starting wall through ONLY if we have enough walls for a valid room
      const filteredNextWalls = nextWalls.filter((wall) => {
        // If this is the starting wall, only allow it if we have at least 3 walls in the path
        // This prevents "short circuit" loops like start → wall1 → start
        if (wall.id === startObject.id) {
          return path.length >= 3;
        }
        // Otherwise, exclude walls already in the path
        const alreadyInPath = path.some((p) => p.id === wall.id);
        return !alreadyInPath;
      });

      for (const nextWall of filteredNextWalls) {
        const nextVertex = this.getOppositeVertex(nextWall, currentVertex);
        const nextRoundedX = Math.round(nextVertex.x * 100) / 100;
        const nextRoundedY = Math.round(nextVertex.y * 100) / 100;
        const nextEdgeKey = `${nextWall.id}-${nextRoundedX},${nextRoundedY}`;

        // Check if we've closed the loop - either via geometric vertex match or edge connection
        const closesLoopGeometrically = this.vectorsEqual(nextVertex, startVertex, roomDetectionTolerance);
        const closesLoopViaEdge = hasEdgeConnectionBetween(nextWall, startObject, nextVertex);
        const closesLoop = closesLoopGeometrically || closesLoopViaEdge;

        if (closesLoop && path.length >= 3) {
          // Found a cycle (room) - but ensure we don't add duplicate walls

          let roomObjects = [...path];

          // Only add nextWall if it's not already in the path (closing the loop doesn't mean adding it again)
          const nextWallAlreadyInPath = roomObjects.some((obj) => obj.id === nextWall.id);
          if (!nextWallAlreadyInPath) {
            roomObjects.push(nextWall);
          }

          // Deduplicate by wall ID to prevent the same wall from appearing multiple times
          const uniqueWalls = new Map<string, WallType | SingleLineType>();
          for (const obj of roomObjects) {
            if (!uniqueWalls.has(obj.id)) {
              uniqueWalls.set(obj.id, obj);
            }
          }
          roomObjects = Array.from(uniqueWalls.values());

          // Validate: We need at least 3 unique walls to form a valid room
          if (roomObjects.length < 3) {
            continue;
          }

          const roomObjectIds = roomObjects
            .map((w) => w.id)
            .sort()
            .join(',');
          if (!this.roomsFound.has(roomObjectIds)) {
            this.roomsFound.add(roomObjectIds);
            this.addObjectsToFoundRooms(roomObjects);
            this.createRoomFromPath(roomObjects);
          }
        } else {
          // Only recurse if we haven't visited this edge yet
          if (!visitedEdges.has(nextEdgeKey)) {
            dfs(nextWall, startVertex, nextVertex);
          }
        }
      }

      path.pop();
      // Do not delete from visitedEdges to prevent revisiting the same edge
    };

    // Start DFS from both vertices of the startWall
    dfs(startObject, startObject.start, startObject.end);
    dfs(startObject, startObject.end, startObject.start);
  };

  normalizeCycle = (vertices: THREE.Vector2[]): string => {
    // Create a list of vertex positions
    const positions = vertices.map((v) => `${v.x.toFixed(6)},${v.y.toFixed(6)}`);

    // Find the index of the vertex with the lowest x (and then y)
    let minIndex = 0;
    for (let i = 1; i < positions.length; i++) {
      if (positions[i] < positions[minIndex]) {
        minIndex = i;
      }
    }

    // Rotate the list so that it starts from the vertex with lowest x,y
    const rotatedPositions = positions.slice(minIndex).concat(positions.slice(0, minIndex));

    // Create two representations: one in the original order, one in reversed order
    const clockwise = rotatedPositions.join(';');
    const counterClockwise = rotatedPositions.slice().reverse().join(';');

    // Return the lexicographically smaller one
    return clockwise < counterClockwise ? clockwise : counterClockwise;
  };

  vectorsEqual = (v1: THREE.Vector2, v2: THREE.Vector2, tolerance = 1e-6): boolean => {
    return v1.distanceToSquared(v2) < tolerance * tolerance;
  };
  isObjectInFoundRooms = (objectId: string): boolean => {
    return this.objectIdsInFoundRooms.has(objectId);
  };

  addObjectsToFoundRooms = (objects: (WallType | SingleLineType)[]) => {
    objects.forEach((object) => this.objectIdsInFoundRooms.add(object.id));
  };

  private getRoomObjectSignature = (objectIds: string[]): string => {
    return objectIds
      .map((id) => id)
      .sort()
      .join(',');
  };

  private registerPersistedRoom = (room: RoomType): void => {
    this.roomsFound.add(this.getRoomObjectSignature(room.objectIds));

    room.objectIds.forEach((objectId) => {
      const object = this.findObjectId(objectId);
      if (object && (isWallType(object) || isSingleLineType(object))) {
        object.roomIds = object.roomIds ?? [];
        if (!object.roomIds.includes(room.id)) {
          object.roomIds.push(room.id);
        }
        this.setRoomObjectLink(objectId, room.id);
      }
    });
  };

  private discoverMissingRoomsFromLoadedGeometry = (): void => {
    // Recompute every face from the loaded geometry. recomputeAllRooms()
    // preserves persisted rooms' ids/metadata by signature, so loaded rooms
    // keep their identity while any faces missing from the saved data (or
    // stale faces produced by the old detector) are corrected.
    this.recomputeAllRooms();
  };

  // Helper method to get walls connected at a vertex
  // This considers both geometric vertex matching AND edge/center connections
  // Uses spatial indexing for O(1) lookups when available
  getConnectedObjectsAtVertex = (object: WallType | SingleLineType, vertex: THREE.Vector2): (WallType | SingleLineType)[] => {
    const connectedObjectsMap = new Map<string, WallType | SingleLineType>();
    // Use a more forgiving tolerance for room detection to handle floating-point precision issues
    const roomDetectionTolerance = 0.01;

    // Determine which endpoint of the current object matches the vertex
    const isStartVertex = this.vectorsEqual(object.start, vertex, roomDetectionTolerance);
    const isEndVertex = this.vectorsEqual(object.end, vertex, roomDetectionTolerance);

    if (isWallType(object)) {
      // Method 1: Use prebuilt connection graph (O(1) lookup)
      const wallConns = this.roomDetectionConnectionGraph.get(object.id);
      if (wallConns) {
        const connectedIds = isStartVertex ? wallConns.start : isEndVertex ? wallConns.end : new Set<string>();
        for (const connId of connectedIds) {
          const connectedWall = this.wallsMap.get(connId);
          if (connectedWall && connectedWall.id !== object.id) {
            connectedObjectsMap.set(connectedWall.id, connectedWall);
          }
        }
      }

      // Method 2: Fallback to checking edge connections directly if graph not built
      if (!wallConns && object.edgeConnections) {
        for (const edgeConn of object.edgeConnections) {
          // Only consider connections from the endpoint we're examining
          if ((isStartVertex && edgeConn.sourceEndpoint === 'start') || (isEndVertex && edgeConn.sourceEndpoint === 'end')) {
            const connectedWall = this.wallsMap.get(edgeConn.targetWallId);
            if (connectedWall && connectedWall.id !== object.id) {
              connectedObjectsMap.set(connectedWall.id, connectedWall);
            }
          }
        }
      }

      // Method 3: Fallback to center connections if graph not built
      if (!wallConns && object.centerConnections) {
        for (const centerConn of object.centerConnections) {
          // Check if this connection is from the current vertex
          const isFromStart = centerConn.sourcePosition === 0; // WallConnectionStart
          const isFromEnd = centerConn.sourcePosition === 10000; // WallConnectionEnd
          if ((isStartVertex && isFromStart) || (isEndVertex && isFromEnd)) {
            const connectedWall = this.wallsMap.get(centerConn.id);
            if (connectedWall && connectedWall.id !== object.id) {
              connectedObjectsMap.set(connectedWall.id, connectedWall);
            }
          }
        }
      }

      // Method 4: Use spatial index for geometric vertex matching (O(1) grid lookup)
      const getGridKey = (v: THREE.Vector2): string => {
        const gx = Math.floor(v.x / this.ROOM_DETECTION_GRID_SIZE);
        const gy = Math.floor(v.y / this.ROOM_DETECTION_GRID_SIZE);
        return `${gx},${gy}`;
      };

      // Check the grid cell for the vertex and adjacent cells
      const gridKey = getGridKey(vertex);
      const gx = Math.floor(vertex.x / this.ROOM_DETECTION_GRID_SIZE);
      const gy = Math.floor(vertex.y / this.ROOM_DETECTION_GRID_SIZE);

      // Check 3x3 grid of cells around the vertex to handle edge cases
      for (let dx = -1; dx <= 1; dx++) {
        for (let dy = -1; dy <= 1; dy++) {
          const cellKey = `${gx + dx},${gy + dy}`;
          const wallsInCell = this.roomDetectionVertexIndex.get(cellKey);
          if (wallsInCell) {
            for (const wallId of wallsInCell) {
              if (wallId === object.id) continue;
              if (connectedObjectsMap.has(wallId)) continue;
              const otherWall = this.wallsMap.get(wallId);
              if (otherWall && (this.vectorsEqual(otherWall.start, vertex, roomDetectionTolerance) || this.vectorsEqual(otherWall.end, vertex, roomDetectionTolerance))) {
                connectedObjectsMap.set(otherWall.id, otherWall);
              }
            }
          }
        }
      }
    } else if (isSingleLineType(object)) {
      // For single lines, use spatial index for geometric matching
      const getGridKey = (v: THREE.Vector2): string => {
        const gx = Math.floor(v.x / this.ROOM_DETECTION_GRID_SIZE);
        const gy = Math.floor(v.y / this.ROOM_DETECTION_GRID_SIZE);
        return `${gx},${gy}`;
      };

      const gx = Math.floor(vertex.x / this.ROOM_DETECTION_GRID_SIZE);
      const gy = Math.floor(vertex.y / this.ROOM_DETECTION_GRID_SIZE);

      // Check 3x3 grid of cells around the vertex
      for (let dx = -1; dx <= 1; dx++) {
        for (let dy = -1; dy <= 1; dy++) {
          const cellKey = `${gx + dx},${gy + dy}`;
          const objectsInCell = this.roomDetectionVertexIndex.get(cellKey);
          if (objectsInCell) {
            for (const objId of objectsInCell) {
              if (objId === object.id) continue;
              if (connectedObjectsMap.has(objId)) continue;
              const otherLine = this.singleLinesMap.get(objId);
              if (otherLine && (this.vectorsEqual(otherLine.start, vertex, roomDetectionTolerance) || this.vectorsEqual(otherLine.end, vertex, roomDetectionTolerance))) {
                connectedObjectsMap.set(otherLine.id, otherLine);
              }
            }
          }
        }
      }
    }

    return Array.from(connectedObjectsMap.values());
  };

  // Helper method to get the opposite vertex of a wall given one vertex
  getOppositeVertex = (object: WallType | SingleLineType, vertex: THREE.Vector2): THREE.Vector2 => {
    const roomDetectionTolerance = 0.01;
    if (this.vectorsEqual(object.start, vertex, roomDetectionTolerance)) {
      return object.end;
    } else {
      return object.start;
    }
  };

  // Method to create a room from a path of objects.
  // When `preserved` is supplied (a previously detected room with the same
  // object signature) its id and user-set metadata are carried over so a full
  // recompute does not regenerate ids/names for unchanged rooms.
  createRoomFromPath = (objects: (WallType | SingleLineType)[], preserved?: RoomType, polygonOverride?: THREE.Vector2[]) => {
    const roomId = preserved?.id ?? Math.random().toString(36).substr(2, 9);

    // Check for duplicate wall IDs (should have been filtered by DFS, but double-check)
    const uniqueIds = new Set(objects.map((o) => o.id));
    if (uniqueIds.size !== objects.length) {
      return; // Don't create room with duplicate walls
    }

    // The room polygon. Planar-face detection supplies it directly (correct for
    // split T-junction walls); otherwise derive it from the ordered walls.
    const roomVertices = polygonOverride ?? this.getRoomVertices(objects, 'inner');

    // Validate: Check if vertices are degenerate (all collinear or on same line)
    if (roomVertices.length >= 3) {
      const uniquePositions = new Map<string, THREE.Vector2>();
      for (const v of roomVertices) {
        const key = `${v.x.toFixed(6)},${v.y.toFixed(6)}`;
        uniquePositions.set(key, v);
      }

      if (uniquePositions.size < 3) {
        return; // Don't create room with degenerate polygon
      }

      // Check if all vertices are on the same line (collinear)
      const firstVertex = roomVertices[0];
      const secondVertex = roomVertices[1];
      let allCollinear = true;

      if (roomVertices.length > 2) {
        const line = new THREE.Vector2().subVectors(secondVertex, firstVertex);
        const lineLength = line.length();

        if (lineLength > 1e-10) {
          line.normalize();

          for (let i = 2; i < roomVertices.length - 1; i++) {
            // Skip last vertex (should be same as first)
            const toVertex = new THREE.Vector2().subVectors(roomVertices[i], firstVertex);
            const projection = toVertex.dot(line);
            const perpDist = toVertex.clone().sub(line.clone().multiplyScalar(projection)).length();

            if (perpDist > 0.001) {
              // If any vertex is off the line by more than 1mm
              allCollinear = false;
              break;
            }
          }

          if (allCollinear) {
            return; // Don't create room with collinear vertices
          }
        }
      }
    }

    // Compute the centroid of the polygon
    const centroid = this.computeRoomCentroid(roomVertices);

    // Compute the area of the polygon
    const areaWorldUnits = this.computeRoomArea(roomVertices);

    if (areaWorldUnits < 1e-10) {
      return; // Don't create zero-area rooms
    }

    const objectIds = objects.map((object) => object.id);

    const room: RoomType = {
      id: roomId,
      type: 'room',
      objectIds: objectIds,
      vertices: roomVertices.map((vertex) => vertex.clone()),
      centroid: centroid,
      areaWorldUnits: areaWorldUnits,
    };

    // Carry over user-set metadata from the matching previous room, if any.
    if (preserved) {
      if (preserved.name !== undefined) room.name = preserved.name;
      if (preserved.selectable !== undefined) room.selectable = preserved.selectable;
      if (preserved.selected !== undefined) room.selected = preserved.selected;
      if (preserved.lineColor !== undefined) room.lineColor = preserved.lineColor;
      if (preserved.fillColor !== undefined) room.fillColor = preserved.fillColor;
      if (preserved.hoverColor !== undefined) room.hoverColor = preserved.hoverColor;
    }

    this.roomsMap.set(roomId, room);

    // Update object.roomIds
    objects.forEach((object) => {
      const w = this.findObjectId(object.id);
      if (w && (isWallType(w) || isSingleLineType(w))) {
        if (!w.roomIds) w.roomIds = [];
        if (!w.roomIds.includes(roomId)) w.roomIds.push(roomId);
        this.setRoomObjectLink(w.id, roomId);
      }
    });

    this.setDirty();
  };

  getObjectsOfRoom = (room: RoomType): (WallType | SingleLineType)[] => {
    if (!room.objectIds || room.objectIds.length === 0) return [];
    const objects: (WallType | SingleLineType)[] = [];
    room.objectIds.forEach((objectId) => {
      const object = this.findObjectId(objectId);
      if (object && (isWallType(object) || isSingleLineType(object))) {
        objects.push(object);
      }
    });
    return objects;
  };

  /**
   * Analytically compute the inner (room-facing) corner at a wall's endpoint.
   * Used by the room-vertex builder so the polygon reflects the true curved
   * geometry at a junction rather than the discretised fillShape corner, which
   * can be mislabelled for arc walls and is also decoupled from runtime
   * control-point changes.
   *
   * For arc walls the offset direction is perpendicular to the Bezier tangent
   * at the endpoint and sign-aligned with the inner perpendicular.
   * For straight walls the inner perpendicular is used directly.
   * SingleLines have no thickness and return the endpoint unchanged.
   *
   * `innerPerpOverride` lets the caller supply the room-interior direction
   * derived from the polygon's winding. This is required for partition walls
   * shared by two rooms, where the connection-based `objectPerpendicularInner`
   * is ambiguous (it returns a single side regardless of which room is being
   * built). When omitted it falls back to `objectPerpendicularInner`.
   */
  private computeInnerCornerAtEndpoint = (object: WallType | SingleLineType, endpoint: 'start' | 'end', innerPerpOverride?: THREE.Vector2): THREE.Vector2 => {
    const point = endpoint === 'start' ? object.start : object.end;
    if (!isWallType(object)) {
      return point.clone();
    }

    const innerPerp = innerPerpOverride ?? this.objectPerpendicularInner(object);
    const halfWidth = (object.wallWidth ?? this.wallWidth) / 2;

    let offsetDir: THREE.Vector2;
    if (object.lineForm === 'arc' && object.controlPoint) {
      const t = endpoint === 'start' ? 0 : 1;
      const tangent = getQuadraticBezierTangent(t, object.start, object.controlPoint, object.end);
      if (tangent.lengthSq() > 1e-12) {
        tangent.normalize();
        const perp = new THREE.Vector2(-tangent.y, tangent.x);
        const sign = perp.dot(innerPerp) >= 0 ? 1 : -1;
        offsetDir = perp.multiplyScalar(sign);
      } else {
        offsetDir = innerPerp.clone();
      }
    } else {
      offsetDir = innerPerp.clone();
    }

    return new THREE.Vector2(point.x + offsetDir.x * halfWidth, point.y + offsetDir.y * halfWidth);
  };

  /**
   * For the given wall/line at index `i`, determine which of its endpoints
   * connects to the neighbouring object at index `i + 1` (mod N), based on
   * geometric proximity. Returns "end" if the wall's end is closer to the
   * neighbour, or "start" otherwise.
   */
  private determineOutgoingEndpoint = (object: WallType | SingleLineType, nextObject: WallType | SingleLineType): 'start' | 'end' => {
    const endDist = Math.min(object.end.distanceTo(nextObject.start), object.end.distanceTo(nextObject.end));
    const startDist = Math.min(object.start.distanceTo(nextObject.start), object.start.distanceTo(nextObject.end));
    return endDist <= startDist ? 'end' : 'start';
  };

  /**
   * Samples interior points along an arc wall's room-facing (inner) edge
   * between the two junction corners at its endpoints.
   *
   * The returned list excludes the junction corners themselves - those are
   * already placed into the polygon by the room-vertex builder - and is
   * ordered so traversal proceeds from `prevCorner` toward `endCorner`
   * along the wall's inner edge, regardless of whether `prevCorner`
   * corresponds to the wall's `start` or `end`.
   */
  private sampleInnerArcBetweenCorners = (wall: WallType, prevCorner: THREE.Vector2, endCorner: THREE.Vector2, segmentsCount: number = 16, innerPerpOverride?: THREE.Vector2): THREE.Vector2[] => {
    if (!wall.controlPoint) return [];

    // Determine traversal direction: if prevCorner is closer to wall.start,
    // we walk t: 0 -> 1; otherwise we walk t: 1 -> 0. This maps the wall's
    // intrinsic parameterisation onto the polygon's traversal order.
    const distToStart = prevCorner.distanceTo(wall.start);
    const distToEnd = prevCorner.distanceTo(wall.end);
    const forward = distToStart <= distToEnd;

    // Prefer the winding-derived interior direction (see computeInnerCornerAtEndpoint).
    const innerPerp = innerPerpOverride ?? this.objectPerpendicularInner(wall);
    const halfWidth = (wall.wallWidth ?? this.wallWidth) / 2;

    const samples: THREE.Vector2[] = [];
    for (let i = 1; i < segmentsCount; i++) {
      const tStep = i / segmentsCount;
      const t = forward ? tStep : 1 - tStep;
      const pointOnCurve = getQuadraticBezierPoint(t, wall.start, wall.controlPoint, wall.end);
      const tangent = getQuadraticBezierTangent(t, wall.start, wall.controlPoint, wall.end);
      if (tangent.lengthSq() < 1e-12) continue;
      tangent.normalize();
      const perp = new THREE.Vector2(-tangent.y, tangent.x);
      // Align with room-interior direction using the chord-based innerPerp
      // so the offset always produces the room-facing edge of the wall.
      const sign = perp.dot(innerPerp) >= 0 ? 1 : -1;
      samples.push(new THREE.Vector2(pointOnCurve.x + perp.x * sign * halfWidth, pointOnCurve.y + perp.y * sign * halfWidth));
    }

    // Final safety clamp: ensure the last sample doesn't land too far from
    // endCorner. Small numerical mismatches vs the fillShape's discretised
    // corner are expected and harmless for area integration.
    if (samples.length > 0 && samples[samples.length - 1].distanceTo(endCorner) > 1e3) {
      return [];
    }

    return samples;
  };

  /**
   * Samples interior points along a curved SingleLine between the two
   * junction corners at its endpoints. SingleLines have no thickness, so
   * we sample the centerline directly.
   */
  private sampleSingleLineArcBetweenCorners = (line: SingleLineType, prevCorner: THREE.Vector2, _endCorner: THREE.Vector2, segmentsCount: number = 16): THREE.Vector2[] => {
    if (!line.controlPoint) return [];

    const distToStart = prevCorner.distanceTo(line.start);
    const distToEnd = prevCorner.distanceTo(line.end);
    const forward = distToStart <= distToEnd;

    const samples: THREE.Vector2[] = [];
    for (let i = 1; i < segmentsCount; i++) {
      const tStep = i / segmentsCount;
      const t = forward ? tStep : 1 - tStep;
      samples.push(getQuadraticBezierPoint(t, line.start, line.controlPoint, line.end));
    }
    return samples;
  };

  getRoomVertices = (objects: (WallType | SingleLineType)[], side: 'inner' | 'outer' | 'center' = 'center'): THREE.Vector2[] => {
    const vertices: THREE.Vector2[] = [];
    if (objects.length === 0) return vertices;

    // For "inner" we build the polygon analytically from each wall's own
    // geometry. This keeps the room polygon in sync with the wall's runtime
    // state (including the current `controlPoint` for arc walls) rather than
    // relying on the fillShape's discretised corner descriptors, which are
    // mislabelled for arc walls and stale when geometry has not been
    // recomposed. Curved walls additionally contribute intermediate arc
    // samples so the shoelace area reflects the bulge.
    if (side === 'inner' && objects.length >= 2) {
      // First pass: for each wall determine which endpoint connects to the
      // next wall in the sequence. outgoingEndpoints[i] is the endpoint of
      // objects[i] shared with objects[i + 1]; its position is a centerline
      // junction of the room polygon.
      const outgoingEndpoints: ('start' | 'end')[] = [];
      const centerlineJunctions: THREE.Vector2[] = [];
      for (let i = 0; i < objects.length; i++) {
        const currentObject = objects[i];
        const nextObject = objects[(i + 1) % objects.length];
        const outgoing = this.determineOutgoingEndpoint(currentObject, nextObject);
        outgoingEndpoints.push(outgoing);
        centerlineJunctions.push((outgoing === 'end' ? currentObject.end : currentObject.start).clone());
      }

      // Determine the polygon winding from the centerline junctions so the
      // room-interior offset direction can be derived geometrically rather
      // than from per-wall connections. This is what makes a partition wall
      // shared by two rooms offset toward the correct room on each side.
      let signedArea = 0;
      for (let i = 0; i < centerlineJunctions.length; i++) {
        const p = centerlineJunctions[i];
        const q = centerlineJunctions[(i + 1) % centerlineJunctions.length];
        signedArea += p.x * q.y - q.x * p.y;
      }
      const windingSign = signedArea >= 0 ? 1 : -1;

      // The room-interior perpendicular for objects[i] points to the inside
      // of the polygon: the left normal of the traversal direction for a
      // counter-clockwise polygon, flipped for a clockwise one.
      const innerPerpFor = (i: number): THREE.Vector2 => {
        const object = objects[i];
        const outgoingPos = outgoingEndpoints[i] === 'end' ? object.end : object.start;
        const incomingPos = outgoingEndpoints[i] === 'end' ? object.start : object.end;
        const dir = new THREE.Vector2().subVectors(outgoingPos, incomingPos);
        if (dir.lengthSq() < 1e-12) {
          return this.objectPerpendicularInner(object);
        }
        dir.normalize();
        return new THREE.Vector2(-dir.y * windingSign, dir.x * windingSign);
      };

      // junctionCorners[i] is the inner corner shared between objects[i]
      // (outgoing endpoint) and objects[i + 1] (incoming endpoint).
      const junctionCorners: THREE.Vector2[] = [];
      for (let i = 0; i < objects.length; i++) {
        junctionCorners.push(this.computeInnerCornerAtEndpoint(objects[i], outgoingEndpoints[i], innerPerpFor(i)));
      }

      // Second pass: walk each wall. The inner edge of objects[i] spans from
      // junctionCorners[i - 1] (its incoming/start-junction) to
      // junctionCorners[i] (its outgoing/end-junction). For curved walls we
      // sample intermediate points along the inner arc so the polygon area
      // reflects the curvature.
      for (let i = 0; i < objects.length; i++) {
        const currentObject = objects[i];
        const prevCorner = junctionCorners[(i - 1 + objects.length) % objects.length];
        const endCorner = junctionCorners[i];

        if (isWallType(currentObject) && currentObject.lineForm === 'arc' && currentObject.controlPoint) {
          const arcSamples = this.sampleInnerArcBetweenCorners(currentObject, prevCorner, endCorner, 16, innerPerpFor(i));
          for (const sample of arcSamples) {
            vertices.push(sample);
          }
        } else if (isSingleLineType(currentObject) && currentObject.lineForm === 'arc' && currentObject.controlPoint) {
          const arcSamples = this.sampleSingleLineArcBetweenCorners(currentObject, prevCorner, endCorner);
          for (const sample of arcSamples) {
            vertices.push(sample);
          }
        }

        vertices.push(endCorner);
      }

      if (vertices.length > 0 && !vertices[vertices.length - 1].equals(vertices[0])) {
        vertices.push(vertices[0].clone());
      }

      return vertices;
    }

    // For "center" and "outer", use the existing offset-based logic
    const offsetLines: {
      start: THREE.Vector2;
      end: THREE.Vector2;
      objectId: string;
      wallLength: number;
    }[] = [];

    // Step 1: Create offset lines for each wall
    for (let i = 0; i < objects.length; i++) {
      const currentObject = objects[i];

      // Calculate wall length for debugging
      const wallLength = currentObject.start.distanceTo(currentObject.end);

      // Guard against zero-length walls
      if (wallLength < 1e-6) {
        continue; // Skip this wall
      }

      const fullWallWidth = isWallType(currentObject) ? currentObject?.wallWidth || this.wallWidth : 0;
      const halfWallWidth = fullWallWidth / 2;

      // Determine offset based on side
      // For "inner" (fallback), offset inward by half wall width
      // For "outer", offset outward by half wall width
      // For "center", no offset
      let offset = 0;
      if (side === 'center') {
        offset = 0;
      } else if (side === 'inner') {
        offset = halfWallWidth;
      } else {
        // outer
        offset = -halfWallWidth;
      }

      // Get direction vector of the wall
      const wallDir = new THREE.Vector2().subVectors(currentObject.end, currentObject.start);
      const wallDirLength = wallDir.length();

      // Guard against invalid normalization
      if (wallDirLength < 1e-10) {
        continue;
      }

      wallDir.normalize();

      // Compute perpendicular vector towards the "side" of the room
      let perpendicular;
      if (side === 'center') {
        perpendicular = new THREE.Vector2(-wallDir.y, wallDir.x);
      } else if (side === 'inner') {
        perpendicular = this.objectPerpendicularInner(currentObject);
      } else {
        // outer
        perpendicular = this.objectPerpendicularOuter(currentObject);
      }

      // Validate perpendicular vector
      const perpLength = perpendicular.length();
      if (perpLength < 1e-10 || isNaN(perpLength)) {
        console.warn(`⚠️ Room Detection: Wall/Line ${currentObject.id} has invalid perpendicular vector (length: ${perpLength}). Using default perpendicular.`);
        perpendicular = new THREE.Vector2(-wallDir.y, wallDir.x);
      } else if (Math.abs(perpLength - 1.0) > 0.01) {
        // Normalize if not already normalized
        perpendicular.normalize();
      }

      // Create the offset line by shifting the wall inwards by half the wall width
      const startOffset = currentObject.start.clone().add(perpendicular.clone().multiplyScalar(offset));
      const endOffset = currentObject.end.clone().add(perpendicular.clone().multiplyScalar(offset));

      // Extend the lines to ensure they intersect
      const extendBy = 1000;
      startOffset.add(wallDir.clone().multiplyScalar(extendBy));
      endOffset.add(wallDir.clone().multiplyScalar(-extendBy));

      // Store the offset line
      offsetLines.push({
        start: startOffset,
        end: endOffset,
        objectId: currentObject.id,
        wallLength: wallLength,
      });
    }

    // Guard against insufficient walls
    if (offsetLines.length < 3) {
      console.warn(`⚠️ Room Detection: Only ${offsetLines.length} valid walls found (need at least 3). Room cannot be formed.`);
      return vertices;
    }

    // Step 2: Find intersection points between adjacent offset lines
    for (let i = 0; i < offsetLines.length; i++) {
      const currentLine = offsetLines[i];
      const nextLine = offsetLines[(i + 1) % offsetLines.length]; // Ensure we wrap around to close the polygon

      const intersectionPoint = this.getIntersection(currentLine.start, currentLine.end, nextLine.start, nextLine.end);

      if (intersectionPoint) {
        vertices.push(intersectionPoint);
      } else {
        // Fallback: Use the ORIGINAL wall endpoints (not the extended ones!)
        // Find the actual wall objects to get their real endpoints
        const currentWallObj = objects[i];
        const nextWallObj = objects[(i + 1) % objects.length];

        // For adjacent walls in a room, we want to connect:
        // currentWall.end to nextWall.start (in most cases)
        // But check which endpoints are actually closer
        const currentEnd = currentWallObj.end;
        const nextStart = nextWallObj.start;
        const currentStart = currentWallObj.start;
        const nextEnd = nextWallObj.end;

        // Find the two closest endpoints between the walls
        const dist1 = currentEnd.distanceTo(nextStart);
        const dist2 = currentEnd.distanceTo(nextEnd);
        const dist3 = currentStart.distanceTo(nextStart);
        const dist4 = currentStart.distanceTo(nextEnd);

        const minDist = Math.min(dist1, dist2, dist3, dist4);
        let fallbackPoint: THREE.Vector2;

        if (minDist === dist1) {
          fallbackPoint = new THREE.Vector2((currentEnd.x + nextStart.x) / 2, (currentEnd.y + nextStart.y) / 2);
        } else if (minDist === dist2) {
          fallbackPoint = new THREE.Vector2((currentEnd.x + nextEnd.x) / 2, (currentEnd.y + nextEnd.y) / 2);
        } else if (minDist === dist3) {
          fallbackPoint = new THREE.Vector2((currentStart.x + nextStart.x) / 2, (currentStart.y + nextStart.y) / 2);
        } else {
          fallbackPoint = new THREE.Vector2((currentStart.x + nextEnd.x) / 2, (currentStart.y + nextEnd.y) / 2);
        }

        // Validate the fallback point is reasonable (within 10 units of the walls)
        if (Math.abs(fallbackPoint.x) > 1000 || Math.abs(fallbackPoint.y) > 1000) {
          fallbackPoint = new THREE.Vector2(0, 0);
        }

        vertices.push(fallbackPoint);
      }
    }

    // Ensure the polygon is closed
    if (vertices.length > 1 && !vertices[vertices.length - 1].equals(vertices[0])) {
      vertices.push(vertices[0].clone());
    }

    return vertices;
  };

  // Helper function to calculate the intersection of two lines
  // Note: This function is used for extended lines (lines extended by 1000 units)
  // so we don't check if the intersection is within the original segment bounds
  getIntersection = (line1Start: THREE.Vector2, line1End: THREE.Vector2, line2Start: THREE.Vector2, line2End: THREE.Vector2): THREE.Vector2 | null => {
    const denominator = (line2End.y - line2Start.y) * (line1End.x - line1Start.x) - (line2End.x - line2Start.x) * (line1End.y - line1Start.y);

    if (Math.abs(denominator) < 1e-6) {
      // Lines are parallel or coincident - this is expected for collinear walls
      return null;
    }

    const a = line1Start.y - line2Start.y;
    const b = line1Start.x - line2Start.x;
    const numerator1 = (line2End.x - line2Start.x) * a - (line2End.y - line2Start.y) * b;

    const r = numerator1 / denominator;

    // Calculate intersection point
    // Since we're using extended lines, we don't check r bounds
    const intersectionX = line1Start.x + r * (line1End.x - line1Start.x);
    const intersectionY = line1Start.y + r * (line1End.y - line1Start.y);

    // Validate the result
    if (isNaN(intersectionX) || isNaN(intersectionY) || !isFinite(intersectionX) || !isFinite(intersectionY)) {
      return null;
    }

    // Additional check: If intersection is very far from the input lines,
    // the lines are effectively parallel
    const maxDist = 100; // Maximum reasonable distance from wall area
    if (Math.abs(intersectionX) > maxDist || Math.abs(intersectionY) > maxDist) {
      return null;
    }

    return new THREE.Vector2(intersectionX, intersectionY);
  };

  computeRoomCentroid = (vertices: THREE.Vector2[]): THREE.Vector2 => {
    // Validate input
    if (vertices.length < 3) {
      console.warn(`⚠️ Room Centroid: Insufficient vertices (${vertices.length}). Need at least 3. Returning (0, 0).`);
      return new THREE.Vector2(0, 0);
    }

    let area = 0;
    let Cx = 0;
    let Cy = 0;
    const N = vertices.length;

    for (let i = 0; i < N - 1; i++) {
      const current = vertices[i];
      const next = vertices[i + 1];

      // Validate vertices
      if (isNaN(current.x) || isNaN(current.y) || isNaN(next.x) || isNaN(next.y)) {
        console.warn(`⚠️ Room Centroid: NaN detected in vertex ${i} or ${i + 1}. Skipping this edge.`);
        continue;
      }

      const cross = current.x * next.y - next.x * current.y;
      area += cross;
      Cx += (current.x + next.x) * cross;
      Cy += (current.y + next.y) * cross;
    }

    area = area / 2;

    if (Math.abs(area) < 1e-10) {
      // Avoid division by zero - this is expected during drag operations when walls are being moved
      return new THREE.Vector2(0, 0);
    }

    Cx = Cx / (6 * area);
    Cy = Cy / (6 * area);

    // Validate result
    if (isNaN(Cx) || isNaN(Cy)) {
      console.warn(`⚠️ Room Centroid: Result contains NaN (${Cx}, ${Cy}). Returning (0, 0).`);
      return new THREE.Vector2(0, 0);
    }

    return new THREE.Vector2(Cx, Cy);
  };

  computeRoomArea = (vertices: THREE.Vector2[]): number => {
    // Validate input
    if (vertices.length < 3) {
      console.warn(`⚠️ Room Area: Insufficient vertices (${vertices.length}). Need at least 3. Returning 0.`);
      return 0;
    }

    let area = 0;
    // Compute the area of the polygon
    const N = vertices.length;

    for (let i = 0; i < N - 1; i++) {
      const current = vertices[i];
      const next = vertices[i + 1];

      // Validate vertices
      if (isNaN(current.x) || isNaN(current.y) || isNaN(next.x) || isNaN(next.y)) {
        console.warn(`⚠️ Room Area: NaN detected in vertex ${i} or ${i + 1}. Skipping this edge.`);
        continue;
      }

      area += current.x * next.y - next.x * current.y;
    }

    area = Math.abs(area) / 2;

    // Near-zero area is expected during drag operations when walls are being moved
    // Suppress logging to avoid console spam

    return area;
  };

  updateRoomProperties = (room: RoomType) => {
    const objects = this.getObjectsOfRoom(room);
    if (objects.length < 3) {
      // Not enough objects to form a valid room
      this.roomsMap.delete(room.id);
      return;
    }

    const roomVertices = this.getRoomFillVertices(room, { forceCurrent: true });
    if (roomVertices.length < 3) {
      this.roomsMap.delete(room.id);
      return;
    }
    room.vertices = roomVertices.map((vertex) => vertex.clone());
    room.centroid = this.computeRoomCentroid(roomVertices);
    room.areaWorldUnits = this.computeRoomArea(roomVertices);
  };

  /**
   * Snaps a wall to other walls or objects.
   *
   * INTELLIGENT SNAPPING:
   * - For endpoint connections: Uses edge-based snapping that preserves wall lengths
   * - For mid-point connections: Uses center-based snapping
   * - The system automatically chooses the appropriate connection type based on snap location
   */
  snapToOtherObjects = (source: string, snappingEnd: number, edge: THREE.Vector2, updateOtherEnd: boolean = true, options: { allowDuringDeferred?: boolean; forceCenterMode?: boolean } = {}): SnappingResult => {
    const result = snapToOtherObjects(source, snappingEnd, edge, updateOtherEnd, options);

    // Normalize the return type to always be SnappingResult
    if (typeof result === 'boolean') {
      return { snapped: result };
    }
    return result;
  };

  snapToAlignmentLines = (source: string, snappingEnd: number, edge: THREE.Vector2): AlignmentSnapResult => {
    return snapToAlignmentLines(source, snappingEnd, edge);
  };

  previewAlignmentSnap = (source: string, snappingEnd: number, edge: THREE.Vector2, overrides?: { start: THREE.Vector2; end: THREE.Vector2 }): AlignmentSnapResult => {
    const object = this.findObjectId(source);
    if (!object) {
      const fallback = new THREE.Vector2(edge.x, edge.y);
      return {
        snapped: false,
        snappedX: false,
        snappedY: false,
        start: fallback.clone(),
        end: fallback.clone(),
      };
    }

    const dragState = this.wallDragState.get(source);
    const previousKey = snappingEnd === WallConnectionStart ? 'start' : 'end';
    const previous = dragState?.alignmentSnap?.[previousKey];

    const result = calculateAlignmentSnap(object, snappingEnd, edge, {
      overrides,
      previous,
    });

    if (dragState) {
      if (!dragState.alignmentSnap) {
        dragState.alignmentSnap = {};
      }
      dragState.alignmentSnap[previousKey] = {
        snappedX: result.snappedX,
        snappedY: result.snappedY,
        targetX: result.targetX,
        targetY: result.targetY,
      };
    }

    return result;
  };

  getSnappingPositionForDrag = (wallId: string, end: number, position: THREE.Vector2): THREE.Vector2 | null => {
    return getSnappingPoint(wallId, end, position, false);
  };

  getEdgeSnappingPositionForDrag = (wallId: string, endpoint: 'start' | 'end', position: THREE.Vector2, overrides?: EdgeSnappingSourceOverrides): EdgeSnappingPreviewResult | null => {
    return getEdgeSnappingPositionForDrag(wallId, endpoint, position, overrides);
  };

  snapToOtherObjectsEdgeBased = (sourceObject: string, snappingEnd: number, edge: THREE.Vector2, updateOtherEnd: boolean = true, options: { allowDuringDeferred?: boolean } = {}): boolean | SnappingResult => {
    return snapToOtherObjectsEdgeBasedFn(sourceObject, snappingEnd, edge, updateOtherEnd, options);
  };

  getAlignmentSnappingForDrag = (wallId: string, end: number, position: THREE.Vector2): THREE.Vector2 | null => {
    return getSnappingPointFromAlignmentLines(wallId, end, position);
  };

  cloneWall = (id: string, newPosition?: THREE.Vector2) => {
    const wall = this.wallsMap.get(id);
    if (wall) {
      // Make a deep copy of the wall
      const newWall = JSON.parse(JSON.stringify(wall));
      newWall.id = Math.random().toString(36).substr(2, 9);
      newWall.start = newPosition || wall.start.clone().add(new THREE.Vector2(0.2, 0.2));
      newWall.end = newPosition ? newPosition.clone().add(wall.end.clone().sub(wall.start)) : wall.end.clone().add(new THREE.Vector2(0.2, 0.2));
      newWall.centerConnections = [];
      newWall.symbolAttachments = [];
      newWall.clippingPolygons = [];
      this.addWall(newWall);
    }
  };
  cloneLine = (id: string, newPosition?: THREE.Vector2) => {
    const line = this.singleLinesMap.get(id);
    if (line) {
      // Make a deep copy of the wall
      const newLine = JSON.parse(JSON.stringify(line));
      newLine.id = Math.random().toString(36).substr(2, 9);
      newLine.start = newPosition || line.start.clone().add(new THREE.Vector2(0.2, 0.2));
      newLine.end = newPosition ? newPosition.clone().add(line.end.clone().sub(line.start)) : line.end.clone().add(new THREE.Vector2(0.2, 0.2));
      newLine.centerConnections = [];
      newLine.symbolAttachments = [];
      newLine.clippingPolygons = [];
      this.addSingleLine(newLine);
    }
  };
  cloneRuler = (id: string, newPosition?: THREE.Vector2) => {
    const line = this.rulerLinesMap.get(id);
    if (line) {
      // Make a deep copy of the wall
      const newLine = JSON.parse(JSON.stringify(line));
      newLine.id = Math.random().toString(36).substr(2, 9);
      newLine.start = newPosition || line.start.clone().add(new THREE.Vector2(0.2, 0.2));
      newLine.end = newPosition ? newPosition.clone().add(line.end.clone().sub(line.start)) : line.end.clone().add(new THREE.Vector2(0.2, 0.2));
      newLine.centerConnections = [];
      newLine.symbolAttachments = [];
      newLine.clippingPolygons = [];
      this.addRulerLine(newLine);
    }
  };
  getClippingPolygon = (symbol: SymbolType, wall: WallType): ClippingPolygonType => {
    const objectWidth = isSlidingDoorType(symbol)
      ? symbol.doorWidth * 2 + ((symbol.doorFrameWidth || this.doorFrameWidth) + this.convertLineWeightToWorld(wall.lineWeight || this.wallLineWeight)) * 2
      : isDoorType(symbol)
        ? symbol.doorWidth + ((symbol.doorFrameWidth || this.doorFrameWidth) + this.convertLineWeightToWorld(wall.lineWeight || this.wallLineWeight)) * 2
        : isDoubleDoorType(symbol)
          ? (symbol.doubleDoorWidth + (symbol.doorFrameWidth || this.doorFrameWidth)) * 2
          : isWindowType(symbol)
            ? symbol.windowLength
            : 0.8;

    const attachment = wall.symbolAttachments?.find((a) => a.symbolId === symbol.id);
    const posFromStart =
      attachment?.positionFromStart ??
      (() => {
        const wallDir = wall.end.clone().sub(wall.start).normalize();
        return symbol.position.clone().sub(wall.start).dot(wallDir);
      })();
    const insertGeom = getSymbolInsertGeometry(wall, symbol, posFromStart, this.doorFrameWidth);
    const objectLength = getSymbolRenderTotalWidth(wall, symbol, posFromStart, this.doorFrameWidth) / 2;
    const objectDirection = insertGeom.tangent.clone();
    const objectPositionOffset = insertGeom.midpoint.clone();

    const wallThickness = wall.wallWidth || this.wallWidth;
    const clippingDepth = Math.max(objectWidth, wallThickness * 4);

    const objectLengthVector = objectDirection.clone().multiplyScalar(objectLength);
    const objectWidthVector = objectDirection
      .clone()
      .rotateAround(new THREE.Vector2(0, 0), Math.PI / 2)
      .multiplyScalar(clippingDepth / 2);
    const topLeft = objectPositionOffset.clone().add(objectLengthVector).add(objectWidthVector);
    const topRight = objectPositionOffset.clone().add(objectLengthVector).sub(objectWidthVector);
    const bottomRight = objectPositionOffset.clone().sub(objectLengthVector).sub(objectWidthVector);
    const bottomLeft = objectPositionOffset.clone().sub(objectLengthVector).add(objectWidthVector);

    const clippingPolygon: ClippingPolygonType = {
      belongsTo: symbol.id,
      polygon: [new THREE.Vector2(topLeft.x, topLeft.y), new THREE.Vector2(bottomLeft.x, bottomLeft.y), new THREE.Vector2(bottomRight.x, bottomRight.y), new THREE.Vector2(topRight.x, topRight.y)],
      objectType: 'symbol',
    };
    return clippingPolygon;
  };

  unSelectAll = () => {
    runInAction(() => {
      Array.from(this.wallsMap.values()).forEach((wall) => {
        if (wall.selected) {
          wall.selected = false;
          this.wallsMap.set(wall.id, wall);
        }
      });

      Array.from(this.symbolsMap.values()).forEach((symbol) => {
        if (symbol.selected) {
          symbol.selected = false;
          this.symbolsMap.set(symbol.id, symbol);
        }
      });

      Array.from(this.singleLinesMap.values()).forEach((line) => {
        if (line.selected) {
          line.selected = false;
          this.singleLinesMap.set(line.id, line);
        }
      });
      Array.from(this.rulerLinesMap.values()).forEach((line) => {
        if (line.selected) {
          line.selected = false;
          this.rulerLinesMap.set(line.id, line);
        }
      });
      Array.from(this.rulers3DMap.values()).forEach((ruler) => {
        if (ruler.selected) {
          ruler.selected = false;
          this.rulers3DMap.set(ruler.id, ruler);
        }
      });
    });
  };

  unSelectObjects = (ids: string[]) => {
    if (ids.length === 0) return;
    runInAction(() => {
      ids.forEach((id) => {
        const wall = this.wallsMap.get(id);
        if (wall?.selected) {
          wall.selected = false;
          this.wallsMap.set(id, wall);
        }

        const symbol = this.symbolsMap.get(id);
        if (symbol?.selected) {
          symbol.selected = false;
          this.symbolsMap.set(id, symbol);
        }

        const line = this.singleLinesMap.get(id);
        if (line?.selected) {
          line.selected = false;
          this.singleLinesMap.set(id, line);
        }

        const ruler = this.rulerLinesMap.get(id);
        if (ruler?.selected) {
          ruler.selected = false;
          this.rulerLinesMap.set(id, ruler);
        }

        const ruler3D = this.rulers3DMap.get(id);
        if (ruler3D?.selected) {
          ruler3D.selected = false;
          this.rulers3DMap.set(id, ruler3D);
        }
      });
    });
  };

  selectWall = (id: string) => {
    const wall = this.wallsMap.get(id);
    if (wall) {
      wall.selected = true;
      this.wallsMap.set(id, wall);
    }
  };

  /**
   * Increment wall geometry version when wall is modified.
   * This invalidates cached geometry and triggers rebuild on next render.
   * Call this after ANY modification that affects wall geometry:
   * - Position changes (start, end, controlPoint)
   * - Width changes
   * - Connection changes
   * - Style changes that affect rendering
   */
  incrementWallGeometryVersion = (wallId: string) => {
    const wall = this.wallsMap.get(wallId);
    if (wall) {
      wall.geometryVersion = (wall.geometryVersion || 0) + 1;
      wall.lastUpdate = Date.now();
      this.wallsMap.set(wallId, wall);
    }
  };

  private resetWallGeometryAfterConnectionChange = (wallId: string): boolean => {
    const wall = this.wallsMap.get(wallId);
    if (!wall || !isWallType(wall)) {
      return false;
    }

    if (wall.cornerState) {
      delete wall.cornerState;
      this.wallsMap.set(wallId, wall);
    }

    renderStore.clearWallShapes(wallId);
    wallGeometryCache.clearWall(wallId);
    this.incrementWallGeometryVersion(wallId);
    return true;
  };

  selectSymbol = (id: string) => {
    const symbol = this.symbolsMap.get(id);
    if (symbol) {
      symbol.selected = true;
      this.symbolsMap.set(id, symbol);
    }
  };

  selectLine = (id: string) => {
    const line = this.singleLinesMap.get(id);
    if (line) {
      line.selected = true;
      this.singleLinesMap.set(id, line);
    }
  };

  selectRuler = (id: string) => {
    const line = this.rulerLinesMap.get(id);
    if (line) {
      line.selected = true;
      this.rulerLinesMap.set(id, line);
    }
  };

  selectRuler3D = (id: string) => {
    const ruler = this.rulers3DMap.get(id);
    if (ruler) {
      ruler.selected = true;
      this.rulers3DMap.set(id, ruler);
    }
  };

  normalizeCornerState = (raw: any, wall: Pick<WallType, 'start' | 'end' | 'lineForm'>): WallCornerPersistedMap | undefined => {
    if (!raw) {
      return undefined;
    }

    if (wall.lineForm === 'arc') {
      return undefined;
    }

    // Filter out center corners for backward compatibility with old saved data
    // With edge connections, walls are always 4-point rectangles

    const startSource: any = wall.start;
    const endSource: any = wall.end;

    const start = startSource instanceof THREE.Vector2 ? startSource.clone() : new THREE.Vector2(startSource?.x ?? 0, startSource?.y ?? 0);
    const end = endSource instanceof THREE.Vector2 ? endSource.clone() : new THREE.Vector2(endSource?.x ?? start.x, endSource?.y ?? start.y);

    const direction = end.clone().sub(start);
    const length = direction.length();
    if (length <= CORNER_EPSILON) {
      return undefined;
    }
    const dirNorm = direction.clone().normalize();
    const perp = new THREE.Vector2(-dirNorm.y, dirNorm.x);

    const convertPointToState = (point: { x: number; y: number }): PersistedCorner | undefined => {
      if (!Number.isFinite(point.x) || !Number.isFinite(point.y)) {
        return undefined;
      }
      const vector = new THREE.Vector2(point.x, point.y).sub(start);
      const s = vector.dot(dirNorm);
      const t = vector.dot(perp);
      if (!Number.isFinite(s) || !Number.isFinite(t)) {
        return undefined;
      }
      return { s, t };
    };

    const normalized: WallCornerPersistedMap = {};

    const assignCorner = (key: WallCornerKey | undefined, value: any) => {
      if (!key || value == null) {
        return;
      }

      if (typeof value.s === 'number' && typeof value.t === 'number') {
        normalized[key] = { s: value.s, t: value.t };
        return;
      }

      if (typeof value.x === 'number' && typeof value.y === 'number') {
        const converted = convertPointToState({ x: value.x, y: value.y });
        if (converted) {
          normalized[key] = converted;
        }
        return;
      }

      if (Array.isArray(value) && value.length >= 2) {
        const [x, y] = value;
        const converted = convertPointToState({ x: Number(x), y: Number(y) });
        if (converted) {
          normalized[key] = converted;
        }
        return;
      }
    };

    if (Array.isArray(raw)) {
      raw.forEach((entry, index) => assignCorner(PERSISTED_CORNER_KEYS[index], entry));
    } else if (typeof raw === 'object') {
      Object.entries(raw).forEach(([key, value]) => {
        if (PERSISTED_CORNER_KEYS.includes(key as WallCornerKey)) {
          assignCorner(key as WallCornerKey, value);
        }
      });
    } else {
      return undefined;
    }

    // Explicitly remove center corners for backward compatibility
    // (in case legacy data somehow bypassed the PERSISTED_CORNER_KEYS filter)
    delete (normalized as any).topCenter;
    delete (normalized as any).bottomCenter;

    return Object.keys(normalized).length > 0 ? normalized : undefined;
  };

  getDefaultCornerState = (wall: WallType): WallCornerPersistedMap | undefined => {
    if (wall.lineForm === 'arc') {
      return undefined;
    }

    const start = wall.start.clone();
    const direction = wall.end.clone().sub(start);
    const length = direction.length();
    if (length <= CORNER_EPSILON) {
      return undefined;
    }

    const width = wall.wallWidth ?? this.wallWidth;
    const halfWidth = width / 2;

    // Center corners are no longer supported - only 4 base corners
    return {
      topLeft: { s: 0, t: halfWidth },
      bottomLeft: { s: 0, t: -halfWidth },
      topRight: { s: length, t: halfWidth },
      bottomRight: { s: length, t: -halfWidth },
    };
  };

  private getSanitizedCornerState(wall: WallType): WallCornerPersistedMap | undefined {
    const normalized = this.normalizeCornerState(wall.cornerState, wall);
    const filtered = this.filterCornerStateForWall(normalized, wall);
    return filtered && Object.keys(filtered).length > 0 ? filtered : undefined;
  }

  private refreshCornerStateForWall(wall: WallType, persistToStore: boolean = false): void {
    const sanitized = this.getSanitizedCornerState(wall);
    const hadCornerState = !!wall.cornerState;

    if (sanitized) {
      wall.cornerState = sanitized;
    } else if (hadCornerState) {
      delete wall.cornerState;
    }

    if (persistToStore) {
      if (sanitized) {
        this.setObjectProperty(wall.id, 'cornerState', sanitized);
      } else if (hadCornerState) {
        this.setObjectProperty(wall.id, 'cornerState', undefined);
      }
    }
  }

  filterCornerStateForWall = (state: WallCornerPersistedMap | undefined, wall: WallType): WallCornerPersistedMap | undefined => {
    if (!state || wall.lineForm === 'arc') {
      return undefined;
    }

    const defaultState = this.getDefaultCornerState(wall);
    if (!defaultState) {
      return undefined;
    }

    const filtered: WallCornerPersistedMap = {};
    // Edge-based walls stay rectangular; persisted corner trimming is only for
    // legacy center connections that genuinely alter wall geometry. Corrupt
    // floorplans can retain stale centerConnections after a wall is detached;
    // those must not keep old cornerState alive because attachments are placed
    // from the wall's centerline while the wall body would render from stale
    // trimmed corners.
    const hasValidLegacyConnectionAtEndpoint = (endpoint: typeof WallConnectionStart | typeof WallConnectionEnd): boolean => {
      const sourcePoint = endpoint === WallConnectionStart ? wall.start : wall.end;
      return wall.centerConnections?.some((connection) => {
        if (connection.mode === 'edge' || connection.sourcePosition !== endpoint) {
          return false;
        }

        const targetObject = this.findObjectId(connection.id);
        if (!targetObject) {
          return false;
        }

        const targetPoint = getConnectionPoint(targetObject.start, targetObject.end, connection.targetPosition);
        return sourcePoint.distanceTo(targetPoint) <= LEGACY_CONNECTION_POINT_TOLERANCE;
      }) ?? false;
    };
    const hasStartConnection = hasValidLegacyConnectionAtEndpoint(WallConnectionStart);
    const hasEndConnection = hasValidLegacyConnectionAtEndpoint(WallConnectionEnd);

    const differsFromDefault = (key: WallCornerKey, corner: PersistedCorner | undefined): boolean => {
      if (!corner || typeof corner.s !== 'number' || typeof corner.t !== 'number') {
        return false;
      }
      const defaults = defaultState[key];
      if (!defaults || typeof defaults.s !== 'number' || typeof defaults.t !== 'number') {
        return true;
      }
      return Math.abs(corner.s - defaults.s) > CORNER_EPSILON || Math.abs(corner.t - defaults.t) > CORNER_EPSILON;
    };

    const cloneCorner = (corner: PersistedCorner): PersistedCorner => ({
      s: corner.s,
      t: corner.t,
    });

    const maybeStorePair = (firstKey: WallCornerKey, secondKey: WallCornerKey, enabled: boolean) => {
      if (!enabled) {
        return;
      }
      const first = state[firstKey];
      const second = state[secondKey];
      if (!first || !second || typeof first.s !== 'number' || typeof first.t !== 'number' || typeof second.s !== 'number' || typeof second.t !== 'number') {
        return;
      }
      if (differsFromDefault(firstKey, first) || differsFromDefault(secondKey, second)) {
        filtered[firstKey] = cloneCorner(first);
        filtered[secondKey] = cloneCorner(second);
      }
    };

    maybeStorePair('topLeft', 'bottomLeft', hasStartConnection);
    maybeStorePair('topRight', 'bottomRight', hasEndConnection);
    // Center corners are filtered out for backward compatibility

    return Object.keys(filtered).length > 0 ? filtered : undefined;
  };

  getCornerStateForApplication = (wall: WallType): WallCornerPersistedMap | undefined => {
    // CRITICAL: Always get fresh wall from store to ensure we have current connection state
    // This prevents stale wall references from providing outdated corner state
    const freshWall = this.wallsMap.get(wall.id) ?? wall;

    const normalized = this.normalizeCornerState(freshWall.cornerState, freshWall);
    const filtered = this.filterCornerStateForWall(normalized, freshWall);
    if (!filtered) {
      return undefined;
    }
    const cloned: WallCornerPersistedMap = {};
    Object.entries(filtered).forEach(([key, value]) => {
      if (value) {
        cloned[key as WallCornerKey] = { s: value.s, t: value.t };
      }
    });
    return Object.keys(cloned).length > 0 ? cloned : undefined;
  };

  extractCornerStateFromShape = (shape: WallShapeType): WallCornerPersistedMap | undefined => {
    if (shape.lineSide !== 'inner') {
      return undefined;
    }

    populateDefaultCorners(shape);

    const wall = this.wallsMap.get(shape.wall.id) ?? shape.wall;
    if (wall.lineForm === 'arc') {
      return undefined;
    }

    const start = wall.start.clone();
    const direction = wall.end.clone().sub(start);
    const length = direction.length();
    if (length <= CORNER_EPSILON) {
      return undefined;
    }
    const dirNorm = direction.clone().normalize();
    const perp = new THREE.Vector2(-dirNorm.y, dirNorm.x);

    const persisted: WallCornerPersistedMap = {};
    PERSISTED_CORNER_KEYS.forEach((key) => {
      const point = getCornerPoint(shape, key);
      if (!point) {
        return;
      }
      const vector = point.clone().sub(start);
      const s = vector.dot(dirNorm);
      const t = vector.dot(perp);
      if (Number.isFinite(s) && Number.isFinite(t)) {
        persisted[key] = { s, t };
      }
    });

    return Object.keys(persisted).length > 0 ? persisted : undefined;
  };

  updateWallCornerStateFromShape = (shape: WallShapeType): void => {
    if (shape.lineSide !== 'inner') {
      return;
    }

    const wall = this.wallsMap.get(shape.wall.id);
    if (!wall) {
      return;
    }

    const state = this.filterCornerStateForWall(this.extractCornerStateFromShape(shape), wall);
    if (state) {
      if (!this.cornerStatesEqual(wall.cornerState, state)) {
        wall.cornerState = state;
        this.wallsMap.set(wall.id, wall);
      }
    } else if (wall.cornerState) {
      delete wall.cornerState;
      this.wallsMap.set(wall.id, wall);
    }
  };

  getPersistedCornerState = (wall: WallType): WallCornerPersistedMap | undefined => {
    // CRITICAL: Always get fresh wall from store to ensure we have current connection state
    // This prevents stale cached shapes from providing outdated corner state
    const freshWall = this.wallsMap.get(wall.id) ?? wall;

    const innerShapes = renderStore.innerWallShapesMap.get(wall.id);
    if (innerShapes && innerShapes.length > 0) {
      const filtered = this.filterCornerStateForWall(this.extractCornerStateFromShape(innerShapes[0]), freshWall);
      if (filtered) {
        return JSON.parse(JSON.stringify(filtered)) as WallCornerPersistedMap;
      }
    }
    const normalized = this.normalizeCornerState(freshWall.cornerState, freshWall);
    const filtered = this.filterCornerStateForWall(normalized, freshWall);
    if (filtered) {
      return JSON.parse(JSON.stringify(filtered)) as WallCornerPersistedMap;
    }
    return undefined;
  };

  cornerStatesEqual = (left?: WallCornerPersistedMap, right?: WallCornerPersistedMap): boolean => {
    if (!left && !right) {
      return true;
    }
    if (!left || !right) {
      return false;
    }

    const keys = new Set<WallCornerKey>([...(Object.keys(left) as WallCornerKey[]), ...(Object.keys(right) as WallCornerKey[])]);

    const valuesEqual = (l?: PersistedCorner, r?: PersistedCorner): boolean => {
      if (!l && !r) {
        return true;
      }
      if (!l || !r) {
        return false;
      }
      if (typeof l.s === 'number' && typeof l.t === 'number' && typeof r.s === 'number' && typeof r.t === 'number') {
        return Math.abs(l.s - r.s) <= 1e-9 && Math.abs(l.t - r.t) <= 1e-9;
      }
      if (typeof l.x === 'number' && typeof l.y === 'number' && typeof r.x === 'number' && typeof r.y === 'number') {
        return Math.abs(l.x - r.x) <= 1e-9 && Math.abs(l.y - r.y) <= 1e-9;
      }
      return false;
    };

    for (const key of keys) {
      const l = left[key];
      const r = right[key];
      if (!valuesEqual(l, r)) {
        return false;
      }
    }

    return true;
  };

  private compactCatalogDictionary = (): FloorplanCatalogDictionary => {
    const referencedProducts = new Set<string>();
    const referencedSurfaces = new Set<string>();
    const referencedMaterials = new Set<string>();

    const collectSurfaceRef = (
      surfaceRef:
        | NonNullable<WallType["threeD"]>["surfaceRef"]
        | NonNullable<SymbolType["threeD"]>["surfaceRef"]
        | undefined,
    ) => {
      if (!surfaceRef?.id) return;
      if (surfaceRef.source === "material") {
        referencedMaterials.add(surfaceRef.id);
      } else if (surfaceRef.source === "product") {
        referencedProducts.add(surfaceRef.id);
      } else {
        referencedSurfaces.add(surfaceRef.id);
      }
    };

    this.wallsMap.forEach((wall) => {
      collectSurfaceRef(wall.threeD?.surfaceRef);
      // Per-face paints must keep their catalog entries too.
      collectSurfaceRef(wall.threeD?.faceMaterials?.left?.surfaceRef);
      collectSurfaceRef(wall.threeD?.faceMaterials?.right?.surfaceRef);
    });

    this.symbolsMap.forEach((symbol) => {
      if (symbol.threeD?.product?.productId) {
        referencedProducts.add(symbol.threeD.product.productId);
      }
      collectSurfaceRef(symbol.threeD?.surfaceRef);
      Object.values(symbol.threeD?.materialAssignments ?? {}).forEach(
        (assignment) => {
          if (assignment.productId) referencedProducts.add(assignment.productId);
          if (assignment.surfaceRef) {
            collectSurfaceRef(assignment.surfaceRef);
          } else if (assignment.materialId) {
            referencedMaterials.add(assignment.materialId);
          }
        },
      );
    });

    const filterEntries = <T extends Record<string, unknown>>(
      entries: T,
      ids: Set<string>,
    ): T =>
      Object.fromEntries(
        Object.entries(entries).filter(([id]) => ids.has(id)),
      ) as T;

    return {
      productsById: filterEntries(
        this.catalogDictionary.productsById,
        referencedProducts,
      ),
      surfacesById: filterEntries(
        this.catalogDictionary.surfacesById,
        referencedSurfaces,
      ),
      materialsById: filterEntries(
        this.catalogDictionary.materialsById,
        referencedMaterials,
      ),
    };
  };

  upsertCatalogDictionaryEntry = (
    kind: "product" | "surface" | "material",
    id: string,
    entry: FloorplanCatalogDictionary["productsById"][string],
  ) => {
    const target =
      kind === "product"
        ? this.catalogDictionary.productsById
        : kind === "surface"
          ? this.catalogDictionary.surfacesById
          : this.catalogDictionary.materialsById;
    // Skip undefined so partial re-applies (e.g. used-wallpaper stubs) do not
    // wipe cached price / brand / supplier / categories.
    const definedEntry = Object.fromEntries(
      Object.entries(entry).filter(([, value]) => value !== undefined),
    ) as FloorplanCatalogDictionary["productsById"][string];
    const nextEntry = {
      ...target[id],
      ...definedEntry,
      id,
      lastResolvedAt:
        entry.lastResolvedAt ?? target[id]?.lastResolvedAt ?? new Date().toISOString(),
    };
    if (JSON.stringify(target[id]) === JSON.stringify(nextEntry)) {
      return;
    }
    target[id] = nextEntry;
    this.setDirty();
  };

  updateThreeDMaterialDefault = (
    slot: keyof FloorplanThreeDMaterialDefaults,
    overrides: FloorplanThreeDMaterialDefaults[keyof FloorplanThreeDMaterialDefaults],
  ) => {
    this.threeDMaterialDefaults = {
      ...this.threeDMaterialDefaults,
      [slot]: {
        ...this.threeDMaterialDefaults[slot],
        ...overrides,
      },
    };
    this.setDirty();
  };

  toJSON = (excludeProperties: string[] | undefined = undefined) => {
    if (!excludeProperties) excludeProperties = [];
    excludeProperties.push('groupRef', 'lastUpdate', 'objectToRoomsMap', 'objectsNeedingRoomUpdate');
    const walls = Array.from(this.wallsMap.values()).map((wall) => {
      const wallCopy: Partial<WallType> = { ...wall };
      delete wallCopy.selected;
      if (excludeProperties) {
        excludeProperties.forEach((property) => {
          delete wallCopy[property as keyof WallType];
        });
      }
      // Serialize corner state - only 4 corners are persisted (topCenter/bottomCenter filtered out)
      const cornerState = this.getPersistedCornerState(wall);
      if (cornerState) {
        wallCopy.cornerState = cornerState;
      } else {
        delete wallCopy.cornerState;
      }
      // Serialize edge connections
      if (wall.edgeConnections) {
        wallCopy.edgeConnections = wall.edgeConnections.map((conn) => ({
          ...conn,
          sourceEdgePoint: {
            x: conn.sourceEdgePoint.x,
            y: conn.sourceEdgePoint.y,
          },
          targetEdgePoint: {
            x: conn.targetEdgePoint.x,
            y: conn.targetEdgePoint.y,
          },
        })) as WallEdgeConnection[];
      }
      return wallCopy;
    });
    const symbols = Array.from(this.symbolsMap.values()).map((symbol) => {
      const symbolCopy: Partial<SymbolType | DoorType | DoubleDoorType | WindowType | CircleStairsType | RectStairsType | SquareSymbolType | SvgType | TextType | AreaSymbolType | AreaFillSymbolType | BackdropImageType> = { ...symbol };
      delete symbolCopy.selected;
      if (excludeProperties) {
        excludeProperties.forEach((property) => {
          delete symbolCopy[property as keyof (SymbolType | DoorType | DoubleDoorType | WindowType | CircleStairsType | RectStairsType | SquareSymbolType | SvgType | TextType | AreaSymbolType | AreaFillSymbolType | BackdropImageType)];
        });
      }
      // Drop redundant catalog footprintSvg: svgPath already holds the 2D silhouette,
      // and the catalog re-supplies the SVG on hydrate. Deep-copy so the live symbol
      // is not mutated (shallow {...symbol} shares threeD/product/asset references).
      const productAsset = symbol.threeD?.product?.asset;
      if (productAsset?.footprintSvg !== undefined && symbol.threeD?.product) {
        symbolCopy.threeD = {
          ...symbol.threeD,
          product: {
            ...symbol.threeD.product,
            asset: omitProduct3DFootprintSvg(productAsset),
          },
        };
      }
      return symbolCopy;
    });
    const singleLines = Array.from(this.singleLinesMap.values()).map((line) => {
      const lineCopy: Partial<SingleLineType> = { ...line };
      delete lineCopy.selected;
      if (excludeProperties) {
        excludeProperties.forEach((property) => {
          delete lineCopy[property as keyof SingleLineType];
        });
      }
      return lineCopy;
    });
    const rulerLines = Array.from(this.rulerLinesMap.values()).map((line) => {
      const lineCopy: Partial<RulerLineType> = { ...line };
      delete lineCopy.selected;
      if (excludeProperties) {
        excludeProperties.forEach((property) => {
          delete lineCopy[property as keyof RulerLineType];
        });
      }
      return lineCopy;
    });
    const rulers3D = Array.from(this.rulers3DMap.values()).map((ruler) => {
      const rulerCopy: Partial<Ruler3DType> = { ...ruler };
      delete rulerCopy.selected;
      if (excludeProperties) {
        excludeProperties.forEach((property) => {
          delete rulerCopy[property as keyof Ruler3DType];
        });
      }
      return {
        ...rulerCopy,
        start: { x: ruler.start.x, y: ruler.start.y, z: ruler.start.z },
        end: { x: ruler.end.x, y: ruler.end.y, z: ruler.end.z },
      };
    });
    const rooms = Array.from(this.roomsMap.values()).map((room) => {
      const roomCopy: Partial<RoomType> = { ...room };
      delete roomCopy.selected;
      if (excludeProperties) {
        excludeProperties.forEach((property) => {
          delete roomCopy[property as keyof RoomType];
        });
      }
      return {
        ...roomCopy,
        centroid: { x: room.centroid.x, y: room.centroid.y },
        vertices: room.vertices?.map((vertex) => ({ x: vertex.x, y: vertex.y })),
      };
    });
    const groups = Array.from(this.groupsMap.values()).map((group) => {
      return { ...group };
    });
    const catalogDictionary = this.compactCatalogDictionary();

    return JSON.stringify({
      id: this.id,
      name: this.name,
      lighting: this.lighting,
      catalogDictionary,
      threeDMaterialDefaults: this.threeDMaterialDefaults,
      walls: walls,
      symbols: symbols,
      singleLines: singleLines,
      rulerLines: rulerLines,
      rulers3D: rulers3D,
      rooms: rooms,
      groups: groups,
      suppressedAutoAreaFillRoomSignatures: Array.from(this.suppressedAutoAreaFillRoomSignatures),
    });
  };

  private mergeEdgeConnectionsIntoCenterConnections = () => {
    const endpointToLegacyPosition = (endpoint: 'start' | 'end' | 'middle') => {
      if (endpoint === 'start') return WallConnectionStart;
      if (endpoint === 'end') return WallConnectionEnd;
      return WallConnectionMiddle;
    };

    this.wallsMap.forEach((wall) => {
      if (!wall.edgeConnections || wall.edgeConnections.length === 0) {
        return;
      }

      if (!wall.centerConnections) {
        wall.centerConnections = [];
      }

      wall.edgeConnections.forEach((conn) => {
        if (!conn?.targetWallId) {
          return;
        }
        const sourcePosition = endpointToLegacyPosition(conn.sourceEndpoint);
        const targetPosition = endpointToLegacyPosition(conn.targetEndpoint);
        const existing = wall.centerConnections?.find((connection) => connection.id === conn.targetWallId && connection.sourcePosition === sourcePosition && connection.targetPosition === targetPosition);

        if (existing) {
          if (existing.mode !== 'edge') {
            existing.mode = 'edge';
          }
        } else {
          wall.centerConnections!.push({
            id: conn.targetWallId,
            sourcePosition,
            targetPosition,
            mode: 'edge',
          });
        }

        const targetWall = this.wallsMap.get(conn.targetWallId);
        if (!targetWall) {
          return;
        }
        if (!targetWall.centerConnections) {
          targetWall.centerConnections = [];
        }
        const reciprocal = targetWall.centerConnections.find((connection) => connection.id === wall.id && connection.sourcePosition === targetPosition && connection.targetPosition === sourcePosition);
        if (reciprocal) {
          if (reciprocal.mode !== 'edge') {
            reciprocal.mode = 'edge';
          }
          return;
        }

        targetWall.centerConnections.push({
          id: wall.id,
          sourcePosition: targetPosition,
          targetPosition: sourcePosition,
          mode: 'edge',
        });
      });
    });
  };

  /**
   * Refresh serialized edge-point geometry after loading a floorplan.
   *
   * Persisted edgeConnections include precomputed edge points. Those points can
   * drift from the current arc endpoint math, which makes the first render show
   * small wedges until some interaction recomputes the connection. Rebuilding the
   * edge points here keeps load-time rendering consistent with post-click rendering.
   */
  private refreshLoadedEdgeConnections = () => {
    const inferSideFromSavedPoint = (
      wall: WallType,
      endpoint: 'start' | 'end' | 'middle',
      connectionAngle: number,
      savedPoint: THREE.Vector2,
      projectedPoint?: THREE.Vector2,
    ): 'left' | 'right' | undefined => {
      if (!Number.isFinite(savedPoint.x) || !Number.isFinite(savedPoint.y)) {
        return undefined;
      }

      const left = calculateWallEdgePoint(wall, endpoint, connectionAngle, undefined, projectedPoint, 'left').edgePosition;
      const right = calculateWallEdgePoint(wall, endpoint, connectionAngle, undefined, projectedPoint, 'right').edgePosition;
      return savedPoint.distanceTo(left) <= savedPoint.distanceTo(right) ? 'left' : 'right';
    };

    this.wallsMap.forEach((wall) => {
      if (!wall.edgeConnections || wall.edgeConnections.length === 0) {
        return;
      }

      wall.edgeConnections = wall.edgeConnections.map((connection) => {
        const sourceWall = this.wallsMap.get(connection.sourceWallId);
        const targetWall = this.wallsMap.get(connection.targetWallId);

        if (!sourceWall || !targetWall) {
          return connection;
        }

        let targetProjectedPoint: THREE.Vector2 | undefined;
        if (connection.targetEndpoint === 'middle') {
          const sourcePosition = connection.sourceEdgePoint?.clone
            ? connection.sourceEdgePoint.clone()
            : (connection.sourceEndpoint === 'start' ? sourceWall.start : sourceWall.end);
          const closestPosition = getClosestPosition(sourcePosition, targetWall);
          targetProjectedPoint =
            closestPosition.projectedPoint ??
            (closestPosition.position === 'start'
              ? targetWall.start.clone()
              : closestPosition.position === 'end'
                ? targetWall.end.clone()
                : undefined);
        }

        try {
          const sourceAngle = connection.targetEndpoint === 'middle'
            ? getWallAngle(targetWall)
            : getWallAngleAtEndpoint(targetWall, connection.targetEndpoint);
          const targetAngle = getWallAngleAtEndpoint(sourceWall, connection.sourceEndpoint);
          const sourceSide = connection.sourceSide ?? inferSideFromSavedPoint(
            sourceWall,
            connection.sourceEndpoint,
            sourceAngle,
            connection.sourceEdgePoint,
          );
          const targetSide = connection.targetSide ?? inferSideFromSavedPoint(
            targetWall,
            connection.targetEndpoint,
            targetAngle,
            connection.targetEdgePoint,
            targetProjectedPoint,
          );
          const refreshed = createEdgeConnection(
            sourceWall,
            connection.sourceEndpoint as 'start' | 'end',
            targetWall,
            connection.targetEndpoint,
            targetProjectedPoint,
            sourceSide || targetSide
              ? {
                  sourceSide,
                  targetSide,
                }
              : undefined,
          );

          refreshed.id = connection.id;
          if (connection.voidFill) {
            refreshed.voidFill = { ...connection.voidFill };
          }

          return refreshed;
        } catch (error) {
          console.warn('refreshLoadedEdgeConnections: Failed to recompute edge connection', {
            sourceWallId: connection.sourceWallId,
            targetWallId: connection.targetWallId,
            sourceEndpoint: connection.sourceEndpoint,
            targetEndpoint: connection.targetEndpoint,
            error: error instanceof Error ? error.message : error,
          });
          return connection;
        }
      });
    });
  };

  /**
   * Rebuild attached symbol positions from their wall attachment records after
   * deserialization. Persisted symbol coordinates can be stale, and arc walls
   * must use the shared straight-insert geometry instead of chord projection.
   */
  private refreshLoadedSymbolAttachments = () => {
    this.wallsMap.forEach((wall) => {
      if (!wall.symbolAttachments || wall.symbolAttachments.length === 0) {
        return;
      }

      wall.symbolAttachments.forEach((attachment) => {
        const symbol = this.symbolsMap.get(attachment.symbolId);
        if (!symbol) {
          return;
        }

        const insertGeom = getSymbolInsertGeometry(wall, symbol, attachment.positionFromStart, this.doorFrameWidth);
        const renderCenterOffset = getSymbolRenderCenterOffset(wall, symbol, attachment.positionFromStart, this.doorFrameWidth);
        const position = insertGeom.midpoint.clone().sub(insertGeom.tangent.clone().multiplyScalar(renderCenterOffset));

        this.symbolsMap.set(attachment.symbolId, {
          ...symbol,
          attachedTo: symbol.attachedTo ?? wall.id,
          position,
          rotation: Math.atan2(insertGeom.tangent.y, insertGeom.tangent.x) || 0.0001,
        });
      });
    });
  };

  fromJSON = (json: string) => {
    const parsedStructure = JSON.parse(json);
    if (!parsedStructure) {
      return;
    }

    const endpointToLegacyPosition = (endpoint: WallEdgeConnection['targetEndpoint']): number => {
      if (endpoint === 'start') {
        return WallConnectionStart;
      }
      if (endpoint === 'end') {
        return WallConnectionEnd;
      }
      return WallConnectionMiddle;
    };

    const isCenterConnectionBackedByEdge = (
      wall: WallType,
      connection: WallConnectionType,
      edgeConnections: WallEdgeConnection[] | undefined,
    ): boolean => {
      if (!edgeConnections || edgeConnections.length === 0) {
        return false;
      }

      return edgeConnections.some((edgeConnection) => (
        edgeConnection.sourceWallId === wall.id &&
        edgeConnection.targetWallId === connection.id &&
        endpointToLegacyPosition(edgeConnection.sourceEndpoint) === connection.sourcePosition &&
        endpointToLegacyPosition(edgeConnection.targetEndpoint) === connection.targetPosition
      ));
    };

    // Make mobx observable changes but batch them for all properties
    runInAction(() => {
      // Temporary 3D wall hide is session UI state; never carry it across plan loads.
      getEditorStore()?.clearHiddenWalls();
      this.wallsMap.clear();
      this.symbolsMap.clear();
      this.singleLinesMap.clear();
      this.rulerLinesMap.clear();
      this.rulers3DMap.clear();
      this.roomsMap.clear();
      this.groupsMap.clear();
      this.objectToRoomsMap.clear();
      this.roomsFound.clear();
      this.objectIdsInFoundRooms.clear();
      this.objectsNeedingRoomUpdate.clear();
      this.suppressedAutoAreaFillRoomSignatures.clear();
      this.invalidateRoomDetectionIndex();
      this.dirty = false;
      this.blockDirty = false;
      this.blockDirty = false;
      this.name = 'Untitled';
      if (parsedStructure.name) {
        this.name = parsedStructure.name;
      }
      this.lighting = {
        sunEnabled: typeof parsedStructure.lighting?.sunEnabled === 'boolean' ? parsedStructure.lighting.sunEnabled : true,
        sunTimeOfDay: THREE.MathUtils.clamp(typeof parsedStructure.lighting?.sunTimeOfDay === 'number' ? parsedStructure.lighting.sunTimeOfDay : 0.45, 0, 1),
        roomLightsEnabled: typeof parsedStructure.lighting?.roomLightsEnabled === 'boolean' ? parsedStructure.lighting.roomLightsEnabled : false,
        ceilingColor:
          typeof parsedStructure.lighting?.ceilingColor === 'number' &&
          Number.isFinite(parsedStructure.lighting.ceilingColor)
            ? parsedStructure.lighting.ceilingColor >>> 0
            : 0xd8d0c4,
      };
      this.catalogDictionary = normalizeCatalogDictionary(
        parsedStructure.catalogDictionary,
      );
      this.threeDMaterialDefaults =
        parsedStructure.threeDMaterialDefaults &&
        typeof parsedStructure.threeDMaterialDefaults === "object"
          ? { ...parsedStructure.threeDMaterialDefaults }
          : {};
      if (Array.isArray(parsedStructure.suppressedAutoAreaFillRoomSignatures)) {
        parsedStructure.suppressedAutoAreaFillRoomSignatures.forEach((signature: unknown) => {
          if (typeof signature === 'string' && signature.length > 0) {
            this.suppressedAutoAreaFillRoomSignatures.add(signature);
          }
        });
      }

      if (parsedStructure.walls) {
        parsedStructure.walls.forEach((wall: WallType) => {
          const parsedWall = wall as any;
          const normalizedCornerState = this.normalizeCornerState(parsedWall.cornerState, wall);
          const edgeConnections: WallEdgeConnection[] | undefined = Array.isArray(parsedWall.edgeConnections)
            ? (parsedWall.edgeConnections as any[]).map(
                (conn): WallEdgeConnection => ({
                  ...conn,
                  sourceEdgePoint: toVector2(conn?.sourceEdgePoint),
                  targetEdgePoint: toVector2(conn?.targetEdgePoint),
                }),
              )
            : undefined;

          // Migration: legacy `connections` -> `centerConnections` (keep `centerConnections` if present)
          const centerConnections = (parsedWall.centerConnections ?? parsedWall.connections ?? []) as WallConnectionType[];
          const hasLegacyCornerConnection = centerConnections.some((connection) => (
            connection.mode !== 'edge' &&
            !isCenterConnectionBackedByEdge(wall, connection, edgeConnections) &&
            (connection.sourcePosition === WallConnectionStart || connection.sourcePosition === WallConnectionEnd)
          ));
          const cornerStateForLoad = hasLegacyCornerConnection ? normalizedCornerState : undefined;

          // Strip clipboard/internal-only metadata that has leaked into persisted JSON in older versions.
          const { start, end, controlPoint, cutouts, cornerState, edgeConnections: edgeConnectionsIgnored, centerConnections: centerConnectionsIgnored, connections: legacyConnectionsIgnored, originalId, position: rawPosition, selectedConnections, selectedEdgeConnections, ...wallProps } = parsedWall;
          void start;
          void end;
          void controlPoint;
          void cutouts;
          void cornerState;
          void edgeConnectionsIgnored;
          void centerConnectionsIgnored;
          void legacyConnectionsIgnored;
          void originalId;
          const positionTuple = Array.isArray(rawPosition) && rawPosition.length === 2 && rawPosition.every((value: unknown) => typeof value === 'number' && Number.isFinite(value)) ? (rawPosition as [number, number]) : undefined;
          void selectedConnections;
          void selectedEdgeConnections;

          this.wallsMap.set(wall.id, {
            ...wallProps,
            id: wall.id,
            type: 'wall',
            ...(positionTuple ? { position: positionTuple } : {}),
            start: toVector2(wall.start),
            end: toVector2(wall.end),
            controlPoint: toOptionalVector2(wall.controlPoint),
            cutouts: wall.cutouts
              ? wall.cutouts.map((cutout: CutoutType) => ({
                  ...cutout,
                  appliesTo: new Set(cutout.appliesTo),
                }))
              : [],
            cornerState: cornerStateForLoad,
            edgeConnections,
            centerConnections,
          });
        });
      }

      // Walls already exist in 4-point format; legacy migration kept for historical reference
      // but no longer executed at load time.

      // Refresh persisted edge points before the first render so curved-wall joints
      // use current geometry without requiring a click-driven recompute.
      this.refreshLoadedEdgeConnections();

      // Lightweight mirror of edge connections into centerConnections for legacy consumers.
      // This avoids triggering expensive geometry updates during bulk load.
      this.mergeEdgeConnectionsIntoCenterConnections();

      if (parsedStructure.symbols) {
        parsedStructure.symbols.forEach((symbol: SymbolType | DoorType | DoubleDoorType | CircleStairsType | RectStairsType | SquareSymbolType | WindowType | SvgType | TextType | AreaSymbolType | AreaFillSymbolType | BackdropImageType) => {
          // Ensure position is valid
          const position = symbol.position ? new THREE.Vector2(Number.isFinite(symbol.position.x) ? symbol.position.x : 0, Number.isFinite(symbol.position.y) ? symbol.position.y : 0) : new THREE.Vector2(0, 0);
          // Strip clipboard/internal-only metadata that has leaked into persisted JSON in older versions.
          const parsedSymbol = symbol as any;
          const { originalId, attachedToOriginal, ...symbolProps } = parsedSymbol;
          void originalId;
          void attachedToOriginal;

          // Create symbol with validated position
          const newSymbol: SymbolType = {
            ...symbolProps,
            id: symbol.id,
            type: symbol.type,
            position,
            // Ensure rotation is valid
            rotation: Number.isFinite(Number(parsedSymbol.rotation)) ? Number(parsedSymbol.rotation) : 0.0001,
            // Ensure zIndex is valid
            zIndex: Number.isFinite(Number(parsedSymbol.zIndex)) ? Number(parsedSymbol.zIndex) : 0,
          };

          if ((symbol.type === 'area' || symbol.type === 'areaFill') && Array.isArray(parsedSymbol.vertices)) {
            (newSymbol as AreaSymbolType | AreaFillSymbolType).vertices = parsedSymbol.vertices.map((vertex: any) => new THREE.Vector2(Number.isFinite(vertex?.x) ? vertex.x : 0, Number.isFinite(vertex?.y) ? vertex.y : 0));
          }

          this.symbolsMap.set(symbol.id, newSymbol);

          const storedSymbol = this.symbolsMap.get(symbol.id);
          if (!storedSymbol) {
            return;
          }

          // If the object is a door, set some default properties
          if (isDoorType(storedSymbol)) {
            if (!(storedSymbol as DoorType).doorWidth) {
              (storedSymbol as DoorType).doorWidth = this.doorWidth;
            }
            if (!(storedSymbol as DoorType).openAngle) {
              (storedSymbol as DoorType).openAngle = this.openAngle;
            }
          }
          if (isSlidingDoorType(storedSymbol)) {
            if (!storedSymbol.doorWidth) {
              storedSymbol.doorWidth = this.doorWidth;
            }
            if (storedSymbol.openAngle === undefined || storedSymbol.openAngle === null) {
              storedSymbol.openAngle = 0;
            }
          }
          // If the object is a Double door, set some default properties
          if (isDoubleDoorType(storedSymbol)) {
            if (!(storedSymbol as DoubleDoorType).doubleDoorWidth) {
              (storedSymbol as DoubleDoorType).doubleDoorWidth = this.doubleDoorWidth;
            }
            if (!(storedSymbol as DoubleDoorType).openAngle) {
              (storedSymbol as DoubleDoorType).openAngle = this.openAngle;
            }
          }
          // If the object is a window, set some default properties
          if (isWindowType(storedSymbol)) {
            if (!(storedSymbol as WindowType).windowWidth) {
              (storedSymbol as WindowType).windowWidth = 0.2;
            }
            if (!(storedSymbol as WindowType).windowLength) {
              (storedSymbol as WindowType).windowLength = 1;
            }
          }
          // If the object is a Circle Stairs, set some default properties
          if (isCircleStairsType(storedSymbol)) {
            if (!(storedSymbol as CircleStairsType).circleStairsWidth) {
              (storedSymbol as CircleStairsType).circleStairsWidth = this.circleStairsWidth;
            }
            if (!(storedSymbol as CircleStairsType).openAngle) {
              (storedSymbol as CircleStairsType).openAngle = this.openAngle;
            }
            if (!(storedSymbol as CircleStairsType).stairStepSize) {
              (storedSymbol as CircleStairsType).stairStepSize = this.stairStepSize;
            }
          }
          // If the object is a Rectangle Stairs, set some default properties
          if (isRectStairsType(storedSymbol)) {
            if (!(storedSymbol as RectStairsType).rectStairsWidth) {
              (storedSymbol as RectStairsType).rectStairsWidth = this.rectStairsWidth;
            }
            if (!(storedSymbol as RectStairsType).rectStairsHeight) {
              (storedSymbol as RectStairsType).rectStairsHeight = this.rectStairsHeight;
            }
            if (!(storedSymbol as RectStairsType).openAngle) {
              (storedSymbol as RectStairsType).openAngle = this.openAngle;
            }
            if (!(storedSymbol as RectStairsType).stairStepSize) {
              (storedSymbol as RectStairsType).stairStepSize = this.stairStepSize;
            }
          }
          if (isTurnStairsType(storedSymbol)) {
            const turnStairs = storedSymbol as TurnStairsType;
            if (!turnStairs.stairShape) {
              turnStairs.stairShape = 'L';
            }
            if (!turnStairs.turnDirection) {
              turnStairs.turnDirection = 'right';
            }
            if (!turnStairs.stairWidth) {
              turnStairs.stairWidth = this.turnStairsWidth;
            }
            if (!turnStairs.firstFlightSteps) {
              turnStairs.firstFlightSteps = this.turnStairsFirstFlightSteps;
            }
            if (!turnStairs.secondFlightSteps) {
              turnStairs.secondFlightSteps = this.turnStairsSecondFlightSteps;
            }
            if (!turnStairs.landingDepth) {
              turnStairs.landingDepth = this.turnStairsLandingDepth;
            }
            if (!turnStairs.stairStepSize) {
              turnStairs.stairStepSize = this.stairStepSize;
            }
          }
          // If the object is a Square, set some default properties
          if (isSquareSymbolType(storedSymbol)) {
            if (!(storedSymbol as SquareSymbolType).squareWidth) {
              (storedSymbol as SquareSymbolType).squareWidth = this.squareWidth;
            }
            if (!(storedSymbol as SquareSymbolType).squareHeight) {
              (storedSymbol as SquareSymbolType).squareHeight = this.squareHeight;
            }
            if (!(storedSymbol as SquareSymbolType).openAngle) {
              (storedSymbol as SquareSymbolType).openAngle = this.openAngle;
            }
          }
          if (isPolygonSymbolType(storedSymbol)) {
            const polygonSymbol = storedSymbol as PolygonSymbolType;
            if (!polygonSymbol.polygonKind) {
              polygonSymbol.polygonKind = polygonSymbol.polygonSides === 5
                ? 'pentagon'
                : polygonSymbol.polygonSides === 8
                  ? 'octagon'
                  : 'hexagon';
            }
            if (!polygonSymbol.polygonSides) {
              polygonSymbol.polygonSides = polygonSymbol.polygonKind === 'pentagon'
                ? 5
                : polygonSymbol.polygonKind === 'octagon'
                  ? 8
                  : 6;
            }
            if (!polygonSymbol.polygonWidth) {
              polygonSymbol.polygonWidth = this.polygonWidth;
            }
            if (!polygonSymbol.polygonHeight) {
              polygonSymbol.polygonHeight = this.polygonHeight;
            }
            if (!polygonSymbol.openAngle) {
              polygonSymbol.openAngle = this.openAngle;
            }
          }

          // If the object is a Rectangle Stairs, set some default properties
          if (isTextType(storedSymbol)) {
            const textSymbol = storedSymbol as TextType;
            const rawTextSymbol = parsedSymbol as Partial<TextType>;
            const isLegacyTextSymbol = rawTextSymbol.fontFamily === undefined && rawTextSymbol.textAlign === undefined;
            const legacyFontSize = typeof textSymbol.fontSize === 'number' ? textSymbol.fontSize : undefined;

            // Legacy text symbols stored fontSize in pixels; normalize to world units.
            if (legacyFontSize !== undefined && legacyFontSize > 5) {
              textSymbol.fontSize = legacyFontSize / 100;
              if (!Number.isFinite(textSymbol.lineHeight) || textSymbol.lineHeight <= 0) {
                textSymbol.lineHeight = 1.2;
              }
            }

            // Legacy text symbols stored textBoxWidth/Height as absolute sizes.
            // Normalize to scale=1 to preserve original visual size.
            if (isLegacyTextSymbol) {
              textSymbol.textBoxWidth = this.textBoxWidth;
              textSymbol.textBoxHeight = this.textBoxHeight;
            }

            if (!Number.isFinite(textSymbol.textBoxWidth) || textSymbol.textBoxWidth <= 0) {
              textSymbol.textBoxWidth = this.textBoxWidth;
            }
            if (!Number.isFinite(textSymbol.textBoxHeight) || textSymbol.textBoxHeight <= 0) {
              textSymbol.textBoxHeight = this.textBoxHeight;
            }
            if (!textSymbol.fontFamily) {
              textSymbol.fontFamily = this.fontFamily;
            }
            if (!textSymbol.textAlign) {
              textSymbol.textAlign = this.textAlign;
            }
            if (!textSymbol.text) {
              textSymbol.text = this.text;
            }
            if (!Number.isFinite(textSymbol.fontSize) || textSymbol.fontSize <= 0) {
              textSymbol.fontSize = this.fontSize;
            }
            if (!textSymbol.fontStyle) {
              textSymbol.fontStyle = this.fontStyle;
            }
            if (!textSymbol.fontWeight) {
              textSymbol.fontWeight = this.fontWeight;
            }
            if (!Number.isFinite(textSymbol.lineHeight) || textSymbol.lineHeight <= 0) {
              textSymbol.lineHeight = this.lineHeight;
            }
          }
          // If the object is an SVG, set some default properties
          if (isSvgType(storedSymbol)) {
            const repairedSymbol = repairLoadedSvgSymbol(
              storedSymbol as SvgType,
              this.svgPath,
            );
            this.symbolsMap.set(
              storedSymbol.id,
              repairedSymbol,
            );
          }
          if (isSmartTableType(storedSymbol)) {
            const storedSmartTable = (storedSymbol as SmartTableType).smartTable;
            const inferredShape =
              (storedSmartTable as any)?.shape === 'round' ||
              storedSymbol.libraryKey === SMART_ROUND_TABLE_LIBRARY_KEY ||
              storedSymbol.threeD?.modelKey === SMART_ROUND_TABLE_MODEL_KEY
                ? 'round'
                : 'rectangle';
            const smartTable = normalizeSmartTableConfig(
              storedSmartTable && typeof storedSmartTable === 'object'
                ? { ...(storedSmartTable as any), shape: inferredShape }
                : { shape: inferredShape },
            );
            const currentThreeD = storedSymbol.threeD ?? {};
            this.symbolsMap.set(storedSymbol.id, {
              ...storedSymbol,
              smartTable,
              threeD: {
                ...createSmartTable3DMetadata(smartTable.shape),
                ...currentThreeD,
                sourceMode: "primitiveOnly",
                modelKey: createSmartTable3DMetadata(smartTable.shape).modelKey,
                height: normalizeSmartTableHeight(
                  currentThreeD.height ?? currentThreeD.dimensions?.height,
                ),
                product: undefined,
                userModel: undefined,
              },
            } as SmartTableType);
          }
          if (isBookShelfType(storedSymbol)) {
            const bookShelf = normalizeBookShelfConfig(
              (storedSymbol as BookShelfType).bookShelf,
            );
            const defaults3D = createBookShelf3DMetadata();
            const currentThreeD = storedSymbol.threeD ?? {};
            this.symbolsMap.set(storedSymbol.id, {
              ...storedSymbol,
              bookShelf,
              threeD: {
                ...defaults3D,
                ...currentThreeD,
                sourceMode: "primitiveOnly",
                modelKey: defaults3D.modelKey,
                height: bookShelf.height,
                dimensions: {
                  ...currentThreeD.dimensions,
                  width: bookShelf.width,
                  depth: bookShelf.depth,
                  height: bookShelf.height,
                },
                product: undefined,
                userModel: undefined,
              },
            } as BookShelfType);
          }
          if (isBackdropImageType(storedSymbol)) {
            const backdropImageSymbol = storedSymbol as BackdropImageType;
            backdropImageSymbol.backdropImagePath = backdropImageSymbol.backdropImagePath || this.backdropImagePath;
            backdropImageSymbol.backdropSize = backdropImageSymbol.backdropSize || this.backdropSize;
            backdropImageSymbol.backdropRatio = backdropImageSymbol.backdropRatio || this.backdropRatio;
          }
          if (isAreaSymbolType(storedSymbol) || isAreaFillSymbolType(storedSymbol)) {
            const areaSymbol = storedSymbol as AreaSymbolType | AreaFillSymbolType;
            areaSymbol.vertices = Array.isArray(areaSymbol.vertices) ? areaSymbol.vertices.map((vertex: any) => (vertex instanceof THREE.Vector2 ? vertex : new THREE.Vector2(Number.isFinite(vertex?.x) ? vertex.x : 0, Number.isFinite(vertex?.y) ? vertex.y : 0))) : [];
          }

          const normalizedStoredSymbol = this.symbolsMap.get(symbol.id);
          if (normalizedStoredSymbol) {
            this.symbolsMap.set(
              symbol.id,
              reconcileLoadedLibraryPrimitive3D(normalizedStoredSymbol),
            );
          }
        });
      }

      this.refreshLoadedSymbolAttachments();

      if (parsedStructure.singleLines) {
        parsedStructure.singleLines.forEach((line: SingleLineType) => {
          const parsedLine = line as any;
          // Migration: legacy `connections` -> `centerConnections` (keep `centerConnections` if present)
          const centerConnections = (parsedLine.centerConnections ?? parsedLine.connections ?? []) as WallConnectionType[];

          // Strip clipboard/internal-only metadata that has leaked into persisted JSON in older versions.
          const {
            start,
            end,
            controlPoint,
            connections,
            originalId,
            position: rawPosition,
            ...lineProps
          } = parsedLine as {
            start?: unknown;
            end?: unknown;
            controlPoint?: unknown;
            connections?: unknown;
            originalId?: unknown;
            position?: unknown;
            [key: string]: unknown;
          };
          void start;
          void end;
          void controlPoint;
          void connections;
          void originalId;
          const positionTuple = Array.isArray(rawPosition) && rawPosition.length === 2 && rawPosition.every((value: unknown) => typeof value === 'number' && Number.isFinite(value)) ? (rawPosition as [number, number]) : undefined;

          this.singleLinesMap.set(line.id, {
            ...lineProps,
            id: line.id,
            type: 'singleLine',
            ...(positionTuple ? { position: positionTuple } : {}),
            start: toVector2(line.start),
            end: toVector2(line.end),
            controlPoint: toOptionalVector2(line.controlPoint),
            centerConnections,
          });
        });
      }
      if (parsedStructure.rulerLines) {
        parsedStructure.rulerLines.forEach((line: RulerLineType) => {
          const parsedLine = line as any;
          // Migration: legacy `connections` -> `centerConnections` (keep `centerConnections` if present)
          const centerConnections = (parsedLine.centerConnections ?? parsedLine.connections ?? []) as WallConnectionType[];

          // Strip clipboard/internal-only metadata that has leaked into persisted JSON in older versions.
          const {
            start,
            end,
            controlPoint,
            connections,
            originalId,
            position: rawPosition,
            ...lineProps
          } = parsedLine as {
            start?: unknown;
            end?: unknown;
            controlPoint?: unknown;
            connections?: unknown;
            originalId?: unknown;
            position?: unknown;
            [key: string]: unknown;
          };
          void start;
          void end;
          void controlPoint;
          void connections;
          void originalId;
          const positionTuple = Array.isArray(rawPosition) && rawPosition.length === 2 && rawPosition.every((value: unknown) => typeof value === 'number' && Number.isFinite(value)) ? (rawPosition as [number, number]) : undefined;

          this.rulerLinesMap.set(line.id, {
            ...lineProps,
            id: line.id,
            type: 'rulerLine',
            ...(positionTuple ? { position: positionTuple } : {}),
            start: toVector2(line.start),
            end: toVector2(line.end),
            controlPoint: toOptionalVector2(line.controlPoint),
            centerConnections,
          });
        });
      }
      if (Array.isArray(parsedStructure.rulers3D)) {
        parsedStructure.rulers3D.forEach((ruler: Ruler3DType) => {
          const parsedRuler = ruler as any;

          // Strip clipboard/internal-only metadata that has leaked into persisted JSON in older versions.
          const {
            start,
            end,
            originalId,
            ...rulerProps
          } = parsedRuler as {
            start?: unknown;
            end?: unknown;
            originalId?: unknown;
            [key: string]: unknown;
          };
          void start;
          void end;
          void originalId;

          const startPoint = toVector3(ruler.start);
          this.rulers3DMap.set(ruler.id, {
            ...rulerProps,
            id: ruler.id,
            type: 'ruler3D',
            measurementMode: parsedRuler.measurementMode === 'free' ? 'free' : 'vertical',
            start: startPoint,
            end: toVector3(ruler.end, {
              x: startPoint.x,
              y: startPoint.y,
              z: startPoint.z + 2.4,
            }),
          });
        });
      }
      // Deserialize rooms
      if (parsedStructure.rooms) {
        parsedStructure.rooms.forEach((room: RoomType) => {
          if (room.objectIds) {
            // Filter out objectIds that do not exist in wallsMap or singleLinesMap
            const validObjectIds = room.objectIds.filter((objectId) => {
              return this.wallsMap.has(objectId) || this.singleLinesMap.has(objectId);
            });

            if (validObjectIds.length < 3) {
              // Not enough objects to form a valid room, skip this room
              return;
            }

            const deserializedRoom: RoomType = {
              ...room,
              centroid: new THREE.Vector2(room.centroid.x, room.centroid.y),
              vertices: Array.isArray(room.vertices) ? toVector2Array(room.vertices) : undefined,
              objectIds: validObjectIds,
              areaWorldUnits: room.areaWorldUnits,
            };

            // Recalculate centroid and area
            this.updateRoomProperties(deserializedRoom);

            this.roomsMap.set(room.id, deserializedRoom);
            this.registerPersistedRoom(deserializedRoom);
          }
        });
      }

      if (this.roomsMap.size === 0) {
        this.wallsMap.forEach((wall) => {
          this.markObjectForRoomUpdate(wall.id);
        });
        this.singleLinesMap.forEach((line) => {
          this.markObjectForRoomUpdate(line.id);
        });
        // Respect bulk-load deferral: room computation is scheduled when deferral is lifted.
        if (!this.deferRoomUpdates) {
          this.processRoomUpdates();
        }
      } else {
        this.discoverMissingRoomsFromLoadedGeometry();
      }

      // Reconstruct objectToRoomsMap
      this.wallsMap.forEach((wall) => {
        if (wall.roomIds) {
          wall.roomIds = wall.roomIds.filter((roomId) => this.roomsMap.has(roomId));
          wall.roomIds.forEach((roomId) => {
            if (!this.objectToRoomsMap.has(wall.id)) {
              this.objectToRoomsMap.set(wall.id, new Set<string>());
            }
            this.objectToRoomsMap.get(wall.id)!.add(roomId);
          });
        }
      });
      this.singleLinesMap.forEach((line) => {
        if (line.roomIds) {
          line.roomIds = line.roomIds.filter((roomId) => this.roomsMap.has(roomId));
          line.roomIds.forEach((roomId) => {
            if (!this.objectToRoomsMap.has(line.id)) {
              this.objectToRoomsMap.set(line.id, new Set<string>());
            }
            this.objectToRoomsMap.get(line.id)!.add(roomId);
          });
        }
      });
      // Update clipping polygons and regenerate outline segments for walls with symbol attachments
      // This must happen after symbols are fully loaded to ensure outline gaps are correctly calculated
      setTimeout(() => {
        renderStore?.clearClippings();
        const wallsWithAttachments: string[] = [];
        Array.from(this.wallsMap.values()).forEach((wall) => {
          if (wall.symbolAttachments && wall.symbolAttachments.length > 0) {
            wallsWithAttachments.push(wall.id);
          }
          wall.symbolAttachments?.forEach((symbolAttachment) => {
            const symbol = this.symbolsMap.get(symbolAttachment.symbolId);
            if (!symbol) return;
            const clippingPolygon = renderStore.getClippingPolygon(symbol, wall);
            if (clippingPolygon) {
              renderStore.addWallClipping(wall.id, clippingPolygon);
            }
          });
        });

        // Regenerate outline segments for walls with symbol attachments
        // This ensures the wall outlines have proper gaps for attached symbols (doors, windows, voids)
        // First clear any existing (stale) outline segments, then regenerate with updated symbol data
        wallsWithAttachments.forEach((wallId) => {
          renderStore.clearOutlineSegments(wallId);
          renderStore.generateOutlineSegments(wallId);
        });
      }, 0);

      // Load groups
      if (parsedStructure.groups) {
        parsedStructure.groups.forEach((group: GroupType) => {
          // Filter out any object IDs that don't exist in the current data
          const validObjectIds = group.objectIds.filter((id) => this.wallsMap.has(id) || this.symbolsMap.has(id) || this.singleLinesMap.has(id) || this.rulerLinesMap.has(id));

          // Import marker groups may contain one object so applying a draft can
          // remain idempotent after saving and reopening the floorplan.
          if (
            validObjectIds.length >= 2 ||
            (group.sourceImportId && validObjectIds.length >= 1)
          ) {
            this.groupsMap.set(group.id, {
              ...group,
              objectIds: validObjectIds,
            });
          }
        });
      }

      // Reconstruct objectToRoomsMap
      this.wallsMap.forEach((wall) => {
        if (wall.roomIds) {
          wall.roomIds.forEach((roomId) => {
            if (!this.objectToRoomsMap.has(wall.id)) {
              this.objectToRoomsMap.set(wall.id, new Set<string>());
            }
            this.objectToRoomsMap.get(wall.id)!.add(roomId);
          });
        }
      });
    });
  };

  ensureCloudFloorplanId = async (): Promise<string> => {
    if (this.id && this.loadedFloorplanId) {
      return this.id;
    }

    if (!this.client) {
      const editorClient = getEditorStore()?.client;
      if (editorClient) {
        this.client = editorClient;
      }
    }

    if (!this.client) {
      throw new Error('Apollo client not initialized');
    }

    const json = this.toJSON();
    const result = await this.client.mutate<EditFloorplanMutation>({
      variables: {
        name: this.name,
        json,
        id: this.id || '',
        image: null,
        currency: this.currency,
      },
      mutation: EditFloorplanDocument,
    });

    const savedFloorplan = result?.data?.editFloorplan;
    if (!savedFloorplan?.id) {
      throw new Error('Failed to create floorplan before uploading backdrop');
    }

    this.id = savedFloorplan.id;
    this.loadedFloorplanId = savedFloorplan.id;
    this.updatedAt = savedFloorplan.updatedAt ?? null;
    if (savedFloorplan.currency) {
      this.currency = normalizeCurrency(savedFloorplan.currency);
    }
    this.lastSavedJson = json;
    this.setDirty(false);
    this.persistStore(json);

    if (window.location.pathname === '/floorplan/') {
      window.history.replaceState({}, document.title, `/floorplan/${this.id}`);
    }

    return this.id;
  };

  saveToCloud = async (
    withSnapshotImage: boolean = false,
    force: boolean = false,
    retryCount: number = 3,
    retryDelay: number = 1000, // 1 second initial delay
  ) => {
    if (!force && (!this.dirty || this.blockDirty)) {
      return;
    }

    // Do not overwrite while another collaborator holds the edit lease.
    if (!force && (this.lockedByOther || this.collabAccessLost)) {
      return;
    }

    if (!force) {
      const urlFloorplanId = this.getFloorplanIdFromLocation();
      if (this.id && urlFloorplanId && this.id !== urlFloorplanId) {
        return;
      }
      if (this.loadedFloorplanId && this.id !== this.loadedFloorplanId) {
        return;
      }
      if (!this.loadedFloorplanId && this.id) {
        return;
      }
    }

    // Prevent overlapping executions
    if (this.isSaving) {
      return;
    }

    this.isSaving = true;
    try {
      const j = this.toJSON();
      // Skip save if content hasn't changed since last save
      if (!force && this.lastSavedJson && this.lastSavedJson === j) {
        this.isSaving = false;
        this.setDirty(false);
        return;
      }
      const id = this.id;
      const file = null;

      for (let attempt = 1; attempt <= retryCount; attempt++) {
        try {
          // Ensure apollo client exists before attempting mutation
          if (!this.client) {
            const editorClient = getEditorStore()?.client;
            if (editorClient) {
              this.client = editorClient;
            } else {
              console.warn('Skipping auto-save: Apollo client not initialized yet');
              this.isSaving = false;
              return;
            }
          }
          const result = await this.client.mutate({
            variables: {
              name: this.name,
              json: j,
              id: id,
              image: file,
              // Currency is written only by setCurrency so an in-flight
              // autosave cannot overwrite a newer choice. A brand-new plan
              // has no server row yet, so it must carry the local value.
              currency: id ? undefined : this.currency,
              // Simple take-over: lease is the only concurrency control. Never
              // send a version token — lease holders save like pre-collab.
              expectedUpdatedAt: null,
            },
            mutation: EditFloorplanWithVersionDocument,
          });
          const savedSnapshotIsCurrent = this.toJSON() === j;
          this.id = result?.data?.editFloorplan.id || '';
          this.loadedFloorplanId = this.id || null;
          this.noteServerUpdatedAt(result?.data?.editFloorplan.updatedAt);
          this.saveConflict = false;
          const currentJson = this.toJSON();
          this.lastSavedJson = savedSnapshotIsCurrent ? currentJson : j;
          this.setDirty(!savedSnapshotIsCurrent);
          this.persistStore(currentJson);
          this.isSaving = false;
          const path = window.location.pathname;
          if (path === '/floorplan/') {
            window.location.href = '/floorplan/' + this.id;
          }
          return; // Successfully saved, exit the function
        } catch (error: ApolloError | any) {
          // If the error is user not authenticated, redirect to login
          if (error.message === 'User not authenticated' || error.message === 'invalid_token') {
            const app_url = process.env.REACT_APP_CLIENT_URI || '';
            window.location.href = app_url + '/login' + '?redirect=' + window.location.href;
            return;
          }
          // Surface the server-side freemium caps in the paywall modal so the
          // user understands why the save was rejected. These codes are thrown
          // by assertUserCanCreateFloorplan / assertFloorplanWithinShapeLimit
          // on the server. We stop retrying — retries will not succeed.
          const errorCode =
            error?.graphQLErrors?.[0]?.extensions?.code ?? error?.code;
          if (errorCode === "MAX_FLOORPLANS_EXCEEDED") {
            getEditorStore()?.showPaywall("project");
            this.isSaving = false;
            return;
          }
          if (errorCode === "MAX_FLOORPLAN_SHAPES_EXCEEDED") {
            getEditorStore()?.showPaywall("shapes");
            this.isSaving = false;
            return;
          }
          if (errorCode === 'FLOORPLAN_LOCKED') {
            // Unshared plans have no collab UI / take-over. Do not enter
            // panning-only lock from a leftover lease.
            if (this.isShared) {
              const extensions =
                error?.graphQLErrors?.[0]?.extensions ?? {};
              this.setCollabLock({
                lockedByOther: true,
                editingByName:
                  (typeof extensions.lockedByUserName === 'string' &&
                    extensions.lockedByUserName) ||
                  this.editingByName,
                editingById:
                  (typeof extensions.lockedByUserId === 'string' &&
                    extensions.lockedByUserId) ||
                  this.editingById,
                isMine: false,
              });
            }
            this.isSaving = false;
            return;
          }
          if (errorCode === '403') {
            const message = String(error?.message || '');
            // Access can only be revoked on a shared plan. Unshared plans must
            // never enter the read-only collab state, same as FLOORPLAN_LOCKED
            // above — otherwise an unrelated 403 would freeze a solo canvas.
            if (
              this.isShared &&
              (message.includes('do not have access') ||
                message.includes('not allowed to edit'))
            ) {
              this.setCollabAccessLost();
              this.isSaving = false;
              return;
            }
          }
          if (attempt < retryCount) {
            console.warn(`Save failed, retrying (${attempt}/${retryCount})...`);
            await new Promise((res) => setTimeout(res, retryDelay));
            retryDelay *= 2; // Exponential backoff
          } else {
            console.error('Failed to save after multiple attempts:', error);
            this.isSaving = false;
            // Optionally notify the user of the failure
            // this.notifyUser("Failed to save floorplan. Please try again later.");
            throw error; // Re-throw the error after exhausting retries
          }
        }
      }
    } catch (error) {
      console.error('Failed to save floorplan:', error);
      this.isSaving = false;
    }
  };

  /**
   * Drop in-flight loadFromCloud results so they cannot apply after take-over
   * or a newer fetch has already started.
   */
  invalidateCloudLoads = () => {
    this.cloudLoadGeneration += 1;
  };

  private isCurrentCloudLoad = (generation: number, abortIfDirty?: boolean): boolean => {
    if (generation !== this.cloudLoadGeneration) {
      return false;
    }
    if (abortIfDirty && this.dirty) {
      return false;
    }
    return true;
  };

  /**
   * Loads a floorplan from the cloud.
   * @param abortIfDirty When true, skip applying the fetched JSON if the store
   *   became dirty while the request was in flight (avoids wiping concurrent edits).
   * @param preserveCamera When true, integrity repairs redraw geometry without
   *   resetting the camera. Collaborative reloads restore their saved camera.
   */
  loadFromCloud = async (
    id: string,
    options?: { abortIfDirty?: boolean; preserveCamera?: boolean },
  ): Promise<boolean> => {
    const generation = ++this.cloudLoadGeneration;
    const loadStartedAt = performance.now();
    try {
      this.setLoadingStep(2, 'Fetching floorplan from database');
      const fetchStartedAt = performance.now();
      const { data, error } = (await this.client?.query({
        query: FloorplanWithSharingDocument,
        variables: { id },
        fetchPolicy: 'network-only',
      })) || { data: { floorplan: null } };
      logLoadTiming('loadFromCloud fetch', fetchStartedAt, { id });

      // Handle authentication errors
      if (error?.message === 'User not authenticated' || error?.message === 'invalid_token') {
        const app_url = process.env.REACT_APP_CLIENT_URI || '';
        window.location.href = app_url + '/login' + '?redirect=' + window.location.href;
        return false;
      }

      if (!data.floorplan) {
        return false;
      }

      if (!this.isCurrentCloudLoad(generation, options?.abortIfDirty)) {
        return false;
      }

      // Store root-level backdrop properties from GraphQL response
      // These properties exist at the floorplan level and may be used to add/update backdrop images
      const backdropProps = {
        backdropImage: data.floorplan.backdropImage, // URL/path to the backdrop image
        backdropSize: data.floorplan.backdropSize, // Size in millimeters (e.g., 1000mm = 1m)
        backdropRatio: data.floorplan.backdropRatio, // Portion of image width the size represents (e.g., 0.5 means size is half the width)
      };

      this.setLoadingStep(3, 'Parsing floorplan data');
      const serializedFloorplan = data.floorplan.json;
      console.log('[load] floorplan json chars', serializedFloorplan?.length ?? 0);
      // Measure the JSON we actually loaded so the size banner fires for Lionbar-scale plans.
      this.notePersistedJsonSize(serializedFloorplan?.length ?? 0);

      if (!this.isCurrentCloudLoad(generation, options?.abortIfDirty)) {
        return false;
      }

      this.setLoadingStep(4, 'Loading walls and symbols');
      const fromJsonStartedAt = performance.now();
      runInAction(() => {
        // Defer room updates during bulk loading to avoid per-wall recalculation
        this.setDeferRoomUpdates(true);

        // Load the floorplan data and update store properties
        this.fromJSON(serializedFloorplan);
      });
      logLoadTiming('loadFromCloud fromJSON', fromJsonStartedAt, {
        walls: this.wallsMap.size,
        symbols: this.symbolsMap.size,
      });

      this.setLoadingStep(5, 'Running integrity check');
      const integrityStartedAt = performance.now();
      runInAction(() => {
        // Run integrity check immediately after loading data, while still in loading state
        // This ensures any fixes are applied before the floorplan becomes visible
        this.integrityCheck({
          resetCameraAfterRedraw: !options?.preserveCamera,
        });

        const previousId = this.id;
        this.id = data.floorplan.id;
        this.loadedFloorplanId = data.floorplan.id;
        this.name = data.floorplan.name;
        this.updatedAt = data.floorplan.updatedAt ?? null;
        this.currency = normalizeCurrency(data.floorplan.currency);
        this.dirty = false;
        this.blockDirty = false;
        this.saveConflict = false;
        this.lastSavedJson = serializedFloorplan;
        this.applySharingMetadata(data.floorplan);
        // Drop lease UI from the previous plan immediately; useFloorplanCollab
        // re-claims for the new id (and releases the old lease via held-id ref).
        if (previousId !== data.floorplan.id) {
          this.clearCollabLock();
        }
      });
      logLoadTiming('loadFromCloud integrityCheck', integrityStartedAt);

      this.setLoadingStep(6, 'Processing backdrop image');
      runInAction(() => {
        // Check for addBackdrop URL parameter to handle backdrop image updates
        const urlParams = new URLSearchParams(window.location.search);
        const addBackdrop = urlParams.get('addBackDrop');

        // Check if the project is empty (no walls, symbols, or lines)
        // This helps distinguish between a fresh project and one where the user manually deleted the backdrop
        const isProjectEmpty = this.wallsMap.size === 0 && this.symbolsMap.size === 0 && this.singleLinesMap.size === 0 && this.rulerLinesMap.size === 0 && this.rulers3DMap.size === 0;

        // Only process backdrop if we have an image AND (explicit parameter OR fresh project), and we're on the correct floorplan
        if ((addBackdrop || isProjectEmpty) && backdropProps.backdropImage && window.location.href.includes(data.floorplan.id)) {
          // Remove any existing backdrop images to prevent duplicates
          this.symbolsMap.forEach((symbol, key) => {
            if (isBackdropImageType(symbol)) {
              this.symbolsMap.delete(key);
            }
          });

          // Add the new backdrop image as a symbol
          // Position is set to [0, 0] as the backdrop should be centered
          // Size and ratio determine the physical dimensions in the scene
          this.addSymbol('backdropImage', [0, 0], {
            backdropImagePath: backdropProps.backdropImage,
            backdropSize: backdropProps.backdropSize ?? 1000, // Default to 1000mm if not specified
            backdropRatio: backdropProps.backdropRatio ?? 1, // Default to 1 (full width) if not specified
          });

          // Clean up URL parameters after processing
          window.history.replaceState({}, document.title, window.location.pathname);
        }

        // Reset undo/redo stacks and save initial state
        this.resetUndoRedoStacks({ skipPersist: true });
        this.pushToUndoStack({ skipPersist: true });

        // Re-enable room updates after bulk load. If rooms were missing in the JSON,
        // pending room calculations will be scheduled by setDeferRoomUpdates(false).
        this.setDeferRoomUpdates(false);
      });

      const persistStartedAt = performance.now();
      this.persistStore(serializedFloorplan);
      logLoadTiming('loadFromCloud persistStore', persistStartedAt);
      logLoadTiming('loadFromCloud total', loadStartedAt, { id });
      return true;
    } catch (error: any) {
      // Handle specific error cases
      if (error?.message === 'User not authenticated' || error?.message === 'invalid_token') {
        const app_url = process.env.REACT_APP_CLIENT_URI || '';
        window.location.href = app_url + '/login' + '?redirect=' + window.location.href;
        return false;
      } else if (error?.message === 'The floorplan was not found') {
        window.location.href = '/floorplan/';
        return false;
      }
      console.error('Failed to load floorplan:', error);
      return false;
    }
  };

  deleteFromCloud = async (id: string) => {
    await this.client?.mutate({
      mutation: DeleteFloorplanDocument,
      variables: { id },
    });
    const floorplans = await this.findFloorplans();
    // Prefer an owned floorplan — the list now includes shared plans.
    const owned = floorplans.filter(
      (fp: { id?: string; isOwner?: boolean | null }) =>
        fp?.id && fp.id !== id && fp.isOwner !== false,
    );
    const next = owned[0] ?? floorplans.find(
      (fp: { id?: string }) => fp?.id && fp.id !== id,
    );
    if (next?.id) {
      await this.loadFromCloud(next.id);
      window.history.replaceState({}, document.title, `/floorplan/${next.id}`);
    } else {
      this.loadDefaultFloorplan();
    }
  };

  findFloorplans = async () => {
    const { data } = (await this.client?.query({
      query: FloorplansWithSharingDocument,
      fetchPolicy: 'network-only',
    })) || { data: { floorplans: [] } };
    return data.floorplans;
  };

  /* Duplicate a floorplan
   * Will copy current floorplan and create a new floorplan with the same name + "copy" suffix then reload the new floorplan
   * @param id - id of the floorplan to duplicate
   */
  duplicateProject = async () => {
    try {
      if (!this.client) {
        console.error('Failed to duplicate floorplan: client not initialized');
        return;
      }
      const { data, error } = (await this.client?.query({
        query: FloorplanDocument,
        variables: { id: this.id },
      })) || { data: { floorplan: null } };
      if (error) {
        console.error('Failed to load current floorplan:', error);
        return;
      }
      const id = '';
      const floorplan = data.floorplan;
      const name = floorplan.name + ' copy';
      const json = floorplan.json;
      const result = await this.client?.mutate({
        mutation: EditFloorplanDocument,
        variables: {
          id,
          name,
          json,
          currency: normalizeCurrency(
            (floorplan as { currency?: string | null }).currency,
          ),
        },
      });
      if (result?.data?.editFloorplan) {
        window.location.href = '/floorplan/' + result.data.editFloorplan.id;
      }
    } catch (error: any) {
      console.error('Failed to duplicate floorplan:', error);
    }
  };

  // Group management methods
  createGroup = (objectIds: string[]): string => {
    // Another collaborator holds the edit lease, so this view is read-only.
    if (this.lockedByOther) return '';
    if (objectIds.length < 2) {
      console.warn('Cannot create group with less than 2 objects');
      return '';
    }

    // Remove any objects that are already in groups
    const filteredIds = objectIds.filter((id) => !this.getObjectGroup(id));

    if (filteredIds.length < 2) {
      console.warn('Not enough ungrouped objects to create a group');
      return '';
    }

    const groupId = this.generateId();
    const group: GroupType = {
      id: groupId,
      type: 'group',
      objectIds: [...filteredIds],
      created: Date.now(),
    };

    this.groupsMap.set(groupId, group);
    this.setDirty();
    return groupId;
  };

  getObjectGroup = (objectId: string): string | null => {
    for (const group of this.groupsMap.values()) {
      if (group.objectIds.includes(objectId)) {
        return group.id;
      }
    }
    return null;
  };

  getGroupObjects = (groupId: string): (WallType | SymbolType | SingleLineType | RulerLineType)[] => {
    const group = this.groupsMap.get(groupId);
    if (!group) return [];

    const objects: (WallType | SymbolType | SingleLineType | RulerLineType)[] = [];

    for (const objectId of group.objectIds) {
      const wall = this.wallsMap.get(objectId);
      if (wall) {
        objects.push(wall);
        continue;
      }

      const symbol = this.symbolsMap.get(objectId);
      if (symbol) {
        objects.push(symbol);
        continue;
      }

      const singleLine = this.singleLinesMap.get(objectId);
      if (singleLine) {
        objects.push(singleLine);
        continue;
      }

      const rulerLine = this.rulerLinesMap.get(objectId);
      if (rulerLine) {
        objects.push(rulerLine);
        continue;
      }

    }

    return objects;
  };

  ungroupObjects = (groupId: string): void => {
    // Another collaborator holds the edit lease, so this view is read-only.
    if (this.lockedByOther) return;
    const group = this.groupsMap.get(groupId);
    if (!group) return;

    this.groupsMap.delete(groupId);
    this.setDirty();
  };

  canObjectsBeGrouped = (objectIds: string[]): boolean => {
    if (objectIds.length < 2) return false;

    // Check if any of the objects are already in a group
    return !objectIds.some((id) => this.getObjectGroup(id) !== null);
  };

  isObjectInGroup = (objectId: string): boolean => {
    return this.getObjectGroup(objectId) !== null;
  };

  removeObjectFromGroups = (objectId: string): void => {
    for (const [groupId, group] of this.groupsMap.entries()) {
      const index = group.objectIds.indexOf(objectId);
      if (index >= 0) {
        group.objectIds.splice(index, 1);

        // If group has less than 2 objects, remove the group entirely
        if (group.objectIds.length < 2) {
          this.groupsMap.delete(groupId);
        }

        this.setDirty();
        break; // Object can only be in one group
      }
    }
  };

  triggerFullWallRedraw = async (): Promise<void> => {
    // Trigger a full redraw of all walls progressively to avoid blocking UI
    // Returns a Promise that resolves when all wall batches have been processed
    const startedAt = performance.now();
    const wallCount = this.wallsMap.size;
    await new Promise<void>((resolve) => {
      const allWallIds = Array.from(this.wallsMap.keys());
      const BATCH_SIZE = 10; // Process walls in batches
      let index = 0;

      const processBatch = () => {
        const endIndex = Math.min(index + BATCH_SIZE, allWallIds.length);

        for (let i = index; i < endIndex; i++) {
          this.objectNeedsUpdate(allWallIds[i]);
        }

        index = endIndex;

        // Continue processing remaining walls
        if (index < allWallIds.length) {
          if ('requestIdleCallback' in window) {
            (window as any).requestIdleCallback(processBatch, { timeout: 50 });
          } else {
            setTimeout(processBatch, 0);
          }
        } else {
          // All batches complete, resolve the promise
          resolve();
        }
      };

      // Start processing first batch immediately for faster initial feedback
      processBatch();
    });
    logLoadTiming('2D queue wall updates', startedAt, { walls: wallCount });

    const processStartedAt = performance.now();
    const { wallUpdateManager } = await import('../utils/wallUpdateManager');
    wallUpdateManager.initialize();
    await wallUpdateManager.processPendingUpdatesNow();
    logLoadTiming('2D processPendingUpdatesNow', processStartedAt, { walls: wallCount });
    logLoadTiming('2D triggerFullWallRedraw total', startedAt, { walls: wallCount });
  };
}

export const floorplannerStore = new FloorplannerStore();
export const FloorplannerStoreContext = createContext(floorplannerStore);
export const useFloorplannerStore = () => useContext(FloorplannerStoreContext);

gql`
  query floorplan($id: String!) {
    floorplan(id: $id) {
      id
      name
      json
      backdropImage
      backdropRatio
      backdropSize
      currency
      updatedAt
      isOwner
      user {
        id
        userName
        email
      }
      sharedWith {
        id
        userName
        email
      }
    }
  }
`;

gql`
  query floorplans {
    floorplans {
      id
      name
      json
      backdropImage
      backdropRatio
      backdropSize
      isOwner
      user {
        id
        userName
        email
      }
    }
  }
`;

gql`
  mutation editFloorplan(
    $id: ID!
    $name: String
    $json: String
    $image: Upload
    $currency: String
    $expectedUpdatedAt: DateTime
  ) {
    editFloorplan(
      input: {
        id: $id
        name: $name
        json: $json
        image: $image
        currency: $currency
        expectedUpdatedAt: $expectedUpdatedAt
      }
    ) {
      id
      name
      image
      thumbnail
      currency
      updatedAt
    }
  }
`;

gql`
  mutation deleteFloorplan($id: String!) {
    deleteFloorplan(id: $id) {
      id
    }
  }
`;
